import { Component, signal, HostListener, computed, ViewChildren, QueryList, ElementRef, AfterViewInit, AfterViewChecked, inject, OnInit, OnDestroy, WritableSignal, ViewChild, CUSTOM_ELEMENTS_SCHEMA, effect, untracked, type AnimationCallbackEvent, forwardRef } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { Subscription, forkJoin, firstValueFrom, of } from 'rxjs';
import { DASHBOARD_ANALYTICS_FIELD_DEFAULTS, DASHBOARD_ANALYTICS_FIELDS_STORAGE_KEY, type DashboardAnalyticsCardKey, normalizeDashboardAnalyticsFieldSelection } from './dashboard-analytics-fields';
import { PmLettersPageComponent } from '../../features/clients/pm-letters-page/pm-letters-page.component';
import { finalize, catchError, map, timeout } from 'rxjs/operators';
import { toast } from '../../shared/components/pm-toast/pm-toast';
import { type CustomFieldApiRow, PmSettingsApiService, type ClientCustomServiceApiRow, type ClientCustomServiceStepApi, type DefaultServiceApiRow, type ServiceAssignmentsMatrixPageApi, type ServiceAssignmentsMatrixRowApi, type ServicePackageApiRow, type ClientSavedFilterViewItemApi } from '../../core/services/api/pm-settings-api.service';
import { CLIENTS_CATALOG_FLAT_ORDERED, defaultClientsColumnVisibilityMap, type ClientsCatalogColumnDef, type ClientsCatalogFlatCol } from '../../features/clients/data/clients-column-catalog';
import { formatAmlCheckTypeLabel, formatCapiumPayStatusLabelForClient } from '../../features/clients/data/clients-list-display.util';
import type { PmToolbarAction, PmToolbarView } from '../../shared/components/pm-page-toolbar/pm-page-toolbar.types';
import { PmClientDetailPageComponent } from '../../features/clients/pm-client-detail-page/pm-client-detail-page.component';
import { PmSkeletonComponent } from '../../shared/components/pm-skeleton/pm-skeleton.component';
import { pmMessagePreviewText } from '../../shared/components/pm-message-preview/pm-message-preview.component';
import { PmSmoothScrollDirective } from '../../shared/directives/pm-smooth-scroll.directive';
import { resolvePortalClientUserDisplayName } from '../../core/utils/portal-client-user-display-name';
import { PmCapiumPayApiService } from '../../core/services/api/pm-capium-pay-api.service';
import { type CompaniesHouseSearchItemApi, type CompaniesHouseOfficerApi } from '../../core/services/api/pm-companies-house-api.service';
import { PmDirectoryApiService, type DirectoryClientRowApi, type DirectoryCommunicationRecipientApi, type DirectoryClientUserRowApi, type CreateDirectoryContactPayload, type DirectoryClientTypeApi, type DirectoryMasterDataItemApi, type DirectorRowApi, type ClientDraftApi, type DirectoryUnassignedClientRowApi, type Capium3ContactRowApi, type BulkImportClientsResponseApi } from '../../core/services/api/pm-directory-api.service';
import { isSelectableDirectoryClientRow } from '../../core/utils/directory-client-picker-options.util';
import { mapPracticeToClientStoreContactIds, practiceContactsLinkedToClient } from '../../core/utils/client-contact-links.util';
import { CLIENT_LEGACY_NON_VAT_REGISTERED_SCHEME_ID, defaultClientsAddYearEndIsoDate, isNonVatRegisteredScheme, normalizeClientIdvInput, normalizeClientPhoneInput, normalizeClientRegistrationNumberInput, normalizeClientUtrInput, normalizeClientVatRegInput, sanitizeClientsAddValidatedFields } from '../../core/utils/client-form-validators.util';
import { PmDirectoryClientsCacheService } from '../../core/services/pm-directory-clients-cache.service';
import { PmAmlApiService } from '../../core/services/api/pm-aml-api.service';
import { PmUserContextService } from '../../core/services/pm-user-context.service';
import { PmPermissionsAccessService } from '../../core/services/pm-permissions-access.service';
import { PmWorkspaceFabComponent } from './pm-workspace-fab.component';
import { PmCustomLabelsService, emptyRenamableLabels } from '../../core/services/pm-custom-labels.service';
import { PmFloatingMenuSettingsService, type FloatingMenuToolKey } from '../../core/services/pm-floating-menu-settings.service';
import { PmEventSchedulesApiService, type EventScheduleResponseApi } from '../../core/services/api/pm-event-schedules-api.service';
import { PmTemplateLibraryApiService, type ClientTypeLookupApiRow } from '../../core/services/api/pm-template-library-api.service';
import { PmClientCommunicationsApiService, type ClientCommunicationSummaryApi } from '../../core/services/api/pm-client-communications-api.service';
import { parseCommunicationAttachments } from '../../core/utils/communication-attachments.util';
import { PmSubmissionsApiService, type SubmissionsRowApi } from '../../core/services/api/pm-submissions-api.service';
import { PmDeadlinesApiService, type DeadlineResponseApi } from '../../core/services/api/pm-deadlines-api.service';
import { PmPracticeDashboardApiService, type PracticeDashboardOverviewApi, type PracticeAuditActivityRowApi, type PracticeDashboardQuickLinksApi, type PracticeDashboardClientSummaryApi } from '../../core/services/api/pm-practice-dashboard-api.service';
import { PmAnalyticsApiService, type DeadlineAnalyticsResponseApi, type TasksPipelineResponseApi } from '../../core/services/api/pm-analytics-api.service';
import { PmAuditLogsApiService, type PracticeAuditActivityPageApi } from '../../core/services/api/pm-audit-logs-api.service';
import { PmFinanceInvoicesApiService, type Capium3PracticeInvoiceKpiTotalsApi, type FinanceCapium3PracticeInvoiceRowApi, type FinancePracticeInvoicePageResponseApi, type PmInvoiceDetailRowApi, type PmInvoicePracticePageResponseApi } from '../../core/services/api/pm-finance-invoices-api.service';
import { findSearchPages } from '../../core/utils/pm-global-search-pages';
import { pmIntervalTimer } from '../../core/utils/pm-interval-timer.util';
import { type PmGlobalSearchItemApi } from '../../core/services/api/pm-global-search-api.service';
import { mapDeadlineResponseToDashboardRow } from '../../core/utils/deadline-periods.util';
import { normalizeReminderTimingToCascadeTitle, reminderCascadeTypeNameForTitle } from '../../core/utils/pm-reminder-cascade.util';
import { formatAppDate, todayIso, appTimeZoneOption, dateFieldsInAppZone, formatDayMonthInAppOrder, formatAppDateInZone } from '../../core/utils/pm-date-format.util';
import { buildActingUserProfileVm } from '../../core/utils/pm-acting-user-profile.util';
import { resolvePracticeActivityClientDisplayName } from '../../core/utils/pm-practice-activity.util';
import { type AuditLogCategory } from '../../core/utils/pm-audit-category.util';
import { type AmlCheckModalKind, type AmlClientPickerOption } from '../../features/aml/pm-aml-check-modal/pm-aml-check-modal.component';
import { PmPracticePageComponent } from '../../features/practice/pm-practice-page/pm-practice-page.component';
import { PmCrmPageComponent } from '../../features/crm/pm-crm-page/pm-crm-page.component';
import { PmTeamApiService, type TeamMemberRowApi } from '../../core/services/api/pm-team-api.service';
import { PmSmsTemplateApiService } from '../../core/services/api/pm-sms-template-api.service';
import { SCHEDULE_DAY_END_MINUTES, SCHEDULE_DURATION_PRESET_MINUTES, parseHHmmToMinutes, formatMinutesAsHHmm, parseScheduleMeetingInviteMeta } from '../../features/clients/schedule-meeting-shared';
import { PmProfileDirectorySyncService } from '../../core/services/pm-profile-directory-sync.service';
import { PmPermissionsApiService, type PermissionRoleMatrixApi } from '../../core/services/api/pm-permissions-api.service';
import { PmTaskManagementHubComponent } from '../../features/tasks/pm-task-management-hub/pm-task-management-hub.component';
import { PmTaskDetailsPageComponent } from '../../features/tasks/pm-task-details-page/pm-task-details-page.component';
import { PmDeadlinesManagementHubComponent } from '../../features/deadlines/pm-deadlines-management-hub/pm-deadlines-management-hub.component';
import { PmTaskModalService } from '../../features/tasks/pm-task-management-hub/pm-task-modal.service';
import { PmDeadlineModalService } from '../../features/deadlines/pm-deadlines-management-hub/pm-deadline-modal.service';
import { PmFinanceModalService } from '../../features/practice/components/pm-practice-finance/pm-finance-modal.service';
import { PmRecordTimeModalService } from '../../features/practice/components/pm-practice-record-time/pm-record-time-modal.service';
import { PmClientsAddFormBridgeService } from '../../core/services/pm-clients-add-form-bridge.service';
import { PmPracticeAnalyticsPageComponent } from '../../features/analytics/analytics-page.component';
import { PmPracticeSupportTicketsComponent } from '../../features/practice/components/pm-practice-support-tickets/pm-practice-support-tickets.component';
import { ReportsPageComponent } from '../../features/reports/listing/reports-page.component';
import { ReportRunnerComponent } from '../../features/reports/runner/report-runner.component';
import { PmNotificationsPageComponent } from '../../features/notifications/pm-notifications-page/pm-notifications-page.component';
import { PmAnnouncementsPageComponent } from '../../features/announcements/pm-announcements-page/pm-announcements-page.component';
import { AnnouncementListItemApi, PmAnnouncementsApiService } from '../../core/services/api/pm-announcements-api.service';
import { PmProfilePageComponent } from '../../features/profile/pm-profile-page/pm-profile-page.component';
import { PmForgotPasswordPageComponent } from '../../features/profile/pm-forgot-password-page/pm-forgot-password-page.component';
import type {
  NavItem,
  DashboardDnDScope,
  DeadlineData,
  DashboardInvoicePreviewRow,
  Quill,
  QuillModules,
  DashboardActivityActionLabel,
  ActivityItem,
  NewServiceForm,
  ClientsFilterStatus,
  ClientImportPreviewRow,
  ClientsUsersImportPreviewRow,
  ClientsUsersImportDraftRow,
  CatalogServiceVm,
  AssignmentColumnServiceVm,
  DefaultServiceTemplateEntry,
  CustomAddedService,
  AssignClientRow,
  PackageGridRow,
  ReminderItem,
  AutomationSettingsVm,
  CustomFieldType,
  CustomFieldItem,
  RenamableLabel,
  BookMeetingInviteOption,
  ClientListRow,
  ClientUserListRow,
  ClientsWorkspaceSection,
  ClientsScheduleMeeting,
  ClientsCommunicationPriority,
  ClientsCommunicationStatus,
  ClientsCommunicationRow,
  ClientsEmailToOption,
  ClientsSubmissionsRow,
  ClientsSubmissionsSortKey,
  ClientsLetterTemplateRow,
  ClientsLetterSentRow,
  ClientsComplianceCheckType,
  ClientsComplianceResult,
  ClientsAmlCheckRow,
  ClientsAddFormDraft,
  ClientsAddContactDraft,
  ClientsAddContactOption,
  ClientsAddDraftStoragePayload,
  ClientsAddPortalUserOption,
  ClientsAddPendingPortalUserDraft,
  ClientsAddPortalContactOption,
  ClientsAddDirectorDraft,
  ClientsAddPanelKey,
  CreateClientUserFormDraft,
  CreateClientUserContactOption,
  ClientDetailActionItem,
  ClientDetailTimelineRow
} from './pm-workspace.models';
import type { OnboardingKycClientType, RiskAssessmentClientType } from '../../features/settings/template-library/pm-template-library.models';
import {
  PM_SCHEDULE_PRACTICE_INTERNAL_UNIVERSAL_ID,
  STAFF_NAV_ALLOWLIST,
  NAV_LABEL_TO_PERMISSION_SECTION_KEY,
  getDefaultClientTypesForServiceName,
  isoDateLocalFrom,
  CLIENTS_ADD_DRAFT_STORAGE_KEY,
  CLIENTS_ADD_DATE_FIELD_KEYS,
  normalizeClientsAddDateInput,
  CLIENTS_ADD_ACCOUNTING_SYSTEM_OPTIONS,
  CREATE_CLIENT_USER_DIRECTORY_CONTACT_DROPDOWN_ID,
  CREATE_CLIENT_USER_PORTAL_CONTACT_DROPDOWN_ID,
  CLIENTS_ADD_SOURCE_OPTIONS,
  normalizeClientsAddCustomFieldValuesRecord,
  emptyDirectorDraft,
  emptyClientsAddContactDraft,
  emptyClientsAddDraft,
  normalizeServiceNameKey,
  canonicalClientTypeLabel,
  emptyCreateClientUserFormDraft,
  contactInitialsFromName,
  hashPickTone,
  mapAmlPerformCheckApiToRow,
  formatDirectoryActivity
} from './pm-workspace.utils';
import { PmTemplateLibraryContextService } from '../../features/settings/template-library/pm-template-library-context.service';
import { templateLibraryRawToHtml, templateLibraryPreparePreviewHtml } from '../../features/settings/template-library/pm-template-library.util';
import { PmBrandingStateService } from '../../features/settings/pm-settings-branding/pm-branding-state.service';
import { PmCompaniesHouseClientsPageComponent } from '../../features/companies-house/pm-companies-house-clients-page/pm-companies-house-clients-page.component';
import { PmCompaniesHouseClientsStore } from '../../features/companies-house/pm-companies-house-clients-page/pm-companies-house-clients.store';
import { PmDocumentHubPageComponent } from '../../features/document-hub/pm-document-hub-page/pm-document-hub-page.component';
import { PmDocumentHubStore } from '../../features/document-hub/pm-document-hub-page/pm-document-hub.store';
import { PmCapisignComingSoonComponent } from '../../features/capisign/pm-capisign-coming-soon/pm-capisign-coming-soon.component';
import { PmDashboardPageComponent } from '../../features/dashboard/pm-dashboard-page/pm-dashboard-page.component';
import { PM_DASHBOARD_HOST } from '../../features/dashboard/pm-dashboard-page/pm-dashboard-host';
import { PmClientsSubmissionsComponent } from '../../features/clients/pm-clients-submissions/pm-clients-submissions.component';
import { PM_CLIENTS_SUBMISSIONS_HOST } from '../../features/clients/pm-clients-submissions/pm-clients-submissions-host';
import { PmClientsAmlComponent } from '../../features/clients/pm-clients-aml/pm-clients-aml.component';
import { PM_CLIENTS_AML_HOST } from '../../features/clients/pm-clients-aml/pm-clients-aml-host';
import { PmClientsCommunicationComponent } from '../../features/clients/pm-clients-communication/pm-clients-communication.component';
import { PM_CLIENTS_COMMUNICATION_HOST } from '../../features/clients/pm-clients-communication/pm-clients-communication-host';
import { PmClientsScheduleComponent } from '../../features/clients/pm-clients-schedule/pm-clients-schedule.component';
import { PM_CLIENTS_SCHEDULE_HOST } from '../../features/clients/pm-clients-schedule/pm-clients-schedule-host';
import { PmClientsUnassignedComponent } from '../../features/clients/pm-clients-unassigned/pm-clients-unassigned.component';
import { PM_CLIENTS_UNASSIGNED_HOST } from '../../features/clients/pm-clients-unassigned/pm-clients-unassigned-host';
import { PmClientsDraftsComponent } from '../../features/clients/pm-clients-drafts/pm-clients-drafts.component';
import { PM_CLIENTS_DRAFTS_HOST } from '../../features/clients/pm-clients-drafts/pm-clients-drafts-host';
import { PmClientsUsersComponent } from '../../features/clients/pm-clients-users/pm-clients-users.component';
import { PM_CLIENTS_USERS_HOST } from '../../features/clients/pm-clients-users/pm-clients-users-host';
import { PmClientsDirectoryComponent } from '../../features/clients/pm-clients-directory/pm-clients-directory.component';
import { PM_CLIENTS_DIRECTORY_HOST } from '../../features/clients/pm-clients-directory/pm-clients-directory-host';
import { PmClientsToolbarComponent } from '../../features/clients/pm-clients-toolbar/pm-clients-toolbar.component';
import { PM_CLIENTS_TOOLBAR_HOST } from '../../features/clients/pm-clients-toolbar/pm-clients-toolbar-host';
import { PmClientsAddFlowComponent } from '../../features/clients/pm-clients-add-flow/pm-clients-add-flow.component';
import { PM_CLIENTS_ADD_FLOW_HOST } from '../../features/clients/pm-clients-add-flow/pm-clients-add-flow-host';
import { PmClientsImportModalComponent } from '../../features/clients/pm-clients-import-modal/pm-clients-import-modal.component';
import { PM_CLIENTS_IMPORT_MODAL_HOST } from '../../features/clients/pm-clients-import-modal/pm-clients-import-modal-host';
import { PmClientsListPopupsComponent } from '../../features/clients/pm-clients-list-popups/pm-clients-list-popups.component';
import { PM_CLIENTS_LIST_POPUPS_HOST } from '../../features/clients/pm-clients-list-popups/pm-clients-list-popups-host';
import { PmClientsCapiumPayInviteComponent } from '../../features/clients/pm-clients-capium-pay-invite/pm-clients-capium-pay-invite.component';
import { PM_CLIENTS_CAPIUM_PAY_INVITE_HOST } from '../../features/clients/pm-clients-capium-pay-invite/pm-clients-capium-pay-invite-host';
import { PM_SETTINGS_SERVICES_HOST } from '../../features/settings/pm-settings-services/pm-settings-services-host';
import { PM_SETTINGS_CUSTOM_FIELDS_HOST } from '../../features/settings/pm-settings-custom-fields/pm-settings-custom-fields-host';
import { PmSettingsPageComponent } from '../../features/settings/pm-settings-page/pm-settings-page.component';
import { PM_SETTINGS_PAGE_HOST } from '../../features/settings/pm-settings-page/pm-settings-page-host';
import { PmShellQuickAddComponent } from '../../features/shell/pm-shell-quick-add/pm-shell-quick-add.component';
import { PM_SHELL_QUICK_ADD_HOST } from '../../features/shell/pm-shell-quick-add/pm-shell-quick-add-host';
import { PmShellAppLauncherComponent } from '../../features/shell/pm-shell-app-launcher/pm-shell-app-launcher.component';
import { PM_SHELL_APP_LAUNCHER_HOST } from '../../features/shell/pm-shell-app-launcher/pm-shell-app-launcher-host';
import { PmShellProfileMenuComponent } from '../../features/shell/pm-shell-profile-menu/pm-shell-profile-menu.component';
import { PM_SHELL_PROFILE_MENU_HOST } from '../../features/shell/pm-shell-profile-menu/pm-shell-profile-menu-host';
import { PmShellGlobalSearchComponent } from '../../features/shell/pm-shell-global-search/pm-shell-global-search.component';
import { PM_SHELL_GLOBAL_SEARCH_HOST } from '../../features/shell/pm-shell-global-search/pm-shell-global-search-host';
import { PmShellTopBarComponent } from '../../features/shell/pm-shell-top-bar/pm-shell-top-bar.component';
import { PM_SHELL_TOP_BAR_HOST } from '../../features/shell/pm-shell-top-bar/pm-shell-top-bar-host';
import { PmClientsSubnavComponent } from '../../features/clients/pm-clients-subnav/pm-clients-subnav.component';
import { PM_CLIENTS_SUBNAV_HOST } from '../../features/clients/pm-clients-subnav/pm-clients-subnav-host';

import { pmStorageGet, pmStorageRemove, pmStorageSet } from '../../core/utils/pm-storage.util';
import { pmHtmlToText, pmSanitizeHtml } from '../../core/utils/pm-sanitize-html.util';
@Component({
  selector: 'pm-workspace',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    PmClientsSubnavComponent,
    PmShellTopBarComponent,
    PmShellGlobalSearchComponent,
    PmShellProfileMenuComponent,
    PmShellAppLauncherComponent,
    PmShellQuickAddComponent,
    PmSettingsPageComponent,
    PmClientsCapiumPayInviteComponent,
    PmClientsListPopupsComponent,
    PmClientsImportModalComponent,
    PmClientsAddFlowComponent,
    PmClientsToolbarComponent,
    PmClientsDirectoryComponent,
    PmClientsUsersComponent,
    PmClientsDraftsComponent,
    PmClientsUnassignedComponent,
    PmClientsScheduleComponent,
    PmClientsCommunicationComponent,
    PmClientsAmlComponent,
    PmClientsSubmissionsComponent,
    PmDashboardPageComponent,
    PmCapisignComingSoonComponent,
    PmDocumentHubPageComponent,
    PmCompaniesHouseClientsPageComponent,
    PmSmoothScrollDirective,
    PmClientDetailPageComponent,
    NgTemplateOutlet,
    PmPracticePageComponent,
    PmCrmPageComponent,
    PmTaskManagementHubComponent,
    PmTaskDetailsPageComponent,
    PmDeadlinesManagementHubComponent,
    PmPracticeAnalyticsPageComponent,
    PmPracticeSupportTicketsComponent,
    ReportsPageComponent,
    ReportRunnerComponent,
    PmNotificationsPageComponent,
    PmAnnouncementsPageComponent,
    PmProfilePageComponent,
    PmForgotPasswordPageComponent,
    PmLettersPageComponent,
    PmWorkspaceFabComponent,
    PmSkeletonComponent
  ],
  providers: [PmTemplateLibraryContextService, PmBrandingStateService, PmCompaniesHouseClientsStore, PmDocumentHubStore, { provide: PM_DASHBOARD_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_CLIENTS_SUBMISSIONS_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_CLIENTS_AML_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_CLIENTS_COMMUNICATION_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_CLIENTS_SCHEDULE_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_CLIENTS_UNASSIGNED_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_CLIENTS_DRAFTS_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_CLIENTS_USERS_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_CLIENTS_DIRECTORY_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_CLIENTS_TOOLBAR_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_CLIENTS_ADD_FLOW_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_CLIENTS_IMPORT_MODAL_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_CLIENTS_LIST_POPUPS_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_CLIENTS_CAPIUM_PAY_INVITE_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_SETTINGS_SERVICES_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_SETTINGS_CUSTOM_FIELDS_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_SETTINGS_PAGE_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_SHELL_QUICK_ADD_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_SHELL_APP_LAUNCHER_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_SHELL_PROFILE_MENU_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_SHELL_GLOBAL_SEARCH_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_SHELL_TOP_BAR_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }, { provide: PM_CLIENTS_SUBNAV_HOST, useExisting: forwardRef(() => PmWorkspaceComponent) }],
  templateUrl: './pm-workspace.component.html',
  styleUrls: [
    './pm-workspace.component.css',
    '../../shared/components/popups/pm-form-field.css',
    '../../shared/components/popups/pm-add-client-modal/pm-add-client-modal.component.css'
  ]
})
export class PmWorkspaceComponent implements AfterViewInit, AfterViewChecked, OnInit, OnDestroy {
  private readonly lastClientsRouteHydrationLoadKey = signal('');
  readonly lastClientsUsersHydrationLoadKey = signal('');
  private readonly lastClientsSavedViewsHydrationLoadKey = signal('');
  private readonly lastDashboardHydrationLoadKey = signal('');
  private readonly lastCustomLabelsHydrationLoadKey = signal('');
  private readonly lastFloatingMenuHydrationLoadKey = signal('');
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly router = inject(Router);
  readonly pmCustomLabels = inject(PmCustomLabelsService);
  private readonly floatingMenuSettings = inject(PmFloatingMenuSettingsService);
  readonly pmSettingsApi = inject(PmSettingsApiService);
  readonly pmDirectoryApi = inject(PmDirectoryApiService);
  readonly directoryClientsCache = inject(PmDirectoryClientsCacheService);
  private readonly capiumPayApi = inject(PmCapiumPayApiService);
  private readonly pmEventSchedulesApi = inject(PmEventSchedulesApiService);
  private readonly pmAmlApi = inject(PmAmlApiService);
  private readonly pmTemplateLibraryApi = inject(PmTemplateLibraryApiService);
  private readonly templateLibraryContext = inject(PmTemplateLibraryContextService);
  /** Template ref, so the page class is only used inside its @defer block (keeps it lazy). */
  @ViewChild('documentHubPage') documentHubPage?: PmDocumentHubPageComponent;
  /** Document Hub page state; the workspace resets the open folder / search when the URL changes. */
  readonly docHubStore = inject(PmDocumentHubStore);
  /** The settings sections now live inside the Settings page holder; these reach them through it. */
  @ViewChild('settingsPage') private settingsPage?: PmSettingsPageComponent;
  private get securitySection() {
    return this.settingsPage?.securitySection;
  }
  private get teamPermissionsSection() {
    return this.settingsPage?.teamPermissionsSection;
  }
  private get brandingSection() {
    return this.settingsPage?.brandingSection;
  }
  private get floatingMenuSection() {
    return this.settingsPage?.floatingMenuSection;
  }
  private get integrationsSection() {
    return this.settingsPage?.integrationsSection;
  }
  private get notificationsSection() {
    return this.settingsPage?.notificationsSection;
  }
  private get templateLibraryTab() {
    return this.settingsPage?.templateLibraryTab;
  }
  private readonly pmSubmissionsApi = inject(PmSubmissionsApiService);
  private readonly pmDeadlinesApi = inject(PmDeadlinesApiService);
  private readonly pmPracticeDashboardApi = inject(PmPracticeDashboardApiService);
  private readonly pmAnalyticsApi = inject(PmAnalyticsApiService);
  private readonly pmFinanceInvoicesApi = inject(PmFinanceInvoicesApiService);
  private readonly pmAnnouncementsApi = inject(PmAnnouncementsApiService);
  private readonly pmClientCommsApi = inject(PmClientCommunicationsApiService);
  /** Public for template bindings (`practiceContextReady`, `practiceContextLoading`, …). */
  readonly userContext = inject(PmUserContextService);
  /** Public for template bindings — Team & Permissions enforcement (visibleNavItems, action gating). */
  readonly permissionsAccess = inject(PmPermissionsAccessService);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly taskModalService = inject(PmTaskModalService);
  private readonly deadlineModalService = inject(PmDeadlineModalService);
  private readonly financeModalService = inject(PmFinanceModalService);
  private readonly recordTimeModalService = inject(PmRecordTimeModalService);
  private readonly clientsAddFormBridge = inject(PmClientsAddFormBridgeService);
  private routerEventsSubscription: Subscription | null = null;
  /**
   * When the clients search input is removed from the DOM (`ngModel` teardown, tab/section switch),
   * Angular can emit an empty value and overwrite persisted search — ignore those writes.
   */
  private clientsSearchQueryWritesSuspended = false;
  clientsUsersSearchWritesSuspended = false;
  private profileDirectorySyncSubscription: Subscription | null = null;

  @ViewChildren('activityTab') activityTabs!: QueryList<ElementRef>;
  sliderStyle = signal({ left: '0px', width: '0px' });

  // Navigation items
  navItems = signal<NavItem[]>([
    { label: 'Dashboard', link: '/dashboard' },
    { label: 'Clients', link: '/clients' },
    { label: 'Deadlines', link: '/deadlines' },
    { label: 'Tasks', link: '/tasks' },
    { label: 'Practice', link: '/practice' },
    { label: 'CapiSign', link: '/capisign' },
    { label: 'CRM', link: '/crm' },
    { label: 'Analytics', link: '/analytics' },
    { label: 'Reports', link: '/reports' },
    { label: 'Settings', link: '/settings' }
  ]);

  closeDashboardActivityPanelExpand(): void {
    this.dashboardActivityPanelExpanded.set(false);
    this.dashboardActivitySelectedId.set(null);
    this.dashboardActivityRelated.set([]);
    this.dashboardActivityRelatedSub?.unsubscribe();
    this.dashboardActivityRelatedSub = null;
    this.activeDropdown.set(null);
    this.dashboardActivityClientSearch.set('');
  }

  private syncDashboardActivitySelection(): void {
    if (!this.dashboardActivityPanelExpanded()) {
      return;
    }
    const filtered = this.filteredActivities();
    const selectedId = this.dashboardActivitySelectedId();
    if (filtered.length === 0) {
      if (selectedId != null) {
        this.dashboardActivitySelectedId.set(null);
        this.dashboardActivityRelated.set([]);
      }
      return;
    }
    if (selectedId == null || !filtered.some((a) => a.id === selectedId)) {
      this.selectDashboardActivity(filtered[0].id);
    }
  }

  selectDashboardActivity(id: number, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.dashboardActivitySelectedId.set(id);
    const item = this.filteredActivities().find((a) => a.id === id) ?? this.allActivities().find((a) => a.id === id);
    const clientId = (item?.clientUniversalId ?? '').trim();
    if (clientId) {
      this.loadDashboardActivityRelatedForClient(clientId, id);
    } else {
      this.dashboardActivityRelated.set([]);
    }
  }

  @HostListener('document:keydown.escape')
  onDashboardActivityPanelEscape(): void {
    if (this.dashboardAnalyticsMetricsPanelCard()) {
      this.closeDashboardAnalyticsMetricsPanel();
      return;
    }
    if (this.activeItem() === 'Dashboard' && this.dashboardActivityPanelExpanded()) {
      this.closeDashboardActivityPanelExpand();
    }
  }

  /** Dashboard header Export menu (print / PDF / PNG). */
  dashboardExportMenuOpen = signal(false);
  dashboardLayoutUnlocked = signal(false);

  activeItem = signal('Dashboard');
  isSearchExpanded = signal(false);
  headerGlobalSearchQuery = signal('');
  headerGlobalSearchLoading = signal(false);
  headerGlobalSearchDebouncing = signal(false);
  headerGlobalSearchHasSearched = signal(false);
  headerGlobalSearchResults = signal<PmGlobalSearchItemApi[]>([]);
  headerGlobalSearchError = signal<string | null>(null);
  headerGlobalSearchActiveIndex = signal(0);
  headerGlobalSearchModalLeftPx = signal(0);
  headerGlobalSearchModalWidthPx = signal(560);
  headerGlobalSearchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  headerGlobalSearchRequestSub?: Subscription;
  isQuickAddOpen = signal(false);
  activeQaIndex = signal(0);
  quickAddQuery = signal('');
  isNotificationOpen = signal(false);
  headerAnnouncementsUnreadCount = signal(0);
  headerAnnouncementsPreview = signal<AnnouncementListItemApi[]>([]);
  headerAnnouncementsLoading = signal(false);
  private headerAnnouncementsSub?: Subscription;
  isAppLauncherOpen = signal(false);
  isProfileMenuOpen = signal(false);
  /** Collapsed top-nav (hamburger) panel — shown in place of `.nav-menu` once the links stop fitting. */
  isMobileNavOpen = signal(false);

  readonly visibleNavItems = computed(() => {
    // Driven by the OneLogin access token's own `role` claim (via permissionsAccess.isSuperAccountant,
    // which is itself OneLogin-role-based), not UserMaster.UserType / profile().role — anyone who
    // isn't OneLogin's "Owner" is Staff-nav-restricted for now, until the other OneLogin roles get
    // their own mapping.
    let items = this.navItems();
    if (!this.permissionsAccess.isSuperAccountant()) {
      items = items.filter(i => STAFF_NAV_ALLOWLIST.has(i.label));
    }
    // Team & Permissions gating: hide any nav item whose mapped section is explicitly disabled
    // for the acting user (role template, or their individual override if one exists). Labels with
    // no mapped section key (Dashboard, Document Hub, Support Tickets) are never matrix-gated, so
    // they render immediately regardless of `ready()`. A matrix-gated item, though, waits for
    // `permissionsAccess.ready()` before rendering at all — showing it optimistically then hiding
    // it a moment later (once the matrix actually loads) is worse than a brief delay.
    return items.filter((i) => {
      const sectionKey = NAV_LABEL_TO_PERMISSION_SECTION_KEY[i.label];
      if (!sectionKey) {
        return true;
      }
      if (!this.permissionsAccess.ready()) {
        return false;
      }
      return this.permissionsAccess.canAccessSection(sectionKey);
    });
  });

  /** Pages & features whose name matches the query (browser-side; the search API only knows records), limited to what this user can open. */
  readonly headerGlobalSearchPageResults = computed<PmGlobalSearchItemApi[]>(() => {
    const query = this.headerGlobalSearchQuery().trim();
    if (query.length < 2) {
      return [];
    }
    const openable = new Set(this.visibleNavItems().map((i) => i.label));
    return findSearchPages(query, (label) => openable.has(label)).map((p) => ({
      moduleType: p.kind === 'action' ? 'actions' : 'pages',
      id: p.url,
      name: p.title,
      hint: p.hint
    }));
  });

  readonly headerGlobalSearchGroupedResults = computed(() => {
    const groups: Array<{ key: string; title: string; items: PmGlobalSearchItemApi[] }> = [];
    const byKey = new Map<string, PmGlobalSearchItemApi[]>();
    for (const item of [...this.headerGlobalSearchPageResults(), ...this.headerGlobalSearchResults()]) {
      const key = this.searchModuleKey(item);
      const bucket = byKey.get(key);
      if (bucket) {
        bucket.push(item);
      } else {
        byKey.set(key, [item]);
      }
    }
    const ordered: Array<{ key: string; title: string }> = [
      { key: 'actions', title: 'Actions' },
      { key: 'pages', title: 'Pages & features' },
      { key: 'clients', title: 'Clients' },
      { key: 'teams', title: 'Team Members' },
      { key: 'tasks', title: 'Tasks' },
      { key: 'notes', title: 'Notes' },
      { key: 'other', title: 'Other' }
    ];
    for (const o of ordered) {
      const items = byKey.get(o.key);
      if (items?.length) {
        groups.push({ key: o.key, title: o.title, items });
      }
    }
    return groups;
  });

  readonly headerGlobalSearchFlatResults = computed(() => this.headerGlobalSearchGroupedResults().flatMap(g => g.items));
  /** Quick Add lives in its own component; its search box is reached through it (focused on open). */
  @ViewChild(PmShellQuickAddComponent) private quickAddPopup?: PmShellQuickAddComponent;
  get qaSearchInput() {
    return this.quickAddPopup?.qaSearchInput;
  }
  @ViewChild('clientsToolbarEl') clientsToolbarEl?: ElementRef<HTMLElement>;
  clientsToolbarCompact = signal(false);
  private clientsToolbarExpandedWidth = 0;
  private clientsToolbarResizeObserver?: ResizeObserver;

  /** The top bar lives in its own component; checkTopNavFit() and the search popup placement reach its elements through it. */
  @ViewChild(PmShellTopBarComponent) private topBar?: PmShellTopBarComponent;
  get headerSearchWrapper() {
    return this.topBar?.headerSearchWrapper;
  }
  get topNavbarEl() {
    return this.topBar?.topNavbarEl;
  }
  get topNavLeftEl() {
    return this.topBar?.topNavLeftEl;
  }
  get topNavRightEl() {
    return this.topBar?.topNavRightEl;
  }
  get topNavMenuEl() {
    return this.topBar?.topNavMenuEl;
  }
  /** True while the top-nav links are folded away into the hamburger. Set by checkTopNavFit(). */
  topNavCollapsed = signal(false);
  private topNavResizeObserver?: ResizeObserver;

  readonly quickAddItems = [
    { title: 'Add Client', subtitle: 'Register a new client', shortcut: 'c' },
    { title: 'New Task', subtitle: 'Create a task for a client', shortcut: 't' },
    { title: 'New Deadline', subtitle: 'Set a compliance deadline', shortcut: 'd' },
    { title: 'Compose Email', subtitle: 'Send a message to a client', shortcut: 'e' },
    { title: 'New Invoice', subtitle: 'Raise an invoice for work', shortcut: 'i' },
    { title: 'Log Time', subtitle: 'Record billable hours', shortcut: 'l' },
    { title: 'Schedule Meeting', subtitle: 'Book a client appointment', shortcut: 'm' },
    { title: 'Run Report', subtitle: 'Generate a practice report', shortcut: 'r' },
  ] as const;

  readonly quickAddVisibleIndices = computed(() => {
    const q = this.quickAddQuery().trim().toLowerCase();
    if (!q) {
      return [0, 1, 2, 3, 4, 5, 6, 7];
    }
    const out: number[] = [];
    for (let i = 0; i < this.quickAddItems.length; i++) {
      const it = this.quickAddItems[i]!;
      const hay = `${it.title}\n${it.subtitle}\n${it.shortcut}`.toLowerCase();
      if (hay.includes(q)) out.push(i);
    }
    return out;
  });

  announcements = signal<Array<{
    id: string;
    title: string;
    body: string;
    dateIso: string;
    audience: 'All Staff' | 'Accountant' | 'Staff';
    priority: 'Normal' | 'Urgent' | 'Important';
    category: 'Firm Updates' | 'Policy Changes' | 'HMRC & Regulatory' | 'System & Tools' | 'Training & CPD';
    pinned: boolean;
    authorName: string;
    authorInitials: string;
  }>>([]);

  pinnedAnnouncements = computed(() => this.announcements().filter((a) => a.pinned));
  unpinnedAnnouncements = computed(() => this.announcements().filter((a) => !a.pinned));

  // Announcements modal (View All Announcements page)
  announcementsFilterCategory = signal<(typeof this.announcementCategoryOptions)[number] | ''>('');
  announcementsFilterPriority = signal<(typeof this.announcementPriorityOptions)[number] | ''>('');
  announcementsFilterDropdownKey = signal<'category' | 'priority' | null>(null);
  announcementsSearchQuery = signal<string>('');

  filteredAnnouncements = computed(() => {
    const cat = this.announcementsFilterCategory();
    const pri = this.announcementsFilterPriority();
    const q = this.announcementsSearchQuery().trim().toLowerCase();

    return this.announcements().filter((a) => {
      if (cat && a.category !== cat) return false;
      if (pri && a.priority !== pri) return false;
      if (!q) return true;

      const hay = `${a.title}\n${a.body}\n${a.authorName}\n${a.category}\n${a.priority}\n${a.audience}`.toLowerCase();
      return hay.includes(q);
    });
  });

  pinnedAnnouncementsFiltered = computed(() => this.filteredAnnouncements().filter((a) => a.pinned));
  unpinnedAnnouncementsFiltered = computed(() => this.filteredAnnouncements().filter((a) => !a.pinned));

  newAnnouncementOpen = signal(false);
  announcementDraftTitle = signal('');
  announcementDraftBody = signal('');
  readonly announcementPriorityOptions = ['Normal', 'Urgent', 'Important'] as const;
  readonly announcementCategoryOptions = ['Firm Updates', 'Policy Changes', 'HMRC & Regulatory', 'System & Tools', 'Training & CPD'] as const;
  readonly announcementAudienceOptions = ['All Staff', 'Accountant', 'Staff'] as const;
  announcementDraftPriority = signal<(typeof this.announcementPriorityOptions)[number]>('Normal');
  announcementDraftCategory = signal<(typeof this.announcementCategoryOptions)[number]>('Firm Updates');
  announcementDraftAudience = signal<(typeof this.announcementAudienceOptions)[number]>('All Staff');
  announcementDraftPinned = signal(false);
  announcementModalDropdownKey = signal<'priority' | 'category' | 'audience' | null>(null);

  openNewAnnouncement(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.announcementDraftTitle.set('');
    this.announcementDraftBody.set('');
    this.announcementDraftPriority.set('Normal');
    this.announcementDraftCategory.set('Firm Updates');
    this.announcementDraftAudience.set('All Staff');
    this.announcementDraftPinned.set(false);
    this.announcementModalDropdownKey.set(null);
    this.newAnnouncementOpen.set(true);
  }

  toggleAnnouncementsFilterDropdown(key: 'category' | 'priority', event: Event): void {
    event.stopPropagation();
    this.announcementsFilterDropdownKey.update((cur) => (cur === key ? null : key));
  }

  pickAnnouncementsFilterCategory(value: (typeof this.announcementCategoryOptions)[number] | '', event: Event): void {
    event.stopPropagation();
    this.announcementsFilterCategory.set(value);
    this.announcementsFilterDropdownKey.set(null);
  }

  pickAnnouncementsFilterPriority(value: (typeof this.announcementPriorityOptions)[number] | '', event: Event): void {
    event.stopPropagation();
    this.announcementsFilterPriority.set(value);
    this.announcementsFilterDropdownKey.set(null);
  }

  closeNewAnnouncement(): void {
    this.newAnnouncementOpen.set(false);
    this.announcementModalDropdownKey.set(null);
  }

  canPublishAnnouncement(): boolean {
    return !!this.announcementDraftTitle().trim() && !!this.announcementDraftBody().trim();
  }

  publishAnnouncement(): void {
    if (!this.canPublishAnnouncement()) {
      toast.warning('Please fill the required fields.');
      return;
    }
    const nowIso = todayIso();
    const authorName = 'You';
    const authorInitials = 'YO';
    const id = `ann-${Date.now()}`;

    const newAnn = {
      id,
      title: this.announcementDraftTitle().trim(),
      body: this.announcementDraftBody().trim(),
      dateIso: nowIso,
      audience: this.announcementDraftAudience(),
      priority: this.announcementDraftPriority(),
      category: this.announcementDraftCategory(),
      pinned: this.announcementDraftPinned(),
      authorName,
      authorInitials,
    };

    this.announcements.update((list) => [newAnn, ...list]);
    this.persistAnnouncementsToStorage();
    toast.success('Announcement published.');
    this.closeNewAnnouncement();
  }

  toggleAnnouncementModalDropdown(key: 'priority' | 'category' | 'audience', event: Event): void {
    event.stopPropagation();
    this.announcementModalDropdownKey.update((cur) => (cur === key ? null : key));
  }

  pickAnnouncementPriority(value: (typeof this.announcementPriorityOptions)[number], event: Event): void {
    event.stopPropagation();
    this.announcementDraftPriority.set(value);
    this.announcementModalDropdownKey.set(null);
  }

  pickAnnouncementCategory(value: (typeof this.announcementCategoryOptions)[number], event: Event): void {
    event.stopPropagation();
    this.announcementDraftCategory.set(value);
    this.announcementModalDropdownKey.set(null);
  }

  pickAnnouncementAudience(value: (typeof this.announcementAudienceOptions)[number], event: Event): void {
    event.stopPropagation();
    this.announcementDraftAudience.set(value);
    this.announcementModalDropdownKey.set(null);
  }

  readonly defaultPrimaryBrandColor = '#694baf';
  /** Portion of white blended into the primary for `--capium-purple-light` (higher = lighter). */
  private static readonly primaryLightWhiteFraction = 0.82;
  private static readonly primaryColorStorageKey = 'capium-primary-brand-color';
  private static readonly emailHeaderColorStorageKey = 'capium-email-header-color';
  private static readonly portalBrandingPrimaryStorageKey = 'capium-portal-branding-primary';
  private static readonly clientsListUiStorageKey = 'pm-clients-list-ui-v1';
  private static readonly dashboardLayoutStorageKey = 'pm-dashboard-layout-v2';
  private static readonly announcementsStorageKey = 'pm-announcements-v1';
  /** Brand colours live in PmBrandingStateService, shared with Settings > Branding & Portal. */
  private readonly brandingState = inject(PmBrandingStateService);
  primaryBrandColor = this.brandingState.primaryBrandColor;
  /** Client portal branding only — does not change app theme CSS. */
  portalBrandingPrimaryColor = this.brandingState.portalBrandingPrimaryColor;
  emailHeaderColor = this.brandingState.emailHeaderColor;

  /** When present, Tasks page shows the single-task details view. */
  taskDetailId = signal<string | null>(null);

  /** When present, Reports page shows `pm-report-runner` for `/reports/:slug`. */
  reportsRunnerSlug = signal<string | null>(null);

  constructor() {
    this.restoreClientsListUiFromStorage();
    this.restoreDashboardLayoutFromStorage();
    this.restoreDashboardAnalyticsFieldsFromStorage();
    this.restoreAnnouncementsFromStorage();
    // The nav items themselves arrive with the role and permission data, so the bar has to be
    // re-measured when the list changes and not only when the window resizes. The timeout lets
    // Angular render the new links before anything tries to measure them.
    effect(() => {
      this.visibleNavItems();
      untracked(() => setTimeout(() => this.checkTopNavFit()));
    });
    // Closing the search gives back the width it was holding, but the navbar itself does not change
    // size, so the observer never fires — the re-check has to be nudged by hand.
    effect(() => {
      this.isSearchExpanded();
      untracked(() => setTimeout(() => this.checkTopNavFit()));
    });
    effect(() => {
      const parent = this.userContext.parentUserId();
      const acting = this.userContext.userMasterId();
      if (parent <= 0 || acting <= 0) {
        return;
      }
      untracked(() => this.refreshHeaderAnnouncementsData());
    });
    // ngOnInit's permissionsAccess.refresh() call can race ahead of practice-context ids (it
    // silently no-ops when they're not ready yet — see PmPermissionsAccessService.refresh).
    // Retry once ids are actually available, so the nav-gating matrix reliably loads on first
    // paint instead of only on a later navigation that happens to re-run ngOnInit.
    effect(() => {
      const parent = this.userContext.parentUserId();
      const acting = this.userContext.userMasterId();
      if (parent <= 0 || acting <= 0) {
        return;
      }
      untracked(() => void this.permissionsAccess.refresh());
    });
    // Capium Pay access can be requested before practice context is ready on first paint.
    effect(() => {
      const userId = this.userContext.userMasterId();
      const active = this.activeItem();
      if (userId <= 0 || active !== 'Clients') {
        return;
      }
      untracked(() => void this.loadClientsCapiumPayAccess());
    });
    effect(() => {
      this.clientsSearchQuery();
      this.clientsSortField();
      this.clientsSortDir();
      this.clientsFilterStatuses();
      this.clientsFilterClientTypes();
      this.clientsFilterManager();
      this.clientsFilterGrades();
      this.clientsFilterAmlStatuses();
      this.clientsFilterServices();
      this.clientsFilterQuick();
      this.clientsColumnVisibility();
      this.clientsColumnOrder();
      this.clientsViewMode();
      this.clientsAnalyticsStripOpen();
      this.clientsPageTab();
      this.clientsUsersSearchQuery();
      this.clientsWorkspaceSection();
      this.clientsAppliedSavedViewId();
      this.persistClientsListUiToStorage();
    });

    // On hot reload, /clients can render before context ids are hydrated.
    // Retry clients route data load once ids are ready.
    effect(() => {
      const parent = this.effectiveDirectoryParentUserId();
      const acting = this.effectiveDirectoryUserId();
      const active = this.activeItem();
      if (parent <= 0 || acting <= 0 || active !== 'Clients') {
        return;
      }
      const path = this.router.url.split('?')[0];
      const onClientsRoute = path === '/clients' || /^\/clients\/[^/]+$/.test(path);
      if (!onClientsRoute) {
        return;
      }
      const loadKey = `${path}|${parent}|${acting}`;
      if (this.lastClientsRouteHydrationLoadKey() === loadKey) {
        return;
      }
      this.lastClientsRouteHydrationLoadKey.set(loadKey);
      this.maybeRefreshDirectoryClientsList(path);
      void this.ensureWorkspaceProfileFromTeam();
      void this.ensureClientDetailRowFromDirectory();
      if (this.clientsWorkspaceSection() === 'communication') {
        void this.loadCommunicationSectionData();
      }
    });

    // Communication tab after refresh: context ids hydrate after UI restore — load list once ready.
    effect(() => {
      const parent = this.effectiveDirectoryParentUserId();
      const acting = this.effectiveDirectoryUserIdForClientsList();
      const active = this.activeItem();
      const workspace = this.clientsWorkspaceSection();
      if (parent <= 0 || acting <= 0 || active !== 'Clients' || workspace !== 'communication') {
        return;
      }
      if (!this.userContext.practiceContextReady()) {
        return;
      }
      const key = `${parent}|${acting}`;
      if (this.lastCommunicationSectionHydrationKey() === key) {
        return;
      }
      this.lastCommunicationSectionHydrationKey.set(key);
      void this.loadCommunicationSectionData();
    });

    // Add Client master data (VAT scheme, currency, etc.) loads once practice context is ready.
    effect(() => {
      if (!this.clientsAddFormOpen()) {
        return;
      }
      if (!this.userContext.practiceContextReady()) {
        return;
      }
      untracked(() => this.loadClientsAddMasterDataFromClientStore());
    });

    // Re-apply VAT/country defaults once master-data options arrive (native <select> needs options + value together).
    effect(() => {
      if (!this.clientsAddFormOpen()) {
        return;
      }
      if (this.clientsAddVatSchemes().length === 0) {
        return;
      }
      untracked(() => this.applyClientsAddMasterDataDefaults());
    });

    // Fetch the Users-tab badge count as soon as the Clients page context is ready,
    // regardless of which sub-tab is active — otherwise "Users (N)" shows 0 until the
    // user switches to the Users tab, while "Clients (N)" already loads eagerly.
    effect(() => {
      const parent = this.effectiveDirectoryParentUserId();
      const acting = this.effectiveDirectoryActingUserIdForContacts() ?? 0;
      const active = this.activeItem();
      const workspace = this.clientsWorkspaceSection();
      if (parent <= 0 || acting <= 0 || active !== 'Clients' || workspace !== 'list') {
        return;
      }
      const path = this.router.url.split('?')[0];
      if (path !== '/clients') {
        return;
      }
      void this.ensureClientUsersFirmTotalCount();
    });

    // If the saved Clients UI restores directly to the Users tab before context hydrates,
    // load the tab once the context is ready instead of waiting for a tab switch.
    effect(() => {
      const parent = this.effectiveDirectoryParentUserId();
      const acting = this.effectiveDirectoryActingUserIdForContacts() ?? 0;
      const active = this.activeItem();
      const workspace = this.clientsWorkspaceSection();
      const tab = this.clientsPageTab();
      const query = this.clientsUsersSearchQueryForApi();
      const page = this.clientsUsersPageNumber();
      const pageSize = this.clientsUsersPageSize();
      if (
        parent <= 0 ||
        acting <= 0 ||
        active !== 'Clients' ||
        workspace !== 'list' ||
        tab !== 'users'
      ) {
        return;
      }
      const path = this.router.url.split('?')[0];
      if (path !== '/clients') {
        return;
      }
      const loadKey = `${parent}|${acting}|${query}|${page}|${pageSize}`;
      if (this.lastClientsUsersHydrationLoadKey() === loadKey) {
        return;
      }
      this.lastClientsUsersHydrationLoadKey.set(loadKey);
      void this.ensureClientUsersFirmTotalCount();
      void this.loadDirectoryClientUsers(page);
    });

    // Prefetch saved views in background while Clients list is active.
    // This avoids showing a loading state each time the Views popover opens.
    effect(() => {
      const parent = this.effectiveDirectoryParentUserId();
      const acting = this.effectiveDirectoryUserId();
      const active = this.activeItem();
      const workspace = this.clientsWorkspaceSection();
      const tab = this.clientsPageTab();
      if (
        parent <= 0 ||
        acting <= 0 ||
        active !== 'Clients' ||
        workspace !== 'list' ||
        tab !== 'clients' ||
        !this.userContext.practiceContextReady()
      ) {
        return;
      }
      const loadKey = `${parent}|${acting}`;
      if (this.lastClientsSavedViewsHydrationLoadKey() === loadKey) {
        return;
      }
      this.lastClientsSavedViewsHydrationLoadKey.set(loadKey);
      this.loadClientsSavedViews();
    });

    // On hot reload, /dashboard can render before context ids are hydrated.
    // Retry dashboard API load once ids become available.
    effect(() => {
      const parent = this.effectiveDirectoryParentUserId();
      const acting = this.effectiveDirectoryUserId();
      const active = this.activeItem();
      if (parent <= 0 || acting <= 0 || active !== 'Dashboard') {
        return;
      }
      const path = this.router.url.split('?')[0];
      const onDashboardRoute = path === '/dashboard' || path === '' || path === '/';
      if (!onDashboardRoute) {
        return;
      }
      const loadKey = `${path}|${parent}|${acting}`;
      if (this.lastDashboardHydrationLoadKey() === loadKey) {
        return;
      }
      this.lastDashboardHydrationLoadKey.set(loadKey);
      this.refreshPracticeDashboardFromApi();
      void this.ensureWorkspaceProfileFromTeam();
    });

    // Dashboard Activity feed uses an independent period filter from the main dashboard pills.
    effect(() => {
      const parent = this.effectiveDirectoryParentUserId();
      const acting = this.effectiveDirectoryUserId();
      const active = this.activeItem();
      const logsPeriod = this.selectedActivityTime();
      const logsType = this.selectedActivityType();
      const logsCategory = this.selectedActivityCategory();
      const logsClient = this.selectedActivityClient();
      if (parent <= 0 || acting <= 0 || active !== 'Dashboard') {
        return;
      }
      const path = this.router.url.split('?')[0];
      const onDashboardRoute = path === '/dashboard' || path === '' || path === '/';
      if (!onDashboardRoute) {
        return;
      }
      const loadKey = `${path}|${parent}|${acting}|${logsPeriod}|${logsType}|${logsCategory}|${logsClient}`;
      if (this.lastDashboardActivityLoadKey() === loadKey) {
        return;
      }
      this.lastDashboardActivityLoadKey.set(loadKey);
      this.loadDashboardActivityFromApi();
    });

    effect(() => {
      if (!this.dashboardActivityPanelExpanded()) {
        return;
      }
      this.filteredActivities();
      untracked(() => this.syncDashboardActivitySelection());
    });

    // On refresh, /document-hub can load before practice-context ids are hydrated.
    effect(() => {
      const parent = this.effectiveDirectoryParentUserId();
      const acting = this.effectiveDirectoryUserId();
      const active = this.activeItem();
      if (parent <= 0 || acting <= 0 || active !== 'Document Hub') {
        return;
      }
      const path = this.router.url.split('?')[0];
      if (path !== '/document-hub') {
        return;
      }
      const listUserId = this.effectiveDirectoryUserIdForClientsList();
      const loadKey = `${parent}|${listUserId}`;
      if (this.docHubStore.lastDocumentHubHydrationLoadKey() === loadKey) {
        return;
      }
      this.docHubStore.lastDocumentHubHydrationLoadKey.set(loadKey);
      void this.documentHubPage?.loadDocumentHubSectionData();
    });

    effect(() => {
      const parent = this.effectiveDirectoryParentUserId();
      if (parent <= 0) {
        return;
      }
      const loadKey = String(parent);
      if (this.lastCustomLabelsHydrationLoadKey() === loadKey) {
        return;
      }
      this.lastCustomLabelsHydrationLoadKey.set(loadKey);
      this.pmCustomLabels.load(parent).subscribe({
        error: () => {
          // Don't reset the guard here: it's read reactively by this same effect, so
          // resetting it on every failed attempt would re-trigger this effect and hammer
          // the API in a tight retry loop when the backend keeps erroring. The next real
          // parentUserId change will retry naturally via the guard key.
        }
      });
    });

    effect(() => {
      const parent = this.effectiveDirectoryParentUserId();
      const acting = this.effectiveDirectoryUserId();
      if (parent <= 0 || acting <= 0) {
        return;
      }
      const loadKey = `${parent}|${acting}`;
      if (this.lastFloatingMenuHydrationLoadKey() === loadKey) {
        return;
      }
      this.lastFloatingMenuHydrationLoadKey.set(loadKey);
      this.floatingMenuSettings.load(parent, acting).subscribe({
        error: () => {
          // See the customLabels effect above: resetting the guard here would re-read it
          // reactively and re-fire this effect immediately, causing an unbounded retry
          // loop against /api/v1/settings/floating-menu whenever the request errors.
        }
      });
    });

    // Re-resolve service manager names once team data loads. Services may load
    // before the team, leaving manager as '—' or a stale value. Any item that
    // has a managerUserId but no name yet gets updated without triggering extra
    // API calls.
    effect(() => {
      const team = this.teamMembersApi();
      if (team.length === 0) {
        return;
      }
      untracked(() => {
        this.customAddedServices.update((items) =>
          items.map((item) => {
            const uid = item.managerUserId;
            if (uid == null || uid <= 0) {
              return item;
            }
            const resolved = this.teamMemberDisplayNameForUserId(uid, false);
            if (!resolved || resolved === item.manager) {
              return item;
            }
            return { ...item, manager: resolved };
          })
        );
        this.catalogServices.update((items) =>
          items.map((item) => {
            const uid = item.managerUserId;
            if (uid == null || uid <= 0) {
              return item;
            }
            const resolved = this.teamMemberDisplayNameForUserId(uid, false);
            if (!resolved || resolved === item.managerLabel) {
              return item;
            }
            return { ...item, managerLabel: resolved };
          })
        );
      });
    });

    effect(() => {
      if (this.clientsAddFormBridge.pendingOpen()) {
        untracked(() => {
          const contactPrefill = this.clientsAddFormBridge.consumeContactPrefill();
          const companyPrefill = this.clientsAddFormBridge.consumeCompanyPrefill();
          this.clientsAddFormBridge.consume();
          this.openClientsAddFormModal();
          if (companyPrefill) {
            // Company-level fields plus a placeholder Primary Contact (same "not created yet" id
            // convention as the contactPrefill branch below) — companyType is left as whatever the
            // connection actually is (Limited, Individual, etc.), not forced to Individual.
            const pendingContactId =
              typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `company-prefill-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            this.clientsAddDraft.update((d) => ({
              ...d,
              companyName: companyPrefill.companyName || d.companyName,
              companyType: companyPrefill.companyType || d.companyType,
              registrationNumber: companyPrefill.registrationNumber || d.registrationNumber,
              primaryEmail: companyPrefill.primaryEmail || d.primaryEmail,
              phone: companyPrefill.phone || d.phone,
              selectedContactId: companyPrefill.contactName ? pendingContactId : d.selectedContactId,
              contactName: companyPrefill.contactName || d.contactName,
              contactEmail: companyPrefill.contactEmail || d.contactEmail,
              contactPhone: companyPrefill.contactPhone || d.contactPhone,
              contactPersonName: companyPrefill.contactName || d.contactPersonName,
              contactPersonEmail: companyPrefill.contactEmail || d.contactPersonEmail,
              contactPersonPhone: companyPrefill.contactPhone || d.contactPersonPhone
            }));
            // Pre-check Services & Staff with whatever this connection already had agreed in CRM,
            // instead of opening empty — same name-to-trackKey lookup used when editing an existing
            // client's services.
            this.applyAgreedServiceNamesToDraft(companyPrefill.agreedServiceNames);
            // Remembered so saveClientsAddForm() can flip this connection's own status to Client once
            // the new client record actually saves — cleared on cancel by closeClientsAddFormModal().
            this.pendingCrmConversionSource = companyPrefill.sourceCrmConversion;
            // openClientsAddFormModal() (just above) may have restored an abandoned Add Client draft
            // from localStorage — including ITS clientId — and marked it "user edited" so the normal
            // fresh-code fetch never ran (see applyClientsAddCreateDefaults/fetchClientsAddNextClientCode).
            // That code belongs to whatever client that old draft was for; reusing it here for this
            // CRM-sourced client is what produced "Client was not created. The client code you chose
            // already used or not available for the user" once someone (possibly the same session)
            // had since consumed it. A CRM conversion is always a brand-new client, so it always gets
            // its own fresh Client Store code instead of inheriting a stale one.
            this.clientsAddDraft.update((d) => ({ ...d, clientId: '' }));
            this.clientsAddClientIdUserEdited.set(false);
            this.clientsAddClientIdDuplicate.set(false);
            this.fetchClientsAddNextClientCode();
          }
          if (contactPrefill) {
            this.clientsAddContactDraft.update((d) => ({ ...d, ...contactPrefill }));
            this.clientsAddClientTypeRestrictToIndividual.set(true);
            const contactPersonName = [contactPrefill.firstName, contactPrefill.middleName, contactPrefill.lastName]
              .filter(Boolean)
              .join(' ')
              .trim();
            // Non-numeric placeholder id — same pattern as the existing "no API context yet" local-contact
            // fallback in submitClientsAddContactFromAddClientForm(). persistClientsAddPrimaryContact() treats
            // any selectedContactId that doesn't parse to a positive number as "not created yet" and creates
            // a fresh directory contact (from contactPersonName/Email/Phone + address) only when the client is saved.
            const pendingContactId =
              typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `contact-prefill-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            this.clientsAddDraft.update((d) => ({
              ...d,
              companyType: 'Individual',
              selectedContactId: pendingContactId,
              contactName: contactPersonName,
              contactEmail: contactPrefill.email,
              contactPhone: contactPrefill.phone,
              primaryEmail: contactPrefill.email,
              contactPersonName,
              contactPersonEmail: contactPrefill.email,
              contactPersonPhone: contactPrefill.phone,
              address1: contactPrefill.address1,
              address2: contactPrefill.address2,
              city: contactPrefill.city,
              county: contactPrefill.county,
              postCode: contactPrefill.postCode,
              country: contactPrefill.country || d.country,
              countryId: contactPrefill.countryId ?? d.countryId
            }));
          }
        });
      }
    });

    // Drains pendingAgreedServiceNamesForCatalog (see applyAgreedServiceNamesToDraft) once the async
    // catalog load kicked off by primeClientsAddFormServicesCatalog() actually populates one of these
    // signals — without this, a CRM-to-Client conversion's agreed services would only ever match when
    // the catalog happened to already be loaded from an earlier Add Client session.
    effect(() => {
      const catalogReady = this.catalogServices().length > 0 || this.customAddedServices().length > 0;
      const pending = this.pendingAgreedServiceNamesForCatalog();
      if (!catalogReady || !pending) {
        return;
      }
      untracked(() => {
        this.pendingAgreedServiceNamesForCatalog.set(null);
        const keys = this.mapClientServiceNamesToTrackKeys(pending);
        if (keys.length > 0) {
          this.clientsAddDraft.update((d) => ({
            ...d,
            selectedServiceKeys: Array.from(new Set([...d.selectedServiceKeys, ...keys]))
          }));
        }
      });
    });

  }

  // Replaces the former @angular/animations trigger('metricPanelSlide', ...) — animate.enter/animate.leave
  // need the actual pixel height (scrollHeight) since these panels expand to their natural content height.
  metricPanelEnter(event: AnimationCallbackEvent): void {
    const el = event.target as HTMLElement;
    const targetHeight = el.scrollHeight;
    el.style.overflow = 'hidden';
    const anim = el.animate(
      [
        { height: '0px', opacity: 0, transform: 'translateY(-14px)' },
        { height: `${targetHeight}px`, opacity: 1, transform: 'translateY(0)' }
      ],
      { duration: 320, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );
    anim.onfinish = () => {
      el.style.overflow = '';
      el.style.height = '';
      event.animationComplete();
    };
  }

  metricPanelLeave(event: AnimationCallbackEvent): void {
    const el = event.target as HTMLElement;
    const startHeight = el.scrollHeight;
    el.style.overflow = 'hidden';
    const anim = el.animate(
      [
        { height: `${startHeight}px`, opacity: 1, transform: 'translateY(0)' },
        { height: '0px', opacity: 0, transform: 'translateY(-12px)' }
      ],
      { duration: 280, easing: 'cubic-bezier(0.4, 0, 1, 1)' }
    );
    anim.onfinish = () => {
      event.animationComplete();
    };
  }

  ngOnInit() {
    this.userContext.hydrateFromAuthToken();
    void this.permissionsAccess.refresh();
    this.restorePrimaryBrandColor();
    this.restorePortalBrandingPrimaryColor();
    this.restoreEmailHeaderColor();
    // Practice-context hydration is handled by the constructor effect (`lastDashboardHydrationLoadKey`);
    // avoid a timed second refresh — it duplicated overview + submissions query traffic.
    // Resolve acting user display name from pm-api team list (replaces static profile placeholder).
    setTimeout(() => void this.ensureWorkspaceProfileFromTeam(), 900);

    // Read the URL and set activeItem based on the current route
    this.updateActiveFromUrl();

    // Subscribe to router events to update activeItem on navigation
    this.routerEventsSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const path = this.router.url.split('?')[0];
        if (path === '/clients') {
          this.persistClientsListUiToStorage();
          this.clientsSearchQueryWritesSuspended = true;
        }
        return;
      }
      if (event instanceof NavigationEnd) {
        this.updateActiveFromUrl();
        const path = this.router.url.split('?')[0];
        if (path === '/clients') {
          this.clientsSearchQueryWritesSuspended = false;
        }
        if (path === '/announcements') {
          this.refreshHeaderAnnouncementsData(this.isNotificationOpen());
        }
      }
    });

    document.addEventListener('click', this.onDocumentClickCapture, true);
    window.addEventListener('pm-announcements-changed', this.onAnnouncementsChanged);
    document.addEventListener('scroll', this.onClientsToolbarPopoverViewportChange, true);
    if (typeof window !== 'undefined' && window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onClientsToolbarPopoverViewportChange);
      window.visualViewport.addEventListener('scroll', this.onClientsToolbarPopoverViewportChange);
    }

    this.profileDirectorySyncSubscription = this.pmProfileDirectorySync.workspaceProfileRefresh$.subscribe(() => {
      void this.refreshWorkspaceProfileFromTeamLight();
    });
  }

  private updateActiveFromUrl() {
    const url = this.router.url;
    const path = url.split('?')[0];
    const queryString = url.includes('?') ? (url.split('?')[1] ?? '') : '';
    const qs = new URLSearchParams(queryString);
    const clientDetailMatch = path.match(/^\/clients\/([^/]+)$/);
    this.clientDetailId.set(clientDetailMatch ? clientDetailMatch[1] : null);
    if (!clientDetailMatch) {
      this.clientDetailDirectoryFetched.set(null);
      this.clientDetailDirectoryFetchLoading.set(false);
    }
    const taskDetailMatch = path.match(/^\/tasks\/([^/]+)$/);
    this.taskDetailId.set(taskDetailMatch ? taskDetailMatch[1] : null);

    const reportSlugMatch = path.match(/^\/reports\/([^/]+)$/);
    this.reportsRunnerSlug.set(reportSlugMatch ? reportSlugMatch[1]! : null);

    // Use full `navItems` so the URL always drives the workspace (e.g. /analytics). `visibleNavItems`
    // only filters the navbar — using it here left `activeItem` stuck on Dashboard for hidden items.
    const item = this.navItems().find((i) => path === i.link || path.startsWith(i.link + '/'));
    if (item) {
      this.activeItem.set(item.label);
    }
    if (path === '/notifications') {
      this.activeItem.set('Notifications');
    } else if (path === '/announcements') {
      this.activeItem.set('Announcements');
    } else if (path === '/profile') {
      this.activeItem.set('Profile');
    } else if (path === '/forgot-password') {
      this.activeItem.set('Forgot Password');
    } else if (path === '/document-hub') {
      this.activeItem.set('Document Hub');
    } else if (path === '/support-tickets') {
      this.activeItem.set('Support Tickets');
    }

    if (path === '/clients') {
      const ws = (qs.get('ws') ?? '').trim().toLowerCase();
      const map: Record<string, ClientsWorkspaceSection> = {
        list: 'list',
        schedule: 'schedule',
        communication: 'communication',
        submissions: 'submissions',
        letters: 'letters',
        aml: 'aml'
      };
      const target = map[ws];
      if (target) {
        this.setClientsWorkspaceSection(target);
      }
      void this.maybeOpenClientsEditModalFromQuery(qs);
    }

    // ── Quick-Add URL actions ──────────────────────────────────────────────────
    // executeQuickAction encodes the desired action as ?qa=<key> and navigates.
    // We act here (synchronously inside the NavigationEnd handler) so activeItem
    // and modal signals are both set BEFORE Angular runs its first change-detection
    // pass — the page + modal render together in a single pass.
    const qaParam = qs.get('qa');
    if (qaParam) {
      if (path === '/clients') {
        if (qaParam === 'add-client') {
          this.setClientsWorkspaceSection('list');
          this.openClientsAddSelectionModal();
        } else if (qaParam === 'compose-email') {
          this.setClientsWorkspaceSection('communication');
          this.openClientsCommunicationEmail();
        } else if (qaParam === 'schedule-meeting') {
          this.setClientsWorkspaceSection('schedule');
          this.openClientsBookMeetingModal();
        }
      } else if (path === '/tasks' && qaParam === 'new-task') {
        this.taskModalService.open();
      } else if (path === '/deadlines' && qaParam === 'new-deadline') {
        this.deadlineModalService.open();
      } else if (path === '/practice') {
        if (qaParam === 'new-invoice') {
          this.financeModalService.open();
        } else if (qaParam === 'log-time') {
          this.recordTimeModalService.open();
        }
      }
      // Remove the qa param from the URL so it doesn't replay on browser refresh.
      const cleanParams: Record<string, string> = {};
      qs.forEach((v, k) => { if (k !== 'qa') cleanParams[k] = v; });
      void this.router.navigate([path], { queryParams: cleanParams, replaceUrl: true });
    }

    // See visibleNavItems for why this checks OneLogin's role (via isSuperAccountant), not profile().role.
    if (!this.permissionsAccess.isSuperAccountant() && !STAFF_NAV_ALLOWLIST.has(this.activeItem())) {
      this.activeItem.set('Dashboard');
      this.reportsRunnerSlug.set(null);
      this.taskDetailId.set(null);
      void this.router.navigateByUrl('/dashboard');
      return;
    }

    // Team & Permissions gating: block direct/typed-URL navigation into a section that's
    // explicitly disabled for the acting user (mirrors the Staff allowlist redirect above, but
    // driven by the matrix and applies to every role — see visibleNavItems for the nav-hiding
    // half of this same enforcement).
    const activeSectionKey = NAV_LABEL_TO_PERMISSION_SECTION_KEY[this.activeItem()];
    if (
      activeSectionKey &&
      this.activeItem() !== 'Dashboard' &&
      !this.permissionsAccess.canAccessSection(activeSectionKey)
    ) {
      this.activeItem.set('Dashboard');
      this.reportsRunnerSlug.set(null);
      this.taskDetailId.set(null);
      void this.router.navigateByUrl('/dashboard');
      return;
    }

    if (path !== '/document-hub') {
      this.documentHubPage?.closeDocumentHubPreview();
      this.docHubStore.documentHubOpenFolderId.set(null);
      this.docHubStore.documentHubFilesSearchQuery.set('');
    }
    if (path === '/document-hub') {
      void this.documentHubPage?.loadDocumentHubSectionData();
      if (this.userContext.practiceContextReady()) {
        void this.loadDirectoryClientsPage(this.clientsDirectoryPageNumber() || 1);
      }
    }

    this.maybeRefreshDirectoryClientsList(path);
    void this.ensureClientDetailRowFromDirectory();

    if (path === '/settings') {
      const qsTab = qs.get('tab');
      if (qsTab === 'Services') {
        this.activeSettingItem.set('Services');
        const qsSub = qs.get('sub');
        if (qsSub === 'Assign Services' || qsSub === 'Assign to Clients') {
          this.activeServiceTab.set('Assign Services');
          const qsQ = qs.get('q');
          if (qsQ) {
            this.assignSearchQuery.set(this.decodeAssignSearchFromQueryParam(qsQ));
          }
          /** Deep link must load matrix rows; otherwise the search box is filled but filtered list stays empty. */
          this.loadAssignmentsMatrixForTab();
        }
        this.loadSettingsServicesCatalog();
        this.loadCustomFieldsSettings();
      } else if (qsTab === 'Custom Fields') {
        this.activeSettingItem.set('Custom Fields');
        this.activeCustomFieldTab.set('Custom Fields');
        this.loadCustomFieldsSettings();
      } else if (qsTab === 'Team & Permissions') {
        this.activeSettingItem.set('Team & Permissions');
        this.loadTeamAndPermissions();
      } else {
        if (this.activeSettingItem() === 'Services') {
          this.loadSettingsServicesCatalog();
        }
        this.loadCustomFieldsSettings();
      }
    }

    // Dashboard data loads via hydration effect (avoids duplicate overview with NavigationEnd).
  }

  /** Loads dashboard widgets from pm-api `GET /api/v1/practice/dashboard/overview`. */
  practiceDashboardLoading = signal(false);
  /** True while phase-2 overview (`includeCharts: true`) loads pipeline / deadline analytics. */
  practiceDashboardChartsLoading = signal(false);
  practiceDashboard = signal<PracticeDashboardOverviewApi | null>(null);
  readonly dashboardCardOrders = signal<Record<DashboardDnDScope, string[]>>({
    kpi: ['kpiMissingDeadlines', 'kpiTasks', 'kpiOverdue', 'kpiDeadlines', 'kpiActivity'],
    analytics: ['anaInvoices', 'anaDeadlines', 'anaTasks'],
    details: ['detailsTable', 'detailsChart', 'detailsReports', 'detailsActivity'],
    trio: ['trioTasks', 'trioInvoices'],
    double: ['doubleQueuedEmails', 'doubleTodaySchedule']
  });
  readonly dashboardLockedCards = signal<Record<string, boolean>>({});
  /** User-selected mini-stat fields per analytics card (Income / Deadlines / Tasks). */
  readonly dashboardAnalyticsSelectedFields = signal<Record<DashboardAnalyticsCardKey, string[]>>({
    income: [...DASHBOARD_ANALYTICS_FIELD_DEFAULTS.income],
    deadlines: [...DASHBOARD_ANALYTICS_FIELD_DEFAULTS.deadlines],
    tasks: [...DASHBOARD_ANALYTICS_FIELD_DEFAULTS.tasks]
  });

  /** Client summary widget — tier counts + count-delta trend. */
  dashboardClientSummary = signal<PracticeDashboardClientSummaryApi | null>(null);
  dashboardClientSummaryLoading = signal(false);

  /** Configure-metrics panel (Figma) for Income / Deadlines / Tasks analytics cards. */
  readonly dashboardAnalyticsMetricsPanelCard = signal<DashboardAnalyticsCardKey | null>(null);
  readonly dashboardAnalyticsFieldDraft = signal<string[]>([]);

  closeDashboardAnalyticsMetricsPanel(): void {
    this.dashboardAnalyticsMetricsPanelCard.set(null);
    this.dashboardAnalyticsFieldDraft.set([]);
  }

  private restoreDashboardAnalyticsFieldsFromStorage(): void {
    try {
      const raw = pmStorageGet(DASHBOARD_ANALYTICS_FIELDS_STORAGE_KEY);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw) as Partial<Record<DashboardAnalyticsCardKey, unknown>>;
      this.dashboardAnalyticsSelectedFields.set({
        income: normalizeDashboardAnalyticsFieldSelection('income', parsed.income),
        deadlines: normalizeDashboardAnalyticsFieldSelection('deadlines', parsed.deadlines),
        tasks: normalizeDashboardAnalyticsFieldSelection('tasks', parsed.tasks)
      });
    } catch {
      // Ignore invalid persisted payload.
    }
  }

  private restoreDashboardLayoutFromStorage(): void {
    try {
      const raw = pmStorageGet(PmWorkspaceComponent.dashboardLayoutStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        cardOrders?: Partial<Record<DashboardDnDScope, unknown>>;
        lockedCards?: unknown;
      };
      const defaults = this.dashboardCardOrders();
      const nextOrders: Record<DashboardDnDScope, string[]> = {
        kpi: [...defaults.kpi],
        analytics: [...defaults.analytics],
        details: [...defaults.details],
        trio: [...defaults.trio],
        double: [...defaults.double]
      };
      for (const scope of ['kpi', 'analytics', 'details', 'trio', 'double'] as const) {
        const incoming = Array.isArray(parsed.cardOrders?.[scope])
          ? (parsed.cardOrders?.[scope] as unknown[]).filter((x): x is string => typeof x === 'string')
          : [];
        const deduped = [...new Set(incoming)];
        const valid = deduped.filter((id) => defaults[scope].includes(id));
        nextOrders[scope] = [...valid, ...defaults[scope].filter((id) => !valid.includes(id))];
      }
      this.dashboardCardOrders.set(nextOrders);
      if (parsed.lockedCards && typeof parsed.lockedCards === 'object') {
        const locks = parsed.lockedCards as Record<string, unknown>;
        const nextLocks: Record<string, boolean> = {};
        for (const id of [...defaults.kpi, ...defaults.analytics, ...defaults.details, ...defaults.trio, ...defaults.double]) {
          nextLocks[id] = !!locks[id];
        }
        this.dashboardLockedCards.set(nextLocks);
      }
    } catch {
      // Ignore invalid persisted payload.
    }
  }

  private persistAnnouncementsToStorage(): void {
    try {
      pmStorageSet(PmWorkspaceComponent.announcementsStorageKey, JSON.stringify(this.announcements()));
    } catch {
      // Ignore storage failures (quota/private mode).
    }
  }

  private restoreAnnouncementsFromStorage(): void {
    try {
      const raw = pmStorageGet(PmWorkspaceComponent.announcementsStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const valid = parsed.filter((a: any) => !!a && typeof a === 'object' && typeof a['id'] === 'string');
      if (valid.length) this.announcements.set(valid);
    } catch {
      // Ignore invalid persisted payload.
    }
  }

  /** Dashboard header “Quick links” dropdown. */
  dashboardQuickLinksOpen = signal(false);
  /** Activity / audit feed expanded to half-screen panel. */
  dashboardActivityPanelExpanded = signal(false);
  dashboardActivitySelectedId = signal<number | null>(null);
  dashboardActivityRelated = signal<ActivityItem[]>([]);
  dashboardActivityUseCustomRange = signal(false);
  dashboardActivityCustomFrom = signal('');
  dashboardActivityCustomTo = signal('');
  dashboardActivityRelatedSub: Subscription | null = null;
  dashboardQuickLinksCounts = signal<PracticeDashboardQuickLinksApi | null>(null);

  /**
   * UTC ISO bounds for the dashboard overview (tasks summary uses CreatedOn in this window).
   * Returns undefined when Custom is selected but dates are incomplete — API then omits filters (legacy behaviour).
   */
  private practiceDashboardOverviewUtcRange(): { fromUtc: string; toUtc: string } | undefined {
    const period = this.activePeriod();
    const now = new Date();
    const startOfDayIso = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).toISOString();
    const endOfDayIso = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();

    if (period === 'Custom') {
      const f = this.fromDate().trim();
      const t = this.toDate().trim();
      if (!f || !t) {
        return undefined;
      }
      const pf = /^(\d{4})-(\d{2})-(\d{2})$/.exec(f);
      const pt = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
      if (!pf || !pt) {
        return undefined;
      }
      const fs = new Date(Number(pf[1]), Number(pf[2]) - 1, Number(pf[3]), 0, 0, 0, 0);
      const te = new Date(Number(pt[1]), Number(pt[2]) - 1, Number(pt[3]), 23, 59, 59, 999);
      return { fromUtc: fs.toISOString(), toUtc: te.toISOString() };
    }

    if (period === 'This Week') {
      const ref = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const offset = (ref.getDay() + 6) % 7;
      const start = new Date(ref);
      start.setDate(ref.getDate() - offset);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { fromUtc: startOfDayIso(start), toUtc: endOfDayIso(end) };
    }

    if (period === 'This Month') {
      const y = now.getFullYear();
      const m = now.getMonth();
      const start = new Date(y, m, 1);
      const end = new Date(y, m + 1, 0);
      return { fromUtc: startOfDayIso(start), toUtc: endOfDayIso(end) };
    }

    if (period === 'This Quarter') {
      const y = now.getFullYear();
      const q = Math.floor(now.getMonth() / 3);
      const startMonth = q * 3;
      const start = new Date(y, startMonth, 1);
      const end = new Date(y, startMonth + 3, 0);
      return { fromUtc: startOfDayIso(start), toUtc: endOfDayIso(end) };
    }

    if (period === 'This Year') {
      const y = now.getFullYear();
      const start = new Date(y, 0, 1);
      const end = new Date(y, 11, 31);
      return { fromUtc: startOfDayIso(start), toUtc: endOfDayIso(end) };
    }

    return undefined;
  }

  refreshPracticeDashboardFromApi(options?: { includeSummaryTrends?: boolean }): void {
    const parent = this.effectiveDirectoryParentUserId();
    const acting = this.effectiveDirectoryUserId();
    const directoryUserId = this.effectiveDirectoryUserIdForClientsList();
    if (parent <= 0 || acting <= 0) {
      this.practiceDashboardLoading.set(false);
      this.practiceDashboardChartsLoading.set(false);
      this.practiceDashboard.set(null);
      this.allDeadlines.set([]);
      this.allActivities.set([]);
      this.dashboardPracticeInvoices.set([]);
      this.dashboardPracticeInvoicePmLinks.set([]);
      this.dashboardPracticeInvoiceTotalCount.set(null);
      this.dashboardPracticeInvoiceKpis.set(null);
      this.dashboardInvoicesLoading.set(false);
      this.dashboardQuickLinksCounts.set(null);
      return;
    }

    const range = this.practiceDashboardOverviewUtcRange();
    const hasPeriodRange = !!(range?.fromUtc?.trim() && range?.toUtc?.trim());
    const includeSummaryTrends = options?.includeSummaryTrends === true || hasPeriodRange;

    this.ensureDashboardDirectoryClientsLoaded();

    this.practiceDashboardOverviewSub?.unsubscribe();
    this.practiceDashboardChartsSub?.unsubscribe();
    this.practiceDashboardQuickLinksSub?.unsubscribe();
    this.practiceDashboardInvoicesSub?.unsubscribe();
    this.practiceDashboardClientSummarySub?.unsubscribe();

    const refreshGen = ++this.practiceDashboardRefreshGen;
    this.practiceDashboardChartsLoading.set(false);
    this.practiceDashboardLoading.set(true);
    this.practiceDashboardOverviewSub = this.pmPracticeDashboardApi
      .getOverview({
        parentUserId: parent,
        actingUserId: acting,
        recentTake: 12,
        includeCharts: false,
        includeSummaryTrends,
        fromUtc: range?.fromUtc,
        toUtc: range?.toUtc
      })
      .pipe(
        finalize(() => {
          this.practiceDashboardLoading.set(false);
        })
      )
      .subscribe({
        next: (data) => {
          if (refreshGen !== this.practiceDashboardRefreshGen) {
            return;
          }
          this.applyPracticeDashboardOverviewShell(data);
          this.loadPracticeDashboardChartsInBackground(parent, acting, range, refreshGen);
        },
        error: () => {
          if (refreshGen !== this.practiceDashboardRefreshGen) {
            return;
          }
          this.practiceDashboard.set(null);
          this.allDeadlines.set([]);
        }
      });

    if (directoryUserId > 0) {
      this.practiceDashboardQuickLinksSub = this.pmPracticeDashboardApi
        .getQuickLinks({
          parentUserId: parent,
          actingUserId: acting,
          directoryUserId,
          fromUtc: range?.fromUtc,
          toUtc: range?.toUtc
        })
        .subscribe({
          next: (quickLinks) => {
            this.dashboardQuickLinksCounts.set(
              this.mergeDashboardQuickLinksWithOverview(quickLinks, this.practiceDashboard())
            );
          },
          error: () => {
            const overview = this.practiceDashboard();
            if (overview) {
              this.dashboardQuickLinksCounts.set(this.mergeDashboardQuickLinksWithOverview(null, overview));
            }
          }
        });

      this.dashboardInvoicesLoading.set(true);
      this.practiceDashboardInvoicesSub = this.loadDashboardPracticeInvoicesFromApi(
        parent,
        acting,
        directoryUserId
      )
        .pipe(
          finalize(() => {
            this.dashboardInvoicesLoading.set(false);
          })
        )
        .subscribe({
          next: (invoices) => {
            this.applyDashboardPracticeInvoicePage(invoices);
          },
          error: () => {
            this.dashboardPracticeInvoices.set([]);
            this.dashboardPracticeInvoicePmLinks.set([]);
            this.dashboardPracticeInvoiceTotalCount.set(null);
            this.dashboardPracticeInvoiceKpis.set(null);
          }
        });
    } else {
      this.dashboardQuickLinksCounts.set(this.mergeDashboardQuickLinksWithOverview(null, this.practiceDashboard()));
      this.dashboardInvoicesLoading.set(false);
      this.dashboardPracticeInvoices.set([]);
      this.dashboardPracticeInvoicePmLinks.set([]);
      this.dashboardPracticeInvoiceTotalCount.set(null);
      this.dashboardPracticeInvoiceKpis.set(null);
    }

    // Client summary widget — independent call, doesn't block the shell.
    this.dashboardClientSummaryLoading.set(true);
    this.practiceDashboardClientSummarySub = this.pmPracticeDashboardApi
      .getClientSummary({ parentUserId: parent, directoryUserId, fromUtc: range?.fromUtc, toUtc: range?.toUtc })
      .pipe(timeout(12000), finalize(() => this.dashboardClientSummaryLoading.set(false)))
      .subscribe({
        next: (data) => this.dashboardClientSummary.set(data),
        error: () => this.dashboardClientSummary.set(null)
      });
  }

  /** Bumps on each dashboard refresh so stale overview/chart responses are ignored. */
  private practiceDashboardRefreshGen = 0;

  /** Phase 1: KPIs, recent lists, trends — spinner clears when this completes. */
  private applyPracticeDashboardOverviewShell(data: PracticeDashboardOverviewApi): void {
    this.practiceDashboard.set(data);
    this.dashboardQuickLinksCounts.set(
      this.mergeDashboardQuickLinksWithOverview(this.dashboardQuickLinksCounts(), data)
    );
    const dRows = (data.recentDeadlines ?? []).filter(
      (r) => !r.isDeleted && r.isActive !== false
    );
    // No period window: the list is the practice's deadlines, each shown at its current period.
    this.allDeadlines.set(
      dRows
        .map((r) => mapDeadlineResponseToDashboardRow(r as DeadlineResponseApi))
        .filter((row): row is NonNullable<typeof row> => row != null)
    );
    this.selectedServices.set(['All']);
    this.selectedStatuses.set(['All']);
    this.deadlinesSelectedClient.set('All Clients');
    this.dashboardDeadlinesClientSearch.set('');
    this.selectedChartClients.set(['All']);
    this.selectedChartServices.set(['All']);
    this.tasksSelectedClient.set('All Clients');
    this.dashboardTasksSearch.set('');
  }

  private loadPracticeDashboardChartsInBackground(
    parentUserId: number,
    actingUserId: number,
    range: { fromUtc?: string; toUtc?: string } | undefined,
    refreshGen: number
  ): void {
    this.practiceDashboardChartsSub?.unsubscribe();
    this.practiceDashboardChartsLoading.set(true);
    const restrictToUserId = actingUserId !== parentUserId ? actingUserId : undefined;
    const chartOpts = {
      restrictToUserId,
      fromUtc: range?.fromUtc ?? null,
      toUtc: range?.toUtc ?? null
    };
    this.practiceDashboardChartsSub = forkJoin({
      pipeline: this.pmAnalyticsApi.tasksPipeline(parentUserId, chartOpts),
      deadlines: this.pmAnalyticsApi.deadlines(parentUserId, {})
    })
      .pipe(
        finalize(() => {
          if (refreshGen === this.practiceDashboardRefreshGen) {
            this.practiceDashboardChartsLoading.set(false);
          }
        })
      )
      .subscribe({
        next: ({ pipeline, deadlines }) => {
          if (refreshGen !== this.practiceDashboardRefreshGen) {
            return;
          }
          const shell = this.practiceDashboard();
          if (!shell) {
            return;
          }
          this.practiceDashboard.set(
            this.mergePracticeDashboardChartsFromAnalytics(shell, pipeline, deadlines)
          );
        },
        error: () => {
          /* Keep phase-1 shell; charts stay empty until retry. */
        }
      });
  }

  /** Phase 2: analytics chart endpoints only (avoids second full dashboard overview). */
  private mergePracticeDashboardChartsFromAnalytics(
    shell: PracticeDashboardOverviewApi,
    tasksPipeline: TasksPipelineResponseApi,
    deadlineAnalytics: DeadlineAnalyticsResponseApi
  ): PracticeDashboardOverviewApi {
    return {
      ...shell,
      tasksPipeline,
      deadlineAnalytics
    };
  }

  /**
   * Quick-links API supplies timesheets/clients/team/invoices only.
   * Tasks and deadlines always come from overview summary (same period, no duplicate analytics SQL).
   */
  private mergeDashboardQuickLinksWithOverview(
    quickLinks: PracticeDashboardQuickLinksApi | null | undefined,
    overview: PracticeDashboardOverviewApi | null | undefined
  ): PracticeDashboardQuickLinksApi {
    const ql = quickLinks ?? this.emptyPracticeQuickLinks();
    const summary = overview?.summary;
    const invoicePreviewTotal = this.dashboardPracticeInvoiceTotalCount();
    const invoicesTotal =
      invoicePreviewTotal != null && Number.isFinite(invoicePreviewTotal) && invoicePreviewTotal >= 0
        ? Math.max(0, Math.trunc(invoicePreviewTotal))
        : ql.invoicesTotal;
    return {
      tasksTotal: summary ? Math.max(0, summary.tasksTotal ?? 0) : ql.tasksTotal,
      deadlinesTotal: summary ? Math.max(0, summary.deadlinesTotal ?? 0) : ql.deadlinesTotal,
      timesheetsTotal: ql.timesheetsTotal,
      clientsTotal: ql.clientsTotal,
      teamTotal: ql.teamTotal,
      invoicesTotal
    };
  }

  /** Dashboard Income card: same PM list as Firm Finance (avoids slow/failing preview route). */
  private loadDashboardPracticeInvoicesFromApi(
    parentUserId: number,
    actingUserId: number,
    directoryUserId: number
  ) {
    const page = 1;
    const pageSize = 12;
    return this.pmFinanceInvoicesApi
      .listPmLinksForPractice(parentUserId, actingUserId, directoryUserId, page, pageSize)
      .pipe(
        map((pmPage) => this.mapPmPracticePageToDashboardPreview(pmPage)),
        catchError(() => of(this.emptyPracticeInvoicePage()))
      );
  }

  private applyDashboardPracticeInvoicePage(invoices: FinancePracticeInvoicePageResponseApi): void {
    const items = invoices?.capium3?.items ?? [];
    const totalCount = Math.max(0, Number(invoices?.capium3?.totalCount) || 0);
    this.dashboardPracticeInvoices.set(items);
    this.dashboardPracticeInvoicePmLinks.set(invoices?.pmInvoiceLinks ?? []);
    this.dashboardPracticeInvoiceTotalCount.set(totalCount);
    this.dashboardPracticeInvoiceKpis.set(
      this.normalizeDashboardCapium3Kpis(
        invoices?.capium3Kpis,
        totalCount,
        items,
        invoices?.pmInvoiceLinks
      )
    );
    this.dashboardQuickLinksCounts.set(
      this.mergeDashboardQuickLinksWithOverview(this.dashboardQuickLinksCounts(), this.practiceDashboard())
    );
  }

  private mapPmPracticePageToDashboardPreview(
    page: PmInvoicePracticePageResponseApi
  ): FinancePracticeInvoicePageResponseApi {
    const items = (page.items ?? []).map((row) => this.mapPmDetailToDashboardCapium3Row(row));
    const kpis = page.kpis;
    const capium3Kpis: Capium3PracticeInvoiceKpiTotalsApi = {
      revenueGbp: Number(kpis?.revenueGbp ?? 0),
      paidGbp: Number(kpis?.paidGbp ?? 0),
      overdueGbp: Number(kpis?.overdueGbp ?? 0),
      outstandingGbp: Number(kpis?.outstandingGbp ?? 0),
      draftGbp: Number(kpis?.draftGbp ?? 0)
    };
    if (kpis?.totalCount != null) {
      capium3Kpis.totalCount = Math.max(0, Number(kpis.totalCount) || 0);
    }
    if (kpis?.draftCount != null) {
      capium3Kpis.draftCount = Math.max(0, Number(kpis.draftCount) || 0);
    }
    if (kpis?.paidCount != null) {
      capium3Kpis.paidCount = Math.max(0, Number(kpis.paidCount) || 0);
    }
    if (kpis?.overdueCount != null) {
      capium3Kpis.overdueCount = Math.max(0, Number(kpis.overdueCount) || 0);
    }
    if (kpis?.unpaidCount != null) {
      capium3Kpis.unpaidCount = Math.max(0, Number(kpis.unpaidCount) || 0);
    }
    return {
      capium3: {
        items,
        totalCount: Math.max(0, Number(page.totalCount) || 0),
        page: page.page ?? 1,
        pageSize: page.pageSize ?? 12
      },
      capium3Kpis,
      pmInvoiceLinks: page.items ?? []
    };
  }

  private mapPmDetailToDashboardCapium3Row(row: PmInvoiceDetailRowApi): FinanceCapium3PracticeInvoiceRowApi {
    const isDraft = Number(row.status ?? 0) <= 0;
    const amount = Number(row.amount ?? 0);
    return {
      clientUniversalId: (row.f_Client_UniversalId ?? '').trim(),
      companyMasterId: 0,
      invoice: {
        id: row.id,
        vNo:
          row.invoiceNo != null && Number(row.invoiceNo) > 0
            ? String(row.invoiceNo)
            : String(row.id),
        vDate: row.createdOn ?? null,
        dueDate: row.dueDate ?? null,
        grossAmount: amount,
        taxAmount: 0,
        balanceRemaining: isDraft || amount <= 0 ? 0 : amount,
        isDraft,
        f_CompanyMaster: null
      }
    };
  }

  private emptyPracticeInvoicePage(): FinancePracticeInvoicePageResponseApi {
    return {
      capium3: { items: [], totalCount: 0, page: 1, pageSize: 12 },
      capium3Kpis: {
        totalCount: 0,
        draftCount: 0,
        paidCount: 0,
        overdueCount: 0,
        totalGrossAmount: 0,
        totalBalanceRemaining: 0
      },
      pmInvoiceLinks: []
    };
  }

  /** Maps dashboard invoice KPIs (PM `PM_InvoiceDetail` or legacy Capium 3 shape). */
  private normalizeDashboardCapium3Kpis(
    raw: Capium3PracticeInvoiceKpiTotalsApi | null | undefined,
    capium3TotalCount: number,
    items: FinanceCapium3PracticeInvoiceRowApi[],
    pmLinks?: PmInvoiceDetailRowApi[] | null
  ): Capium3PracticeInvoiceKpiTotalsApi {
    const src = (raw ?? {}) as Capium3PracticeInvoiceKpiTotalsApi & Record<string, unknown>;
    const revenueGbp = Number(src.revenueGbp ?? src.totalGrossAmount ?? 0);
    const outstandingGbp = Number(src.outstandingGbp ?? src.totalBalanceRemaining ?? 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const countFromPm = (pmLinks ?? []).length > 0;
    const paidFromItems = countFromPm
      ? (pmLinks ?? []).filter((r) => {
          const isDraft = Number(r.status ?? 0) <= 0;
          const amount = Number(r.amount ?? 0);
          return !isDraft && amount <= 0;
        }).length
      : items.filter((x) => {
          const bal = Number(x.invoice?.balanceRemaining ?? 0);
          const draft = !!x.invoice?.isDraft;
          return !draft && bal <= 0;
        }).length;
    const overdueFromItems = countFromPm
      ? (pmLinks ?? []).filter((r) => {
          const isDraft = Number(r.status ?? 0) <= 0;
          const amount = Number(r.amount ?? 0);
          if (isDraft || amount <= 0) {
            return false;
          }
          const dueRaw = String(r.dueDate ?? '').trim();
          if (!dueRaw) {
            return false;
          }
          const due = new Date(dueRaw);
          if (Number.isNaN(due.getTime())) {
            return false;
          }
          due.setHours(0, 0, 0, 0);
          return due < today;
        }).length
      : items.filter((x) => {
          const dueRaw = String(x.invoice?.dueDate ?? '').trim();
          if (!dueRaw) {
            return false;
          }
          const due = new Date(dueRaw);
          if (Number.isNaN(due.getTime())) {
            return false;
          }
          due.setHours(0, 0, 0, 0);
          const bal = Number(x.invoice?.balanceRemaining ?? 0);
          return due < today && bal > 0;
        }).length;
    const draftFromItems = countFromPm
      ? (pmLinks ?? []).filter((r) => Number(r.status ?? 0) <= 0).length
      : items.filter((x) => !!x.invoice?.isDraft).length;
    const hasApiCounts =
      Number.isFinite(src.totalCount) ||
      Number.isFinite(src.draftCount) ||
      Number.isFinite(src.paidCount) ||
      Number.isFinite(src.overdueCount);
    return {
      totalCount: capium3TotalCount,
      draftCount: Number.isFinite(src.draftCount) ? Math.max(0, Math.trunc(src.draftCount!)) : draftFromItems,
      paidCount: Number.isFinite(src.paidCount) ? Math.max(0, Math.trunc(src.paidCount!)) : paidFromItems,
      overdueCount: Number.isFinite(src.overdueCount)
        ? Math.max(0, Math.trunc(src.overdueCount!))
        : overdueFromItems,
      unpaidCount: Number.isFinite(src.unpaidCount)
        ? Math.max(0, Math.trunc(src.unpaidCount!))
        : hasApiCounts
          ? Math.max(
              0,
              capium3TotalCount -
                (Number.isFinite(src.paidCount) ? Math.trunc(src.paidCount!) : paidFromItems) -
                (Number.isFinite(src.draftCount) ? Math.trunc(src.draftCount!) : draftFromItems)
            )
          : undefined,
      totalGrossAmount: Number.isFinite(revenueGbp) ? revenueGbp : 0,
      totalBalanceRemaining: Number.isFinite(outstandingGbp) ? outstandingGbp : 0,
      revenueGbp: Number.isFinite(revenueGbp) ? revenueGbp : 0,
      paidGbp: Number(src.paidGbp ?? 0),
      overdueGbp: Number(src.overdueGbp ?? 0),
      outstandingGbp: Number.isFinite(outstandingGbp) ? outstandingGbp : 0,
      draftGbp: Number(src.draftGbp ?? 0)
    };
  }

  private emptyPracticeQuickLinks(): PracticeDashboardQuickLinksApi {
    return {
      tasksTotal: 0,
      deadlinesTotal: 0,
      timesheetsTotal: 0,
      clientsTotal: 0,
      teamTotal: 0,
      invoicesTotal: 0
    };
  }

  /**
   * Loads the full directory client list for dashboard client filters (Tasks, Invoices, Activity, etc.).
   * Uses lightweight `getClientsOptional` (same as Tasks / Finance name lookup) and forces a refresh when empty.
   */
  ensureDashboardDirectoryClientsLoaded(): void {
    if (!this.userContext.practiceContextReady()) {
      return;
    }
    const parent = this.effectiveDirectoryParentUserId();
    const listUserId = this.effectiveDirectoryUserIdForClientsList();
    if (parent <= 0 || listUserId <= 0) {
      return;
    }
    const cacheKey = `${parent}:${listUserId}:optional`;
    const force =
      this.directoryClientPickerApiRows().length === 0 || this.bookMeetingPickerCacheKey !== cacheKey;
    void this.loadClientsForBookMeetingPicker(force);
  }

  private activityUtcRangeForSelection(selection: string): { fromUtc?: string; toUtc?: string } {
    if (this.dashboardActivityUseCustomRange()) {
      return this.dashboardActivityCustomUtcRange();
    }
    const now = new Date();
    const startOfDayIso = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).toISOString();
    const endOfDayIso = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();
    const normalized = (selection ?? '').trim();

    if (normalized === 'Today') {
      return { fromUtc: startOfDayIso(now), toUtc: endOfDayIso(now) };
    }
    if (normalized === '7 Days') {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      start.setDate(start.getDate() - 6);
      return { fromUtc: startOfDayIso(start), toUtc: endOfDayIso(now) };
    }
    if (normalized === '30 Days') {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      start.setDate(start.getDate() - 29);
      return { fromUtc: startOfDayIso(start), toUtc: endOfDayIso(now) };
    }
    if (normalized === 'This Week') {
      const ref = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const offset = (ref.getDay() + 6) % 7;
      const start = new Date(ref);
      start.setDate(ref.getDate() - offset);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { fromUtc: startOfDayIso(start), toUtc: endOfDayIso(end) };
    }
    if (normalized === 'This Month') {
      const y = now.getFullYear();
      const m = now.getMonth();
      const start = new Date(y, m, 1);
      const end = new Date(y, m + 1, 0);
      return { fromUtc: startOfDayIso(start), toUtc: endOfDayIso(end) };
    }

    return {};
  }

  private dashboardActivityCustomUtcRange(): { fromUtc?: string; toUtc?: string } {
    const fromRaw = this.dashboardActivityCustomFrom().trim();
    const toRaw = this.dashboardActivityCustomTo().trim();
    if (!fromRaw && !toRaw) {
      return {};
    }
    const startOfDayIso = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).toISOString();
    const endOfDayIso = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();
    const from = fromRaw ? new Date(`${fromRaw}T00:00:00`) : null;
    const to = toRaw ? new Date(`${toRaw}T00:00:00`) : null;
    return {
      fromUtc: from && !Number.isNaN(from.getTime()) ? startOfDayIso(from) : undefined,
      toUtc: to && !Number.isNaN(to.getTime()) ? endOfDayIso(to) : undefined
    };
  }

  loadDashboardActivityFromApi(): void {
    const parent = this.effectiveDirectoryParentUserId();
    const acting = this.effectiveDirectoryUserId();
    if (parent <= 0 || acting <= 0) {
      this.allActivities.set([]);
      return;
    }
    const range = this.activityUtcRangeForSelection(this.selectedActivityTime());
    const typeQ = this.selectedActivityType() === 'All' ? undefined : this.selectedActivityType().trim().toLowerCase();
    const categoryChip = this.selectedActivityCategory();
    const activeTab = this.activeActivityTab();
    // Bulk Activity: only the Add Bulk Deadlines summary rows (pm-api 'Bulk' category — every other tab and
    // category leaves them out server-side, so they never mix into the other tabs).
    const category =
      activeTab === 'Bulk Activity'
        ? 'Bulk'
        : activeTab === 'Email Activity'
          ? 'email'
          : categoryChip !== 'All'
            ? categoryChip
            : undefined;
    const clientFilter = this.selectedActivityClient();
    const clientQ = clientFilter === 'All' ? undefined : clientFilter.trim();
    this.dashboardActivitySub?.unsubscribe();
    this.dashboardActivitySub = this.pmAuditLogsApi
      .listActivity({
        parentUserId: parent,
        actingUserId: acting,
        fromUtc: range.fromUtc,
        toUtc: range.toUtc,
        clientUniversalId: clientQ,
        q: typeQ,
        category,
        performedByUserId: undefined,
        // Bulk summaries only in the Bulk Activity tab (the audit logs page doesn't pass this, so it shows them).
        excludeBulk: activeTab !== 'Bulk Activity',
        pageNumber: 1,
        pageSize: 30
      })
      .subscribe({
        next: (page) => {
          const items = this.mapPracticeAuditRowsToActivityItems(
            (page.items ?? []) as unknown as PracticeAuditActivityRowApi[]
          );
          this.allActivities.set(items);
          const selectedId = this.dashboardActivitySelectedId();
          if (this.dashboardActivityPanelExpanded() && selectedId != null) {
            const still = items.find((a) => a.id === selectedId);
            if (still) {
              const clientId = still.clientUniversalId.trim();
              if (clientId) {
                this.loadDashboardActivityRelatedForClient(clientId, selectedId);
              }
            } else if (items[0]) {
              this.selectDashboardActivity(items[0].id);
            } else {
              this.dashboardActivitySelectedId.set(null);
              this.dashboardActivityRelated.set([]);
            }
          }
        },
        error: () => {
          this.allActivities.set([]);
          this.dashboardActivityRelated.set([]);
        }
      });
  }

  private loadDashboardActivityRelatedForClient(clientUniversalId: string, excludeId: number): void {
    const parent = this.effectiveDirectoryParentUserId();
    const acting = this.effectiveDirectoryUserId();
    const clientId = clientUniversalId.trim();
    if (parent <= 0 || acting <= 0 || !clientId) {
      this.dashboardActivityRelated.set([]);
      return;
    }
    this.dashboardActivityRelatedSub?.unsubscribe();
    this.dashboardActivityRelatedSub = this.pmAuditLogsApi
      .listActivity({
        parentUserId: parent,
        actingUserId: acting,
        clientUniversalId: clientId,
        pageNumber: 1,
        pageSize: 25
      })
      .pipe(
        catchError(() =>
          of({
            items: [],
            totalCount: 0,
            pageNumber: 1,
            pageSize: 25
          } as PracticeAuditActivityPageApi)
        )
      )
      .subscribe({
        next: (page) => {
          const related = this.mapPracticeAuditRowsToActivityItems(
            (page.items ?? []) as unknown as PracticeAuditActivityRowApi[]
          ).filter((a) => a.id !== excludeId);
          this.dashboardActivityRelated.set(related.slice(0, 12));
        },
        error: () => {
          this.dashboardActivityRelated.set([]);
        }
      });
  }

  private inferDashboardActivityAction(r: PracticeAuditActivityRowApi): DashboardActivityActionLabel {
    const hay = `${r.typeOfActivity ?? ''} ${r.name ?? ''} ${r.description ?? ''}`.toLowerCase();
    if (hay.includes('delete') || hay.includes('removed')) {
      return 'DELETED';
    }
    if (hay.includes('update') || hay.includes('edit') || hay.includes('modified')) {
      return 'UPDATED';
    }
    if (hay.includes('receive') || hay.includes('received')) {
      return 'RECEIVED';
    }
    if (hay.includes('create')) {
      return 'CREATED';
    }
    if (hay.includes('add')) {
      return 'ADDED';
    }
    return 'ACTIVITY';
  }

  private dashboardActivityActionColors(
    action: DashboardActivityActionLabel
  ): { bgColor: string; accentColor: string } {
    switch (action) {
      case 'ADDED':
      case 'CREATED':
        return { bgColor: 'var(--ds-palette-emerald-50)', accentColor: 'var(--ds-palette-emerald-600)' };
      case 'UPDATED':
        return { bgColor: 'var(--ds-palette-orange-50)', accentColor: 'var(--ds-palette-orange-600)' };
      case 'DELETED':
        return { bgColor: 'var(--ds-palette-red-50)', accentColor: 'var(--ds-palette-red-600)' };
      case 'RECEIVED':
        return { bgColor: 'var(--ds-palette-blue-50)', accentColor: 'var(--ds-palette-blue-600)' };
      default:
        return { bgColor: 'var(--ds-palette-violet-50)', accentColor: 'var(--ds-palette-violet-700)' };
    }
  }

  private resolveDashboardActivityClientName(clientUniversalId: string): string {
    const id = clientUniversalId.trim().toLowerCase();
    if (!id) {
      return '—';
    }
    for (const r of this.clientsBookMeetingPickerRows()) {
      const u = (r.directoryClientUniversalId ?? '').trim().toLowerCase();
      const rowId = (r.id ?? '').trim().toLowerCase();
      if (u === id || rowId === id) {
        return (r.name ?? '').trim() || clientUniversalId;
      }
    }
    return resolvePracticeActivityClientDisplayName(clientUniversalId);
  }

  private mapPracticeAuditRowsToActivityItems(rows: PracticeAuditActivityRowApi[]): ActivityItem[] {
    return rows.map((r, i) => {
      const when = r.createdOnUtc ? new Date(r.createdOnUtc) : null;
      const ts =
        when && !Number.isNaN(when.getTime())
          ? `${formatAppDateInZone(when)}, ${when.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', timeZone: appTimeZoneOption() })}`
          : '—';
      const title = (r.name ?? r.typeOfActivity ?? 'Activity').trim() || 'Activity';
      const clientUniversalId = (r.clientUniversalId ?? '').trim();
      const clientName = this.resolveDashboardActivityClientName(clientUniversalId);
      const description = (r.description ?? '').trim();
      const subject = description || clientName || clientUniversalId || '—';
      const performerId = r.createdBy ?? r.userId ?? null;
      const mod = `${r.module ?? ''} ${r.typeOfActivity ?? ''} ${r.name ?? ''}`.toLowerCase();
      const type: ActivityItem['type'] = mod.includes('email') || mod.includes('mail')
        ? 'Email'
        : mod.includes('user') || mod.includes('login') || mod.includes('portal')
          ? 'User'
          : 'Activity';
      const actionLabel = this.inferDashboardActivityAction(r);
      const colors = this.dashboardActivityActionColors(actionLabel);
      return {
        id: Number(r.id) || i + 1,
        type,
        content: title,
        subject,
        user: this.resolveDashboardActivityUserName(performerId),
        source: (r.module ?? 'Practice Management').trim() || 'Practice Management',
        timestamp: ts,
        bgColor: colors.bgColor,
        accentColor: colors.accentColor,
        clientUniversalId,
        clientName,
        actionLabel,
        createdOnUtc: r.createdOnUtc ?? null,
        module: (r.module ?? 'Practice Management').trim() || 'Practice Management',
        description: description || title
      };
    });
  }

  private resolveDashboardActivityUserName(userId: number | null): string {
    if (!userId || userId <= 0) {
      return 'System';
    }
    const direct = this.teamMembersApi().find((m) => Number(m.userId) === Number(userId));
    const byName = String(direct?.name ?? '').trim();
    if (byName.length > 0) {
      return byName;
    }
    const byEmail = String(direct?.email ?? '').trim();
    if (byEmail.length > 0) {
      return byEmail
        .split('@')[0]
        .replace(/[._-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (m) => m.toUpperCase());
    }
    const acting = this.effectiveActingUserId();
    const ctxEmail = String(this.pmUserContext.email() ?? '').trim();
    if (acting > 0 && Number(acting) === Number(userId) && ctxEmail.length > 0) {
      return ctxEmail
        .split('@')[0]
        .replace(/[._-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (m) => m.toUpperCase());
    }
    return 'Unknown user';
  }

  /** Firm parent for directory / dashboard / team: `practice-context` via {@link PmUserContextService.firmParentUserIdForApi}. */
  readonly effectiveDirectoryParentUserId = computed(() => this.userContext.firmParentUserIdForApi());

  /** Acting user for directory and practice APIs (no `environment.directoryUserId`). */
  readonly effectiveDirectoryUserId = computed(() => this.userContext.actingUserMasterIdForApi());

  /**
   * `userId` passed to `GetClientsList` for **client lists** (grid, Book Meeting picker, schedule filters).
   * Super accountant: use firm parent id so the legacy SP returns the full practice client list.
   * Staff / non–super accountant: use the acting user id so the list matches their scoped access.
   */
  effectiveDirectoryUserIdForClientsList(): number {
    const parent = this.effectiveDirectoryParentUserId();
    if (this.isCurrentUserSuperAccountant()) {
      return parent > 0 ? parent : this.effectiveDirectoryUserId();
    }
    return this.effectiveDirectoryUserId();
  }

  /**
   * `userId` query for directory **contact** APIs (`GET client-users`, `GET contacts`, `POST contacts`).
   * Must match create — rows use `CompanyContactMaster.F_UserMaster` from this acting user.
   */
  effectiveDirectoryActingUserIdForContacts(): number | undefined {
    const eff = this.effectiveDirectoryUserId();
    return eff > 0 ? eff : undefined;
  }

  /**
   * When the URL is `/clients/:numericId` and that row is not on the loaded directory page,
   * load one row from pm-api so deep links and off-page clients still render detail.
   */
  private async ensureClientDetailRowFromDirectory(): Promise<void> {
    const id = this.clientDetailId();
    if (!id) {
      return;
    }
    if (this.clientsDirectoryRows().some((r) => r.id === id)) {
      this.clientDetailDirectoryFetched.set(null);
      this.clientDetailDirectoryFetchLoading.set(false);
      return;
    }
    if (!this.userContext.practiceContextReady()) {
      this.clientDetailDirectoryFetchLoading.set(false);
      return;
    }
    const idTrim = id.trim();
    if (!idTrim || !/^\d+(\.\d+)?$/.test(idTrim)) {
      this.clientDetailDirectoryFetched.set(null);
      this.clientDetailDirectoryFetchLoading.set(false);
      return;
    }

    const seq = ++this.clientDetailFetchSeq;
    this.clientDetailDirectoryFetchLoading.set(true);
    try {
      const rowApi = await firstValueFrom(
        this.pmDirectoryApi.getClient(
          idTrim,
          this.effectiveDirectoryParentUserId(),
          this.effectiveDirectoryUserIdForClientsList()
        )
      );
      if (seq !== this.clientDetailFetchSeq || this.clientDetailId() !== id) {
        return;
      }
      this.clientDetailDirectoryFetched.set({ id, row: this.mapDirectoryClientRowToListRow(rowApi) });
    } catch (e: unknown) {
      if (seq !== this.clientDetailFetchSeq || this.clientDetailId() !== id) {
        return;
      }
      this.clientDetailDirectoryFetched.set(null);
      const description =
        e instanceof HttpErrorResponse
          ? typeof e.error === 'object' && e.error && 'detail' in e.error
            ? String((e.error as { detail?: string }).detail)
            : e.message
          : e instanceof Error
            ? e.message
            : 'Request failed';
      toast.error('Could not load client', { description: description.slice(0, 240) });
    } finally {
      if (seq === this.clientDetailFetchSeq && this.clientDetailId() === id) {
        this.clientDetailDirectoryFetchLoading.set(false);
      }
    }
  }

  private maybeRefreshDirectoryClientsList(path: string): void {
    if (this.activeItem() !== 'Clients') {
      return;
    }
    if (!this.userContext.practiceContextReady()) {
      return;
    }
    const onList = path === '/clients';
    const onDetail = /^\/clients\/[^/]+$/.test(path);
    if (!onList && !onDetail) {
      return;
    }
    void this.loadDirectoryClientsPage(this.clientsDirectoryPageNumber());
    void this.loadClientsAnalyticsBaseline();
    void this.loadClientsAnalyticsRevenue();
  }

  ngOnDestroy() {
    this.clientsToolbarResizeObserver?.disconnect();
    this.topNavResizeObserver?.disconnect();
    if (this.topNavSettleTimer) clearTimeout(this.topNavSettleTimer);
    document.removeEventListener('click', this.onDocumentClickCapture, true);
    window.removeEventListener('pm-announcements-changed', this.onAnnouncementsChanged);
    document.removeEventListener('scroll', this.onClientsToolbarPopoverViewportChange, true);
    if (typeof window !== 'undefined' && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onClientsToolbarPopoverViewportChange);
      window.visualViewport.removeEventListener('scroll', this.onClientsToolbarPopoverViewportChange);
    }
    this.profileDirectorySyncSubscription?.unsubscribe();
    this.profileDirectorySyncSubscription = null;
    this.headerAnnouncementsSub?.unsubscribe();
    this.headerAnnouncementsSub = undefined;
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }
    this.clearBulkSmsOtpCountdown();
    this.documentHubPage?.closeDocumentHubPreview();
    this.clearBulkSmsOtpCountdown();
    if (this.headerGlobalSearchDebounceTimer) {
      clearTimeout(this.headerGlobalSearchDebounceTimer);
      this.headerGlobalSearchDebounceTimer = null;
    }
    if (this.clientsDirectoryReloadTimer) {
      clearTimeout(this.clientsDirectoryReloadTimer);
      this.clientsDirectoryReloadTimer = null;
    }
    if (this.clientsUsersReloadTimer) {
      clearTimeout(this.clientsUsersReloadTimer);
      this.clientsUsersReloadTimer = null;
    }
    if (this.assignMatrixSearchReloadTimer) {
      clearTimeout(this.assignMatrixSearchReloadTimer);
      this.assignMatrixSearchReloadTimer = null;
    }
    this.assignMatrixRequestSub?.unsubscribe();
    this.assignMatrixRequestSub = undefined;
    this.headerGlobalSearchRequestSub?.unsubscribe();
    this.practiceDashboardOverviewSub?.unsubscribe();
    this.practiceDashboardOverviewSub = null;
    this.practiceDashboardChartsSub?.unsubscribe();
    this.practiceDashboardChartsSub = null;
    this.practiceDashboardQuickLinksSub?.unsubscribe();
    this.practiceDashboardQuickLinksSub = null;
    this.practiceDashboardInvoicesSub?.unsubscribe();
    this.practiceDashboardInvoicesSub = null;
    this.practiceDashboardClientSummarySub?.unsubscribe();
    this.practiceDashboardClientSummarySub = null;
    this.dashboardActivitySub?.unsubscribe();
    this.dashboardActivitySub = null;
  }

  /** Navbar + greeting; hydrated from `GET .../team/members` for the acting user when pm-api context is set. */
  profile = signal({
    initials: '•',
    name: '',
    role: '',
    email: ''
  });

  /** Copies a value shown on the Companies House Company Profile card (e.g. the company number) to
   *  the clipboard, with a toast confirming success or failure. No-ops for the '—' placeholder. */
  copyChValue(value: string, label: string): void {
    if (!value || value === '—') return;
    navigator.clipboard.writeText(value).then(
      () => toast.success(`${label} copied to clipboard.`),
      () => toast.error(`Could not copy ${label.toLowerCase()}.`)
    );
  }
  activePeriod = signal('This Month');
  fromDate = signal('');
  /** Custom range end (yyyy-mm-dd); used with `fromDate` for dashboard overview API. */
  toDate = signal('');
  todayDate = signal(new Date().toISOString().split('T')[0]);

  // Dropdown tracking
  activeDropdown = signal<string | null>(null);

  // Multi-select state (Table)
  selectedServices = signal<string[]>(['All']);
  selectedStatuses = signal<string[]>(['All']);
  deadlinesSelectedClient = signal<string>('All Clients');
  dashboardDeadlinesClientSearch = signal('');

  // Multi-select state (Chart)
  selectedChartClients = signal<string[]>(['All']);
  selectedChartServices = signal<string[]>(['All']);

  // Activity Feed State
  activeActivityTab = signal<string>('Activity');
  activitySearchQuery = signal<string>('');
  selectedActivityType = signal<string>('All');
  selectedActivityCategory = signal<AuditLogCategory | 'All'>('All');
  /** The Activity feed opens on the same window as the dashboard's own default period (This Month), so a week that has just started (every Monday) does not open it empty beside an Activity count of hundreds. */
  selectedActivityTime = signal<string>('This Month');
  selectedActivityClient = signal<string>('All');
  dashboardActivityClientSearch = signal<string>('');
  readonly lastDashboardActivityLoadKey = signal('');

  // New Dropdown States (Row 6 Cards)
  tasksSelectedClient = signal<string>('All Clients');
  tasksSelectedPriority = signal<string>('All Priority');
  tasksSelectedView = signal<'All Tasks' | 'Created by me' | 'Assigned to me'>('All Tasks');
  /** Dashboard tasks card search (filters recent tasks from overview API). */
  dashboardTasksSearch = signal('');
  /** Search text inside the Tasks card client dropdown. */
  dashboardTasksClientSearch = signal('');
  dashboardInvoicesLoading = signal(false);
  /** Cancels prior dashboard overview request when a newer refresh starts. */
  private practiceDashboardOverviewSub: Subscription | null = null;
  private practiceDashboardChartsSub: Subscription | null = null;
  private practiceDashboardQuickLinksSub: Subscription | null = null;
  private practiceDashboardInvoicesSub: Subscription | null = null;
  private practiceDashboardClientSummarySub: Subscription | null = null;
  /** Cancels prior dashboard activity request for independent logs filter loads. */
  private dashboardActivitySub: Subscription | null = null;
  dashboardPracticeInvoices = signal<FinanceCapium3PracticeInvoiceRowApi[]>([]);
  /** Raw PM invoice rows for the dashboard Invoices card table (title, status, etc.). */
  dashboardPracticeInvoicePmLinks = signal<PmInvoiceDetailRowApi[]>([]);
  dashboardPracticeInvoiceTotalCount = signal<number | null>(null);
  dashboardPracticeInvoiceKpis = signal<Capium3PracticeInvoiceKpiTotalsApi | null>(null);
  invoicesSelectedClient = signal<string>('All Clients');
  /** Search text inside the Invoices card client dropdown. */
  dashboardInvoicesClientSearch = signal('');
  /** Dashboard Invoices card: All / Paid / Overdue / Outstanding / Due / Draft (matches Practice → Finance status set). */
  dashboardInvoicesPaymentFilter = signal<'all' | DashboardInvoicePreviewRow['status']>('all');

  // Settings State
  activeSettingItem = signal<string>('Services');
  activeServiceTab = signal<string>('Services');
  settingsTabSwitching = signal(false);

  private readonly floatingMenuAssignDefaults: Record<FloatingMenuToolKey, boolean> = {
    communications: true,
    recordTime: true,
    ticketsHub: true,
    documentHub: true,
    chatBot: true
  };

  // Team & Permissions (pm-api)
  private readonly pmTeamApi = inject(PmTeamApiService);
  private readonly smsTemplateApi = inject(PmSmsTemplateApiService);
  private readonly pmPermissionsApi = inject(PmPermissionsApiService);
  private readonly pmProfileDirectorySync = inject(PmProfileDirectorySyncService);
  private readonly pmUserContext = inject(PmUserContextService);
  private readonly pmAuditLogsApi = inject(PmAuditLogsApiService);

  teamApiLoading = signal(false);
  teamApiError = signal<string | null>(null);
  teamMembersApi = signal<TeamMemberRowApi[]>([]);

  readonly permissionRoleKeys: PermissionRoleMatrixApi['roleKey'][] = ['super-accountant', 'accountant', 'staff'];

  activeCustomFieldTab = signal<string>('Custom Fields');

  /* ---- Template Library toolbars (shared pm-page-toolbar). ---- */
  readonly templateLibVariantViews: PmToolbarView[] = [
    { id: 'standard', label: 'Standard' },
    { id: 'custom', label: 'Custom' }
  ];
  readonly templateLibAddCustomActions: PmToolbarAction[] = [
    { id: 'add', label: 'Add Custom Template', icon: 'plus', variant: 'primary' }
  ];

  /** Icon-only Delete (with the selected count) first when rows are ticked, then the tab's buttons. */
  private templateLibActions(selected: number, busy: boolean, rest: PmToolbarAction[]): PmToolbarAction[] {
    if (selected <= 0) {
      return rest;
    }
    return [
      { id: 'delete', label: `Delete selected (${selected})`, icon: 'trash', variant: 'outline-danger', iconOnly: true, disabled: busy },
      ...rest
    ];
  }
  /**
   * Client-type list returned by `GET /api/v1/lookups/client-types`. Loaded
   * with Services settings and template-library tabs. Held in the context service so the
   * Template Library tab components share it.
   */
  templateLibraryClientTypes = this.templateLibraryContext.clientTypes;
  /** pm-api's AmlChecklist.Title column is nvarchar(200) — mirrored here so the field blocks the
   *  overage client-side instead of only failing at Save with a generic validation error. */
  /**
   * Criteria maps to the `Title` column, which is nvarchar(200) in OnBoardingChecklist,
   * AMLChecklist and RiskAssessment alike — past that the insert fails with SQL error 8152 and the
   * user only finds out on Save. Only AML enforced it before; all three do now.
   */
  readonly templateCriteriaMaxLength = 200;
  /**
   * Notes is nvarchar(max) in the database, so this is a UI limit rather than a schema one: an
   * unbounded note is what pushed the criteria tables into sideways scrolling.
   */
  readonly templateNotesMaxLength = 500;

  /** Clients page — scoped UI only */
  clientsPageTab = signal<'clients' | 'users'>('clients');
  clientsPageTabSwitching = signal(false);
  clientsViewMode = signal<'list' | 'grid'>('list');
  clientsSearchQuery = signal('');
  clientsRowCheck = signal<Record<string, boolean>>({});
  /** Demo: per-client status override (default active when absent) */
  clientsStatusById = signal<Record<string, 'active' | 'inactive'>>({});
  /** Bulk confirmation dialog: null = closed */
  clientsBulkModal = signal<'active' | 'inactive' | 'unassign' | null>(null);
  /** True while POST …/directory/clients/bulk is in flight (or demo bulk applying). */
  clientsBulkSaving = signal(false);
  /** Clients list table row whose ⋯ action menu is open. */
  clientsListRowActionsOpenId = signal<string | null>(null);
  clientsListUnassigningId = signal<string | null>(null);
  clientsListUnassignConfirmOpen = signal(false);
  clientsListUnassignTarget = signal<{
    uiKey: string;
    name: string;
    companyMasterId: string;
    universalId: string;
  } | null>(null);
  /** Import clients CSV wizard modal */
  clientsImportModalOpen = signal(false);
  clientsImportStep = signal<'upload' | 'analysing' | 'preview' | 'results'>('upload');
  clientsImportFileName = signal('');
  clientsImportPreviewRows = signal<ClientImportPreviewRow[]>([]);
  clientsImportFilterTab = signal<'all' | 'valid' | 'warnings' | 'errors'>('all');
  /** True while posting imported rows to the directory API. */
  clientsImportSavePending = signal(false);
  clientsImportSaveResult = signal<BulkImportClientsResponseApi | null>(null);
  clientsImportSaveError = signal<string | null>(null);

  /** Clients → Schedule / Communication / … (Document Hub: /document-hub + header icon) */
  clientsWorkspaceSection = signal<ClientsWorkspaceSection>('list');
  clientsTabSwitching = signal(false);
  /** Any Clients tab/view switch in progress: grids, lists and KPI cards shimmer meanwhile. */
  readonly clientsSwitchLoading = computed(
    () => this.clientsTabSwitching() || this.clientsPageTabSwitching() || this.clientsDraftViewSwitching()
  );
  /** Row count for the Drafts / Unassigned shimmer. */
  readonly clientsShimmerRows = [0, 1, 2, 3, 4, 5, 6, 7];
  /** Set while loading `GET …/event-schedules` (directory API configured). */
  clientsScheduleMeetingsLoading = signal(false);
  private clientsScheduleMeetingsRequestSeq = 0;
  /** Default window (days each side of today) fetched from the server on first load — loading the
   *  practice's entire meeting history unfiltered was the main cause of a slow Schedule tab. Widened
   *  on demand by {@link ensureClientsScheduleRangeLoaded} when a date filter reaches outside it. */
  private static readonly CLIENTS_SCHEDULE_DEFAULT_WINDOW_DAYS = 90;
  /** yyyy-MM-dd bounds of what's currently loaded into {@link clientsScheduleMeetings}; null until the
   *  first load completes. Client-side filtering (search/type/date) only needs to re-fetch when a
   *  requested date falls outside this range. */
  clientsScheduleLoadedFromDate: string | null = null;
  clientsScheduleLoadedToDate: string | null = null;
  /**
   * Full client list for Book Meeting + schedule client filter (`GetClientsList` for
   * {@link effectiveDirectoryUserIdForClientsList}), not the paged grid row set.
   */
  clientsBookMeetingPickerRows = signal<ClientListRow[]>([]);
  /** Raw directory rows from `getClientsOptional` — source for dashboard client filter dropdowns. */
  directoryClientPickerApiRows = signal<DirectoryClientRowApi[]>([]);
  /** True while loading {@link clientsBookMeetingPickerRows}. */
  bookMeetingClientsLoading = signal(false);
  private bookMeetingPickerRequestSeq = 0;
  private bookMeetingPickerCacheKey = '';
  clientsScheduleMeetings = signal<ClientsScheduleMeeting[]>([]);
  clientsScheduleSearchQuery = signal('');
  clientsScheduleClientFilter = signal<string>('all');
  clientsScheduleTypeFilter = signal<string>('all');
  /** “Scheduled by” filter (dropdown value = userId string) */
  clientsScheduleScheduledByFilter = signal<string>('all');
  /** Date range filter (yyyy-mm-dd). Empty = no restriction. */
  clientsScheduleDateFromFilter = signal<string>('');
  clientsScheduleDateToFilter = signal<string>('');
  clientsSchedulePageNumber = signal(1);
  clientsSchedulePageSize = signal(50);
  /** In-panel client picker search (schedule filter dropdown). */
  clientsScheduleFilterClientSearch = signal('');
  /** Keyboard-highlighted row index for the Schedule filter panel "Client" dropdown. */
  clientsScheduleFilterClientHighlighted = signal(0);
  /** Keyboard-highlighted row index for the Schedule filter panel "Scheduled by" dropdown. */
  clientsScheduleFilterScheduledByHighlighted = signal(0);
  clientsScheduleGroupBy = signal<'date' | 'month'>('date');
  /** Server-grouped (`GET …/event-schedules/grouped`) page actually rendered by the grid — grouping,
   *  pagination and Meeting Type filtering all happen on pm-api, not in the browser (MeetingType is a
   *  real PM_EventSchedule column as of `044_pm_event_schedule_meeting_type.sql`). Only the
   *  client-name part of search has no backing column, so that alone is narrowed client-side on top
   *  of this page (see {@link loadClientsScheduleGrouped}) — a result can be short of a full page
   *  while it's active, since pm-api doesn't know to backfill from the next page. */
  clientsScheduleGroupedGroups = signal<{ key: string; items: ClientsScheduleMeeting[] }[]>([]);
  clientsScheduleGroupedTotalCount = signal(0);
  private clientsScheduleGroupedRequestSeq = 0;
  /** Row id (`ClientsScheduleMeeting.id`) whose ⋯ action menu is open. */
  clientsScheduleRowActionsOpenId = signal<string | null>(null);
  /** Viewport-relative position for the fixed-position action menu. */
  clientsScheduleRowActionsPos = signal<{ top: number; left: number } | null>(null);
  clientsBookMeetingModalOpen = signal(false);
  /** When set, modal is edit mode and submit calls PUT. */
  bookMeetingEditId = signal<number | null>(null);
  bookMeetingFrom = signal('');
  /** Committed To email chips. */
  bookMeetingToChips = signal<string[]>([]);
  /** Text currently being typed in the To input, not yet committed to a chip. */
  bookMeetingToDraft = signal('');
  /** Committed Cc email chips. */
  bookMeetingCcChips = signal<string[]>([]);
  /** Text currently being typed in the Cc input, not yet committed to a chip. */
  bookMeetingCcDraft = signal('');
  bookMeetingSubject = signal('');
  bookMeetingClientId = signal('');
  bookMeetingClientPanelOpen = signal(false);
  bookMeetingClientSearch = signal('');
  bookMeetingClientFlyoutStyle = signal<Record<string, string> | null>(null);
  bookMeetingType = signal<'in-person' | 'phone' | 'video'>('in-person');
  bookMeetingDate = signal('');
  bookMeetingTimezone = signal('(UTC+06:00) Dhaka');
  bookMeetingStart = signal('');
  bookMeetingEnd = signal('');
  bookMeetingLocation = signal('');

  bookMeetingDurationPreset = signal<string>('10 min');
  bookMeetingMessage = signal('');
  bookMeetingInviteOptions = signal<BookMeetingInviteOption[]>([]);
  bookMeetingInviteSelected = signal<Record<string, boolean>>({});
  bookMeetingInvitePanelOpen = signal(false);
  bookMeetingInviteSearch = signal('');
  bookMeetingInviteFlyoutStyle = signal<Record<string, string> | null>(null);
  /** Show inline email errors after blur or submit attempt. */
  bookMeetingToShowError = signal(false);
  bookMeetingCcShowError = signal(false);
  bookMeetingTimePanel = signal<'start' | 'end' | null>(null);
  bookMeetingTimeFlyoutStyle = signal<Record<string, string> | null>(null);

  bookMeetingQuill: Quill | null = null;

  clientsCommunicationRows = signal<ClientsCommunicationRow[]>([]);
  clientsCommunicationLoading = signal(false);
  clientsCommunicationTotalCount = signal(0);
  clientsCommunicationPageNumber = signal(0);
  clientsCommunicationPageSize = signal(100);
  clientsCommunicationChannel = signal<'email' | 'sms'>('email');
  /** Compose/Schedule filter popover (Email + SMS). */
  clientsCommunicationFilterPanelOpen = signal(false);
  /** Keyboard-highlighted row index for the Comm filter panel "Client" dropdown. */
  clientsCommFilterClientHighlighted = signal(0);
  /** Keyboard-highlighted row index for the Comm filter panel "Done by" dropdown. */
  clientsCommFilterDoneByHighlighted = signal(0);
  /** Keyboard-highlighted row index for the Comm filter panel "Priority" dropdown. */
  clientsCommFilterPriorityHighlighted = signal(0);
  /** Keyboard-highlighted row index for the Comm filter panel "Status" dropdown. */
  clientsCommFilterStatusHighlighted = signal(0);
  /** Keyboard-highlighted row index for the Comm filter panel "Recurring" dropdown. */
  clientsCommFilterRecurringHighlighted = signal(0);
  /** Email list: side panel row id (null = closed). */
  clientsCommunicationEmailDetailRowId = signal<string | null>(null);
  /** Drives enter/leave transform transition for the detail panel. */
  clientsCommunicationEmailDetailPanelReady = signal(false);
  /** Shared Clients → Communication email composer (Compose vs Schedule). */
  clientsCommEmailModalKind = signal<'compose' | 'schedule' | null>(null);
  /** When set, Send / Save draft / Schedule update this row instead of inserting new rows. */
  clientsCommEmailEditingCommunicationId = signal<string | null>(null);
  /** Multi-select “To” recipients (client list row ids). */
  clientsCommEmailToSelected = signal<Record<string, boolean>>({});
  clientsCommEmailToSearchQuery = signal('');
  /** Committed Cc email chips. */
  clientsCommEmailCcChips = signal<string[]>([]);
  /** Text currently being typed in the Cc input, not yet committed to a chip. */
  clientsCommEmailCcDraft = signal('');
  clientsCommEmailCcShowError = signal(false);
  /** Committed Bcc email chips. */
  clientsCommEmailBccChips = signal<string[]>([]);
  /** Text currently being typed in the Bcc input, not yet committed to a chip. */
  clientsCommEmailBccDraft = signal('');
  clientsCommEmailBccShowError = signal(false);
  clientsCommEmailSubject = signal('');
  clientsCommEmailPriority = signal<ClientsCommunicationPriority>('normal');
  clientsCommEmailMessage = signal('');
  @ViewChild('clientsCommEmailRteBody') clientsCommEmailRteBodyRef?: ElementRef<HTMLDivElement>;
  clientsCommEmailScheduleDate = signal('');
  clientsCommEmailScheduleTime = signal('');
  clientsCommEmailRecurring = signal<'yes' | 'no'>('no');
  clientsCommEmailRecurringFromDate = signal('');
  clientsCommEmailRecurringToDate = signal('');
  clientsCommEmailRecurringTime = signal('09:00');
  clientsCommEmailRecurringFrequency = signal<string>('Week');
  clientsCommEmailRecurringWeekday = signal<'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun'>('Wed');
  clientsCommEmailRecurringWeekOfMonth = signal<'1' | '2' | '3' | '4' | '5'>('1');
  clientsCommEmailRecurringMonthOfYear = signal<'1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12'>('1');
  clientsCommEmailRecurringDayOfMonth = signal<number>(1);
  /** Selected attachment file names (compose / schedule). */
  clientsCommEmailAttachmentNames = signal<readonly string[]>([]);
  /** Actual File objects for attachments not yet uploaded (compose / schedule). */
  clientsCommEmailAttachmentFiles = signal<readonly File[]>([]);
  clientsCommSmsAddContact = signal('');
  clientsCommSmsRecurring = signal<'yes' | 'no'>('no');
  /** When recurring is Yes: frequency + date range + time (SMS / Schedule SMS). */
  clientsCommSmsRecurringFrequency = signal<string>('');
  clientsCommSmsRecurringWeekday = signal<'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun'>('Wed');
  clientsCommSmsRecurringWeekOfMonth = signal<'1' | '2' | '3' | '4' | '5'>('1');
  clientsCommSmsRecurringMonthOfYear = signal<'1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12'>('1');
  clientsCommSmsRecurringDayOfMonth = signal<number>(1);
  clientsCommSmsTemplateId = signal<string>('');
  clientsCommSmsBody = signal('');
  clientsCommSmsTemplates = signal<{ id: string; title: string; templateType?: string; body?: string }[]>([]);

  /** Current UK wall-clock date/time (Europe/London, GMT/BST) - not the viewer's own device/browser
   *  timezone. The Clients → Communications compose/schedule Email & SMS forms must always read as
   *  GB time for every viewer, so every "now"/"today" helper below derives from this instead of
   *  `new Date()` local getters. Scoped to this comm feature only - unrelated `isoDateLocal()` call
   *  sites elsewhere (dashboards, reports, etc.) are untouched. */
  private nowInUkForComms(): { y: string; m: string; day: string; hh: string; mm: string } {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(new Date());
    const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    return { y: part('year'), m: part('month'), day: part('day'), hh: part('hour'), mm: part('minute') };
  }

  initClientsCommEmailRte(): void {
    setTimeout(() => {
      const el = this.clientsCommEmailRteBodyRef?.nativeElement;
      if (el) el.innerHTML = pmSanitizeHtml(this.clientsCommEmailMessage());
    }, 0);
  }

  /** Pre-fills a brand new email's body with the firm's saved Email Signature
   *  (Settings → Branding & Portal). Not applied when editing an existing
   *  draft/scheduled item — those load their own saved message body instead. */
  async applyDefaultClientsCommEmailSignature(): Promise<void> {
    const parentUserId = this.effectiveDirectoryParentUserId();
    if (parentUserId <= 0) return;
    const template = await firstValueFrom(
      this.pmSettingsApi.getBrandingTemplate(parentUserId).pipe(catchError(() => of(null)))
    );
    const signature = template?.emailSignature?.trim();
    if (!signature) return;
    const html = this.clientsCommEmailSignatureToHtml(signature);
    this.clientsCommEmailMessage.set(html);
    const el = this.clientsCommEmailRteBodyRef?.nativeElement;
    if (el) el.innerHTML = pmSanitizeHtml(html);
  }

  clientsCommEmailSignatureToHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML.replace(/\r?\n/g, '<br>');
  }

  clientsBulkEmailModalOpen = signal(false);
  clientsBulkEmailSaving = signal(false);
  clientsBulkSmsOtpPhase = signal<'send' | 'verify'>('send');
  /** Six single-digit boxes for inline OTP entry. */
  clientsBulkSmsOtpDigits = signal<string[]>(['', '', '', '', '', '']);
  clientsBulkSmsOtpVerified = signal(false);
  /** Seconds remaining after Send OTP; null when inactive. */
  clientsBulkSmsOtpSecondsLeft = signal<number | null>(null);
  readonly bulkSmsOtpCountdown = pmIntervalTimer();
  /** Loading states for OTP send/verify buttons. */
  clientsBulkSmsOtpSending = signal(false);
  clientsBulkSmsOtpVerifying = signal(false);
  /** Bulk email/SMS “To” options — loaded from `directory/clients/communication-recipients` (not Clients grid). */
  clientsBulkCommunicationRecipients = signal<ClientsEmailToOption[]>([]);
  clientsBulkCommunicationRecipientsLoading = signal(false);
  private bulkCommunicationRecipientsCacheKey = '';
  private bulkCommunicationRecipientsRequestSeq = 0;
  /** Avoid duplicate communication list hydration when context becomes ready after refresh. */
  private lastCommunicationSectionHydrationKey = signal('');
  /** Cc only on first message in bulk send. */
  clientsBulkEmailApplyCcFirstOnly = signal(false);
  /** Cc on every message (default on; mutually exclusive with first-only when either is turned on). */
  clientsBulkEmailApplyCcToAll = signal(true);

  /** Settings → Template Library (custom email + document bodies). */
  readonly templateLibraryQuillModules: QuillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link']
    ]
  };
  clientsBulkEmailQuill: Quill | null = null;

  submissionsRows = signal<ClientsSubmissionsRow[]>([]);
  submissionsLoading = signal(false);
  private submissionsRequestSeq = 0;
  submissionsClientFilter = signal<string>('all');
  submissionsTypeFilter = signal<string>('all');
  submissionsStatusFilter = signal<string>('all');
  submissionsDateFrom = signal('');
  submissionsDateTo = signal('');
  submissionsPageIndex = signal(0);
  submissionsSortKey = signal<ClientsSubmissionsSortKey>('clientName');
  submissionsSortAsc = signal(true);

  lettersMainTab = signal<'templates' | 'sent'>('templates');
  lettersCategoryFilter = signal<'all' | ClientsLetterTemplateRow['category']>('all');
  lettersSearchQuery = signal('');
  lettersTemplateSelection = signal<Record<string, boolean>>({});
  lettersTemplates = signal<ClientsLetterTemplateRow[]>([]);
  lettersSentRows = signal<ClientsLetterSentRow[]>([]);
  lettersGenerateModalOpen = signal(false);
  lettersGenDocumentName = signal('');
  lettersGenClientId = signal('');
  lettersGenDocumentType = signal('');
  lettersGenTemplateName = signal('');

  amlChecksRows = signal<ClientsAmlCheckRow[]>([]);
  amlComplianceResultFilter = signal<Set<ClientsComplianceResult>>(new Set());
  amlComplianceCheckTypeFilter = signal<Set<ClientsComplianceCheckType>>(new Set());
  amlComplianceDateFrom = signal<string>('');
  amlComplianceDateTo = signal<string>('');
  amlComplianceViewMode = signal<'latest' | 'history'>('latest');
  amlComplianceSearchQuery = signal('');
  amlPerformCheckMenuOpen = signal(false);
  amlTrainingModalOpen = signal(false);
  amlComplianceFilterMenuOpen = signal(false);
  amlComplianceSelectedRowId = signal<string | null>(null);
  amlChecksLoading = signal(false);
  amlCheckModalKind = signal<AmlCheckModalKind | null>(null);
  /** Directory clients for AML compliance “link to client” column. */
  amlComplianceDirectoryClients = signal<AmlClientPickerOption[]>([]);
  amlComplianceLinkOpenRowId = signal<string | null>(null);
  /** Company ids selected in “Add client” multi-select (not yet saved). */
  amlComplianceLinkPickCompanyIds = signal<number[]>([]);
  amlCompliancePageIndex = signal(0);

  /** Advanced filter drawer (Clients list) */
  clientsFilterPanelOpen = signal(false);
  /** Clients list: column visibility popover */
  clientsColumnPanelOpen = signal(false);
  clientsColumnVisibility = signal<Record<string, boolean>>(defaultClientsColumnVisibilityMap());
  clientsColumnOrder = signal<string[]>(CLIENTS_CATALOG_FLAT_ORDERED.map((c) => c.key));
  readonly todayIso = todayIso();

  /** Add Client — selection modal (Company House vs Manual) */
  clientsAddSelectionOpen = signal(false);
  /** Add Client — form bound to directory create-client API */
  clientsAddFormOpen = signal(false);
  clientsAddFormMode = signal<'create' | 'edit'>('create');
  readonly clientsAddSourceSelectOptions = computed((): readonly string[] => {
    const baseSet = new Set(CLIENTS_ADD_SOURCE_OPTIONS.map((n) => n.toLowerCase()));
    const seen = new Set<string>(baseSet);
    const custom: string[] = [];
    for (const row of this.clientsDirectoryRows()) {
      const s = (row.source ?? '').trim();
      if (s && !seen.has(s.toLowerCase())) {
        seen.add(s.toLowerCase());
        custom.push(s);
      }
    }
    if (!custom.length) return CLIENTS_ADD_SOURCE_OPTIONS;
    const withoutOthers = CLIENTS_ADD_SOURCE_OPTIONS.filter((n) => n !== 'Others');
    return [...withoutOthers, ...custom, 'Others'];
  });
  readonly clientsAddActiveDropdown = signal<string | null>(null);
  readonly createClientUserDirectoryContactLoadingVisible = computed(
    () =>
      this.clientsCreateClientUserContactsLoading() &&
      this.clientsAddActiveDropdown() === CREATE_CLIENT_USER_DIRECTORY_CONTACT_DROPDOWN_ID
  );
  readonly createClientUserPortalContactLoadingVisible = computed(
    () =>
      this.clientsAddPortalContactLoading() &&
      this.clientsAddActiveDropdown() === CREATE_CLIENT_USER_PORTAL_CONTACT_DROPDOWN_ID
  );
  private clientsAddFormLastEditQueryKey = '';
  private clientsAddPrimaryEmailSnapshot = '';

  clientsAddDraft = signal<ClientsAddFormDraft>(emptyClientsAddDraft());

  /** The popup's values, rebuilt from the draft. */
  /**
   * Popup controls with no home in the draft: the three phone-country pickers, Tags, Secondary
   * Phone No, Accountant / Staff, Partner Role and Select Company Clients.
   *
   * The popup is fully controlled while a host binds [values] — what it shows is what it is given
   * back. So an id the bridge dropped did not just fail to save, it would not even hold: the
   * dropdown snapped straight back to "Select" the moment it was picked. Kept here so every control
   * behaves while it is on screen. Nothing in here reaches the create payload, which has no column
   * for any of them; it is cleared with the form.
   */
  readonly clientsAddPopupExtras = signal<Record<string, string>>({});
  /** Set when the current Add Client form session was opened from a CRM connection's "Client" status
   *  button. Consumed and cleared once saveClientsAddForm() actually creates the client, at which
   *  point the originating CRM connection's status gets flipped to Client too. Cleared on cancel/close
   *  so it never leaks into an unrelated later Add Client session. */
  pendingCrmConversionSource: { contactId: number; parentUserId: number; actingUserId: number } | null = null;
  /** A CRM conversion's agreedServiceNames, waiting for the service catalog (catalogServices /
   *  customAddedServices) to finish its async load before they can be matched to trackKeys — see the
   *  effect in the constructor that drains this once the catalog signals populate. */
  private readonly pendingAgreedServiceNamesForCatalog = signal<string[] | null>(null);
  /** "Create Contact as Client" flow only — restricts the Client Type dropdown to Sole Trader / Individual. Reset to false by openClientsAddFormModal() on every other open. */
  clientsAddClientTypeRestrictToIndividual = signal(false);
  clientsAddDraftSavePending = signal(false);
  clientsAddDraftFromStorage = signal(false);

  /* ── Draft clients page ─────────────────────────────────────── */
  clientsPageView = signal<'clients' | 'drafts' | 'unassigned'>('clients');
  clientsDraftViewSwitching = signal(false);
  clientsDraftList = signal<ClientDraftApi[]>([]);
  clientsDraftListLoading = signal(false);
  /** Id of the draft currently being edited — cleared when modal is saved/closed. */
  clientsEditingDraftId = signal<number | null>(null);
  /* ── Unassigned clients page ───────────────────────────────────── */
  unassignedClientsRows = signal<DirectoryUnassignedClientRowApi[]>([]);
  unassignedClientsLoading = signal(false);
  unassignedClientsSearchQuery = signal('');
  unassignedClientsPageNumber = signal(1);
  unassignedClientsPageSize = signal(25);
  unassignedClientsTotalCount = signal(0);
  /* ─────────────────────────────────────────────────────────────── */
  clientsAddContactModalOpen = signal(false);
  /** 'primary' = + clicked in Primary Contact row; 'key' = + clicked in Key Contacts row */
  clientsAddContactContext = signal<'primary' | 'key'>('primary');
  /** IDs of contacts selected as key contacts in Add Client form */
  clientsAddKeyContactIds = signal<string[]>([]);
  /** When set, client-list Add Contact uses the add-contact modal and saves via API for this row. */
  clientsListAddContactCompanyMasterId = signal<number | null>(null);
  /** Client Store UniversalId of the Clients-list row "Add contact" was opened for; null when the
   *  contact popup was opened from anywhere else (it is also what tells the popup's Save which path to take). */
  clientsListAddContactClientUniversalId = signal<string | null>(null);
  clientsListAddContactClientName = signal('');
  clientsAddContactDraft = signal<ClientsAddContactDraft>(emptyClientsAddContactDraft());
  clientsAddContactOptions = signal<ClientsAddContactOption[]>([]);
  /** Contacts returned by the available-as-primary endpoint (Rule 21 + Rule 22 enforced server-side). */
  clientsAddPrimaryContactOptionsRaw = signal<ClientsAddContactOption[]>([]);
  clientsAddPrimaryContactLoading = signal(false);
  clientsAddContactOptionsLoading = signal(false);
  clientsAddPrimaryContactDropdownOpen = signal(false);
  clientsAddPrimaryContactPanelStyle = signal<Record<string, string> | null>(null);
  clientsAddKeyContactDropdownOpen = signal(false);
  clientsAddKeyContactPanelStyle = signal<Record<string, string> | null>(null);
  clientsAddSAIndividualOptions = signal<{ id: number; name: string }[]>([]);
  clientsAddSAIndividualLoading = signal(false);

  /** Company clients available for "Assign as Director" (shown when creating an Individual). */
  clientsAddAssignDirectorOptions = signal<{ universalId: string; name: string }[]>([]);
  clientsAddAssignDirectorLoading = signal(false);
  clientsAddAssignDirectorSearch = signal('');
  clientsAddShowPassword = signal(false);
  clientsAddShowConfirmPassword = signal(false);
  clientsAddSicTags = signal<string[]>([]);

  addSicTag(value: string, inputEl: HTMLInputElement): void {
    const code = value.trim().slice(0, 10);
    inputEl.value = '';
    if (!code) return;
    this.clientsAddSicTags.update(tags => tags.includes(code) ? tags : [...tags, code]);
    this.setClientsAddField('sicCode', this.clientsAddSicTags().join(','));
  }

  removeSicTag(index: number): void {
    this.clientsAddSicTags.update(tags => tags.filter((_, i) => i !== index));
    this.setClientsAddField('sicCode', this.clientsAddSicTags().join(','));
  }

  onSicKeydown(event: KeyboardEvent, inputEl: HTMLInputElement): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      event.stopPropagation();
      this.addSicTag(inputEl.value, inputEl);
    } else if (event.key === 'Backspace' && !inputEl.value && this.clientsAddSicTags().length > 0) {
      this.removeSicTag(this.clientsAddSicTags().length - 1);
    }
  }
  /** UniversalIds of company clients this Individual will be assigned as a director to. */
  clientsAddAssignDirectorSelected = signal<string[]>([]);

  /** Officers fetched from Companies House when a CH company is selected. */
  clientsAddChOfficers = signal<CompaniesHouseOfficerApi[]>([]);
  /** Indices of CH officers the user has checked to include as directors. */
  clientsAddChOfficersSelected = signal<Set<number>>(new Set());
  /** Indices of checked CH officers for whom an SA100 individual client should be created. */
  clientsAddChOfficersSa = signal<Set<number>>(new Set());
  /** Directors/officers the user has configured to save when the client is created. */
  clientsAddDirectors = signal<ClientsAddDirectorDraft[]>([]);
  /** Bumped whenever clientsAddDirectors is replaced from outside the Add Client popup (Edit load,
   *  Companies House) while it is open, telling the popup to reload its rows from that list. */
  clientsAddDirectorsSeedVersion = signal(0);
  clientsAddDirectorDeleteIds = signal<number[]>([]);
  clientsAddDirectorFormOpen = signal(false);
  clientsAddDirectorEditIndex = signal<number | null>(null);
  clientsAddDirectorDraft = signal<ClientsAddDirectorDraft>(emptyDirectorDraft());
  clientsAddPortalUserOptions = signal<ClientsAddPortalUserOption[]>([]);
  clientsAddPortalUsersLoading = signal(false);
  clientsAddPortalContactOptions = signal<ClientsAddPortalContactOption[]>([]);
  clientsAddPortalContactLoading = signal(false);
  clientsAddClientIdDuplicate = signal(false);
  clientsAddRegNoDuplicateError = signal<string | null>(null);
  private clientsAddRegNoCheckTimer: ReturnType<typeof setTimeout> | null = null;
  /** False while Client ID is auto-proposed; set true once the user edits it. */
  clientsAddClientIdUserEdited = signal(false);
  clientsAddPendingPortalUserDrafts = signal<Map<string, ClientsAddPendingPortalUserDraft>>(new Map());
  clientsAddMasterDataLoading = signal(false);
  clientsAddCountries = signal<DirectoryMasterDataItemApi[]>([]);
  clientsAddCurrencies = signal<DirectoryMasterDataItemApi[]>([]);
  clientsAddVatSchemes = signal<DirectoryMasterDataItemApi[]>([]);
  clientsAddVatSubmitTypes = signal<DirectoryMasterDataItemApi[]>([]);
  clientsAddClientTypes = signal<DirectoryClientTypeApi[]>([]);
  private clientsAddMasterDataLoaded = false;
  clientsCompanyHouseModalOpen = signal(false);
  clientsCompanyHouseSearch = signal('');
  clientsCompanyHouseResults = signal<CompaniesHouseSearchItemApi[]>([]);
  clientsCompanyHouseSearchError = signal<string | null>(null);
  clientsCompanyHouseApplyingId = signal<string | null>(null);
  clientsAddPanelsOpen = signal<Record<ClientsAddPanelKey, boolean>>({
    clientDetails: true,
    contactPerson: true,
    address: true,
    servicesStaff: false,
    dueDiligence: true,
    additionalFields: false,
    portalAccess: false,
    directors: false
  });
  /** Custom field definitions for Add/Edit client modal (Settings → Custom Fields, section Client). */
  clientsAddCustomFieldDefinitions = signal<CustomFieldItem[]>([]);
  clientsAddCustomFieldsLoading = signal(false);
  /** Values keyed by pm-api custom field id (`PM_CustomField.Id`). */
  clientsAddCustomFieldValues = signal<Record<number, string>>({});
  readonly clientsAddCustomFieldsForModal = computed(() =>
    this.clientsAddCustomFieldDefinitions().filter((f) => f.apiId != null && f.apiId > 0)
  );
  clientsAddEditingEntityId = signal<number | null>(null);
  /** Universal client ID when the Add Client modal is in edit mode. */
  clientsAddEditClientUniversalId = signal<string>('');
  /** CompanyMaster ID (numeric string) when in edit mode. */
  clientsAddEditCompanyMasterId = signal<string>('');
  /** URL to navigate back to after saving in edit mode. */
  clientsAddEditReturnUrl = signal<string>('');
  /** True while client data is being loaded into edit mode. */
  clientsAddEditLoadPending = signal<boolean>(false);
  /** All contact IDs linked to the client when edit mode was opened — used to diff for unlink on save. */
  clientsAddEditOriginalLinkedContactIds = signal<string[]>([]);
  /**
   * Edit mode: practice contact id (PmDb) → the same contact's id on the Client Store client. Linking takes
   * the practice id, but unlinking (`DELETE /clients/{uid}/contacts/{id}`) takes the Client Store id.
   */
  clientsAddEditClientStoreContactIdByPracticeId = signal<Record<string, number>>({});
  clientsAddSelectedServiceKeySet = computed(
    () => new Set(this.clientsAddDraft().selectedServiceKeys)
  );

  clientsAddShowFooterErrors = signal(false);

  clientsCreateClientUserModalOpen = signal(false);
  /** True while openEditClientUser() is fetching the existing contact — the modal is already
   * open and shows a loading overlay over the (blank) form instead of appearing only once loaded. */
  clientsEditClientUserLoading = signal(false);

  /**
   * True while any pm-clients-add-flow popup is open (pm-ui Frontend Audit PERF-19/ARCH-03).
   * pm-clients-add-flow.component.html is just these same 5 @if blocks — when all are false it
   * already renders nothing — so this only stops the outer <pm-clients-add-flow> from being
   * constructed at all until one of them is opened, instead of existing for the whole session.
   */
  readonly clientsAddFlowVisible = computed(() =>
    this.clientsAddSelectionOpen() ||
    this.clientsAddFormOpen() ||
    this.clientsCompanyHouseModalOpen() ||
    this.clientsAddContactModalOpen() ||
    this.clientsCreateClientUserModalOpen()
  );
  clientsCreateClientUserDraft = signal<CreateClientUserFormDraft>(emptyCreateClientUserFormDraft());
  createClientUserConfirmPassword = signal('');
  createClientUserLaunchSource = signal<'directory' | 'add-client' | 'client-list'>('directory');
  /** Client-user contacts for “Contact” dropdown (`GET .../contacts?clientUsersOnly=true`). */
  clientsCreateClientUserContactOptions = signal<CreateClientUserContactOption[]>([]);
  clientsCreateClientUserContactsLoading = signal(false);
  clientsCreateClientUserContactsLoadAttempted = signal(false);
  clientsAddPortalContactsLoadAttempted = signal(false);
  /** Empty string = new contact; otherwise `String(contactId)` for prefill source. */
  createClientUserContactChoice = signal('');
  readonly createClientUserPasswordsMatch = computed(() => {
    const pw = this.clientsCreateClientUserDraft().portalPassword.trim();
    const cpw = this.createClientUserConfirmPassword().trim();
    return pw === cpw;
  });
  /** Portfolio analytics strip (Clients list) */
  clientsAnalyticsStripOpen = signal(false);
  /** Clients list sort: shared by toolbar menu + column headers */
  clientsSortField = signal<'name' | 'grade' | 'revenue' | 'aml'>('name');
  clientsSortDir = signal<'asc' | 'desc'>('asc');
  clientsSortDropdownOpen = signal(false);
  /** Keyboard-highlighted row index for the "Sort clients" dropdown. */
  clientsSortHighlighted = signal(0);

  readonly clientsSortOptions: {
    id: string;
    label: string;
    field: 'name' | 'grade' | 'revenue' | 'aml';
    dir: 'asc' | 'desc';
  }[] = [
    { id: 'az', label: 'A-Z', field: 'name', dir: 'asc' },
    { id: 'za', label: 'Z-A', field: 'name', dir: 'desc' },
    { id: 'bestGrade', label: 'Best grade', field: 'grade', dir: 'asc' },
    { id: 'revenueAsc', label: 'Revenue ↑', field: 'revenue', dir: 'asc' },
    { id: 'revenueDesc', label: 'Revenue ↓', field: 'revenue', dir: 'desc' },
    { id: 'amlAsc', label: 'AML ↑', field: 'aml', dir: 'asc' },
    { id: 'amlDesc', label: 'AML ↓', field: 'aml', dir: 'desc' }
  ];
  /** Empty = all statuses; default selection is active only. */
  clientsFilterStatuses = signal<ClientsFilterStatus[]>(['active']);
  /** Empty = all types */
  clientsFilterClientTypes = signal<string[]>([]);
  clientsFilterManager = signal<string>('All managers');
  /** Empty = all grades */
  clientsFilterGrades = signal<string[]>([]);
  /** Empty = all AML states */
  clientsFilterAmlStatuses = signal<string[]>([]);
  /** Empty = any service */
  clientsFilterServices = signal<string[]>([]);
  clientsFilterQuick = signal<('atRisk' | 'amlIssues' | 'overdue')[]>([]);

  /** Named saved filter sets (`/api/v1/clients/saved-views`) when directory env ids are set. */
  clientsViewsPanelOpen = signal(false);
  clientsSavedViewsLoading = signal(false);
  clientsSavedViews = signal<ClientSavedFilterViewItemApi[]>([]);
  clientsAppliedSavedViewId = signal<number | null>(null);
  /** Filter snapshot captured when a saved view is applied; manual edits that drift from this clear the view highlight only. */
  clientsAppliedSavedViewFilterBaseline = signal<string | null>(null);
  clientsSavedViewDeleteModalOpen = signal(false);
  clientsSavedViewPendingDelete = signal<ClientSavedFilterViewItemApi | null>(null);

  /** Legacy directory API (`/api/v1/directory/*`); enable via non-zero ids in `environment`. */
  clientsDirectoryApiActive = signal(false);
  clientsCapiumPayAccess = signal(false);
  clientsCapiumPayAccessLoaded = signal(false);
  clientsCapiumPayInviteModalOpen = signal(false);
  clientsCapiumPayInviteSaving = signal(false);
  clientsCapiumPayInviteRow = signal<ClientListRow | null>(null);
  clientsCapiumPayInviteDraft = signal({
    clientName: '',
    firstName: '',
    lastName: '',
    emailId: '',
    phoneNo: '',
    countryId: 0
  });
  clientsCapiumPayCountries = signal<DirectoryMasterDataItemApi[]>([]);
  clientsCapiumPayActiveDropdown = signal<string | null>(null);
  clientsDirectoryLoading = signal(false);
  clientsDirectoryLoadFailed = signal(false);
  clientsDirectoryFirstLoadDone = signal(false);
  clientsDirectoryRows = signal<ClientListRow[]>([]);
  clientsDirectoryTotalCount = signal(0);
  /**
   * Full, unfiltered firm-wide client set — used ONLY by {@link clientsAnalyticsSnapshot} so the
   * Health Spread/Grade Distribution/Risk Matrix/Client Types/AML Status/Revenue Leaders/Manager
   * Load/Overdue Tasks cards stay constant while a user filters or searches the table below them.
   * Deliberately separate from {@link clientsDirectoryRows}, which IS meant to react to filters —
   * that signal gets replaced with just the matching subset on every filter/search change (see
   * loadDirectoryClientsPage), so the analytics strip can never read from it safely.
   */
  clientsAnalyticsBaselineRows = signal<ClientListRow[]>([]);
  /** clientUniversalId → total non-draft invoiced revenue (GBP), practice-wide — feeds the Revenue
   *  Leaders card in {@link clientsAnalyticsSnapshot}. Loaded once alongside
   *  clientsAnalyticsBaselineRows via loadClientsAnalyticsRevenue(); see that method for the API. */
  clientsAnalyticsRevenueByClient = signal<Map<string, number>>(new Map());
  /** universalId → { name, type } — loaded once for Communication tab client name + type resolution. */
  private commClientsLookup = signal<Map<string, { name: string; type: string }>>(new Map());
  /** Unfiltered list scope total from `GET …/directory/clients/count` (tab badge / firm KPI). */
  clientsDirectoryFirmTotalCount = signal<number | null>(null);
  clientsDirectoryPageNumber = signal(1);
  clientsDirectoryPageSize = signal(50);
  private directoryClientsRequestSeq = 0;
  private directoryClientsFirmCountRequestSeq = 0;
  private clientsDirectoryReloadTimer: ReturnType<typeof setTimeout> | null = null;

  /** Users tab: client-linked contacts from directory `client-users`. */
  clientsClientUserRows = signal<ClientUserListRow[]>([]);
  clientsUsersSearchQuery = signal('');
  clientsContactsLoading = signal(false);
  clientsUsersPageNumber = signal(1);
  clientsUsersPageSize = signal(50);
  /** Unfiltered firm-wide total for the Users tab badge. */
  clientsUsersFirmTotalCount = signal(0);
  /** Filtered total for pagination footer (search-scoped). */
  clientsUsersFilteredTotalCount = signal(0);
  /** Debounced query sent to pm-api (UI search box updates immediately). */
  clientsUsersSearchQueryForApi = signal('');
  clientsUsersReloadTimer: ReturnType<typeof setTimeout> | null = null;
  private clientsUsersInflightLoadKey = '';
  private clientsUsersRequestSeq = 0;
  private clientsUsersCountRequestSeq = 0;
  lastClientsUsersFirmCountKey = '';
  clientsUsersRowCheck = signal<Record<string, boolean>>({});
  /** Open action menu for one visible row (`companyMasterId-contactId`). */
  clientsUsersActionMenuRowKey = signal<string | null>(null);
  clientsUsersBulkStatusDraft = signal<'' | 'active' | 'inactive'>('');
  clientsUsersBulkDeleteConfirmOpen = signal(false);
  clientsEditClientUserContactId = signal<number | null>(null);
  /** Read-only portal login shown in Edit User (typically contact email). */
  clientsEditClientUserLoginName = signal('');
  createClientUserPasswordVisible = signal(false);
  createClientUserConfirmPasswordVisible = signal(false);

  /** Users tab toolbar: list vs grid (separate from clients directory view). */
  clientsUsersViewMode = signal<'list' | 'grid'>('list');
  clientsUsersFilterPanelOpen = signal(false);
  /** Contact category codes: 1 Client, 4 Staff, 5 Accountant. Empty = all types. */
  clientsUsersFilterUserTypes = signal<readonly number[]>([]);
  clientsUsersFilterStatuses = signal<readonly ('active' | 'inactive')[]>([]);
  clientsUsersFilterClientId = signal('');
  clientsUsersFilterCreatedFrom = signal('');
  clientsUsersFilterCreatedTo = signal('');
  clientsUsersFilterLoginFrom = signal('');
  clientsUsersFilterLoginTo = signal('');
  clientsUsersImportModalOpen = signal(false);
  clientsUsersImportStep = signal<'upload' | 'analysing' | 'preview'>('upload');
  clientsUsersImportFileName = signal('');
  clientsUsersImportColumnsDetected = signal(0);
  clientsUsersImportPreviewRows = signal<ClientsUsersImportPreviewRow[]>([]);
  clientsUsersImportSelectedIds = signal<ReadonlySet<string>>(new Set());
  clientsUsersImportSavePending = signal(false);
  clientsUsersImportErrors = signal<readonly string[]>([]);
  clientsUsersImportStaged = signal(false);
  clientsUsersImportDraftRows = signal<ClientsUsersImportDraftRow[]>([]);
  clientsUsersImportPracticeEmails = signal<ReadonlySet<string>>(new Set());
  clientsUsersImportEmailsLoaded = signal(false);

  readonly clientsFilterTypeOptions = [
    'Limited',
    'LLP',
    'Sole Trader',
    'Individual',
    'Partnership',
    'Trust',
    'Charity',
    'CIS'
  ] as const;

  /** Set from URL `/clients/:id`; null on `/clients` list */
  clientDetailId = signal<string | null>(null);
  /** Row loaded via GET `/directory/clients/{id}` when not on the current list page */
  clientDetailDirectoryFetched = signal<{ id: string; row: ClientListRow } | null>(null);
  clientDetailDirectoryFetchLoading = signal(false);
  private clientDetailFetchSeq = 0;
  clientDetailTab = signal<string>('Overview');

  /** KPI shell: which blocks are visible (pill toggles with eye icons) */
  clientDetailShowKpi = signal(true);
  clientDetailShowDeadlinesHealth = signal(true);
  clientDetailShowTasksHealth = signal(false);
  clientDetailActionsFilter = signal<'overdue' | 'dueSoon'>('overdue');
  /** Activity Timeline: expanded list + filters (View Activities) */
  clientDetailActivityExpanded = signal(false);
  clientDetailActivityCategory = signal<'All' | 'Tasks' | 'Clients' | 'Deadlines' | 'Invoices' | 'Emails' | 'Documents' | 'Portal' | 'AML'>('All');

  readonly clientDetailWorkspaceTabs: ReadonlyArray<{
    label: string;
    icon: 'overview' | 'workspace' | 'communication' | 'documents' | 'compliance' | 'auth' | 'intelligence';
    beta?: boolean;
  }> = [
    { label: 'Overview', icon: 'overview' },
    { label: 'Workspace', icon: 'workspace' },
    { label: 'Communication', icon: 'communication' },
    { label: 'Documents', icon: 'documents' },
    { label: 'Compliance & Due Diligence', icon: 'compliance' },
    { label: 'Agent Auth / 64-8', icon: 'auth', beta: true },
    { label: 'Intelligence', icon: 'intelligence', beta: true }
  ];

  readonly clientDetailTimelineDemo: readonly ClientDetailTimelineRow[] = [];

  private readonly clientDetailActionsDemo: readonly ClientDetailActionItem[] = [];

  clientDetailOverdueActionsCount = computed(
    () => this.clientDetailActionsDemo.filter((item) => item.status === 'Overdue').length
  );

  clientDetailDueSoonActionsCount = computed(
    () => this.clientDetailActionsDemo.filter((item) => item.status === 'Due Soon').length
  );

  clientDetailFilteredActions = computed(() =>
    this.clientDetailActionsDemo.filter((item) =>
      this.clientDetailActionsFilter() === 'overdue' ? item.status === 'Overdue' : item.status === 'Due Soon'
    )
  );

  clientDetailFilteredTimelineRows = computed(() => {
    const active = this.clientDetailActivityCategory();
    if (active === 'All') {
      return this.clientDetailTimelineDemo;
    }
    return this.clientDetailTimelineDemo.filter((row) => row.category === active);
  });

  /** Directory API page only. */
  clientsBaselineRows = computed(() => {
    if (!this.userContext.practiceContextReady()) {
      return [];
    }
    return this.clientsDirectoryRows();
  });

  /** After search + deleted; directory search applies to the loaded page only. */
  clientsSearchFilteredRows = computed(() => {
    if (this.userContext.practiceContextReady() && this.clientsDirectoryApiActive()) {
      return this.clientsBaselineRows();
    }
    const q = this.clientsSearchQuery().trim().toLowerCase();
    const base = this.clientsBaselineRows();
    if (!q) {
      return base;
    }
    return base.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.contactEmail.toLowerCase().includes(q) ||
        r.refLine.toLowerCase().includes(q) ||
        r.regNo.toLowerCase().includes(q)
    );
  });

  /** Denominator for filter panel “N of M clients” (M = current filter match total from paged API). */
  clientsFilterPanelMatchTotalCount = computed(() => {
    if (this.userContext.practiceContextReady() && this.clientsDirectoryApiActive()) {
      const n = this.clientsDirectoryTotalCount();
      return Math.max(0, Number.isFinite(n) ? n : 0);
    }
    return this.filteredClientRows().length;
  });

  /** Server-paged rows with client-side advanced filters applied on the current page. */
  clientsUsersFilteredRows = computed(() => {
    const rows = this.clientsClientUserRows();
    if (!this.clientsUsersHasActiveAdvancedFilters()) {
      return rows;
    }
    return rows.filter((r) => this.clientUserMatchesAdvancedFilters(r));
  });

  clientsUsersActiveFilterCount = computed(() => {
    let n = this.clientsUsersFilterUserTypes().length;
    n += this.clientsUsersFilterStatuses().length;
    if (this.clientsUsersFilterClientId().trim()) {
      n += 1;
    }
    if (this.clientsUsersFilterCreatedFrom().trim()) {
      n += 1;
    }
    if (this.clientsUsersFilterCreatedTo().trim()) {
      n += 1;
    }
    if (this.clientsUsersFilterLoginFrom().trim()) {
      n += 1;
    }
    if (this.clientsUsersFilterLoginTo().trim()) {
      n += 1;
    }
    return n;
  });

  clientsPickerClients = computed(() => {
    if (this.userContext.practiceContextReady()) {
      const pick = this.clientsBookMeetingPickerRows();
      if (pick.length > 0) {
        return pick.map((r) => ({ id: r.id, name: r.name, ref: r.refLine || '', universalId: r.directoryClientUniversalId || '' }));
      }
    }
    return this.filteredClientRows().map((r) => ({ id: r.id, name: r.name, ref: r.refLine || '', universalId: r.directoryClientUniversalId || '' }));
  });

  submissionsFilteredRows = computed(() => {
    let rows = [...this.submissionsRows()];
    const cf = this.submissionsClientFilter();
    const tf = this.submissionsTypeFilter();
    const sf = this.submissionsStatusFilter();
    const dFrom = this.submissionsDateFrom().trim();
    const dTo = this.submissionsDateTo().trim();
    if (cf !== 'all') {
      rows = rows.filter((r) => r.clientId === cf);
    }
    if (tf !== 'all') {
      rows = rows.filter((r) => r.type === tf);
    }
    if (sf === 'submitted') {
      rows = rows.filter((r) => r.status === 'submitted');
    } else if (sf === 'pending') {
      rows = rows.filter((r) => r.status === 'pending');
    } else if (sf === 'error') {
      rows = rows.filter((r) => r.status === 'error');
    }
    if (dFrom && dTo) {
      rows = rows.filter((r) => {
        if (!r.submittedOn) {
          return true;
        }
        return r.submittedOn >= dFrom && r.submittedOn <= dTo;
      });
    }
    const sortKey = this.submissionsSortKey();
    const sortAsc = this.submissionsSortAsc();
    rows.sort((a, b) => {
      const cmp = this.submissionsSortCompare(a, b, sortKey);
      return sortAsc ? cmp : -cmp;
    });
    return rows;
  });

  lettersStats = computed(() => {
    const sent = this.lettersSentRows();
    return {
      templates: this.lettersTemplates().length,
      sent: sent.length,
      signed: sent.filter((r) => r.status === 'signed').length,
      pending: sent.filter((r) => r.status === 'pending').length,
      expired: sent.filter((r) => r.status === 'expired').length
    };
  });

  lettersFilteredTemplates = computed(() => {
    let rows = [...this.lettersTemplates()];
    const cat = this.lettersCategoryFilter();
    const q = this.lettersSearchQuery().trim().toLowerCase();
    if (cat !== 'all') {
      rows = rows.filter((r) => r.category === cat);
    }
    if (q) {
      rows = rows.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q)
      );
    }
    return rows;
  });

  lettersFilteredSent = computed(() => {
    let rows = [...this.lettersSentRows()];
    const cat = this.lettersCategoryFilter();
    const q = this.lettersSearchQuery().trim().toLowerCase();
    if (cat !== 'all') {
      rows = rows.filter((r) => r.category === cat);
    }
    if (q) {
      rows = rows.filter(
        (r) =>
          r.documentName.toLowerCase().includes(q) ||
          r.clientLabel.toLowerCase().includes(q)
      );
    }
    return rows;
  });

  lettersTemplatesAllVisibleSelected = computed(() => {
    const vis = this.lettersFilteredTemplates();
    if (vis.length === 0) {
      return false;
    }
    const sel = this.lettersTemplateSelection();
    return vis.every((r) => !!sel[r.id]);
  });

  /**
   * One rule per quick filter, shared by the chip counts and the local row filter.
   *
   * Mirrors pm-api's ApplyClientQuickFilters, which is what actually filters the list in the
   * API-backed path: "at risk" is onboarding under 65 (a client with no score counts), and "AML
   * issues" is any AML status other than Clear. The counts used to apply a stricter reading of both,
   * so a chip could show a smaller number than the list it produced.
   */
  clientRowMatchesQuickFilter(r: ClientListRow, key: 'atRisk' | 'amlIssues' | 'overdue'): boolean {
    if (key === 'atRisk') {
      return r.healthScore < 65;
    }
    if (key === 'amlIssues') {
      return this.clientAmlStatusLabel(r) !== 'Clear';
    }
    return r.deadlinesVariant === 'overdue';
  }

  /**
   * Every filter except the quick filters.
   *
   * Split out so the quick-filter chips can count against it. Counting against the fully filtered
   * list made a selected chip shrink its own badge — pick "AML Issues" and its count dropped to 0,
   * because by then the list held only the rows it had already matched.
   */
  clientsRowsBeforeQuickFilters = computed(() => {
    if (this.userContext.practiceContextReady() && this.clientsDirectoryApiActive()) {
      return this.clientsSearchFilteredRows();
    }
    const rows = this.clientsSearchFilteredRows();
    const statuses = this.clientsFilterStatuses();
    const types = this.clientsFilterClientTypes();
    const manager = this.clientsFilterManager();
    const grades = this.clientsFilterGrades();
    const amlSel = this.clientsFilterAmlStatuses();
    const svcSel = this.clientsFilterServices();
    const quick = this.clientsFilterQuick();

    return rows.filter((r) => {
      if (statuses.length > 0) {
        const st = this.clientRowStatus(r.id);
        if (!statuses.includes(st)) {
          return false;
        }
      }
      if (types.length > 0) {
        const t = this.clientTypeLabelFromRow(r);
        if (!types.includes(t)) {
          return false;
        }
      }
      if (manager !== 'All managers' && this.clientManagerDemo(r) !== manager) {
        return false;
      }
      if (grades.length > 0 && !grades.includes(this.clientGradeLabel(r))) {
        return false;
      }
      if (amlSel.length > 0 && !amlSel.includes(this.clientAmlStatusLabel(r))) {
        return false;
      }
      if (svcSel.length > 0) {
        const any = svcSel.some((label) =>
          r.services.some((svc) => this.clientServiceMatchesFilterChip(svc, label))
        );
        if (!any) {
          return false;
        }
      }
      if (quick.length > 0 && !quick.some((key) => this.clientRowMatchesQuickFilter(r, key))) {
        return false;
      }
      return true;
    });
  });

  /**
   * The visible client rows.
   *
   * Quick filters are NOT re-applied here. {@link buildDirectoryClientServerFilters} sends them to
   * the directory API, which does the filtering, so a second client-side pass over the response
   * could only drop rows the server had already decided belong — and would do exactly that,
   * because the two rules are not identical. The local (non-API) path applies them inside
   * {@link clientsRowsBeforeQuickFilters}.
   */
  filteredClientRows = computed(() => this.clientsRowsBeforeQuickFilters());

  /**
   * Filtered rows ordered by {@link clientsSortField} + {@link clientsSortDir}.
   *
   * Sorting is done client-side over the FULL loaded list (see {@link loadDirectoryClientsPage},
   * which fetches every matching client in one request). The directory API paginates + sorts each
   * page independently, so relying on it made A-Z ordering restart on every page; ordering the whole
   * set here and slicing in {@link clientsDirectoryDisplayRows} keeps the alphabet continuous.
   */
  clientsSortedRows = computed(() => {
    const rows = [...this.filteredClientRows()];
    const field = this.clientsSortField();
    const dir = this.clientsSortDir();
    const gradeRank = (g: string) => {
      const o: Record<string, number> = { Platinum: 0, Gold: 1, Silver: 2, Bronze: 3 };
      return o[g] ?? 9;
    };
    const amlRank = (r: ClientListRow) => {
      const s = this.clientAmlStatusLabel(r);
      const o: Record<string, number> = { Clear: 0, Pending: 1, Review: 2, None: 3 };
      return o[s] ?? 9;
    };
    const byName = (a: ClientListRow, b: ClientListRow) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    const mul = dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      switch (field) {
        case 'name':
          cmp = byName(a, b);
          break;
        case 'grade':
          cmp = gradeRank(this.clientGradeLabel(a)) - gradeRank(this.clientGradeLabel(b));
          break;
        case 'revenue':
          cmp = this.clientDemoRevenue(a) - this.clientDemoRevenue(b);
          break;
        case 'aml':
          cmp = amlRank(a) - amlRank(b);
          break;
        default:
          cmp = byName(a, b);
      }
      if (cmp === 0) {
        return byName(a, b);
      }
      return mul * cmp;
    });
    return rows;
  });

  clientsSortTriggerLabel = computed(() => {
    const field = this.clientsSortField();
    const d = this.clientsSortDir();
    const arrow = d === 'asc' ? '↑' : '↓';
    switch (field) {
      case 'name':
        return d === 'asc' ? 'A-Z' : 'Z-A';
      case 'grade':
        return d === 'asc' ? 'Best grade' : 'Grade ↓';
      case 'revenue':
        return `Revenue ${arrow}`;
      case 'aml':
        return `AML ${arrow}`;
      default:
        return 'A-Z';
    }
  });

  /** True exactly when the Clients directory's list-view table is the thing on screen — drives an
   *  explicit class binding (not a :has() selector, which proved unreliable here) that switches
   *  .content-area/.clients-screen-inner from "whole page scrolls" to "this table scrolls in its
   *  own bounded panel", matching the Companies House Filing History behavior. */
  clientsTableBoundedActive = computed((): boolean => {
    // Only ever on the Clients page. This flag is read by .content-area (overflow: hidden
    // !important) and by .clients-screen-inner, and that inner element only exists inside
    // `@if (activeItem() === 'Clients')` — but the section signals below are plain state that keeps
    // its last value after you navigate away. So visiting Clients and then going to the Dashboard
    // left the flag true and clamped .content-area to overflow: hidden, which is the one scroll
    // container the Dashboard (and Tasks, Settings, …) has: the page simply stopped scrolling.
    if (this.activeItem() !== 'Clients') {
      return false;
    }
    // Never while a single client's detail page is open: this flag puts `overflow: hidden` on
    // .content-area and .clients-screen-inner so a table can own the only scroll region, and that
    // page is a tall stack of cards with no table of its own — it just got clipped at the fold with
    // no way to scroll. The section signals below keep their last value when you drill into a
    // client, so they can't be relied on to exclude it.
    if (this.clientDetailId()) {
      return false;
    }
    const section = this.clientsWorkspaceSection();
    // Schedule, Communication, Submissions and AML are single full-height <pm-data-grid> views, so
    // they always want the bounded layout — without it their ancestors size to content and the
    // table stops short of the bottom of the screen instead of filling it. (AML joined this list
    // when its hand-written table became a <pm-data-grid>: `.clients-aml-workspace--compliance` is
    // flex:1/min-height:0 and the grid claims the remainder, but that only has space to claim once
    // `.clients-screen-inner` stops being flex:0 0 auto.)
    // Letters joined once its list became a filling <pm-data-grid> too (its By status table scrolls
    // inside its own card, which the bounded layout suits as well).
    if (
      section === 'schedule' ||
      section === 'communication' ||
      section === 'submissions' ||
      section === 'letters' ||
      section === 'aml'
    ) {
      return true;
    }
    if (section !== 'list' || this.clientsPageView() !== 'clients') {
      return false;
    }
    // Users tab list view fills the screen the same way as the Clients list, so both tables end
    // at the same place above the bottom edge.
    if (this.clientsPageTab() === 'users') {
      return this.clientsUsersViewMode() === 'list';
    }
    return this.clientsPageTab() === 'clients' && this.clientsViewMode() === 'list';
  });

  clientsTableVisibleOrderedColumns = computed((): ClientsCatalogFlatCol[] => {
    const v = this.clientsColumnVisibility();
    const byKey = new Map(CLIENTS_CATALOG_FLAT_ORDERED.map((c) => [c.key, c] as const));
    return this.normalizeClientsColumnOrder(this.clientsColumnOrder())
      .map((key) => byKey.get(key) ?? null)
      .filter((col): col is ClientsCatalogFlatCol => !!col && !!v[col.key]);
  });

  /**
   * Toolbar Filter badge: total active **selections** (each chip, non-default manager, search text),
   * not “sections used”. Columns badge uses {@link clientsTableVisibleColumnCount} separately.
   */
  clientsActiveAdvancedFilterCount = computed(() => {
    let n = this.clientsFilterStatuses().length;
    n += this.clientsFilterClientTypes().length;
    if (this.clientsFilterManager() !== 'All managers') {
      n += 1;
    }
    n += this.clientsFilterGrades().length;
    n += this.clientsFilterAmlStatuses().length;
    n += this.clientsFilterServices().length;
    n += this.clientsFilterQuick().length;
    return n;
  });

  /** Selected rows (visible in filtered list) keyed by {@link clientListRowUiKey}. */
  clientsSelectedListRows = computed(() => {
    const visibleByKey = new Map(
      this.filteredClientRows().map((r) => [this.clientListRowUiKey(r), r] as const)
    );
    const m = this.clientsRowCheck();
    const out: ClientListRow[] = [];
    for (const [key, checked] of Object.entries(m)) {
      if (!checked) {
        continue;
      }
      const row = visibleByKey.get(key);
      if (row) {
        out.push(row);
      }
    }
    return out;
  });

  /** Capium3 company master ids for selected rows (optional alongside universal ids). */
  clientsSelectedIds = computed(() =>
    this.clientsSelectedListRows()
      .map((r) => this.clientListRowBulkCompanyMasterId(r))
      .filter((id) => id.length > 0)
  );

  /** Rows selected for bulk that can be sent to directory bulk (universal id and/or company master id). */
  clientsBulkEligibleSelectedRows = computed(() =>
    this.clientsSelectedListRows().filter((r) => this.clientListRowSupportsDirectoryBulk(r))
  );

  clientsBulkSelectionCount = computed(() => this.clientsBulkEligibleSelectedRows().length);

  // Custom Fields
  customFields = signal<CustomFieldItem[]>([]);
  dropdownOptions = signal<string[]>([]);
  newOptionValue = signal<string>('');
  customFieldSettingsLoading = signal(false);
  customFieldSettingsError = signal<string | null>(null);
  customFieldFilterPopoverOpen = signal(false);
  customFieldRowActionsOpenId = signal<string | null>(null);
  showAssignClientsLeaveConfirmModal = signal<boolean>(false);
  pendingAssignLeaveSettingLabel: string | null = null;
  pendingAssignLeaveServiceTab: string | null = null;
  isCurrentUserSuperAccountant = signal<boolean>(true);
  serviceStatusMap = signal<Record<string, boolean>>({});
  catalogServices = signal<CatalogServiceVm[]>([]);
  /** Raw `GET default-services` rows — used to update every catalog bundle that contains a template. */
  defaultServicesCatalogRows = signal<DefaultServiceApiRow[]>([]);
  servicesSettingsLoading = signal(false);
  packageGridRows = signal<PackageGridRow[]>([]);
  packagesLoading = signal(false);
  packagesError = signal<string | null>(null);
  assignmentColumnIds = signal<number[]>([]);
  assignmentsLoading = signal(false);
  assignmentsError = signal<string | null>(null);
  assignMatrixPageNumber = signal(1);
  assignMatrixPageSize = signal(50);
  assignMatrixTotalCount = signal(0);
  assignMatrixSearchReloadTimer: ReturnType<typeof setTimeout> | null = null;
  private assignMatrixRequestSeq = 0;
  private assignMatrixRequestSub?: Subscription;
  assignExportMenuOpen = signal(false);
  /** True while team list for the service-manager dropdown is loading. */
  addServiceManagerLoading = signal(false);
  customLabelsDraft = signal<Record<RenamableLabel, string>>(emptyRenamableLabels());
  extraCustomLabelsDraft = signal<Record<string, string>>({});

  getCustomLabel(key: string): string {
    return this.pmCustomLabels.getLabel(key);
  }

  applyCustomLabels(text: string): string {
    return this.pmCustomLabels.apply(text);
  }

  setNewOptionValue(value: string) {
    this.newOptionValue.set(value);
  }

  addDropdownOption() {
    const newOption = this.newOptionValue().trim();
    if (newOption) {
      this.dropdownOptions.update(options => [...options, newOption]);
      this.newOptionValue.set('');
    }
  }

  removeDropdownOption(index: number) {
    this.dropdownOptions.update(options => options.filter((_, i) => i !== index));
  }
  addServiceAssignClientMultiselectSearch = signal('');
  /** Search text for Add Service reminder staff/client multiselect menus. */
  addServiceReminderMultiselectSearch = signal('');
  reminders = signal<ReminderItem[]>([]);

  readonly emailTemplateLibraryOptions: string[] = [
    'Default Reminder',
    'Monthly Compliance Reminder',
    'Quarterly Deadline Reminder',
    'Urgent Submission Reminder',
    'Final Notice Reminder'
  ];
  newService = signal<NewServiceForm>({
    title: '',
    frequency: 'Monthly',
    billable: 'No',
    fee: '0',
    manager: '',
    clientTypes: [],
    assignToAllClients: true,
    estimatedHours: '10',
    addToCalendar: false,
    isEnableInternalDeadlineRule: false,
    internalDeadlineDateTitle: '',
    mtdItIsEnableInternalDeadlineRule: false,
    mtdItInternalDeadlineDateTitle: ''
  });
  /** Client type pills — labels from `GET /api/v1/lookups/client-types` (ordered by `order`). */
  get availableClientTypes(): readonly string[] {
    return [...this.templateLibraryClientTypes()]
      .sort((a, b) => a.order - b.order)
      .map((r) => r.label);
  }

  async ensureDirectoryClientsLoadedForServiceReminders(): Promise<void> {
    // If directory isn't configured, there is no real client source to load from.
    if (!this.userContext.practiceContextReady()) {
      return;
    }
    // Do not treat demo `clientsSortedRows` as “loaded”: assign pickers need directory rows.
    if (this.clientsDirectoryRows().length > 0 || this.clientsBookMeetingPickerRows().length > 0) {
      return;
    }
    if (this.clientsDirectoryLoading()) {
      return;
    }
    await this.loadDirectoryClientsPage(1);
  }

  /** Team roster only — skips permission matrices (not needed for service-manager dropdown). */
  async loadTeamForAddServiceModalOnly(): Promise<void> {
    this.ensurePmContextLoaded();
    const parentUserId = this.effectivePracticeParentUserId();
    const actingUserId = this.effectiveActingUserId();
    if (parentUserId <= 0 || actingUserId <= 0) {
      return;
    }
    this.addServiceManagerLoading.set(true);
    try {
      const members = await firstValueFrom(
        this.pmTeamApi.listMembers(parentUserId, actingUserId, { includeInactive: true })
      );
      this.teamMembersApi.set(members ?? []);
      this.applyProfileFromActingMember(members ?? [], actingUserId);
    } catch {
      /* keep modal usable */
    } finally {
      this.addServiceManagerLoading.set(false);
    }
  }

  private countGridStepsFromApiSteps(steps: ClientCustomServiceStepApi[] | undefined): number {
    return (steps ?? []).filter((s) => (s.stepTitle ?? '').trim().length > 0).length;
  }

  /** Parse default-catalog `ServiceSteps` from `servicesJson` (no fabricated steps). */
  private parseCatalogTemplateStepsFromJson(stepsRaw: unknown): {
    stepCount: number;
    stepTitles: string[];
    apiSteps: ClientCustomServiceStepApi[];
  } {
    const rawSteps = Array.isArray(stepsRaw) ? stepsRaw : [];
    const apiSteps: ClientCustomServiceStepApi[] = [];
    let order = 0;
    for (const entry of rawSteps) {
      if (!entry || typeof entry !== 'object') {
        continue;
      }
      const o = entry as Record<string, unknown>;
      const title = String(o['StepTitle'] ?? o['stepTitle'] ?? '').trim();
      if (!title) {
        continue;
      }
      order += 1;
      const subRaw = o['subSteps'] ?? o['SubSteps'];
      const subSteps = Array.isArray(subRaw)
        ? subRaw.map((x) => String(x).trim()).filter((x) => x.length > 0)
        : [];
      const actionRaw = o['ActionType'] ?? o['actionType'];
      const actionType =
        typeof actionRaw === 'string' && actionRaw.trim() ? actionRaw.trim() : actionRaw != null ? String(actionRaw) : null;
      apiSteps.push({
        stepTitle: title,
        actionType,
        sortOrder: order,
        ...(subSteps.length > 0 ? { subSteps } : {})
      });
    }
    const stepTitles = apiSteps.map((s) => (s.stepTitle ?? '').trim()).filter((t) => t.length > 0);
    return { stepCount: stepTitles.length, stepTitles, apiSteps };
  }

  private catalogManagerLabelFromTemplate(t: DefaultServiceTemplateEntry): string {
    // Team roster name first, then the name saved on the service, and only then the "User #id"
    // placeholder — asking the roster lookup for its placeholder up front meant the saved name
    // was never reached for a manager missing from the roster (deleted user, other practice).
    const hasManager = t.managerUserId != null && t.managerUserId > 0;
    const fromTeam = hasManager ? this.teamMemberDisplayNameForUserId(t.managerUserId, false) : '';
    if (fromTeam) {
      return fromTeam;
    }
    const fromJson = (t.managerNameFromJson ?? '').trim();
    if (fromJson) {
      return fromJson;
    }
    return hasManager ? `User #${t.managerUserId}` : '—';
  }

  private catalogVmFromTemplateEntry(
    row: DefaultServiceApiRow,
    t: DefaultServiceTemplateEntry
  ): CatalogServiceVm {
    const managerLabel = this.catalogManagerLabelFromTemplate(t);
    return {
      catalogRowId: row.id,
      defaultServiceId: t.serviceId,
      trackKey: this.catalogServiceKey(t.serviceId),
      name: t.serviceTitle,
      frequency: t.frequencyName,
      typeLabel: row.isCustom ? 'Custom' : 'Default',
      isActive: t.isActive,
      stepCount: t.stepCount,
      stepTitles: t.stepTitles,
      catalogApiSteps: [...t.apiSteps],
      reminderTimings: t.reminderTimings,
      templateReminders: t.templateReminders.map((r, idx) => ({ ...r, id: idx + 1 })),
      clientTypes: t.clientTypes,
      feeAmount: t.feeAmount,
      estHours: t.estHours,
      billable: t.billable,
      addToCalendar: t.addToCalendar,
      managerLabel,
      managerUserId: t.managerUserId,
      createdOn: t.createdOn ?? null,
      isEnableInternalDeadlineRule: t.isEnableInternalDeadlineRule,
      internalDeadlineDateTitle: t.internalDeadlineDateTitle,
      mtdItIsEnableInternalDeadlineRule: t.mtdItIsEnableInternalDeadlineRule,
      mtdItInternalDeadlineDateTitle: t.mtdItInternalDeadlineDateTitle
    };
  }

  private catalogStepScore(vm: CatalogServiceVm): number {
    return vm.stepTitles.length > 0 ? vm.stepTitles.length : vm.stepCount;
  }

  /** Higher score = preferred source for scalar fields when deduping template bundles. */
  private catalogBundlePriority(row: DefaultServiceApiRow): number {
    let score = 0;
    if (row.isActive !== false) {
      score += 1000;
    }
    const type = (row.serviceType ?? '').trim().toLowerCase();
    if (type === 'default') {
      score += 100;
    } else if (type.includes('small')) {
      score += 50;
    }
    return score;
  }

  private mergeCatalogServiceVms(
    existing: CatalogServiceVm,
    incoming: CatalogServiceVm,
    existingPriority: number,
    incomingPriority: number
  ): CatalogServiceVm {
    const scalarFrom = incomingPriority >= existingPriority ? incoming : existing;
    const stepsFrom =
      this.catalogStepScore(incoming) > this.catalogStepScore(existing) ? incoming : existing;
    const clientTypes = this.preferredCatalogClientTypes(
      existing,
      incoming,
      existingPriority,
      incomingPriority
    );
    return {
      ...scalarFrom,
      clientTypes,
      stepCount: stepsFrom.stepCount,
      stepTitles: [...stepsFrom.stepTitles],
      catalogApiSteps: [...stepsFrom.catalogApiSteps],
      reminderTimings:
        stepsFrom.reminderTimings.length >= scalarFrom.reminderTimings.length
          ? [...stepsFrom.reminderTimings]
          : [...scalarFrom.reminderTimings],
      templateReminders:
        this.catalogTemplateRemindersScore(stepsFrom.templateReminders) >=
        this.catalogTemplateRemindersScore(scalarFrom.templateReminders)
          ? stepsFrom.templateReminders.map((r, idx) => ({ ...r, id: idx + 1 }))
          : scalarFrom.templateReminders.map((r, idx) => ({ ...r, id: idx + 1 })),
      createdOn: scalarFrom.createdOn ?? stepsFrom.createdOn ?? null
    };
  }

  /** When deduping catalog bundles, never let an empty CompanyType override a populated one — but
   *  when BOTH bundles have a populated (and possibly different) restriction, always defer to the
   *  higher-priority bundle, same as every other field in this merge. Previously a length mismatch
   *  short-circuited straight to "pick whichever list is longer" *before* checking priority, so a
   *  stale, wider restriction from an un-refreshed bundle could silently outrank a just-saved,
   *  deliberately narrowed one (e.g. removing a client type from "Selected client types" and saving
   *  would have no visible effect if another bundle still carried the old, longer list). */
  private preferredCatalogClientTypes(
    existing: CatalogServiceVm,
    incoming: CatalogServiceVm,
    existingPriority: number,
    incomingPriority: number
  ): string[] {
    const a = existing.clientTypes;
    const b = incoming.clientTypes;
    if (a.length === 0) {
      return [...b];
    }
    if (b.length === 0) {
      return [...a];
    }
    return incomingPriority >= existingPriority ? [...b] : [...a];
  }

  private parseTemplateRemindersFromJson(o: Record<string, unknown>): ReminderItem[] {
    const raw = o['TemplateReminders'] ?? o['templateReminders'];
    if (!Array.isArray(raw)) {
      return [];
    }
    const rows: ReminderItem[] = [];
    for (const entry of raw) {
      if (!entry || typeof entry !== 'object') {
        continue;
      }
      const r = entry as Record<string, unknown>;
      const timing = String(r['timing'] ?? r['Timing'] ?? r['name'] ?? r['Name'] ?? '').trim();
      if (!timing) {
        continue;
      }
      const selectedStaffRaw = r['selectedStaff'] ?? r['SelectedStaff'];
      const notifyClientsRaw = r['notifyClients'] ?? r['NotifyClients'];
      const selectedStaff = Array.isArray(selectedStaffRaw)
        ? selectedStaffRaw.map((x) => String(x ?? '').trim()).filter((x) => x.length > 0)
        : [];
      const notifyClients = Array.isArray(notifyClientsRaw)
        ? notifyClientsRaw.map((x) => String(x ?? '').trim()).filter((x) => x.length > 0)
        : [];
      rows.push({
        id: rows.length + 1,
        timing,
        name: timing,
        selectedStaff,
        notifyClients,
        clientEmailTemplate: String(r['clientEmailTemplate'] ?? r['ClientEmailTemplate'] ?? '').trim(),
        ccEmail: String(r['ccEmail'] ?? r['CcEmail'] ?? '').trim()
      });
    }
    return this.dedupeRemindersTimingsFromApi(rows);
  }

  private catalogTemplateRemindersScore(items: readonly ReminderItem[]): number {
    let score = items.length;
    for (const r of items) {
      if ((r.selectedStaff?.length ?? 0) > 0) {
        score += 20;
      }
      if ((r.notifyClients?.length ?? 0) > 0) {
        score += 5;
      }
      if ((r.clientEmailTemplate ?? '').trim().length > 0) {
        score += 20;
      }
    }
    return score;
  }

  acsAssignClientDropdownId(): string {
    return 'acsAssignClient';
  }

  acsServiceManagerDropdownId(): string {
    return 'acsSvcManager';
  }

  addServiceManagerSearch = signal('');

  /** Keep API rows whose timing exists in the PM-V4 cascade for the current service frequency. */
  dedupeRemindersTimingsFromApi(reminders: ReminderItem[]): ReminderItem[] {
    const freq = this.newService().frequency;
    const usedTypes = new Set<string>();
    const out: ReminderItem[] = [];
    for (const r of reminders) {
      const canon = normalizeReminderTimingToCascadeTitle(r.timing, freq);
      if (!canon) {
        continue;
      }
      const typeName = reminderCascadeTypeNameForTitle(canon, freq) ?? canon;
      if (usedTypes.has(typeName)) {
        continue;
      }
      usedTypes.add(typeName);
      out.push({ ...r, timing: canon, name: typeName });
    }
    return out.map((row, i) => ({ ...row, id: i + 1 }));
  }

  teamMemberDisplayNameForUserId(
    userId: number | null | undefined,
    allowUserIdPlaceholder = false
  ): string {
    if (userId == null || !Number.isFinite(Number(userId)) || Number(userId) <= 0) {
      return '';
    }
    const id = Number(userId);
    const m = this.teamMembersApi().find((x) => x.userId === id);
    if (!m) {
      return allowUserIdPlaceholder ? `User #${id}` : '';
    }
    return (m.name?.trim() || m.email?.trim() || `User ${m.userId}`).trim();
  }

  private async ensureTeamLoadedForAddServiceModal(): Promise<void> {
    this.ensurePmContextLoaded();
    const parentUserId = this.effectivePracticeParentUserId();
    const actingUserId = this.effectiveActingUserId();
    if (parentUserId <= 0 || actingUserId <= 0) {
      return;
    }
    if (this.teamMembersApi().length > 0) {
      return;
    }
    await this.loadTeamForAddServiceModalOnly();
  }

  ensureTeamLoadedForServiceManager(): void {
    void this.ensureTeamLoadedForAddServiceModal();
  }
  servicesFilterPopoverOpen = signal(false);
  servicesFilterDropdownOpen = signal<'frequency' | 'manager' | null>(null);
  servicesFilterDdRect = signal<{ top: number; left: number; width: number } | null>(null);

  assignSearchQuery = signal<string>('');

  /** Router often decodes `q` once; tolerate `+` and optional extra decode without throwing. */
  private decodeAssignSearchFromQueryParam(raw: string): string {
    const s = raw.trim();
    if (!s) {
      return '';
    }
    try {
      return decodeURIComponent(s.replace(/\+/g, ' ')).trim();
    } catch {
      return s;
    }
  }

  customAddedServices = signal<CustomAddedService[]>([]);
  readonly settingsClientUniversalId = 'CUST-1';

  /** Resolve legacy `CompanyType` numeric code → label via lookups API. */
  clientTypeLabelForCode(code: string): string | undefined {
    const key = code.trim();
    if (!key) {
      return undefined;
    }
    const hit = this.templateLibraryClientTypes().find((r) => String(r.code) === key);
    return hit?.label;
  }

  catalogServiceKey(defaultServiceId: number): string {
    return `d:${defaultServiceId}`;
  }

  customServiceStatusKey(name: string): string {
    return `c:${name}`;
  }

  private parseTemplateBillableFromJson(o: Record<string, unknown>, fee: number | null): 'Yes' | 'No' {
    const billRaw = String(o['Billable'] ?? o['billable'] ?? '').trim().toLowerCase();
    if (billRaw === 'yes' || billRaw === 'y' || billRaw === 'true' || billRaw === '1') {
      return 'Yes';
    }
    if (billRaw === 'no' || billRaw === 'n' || billRaw === 'false' || billRaw === '0') {
      return 'No';
    }
    return fee != null && fee > 0 ? 'Yes' : 'No';
  }

  /** pm-api writes `CompanyType`; legacy seed rows may only have lowercase `companyType`. */
  private rawCompanyTypeFromCatalogTemplateEntry(o: Record<string, unknown>): string {
    const fromSave = String(o['CompanyType'] ?? '').trim();
    if (fromSave.length > 0) {
      return fromSave;
    }
    return String(o['companyType'] ?? '').trim();
  }

  private parseTemplateAddToCalendarFromJson(o: Record<string, unknown>): boolean {
    // Prefer camelCase keys written by pm-api on save over legacy PascalCase leftovers in servicesJson.
    const raw = o['addToCal'] ?? o['addToCalendar'] ?? o['AddToCalendar'] ?? o['AddToCal'];
    if (raw == null) {
      return false;
    }
    if (typeof raw === 'boolean') {
      return raw;
    }
    const s = String(raw).trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
  }

  private parseTemplateJsonBoolByKeys(o: Record<string, unknown>, ...keys: string[]): boolean {
    let raw: unknown = null;
    for (const key of keys) {
      if (o[key] != null) {
        raw = o[key];
        break;
      }
    }
    if (raw == null) {
      return false;
    }
    if (typeof raw === 'boolean') {
      return raw;
    }
    const s = String(raw).trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
  }

  private parseTemplateJsonStringByKeys(o: Record<string, unknown>, ...keys: string[]): string {
    for (const key of keys) {
      const raw = o[key];
      if (typeof raw === 'string' && raw.trim()) {
        return raw.trim();
      }
    }
    return '';
  }

  /**
   * Global Internal Deadline rule fields from a PM_DefaultService.Services[] template entry.
   * pm-api's ServiceSettingsRepository writes these into the raw JSON blob as PascalCase (matching
   * every other field it writes there, e.g. ServiceTitle/Frequency/AddToCal) — NOT the camelCase used
   * by proper JSON-serialized DTOs elsewhere (e.g. GET .../internal-deadline-rule). The camelCase
   * variants are checked too, defensively, the same way every other field parsed in this function
   * checks both casings. See docs/deadlines/global-internal-deadline-implementation-plan.md §2.
   */
  private parseTemplateInternalDeadlineRuleFromJson(o: Record<string, unknown>): {
    isEnableInternalDeadlineRule: boolean;
    internalDeadlineDateTitle: string;
    mtdItIsEnableInternalDeadlineRule: boolean;
    mtdItInternalDeadlineDateTitle: string;
  } {
    return {
      isEnableInternalDeadlineRule: this.parseTemplateJsonBoolByKeys(
        o,
        'IsEnableInternalDeadlineRule',
        'isEnableInternalDeadlineRule'
      ),
      internalDeadlineDateTitle: this.parseTemplateJsonStringByKeys(
        o,
        'InternalDeadlineDateTitle',
        'internalDeadlineDateTitle'
      ),
      mtdItIsEnableInternalDeadlineRule: this.parseTemplateJsonBoolByKeys(
        o,
        'MtdItIsEnableInternalDeadlineRule',
        'mtdItIsEnableInternalDeadlineRule'
      ),
      mtdItInternalDeadlineDateTitle: this.parseTemplateJsonStringByKeys(
        o,
        'MTDItInternalDeadlineDateTitle',
        'mtdItInternalDeadlineDateTitle'
      )
    };
  }

  parseCatalogTemplateEntries(row: DefaultServiceApiRow): DefaultServiceTemplateEntry[] {
    const raw = row.servicesJson?.trim();
    if (!raw || raw === '[]') {
      return [];
    }
    try {
      const parsed = JSON.parse(raw) as unknown;
      const entries = Array.isArray(parsed) ? parsed : [parsed];
      const mapped: DefaultServiceTemplateEntry[] = [];
      for (const entry of entries) {
        if (!entry || typeof entry !== 'object') {
          continue;
        }
        const o = entry as Record<string, unknown>;
        const serviceId = Number(o['ServiceId'] ?? o['serviceId']);
        if (!Number.isFinite(serviceId) || serviceId <= 0) {
          continue;
        }
        const serviceTitleRaw = o['ServiceTitle'] ?? o['serviceTitle'] ?? o['title'] ?? o['Title'];
        const serviceTitle = typeof serviceTitleRaw === 'string' && serviceTitleRaw.trim()
          ? serviceTitleRaw.trim()
          : `Service ${serviceId}`;
        const freqId = String(o['Frequency'] ?? o['frequency'] ?? '').trim();
        let frequencyName = freqId || '—';
        const defaultFrequency = o['DefaultFrequency'];
        if (Array.isArray(defaultFrequency)) {
          const matched = defaultFrequency.find((f) => {
            if (!f || typeof f !== 'object') {
              return false;
            }
            const fObj = f as Record<string, unknown>;
            return String(fObj['Id'] ?? '').trim() === freqId;
          });
          if (matched && typeof matched === 'object') {
            const name = (matched as Record<string, unknown>)['Name'];
            if (typeof name === 'string' && name.trim()) {
              frequencyName = name.trim();
            }
          }
        }
        const feeRaw = o['Fee'] ?? o['fee'];
        const fee = feeRaw != null && feeRaw !== '' && Number.isFinite(Number(feeRaw)) ? Number(feeRaw) : null;
        const billable = this.parseTemplateBillableFromJson(o, fee);
        const addToCalendar = this.parseTemplateAddToCalendarFromJson(o);
        const estHoursRaw = String(o['Esthours'] ?? o['esthours'] ?? o['EstHours'] ?? '').trim();
        const estHours = estHoursRaw.length > 0 ? estHoursRaw : null;
        const managerIdRaw = Number(o['ManagerId'] ?? o['managerId'] ?? o['UserId'] ?? o['userId'] ?? 0);
        const managerUserId = Number.isFinite(managerIdRaw) && managerIdRaw > 0 ? managerIdRaw : null;
        const managerNameRaw =
          o['ManagerName'] ??
          o['managerName'] ??
          o['Manager'] ??
          o['manager'] ??
          o['AssignedManagerName'] ??
          o['assignedManagerName'];
        let managerNameFromJson: string | null = null;
        if (typeof managerNameRaw === 'string' && managerNameRaw.trim()) {
          managerNameFromJson = managerNameRaw.trim();
        } else if (Array.isArray(o['AssignedManagers']) && o['AssignedManagers'].length > 0) {
          const first = o['AssignedManagers'][0];
          if (first && typeof first === 'object') {
            const name = (first as Record<string, unknown>)['name'] ?? (first as Record<string, unknown>)['Name'];
            if (typeof name === 'string' && name.trim()) {
              managerNameFromJson = name.trim();
            }
          }
        }
        const steps = o['ServiceSteps'] ?? o['serviceSteps'] ?? o['Steps'] ?? o['steps'];
        const { stepCount, stepTitles, apiSteps } = this.parseCatalogTemplateStepsFromJson(steps);
        const companyTypeRaw = this.rawCompanyTypeFromCatalogTemplateEntry(o);
        const clientTypes = companyTypeRaw
          ? this.parseClientTypesFromSettingsCompanyTypeField(companyTypeRaw)
          : [];
        const reminderTimings: string[] = [];
        const templateReminders = this.parseTemplateRemindersFromJson(o);
        if (templateReminders.length > 0) {
          for (const r of templateReminders) {
            const t = (r.timing ?? '').trim();
            if (t.length > 0) {
              reminderTimings.push(t);
            }
          }
        }
        if (reminderTimings.length === 0 && Array.isArray(defaultFrequency)) {
          const matchedReminderFreq = defaultFrequency.find((f) => {
            if (!f || typeof f !== 'object') {
              return false;
            }
            const fObj = f as Record<string, unknown>;
            return String(fObj['Id'] ?? '').trim() === freqId;
          });
          if (matchedReminderFreq && typeof matchedReminderFreq === 'object') {
            const rems = (matchedReminderFreq as Record<string, unknown>)['Reminders'];
            if (Array.isArray(rems)) {
              for (const r of rems) {
                if (!r || typeof r !== 'object') {
                  continue;
                }
                const title = (r as Record<string, unknown>)['Title'];
                if (typeof title === 'string' && title.trim()) {
                  reminderTimings.push(title.trim());
                }
              }
            }
          }
        }
        const activeRaw = o['IsActive'] ?? o['isActive'];
        const isActive = activeRaw == null ? (row.isActive !== false) : String(activeRaw).toLowerCase() !== 'false' && String(activeRaw) !== '0';
        const createdOnRaw = String(o['CreatedOn'] ?? o['createdOn'] ?? '').trim();
        const internalDeadlineRule = this.parseTemplateInternalDeadlineRuleFromJson(o);
        mapped.push({
          serviceId,
          serviceTitle,
          frequencyName,
          feeAmount: fee,
          estHours,
          billable,
          addToCalendar,
          managerUserId,
          managerNameFromJson,
          stepCount,
          stepTitles,
          apiSteps,
          reminderTimings,
          templateReminders,
          clientTypes,
          isActive,
          createdOn: createdOnRaw.length > 0 ? createdOnRaw : null,
          ...internalDeadlineRule
        });
      }
      return mapped;
    } catch {
      return [];
    }
  }

  applyDefaultServicesResponse(rows: DefaultServiceApiRow[]): void {
    this.defaultServicesCatalogRows.set(rows);
    // pm-api returns multiple bundles (Default/Small Business/Corporate), each containing
    // many of the same template services in servicesJson. For the Services tab grid, keep
    // one card per unique ServiceId to avoid inflated counts (e.g. 13 x 3 = 39).
    const byServiceId = new Map<number, { vm: CatalogServiceVm; priority: number }>();
    const fallbackByRowId = new Map<number, CatalogServiceVm>();
    for (const row of rows) {
      const rowPriority = this.catalogBundlePriority(row);
      const templates = this.parseCatalogTemplateEntries(row);
      if (templates.length === 0) {
        fallbackByRowId.set(row.id, {
          catalogRowId: row.id,
          defaultServiceId: row.id,
          trackKey: this.catalogServiceKey(row.id),
          name: row.description?.trim() || row.serviceType?.trim() || `Catalog item ${row.id}`,
          frequency: '—',
          typeLabel: row.isCustom ? 'Custom' : 'Default',
          isActive: row.isActive !== false,
          stepCount: 0,
          stepTitles: [],
          catalogApiSteps: [],
          reminderTimings: [],
          templateReminders: [],
          clientTypes: [],
          feeAmount: null,
          estHours: null,
          billable: 'No',
          addToCalendar: false,
          managerLabel: '—',
          managerUserId: null,
          isEnableInternalDeadlineRule: false,
          internalDeadlineDateTitle: '',
          mtdItIsEnableInternalDeadlineRule: false,
          mtdItInternalDeadlineDateTitle: ''
        });
        continue;
      }
      for (const t of templates) {
        const nextVm = this.catalogVmFromTemplateEntry(row, t);
        const existing = byServiceId.get(t.serviceId);
        if (existing) {
          byServiceId.set(t.serviceId, {
            vm: this.mergeCatalogServiceVms(existing.vm, nextVm, existing.priority, rowPriority),
            priority: Math.max(existing.priority, rowPriority)
          });
          continue;
        }
        byServiceId.set(t.serviceId, { vm: nextVm, priority: rowPriority });
      }
    }
    const vms = [...[...byServiceId.values()].map((x) => x.vm), ...fallbackByRowId.values()];
    this.catalogServices.set(vms);
    const status: Record<string, boolean> = {};
    for (const s of vms) {
      status[this.catalogServiceKey(s.defaultServiceId)] = s.isActive;
    }
    this.serviceStatusMap.update((m) => ({ ...m, ...status }));
    this.assignmentColumnIds.set([...new Set(vms.map((s) => s.defaultServiceId))].sort((a, b) => a - b));
  }

  /** pm-api JSON may use camelCase or PascalCase for id / userId. */
  readClientCustomServiceRowNumericId(r: ClientCustomServiceApiRow): number | undefined {
    const o = r as unknown as Record<string, unknown>;
    const raw = o['id'] ?? o['Id'];
    if (raw == null || raw === '') {
      return undefined;
    }
    const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
    return Number.isFinite(n) && n > 0 ? Math.trunc(n) : undefined;
  }

  readClientCustomServiceRowUserId(r: ClientCustomServiceApiRow): number | undefined {
    const o = r as unknown as Record<string, unknown>;
    const raw = o['userId'] ?? o['UserId'];
    if (raw == null || raw === '') {
      return undefined;
    }
    const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
    return Number.isFinite(n) && n > 0 ? Math.trunc(n) : undefined;
  }

  private readCustomServiceRowIsCustom(r: ClientCustomServiceApiRow): boolean {
    const o = r as unknown as Record<string, unknown>;
    const raw = o['isCustom'] ?? o['IsCustom'];
    if (raw == null) {
      return false;
    }
    if (typeof raw === 'boolean') {
      return raw;
    }
    const s = String(raw).trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
  }

  private applyCustomServicesResponse(rows: ClientCustomServiceApiRow[]): void {
    const customRows = rows.filter((r) => this.readCustomServiceRowIsCustom(r));
    const prevById = new Map<number, CustomAddedService>();
    for (const x of this.customAddedServices()) {
      if (x.id != null && Number.isFinite(Number(x.id)) && Number(x.id) > 0) {
        prevById.set(Number(x.id), x);
      }
    }
    const mapped = customRows.map((r) => {
      const rowId = this.readClientCustomServiceRowNumericId(r);
      const prev = rowId != null ? prevById.get(rowId) : undefined;
      const uid = this.readClientCustomServiceRowUserId(r);
      // Use false so we get '' (not 'User #N') when team hasn't loaded yet — the
      // re-resolution effect below will fill in the real name once team data arrives.
      const fromTeam = uid != null && uid > 0 ? this.teamMemberDisplayNameForUserId(uid, false) : '';
      let manager = fromTeam.trim().length > 0 ? fromTeam.trim() : '';
      if (!manager && prev?.manager && prev.manager !== '—') {
        manager = prev.manager;
      }
      if (!manager) {
        manager = '—';
      }
      const billRaw = (r.billable ?? '').trim().toLowerCase();
      const billable: 'Yes' | 'No' =
        billRaw === 'yes' || billRaw === 'y' || billRaw === 'true'
          ? 'Yes'
          : billRaw === 'no' || billRaw === 'n' || billRaw === 'false'
            ? 'No'
            : (r.fee ?? 0) > 0
              ? 'Yes'
              : 'No';
      const estHoursRaw = (r.esthours ?? '').trim();
      return {
        id: rowId,
        name: (r.serviceTitle ?? '').trim() || `Custom service ${rowId ?? ''}`,
        frequency: (r.frequency ?? '').trim() || '—',
        fee: r.fee ?? 0,
        billable,
        managerUserId: uid != null && uid > 0 ? uid : (prev?.managerUserId ?? null),
        manager,
        clientTypes: this.clientTypesForCustomServiceRow(r),
        steps: this.countGridStepsFromApiSteps(r.steps),
        estHours: estHoursRaw.length > 0 ? estHoursRaw : null,
        isActive: r.isActive !== false,
        createdOn: r.createdOn ?? null
      };
    });
    this.customAddedServices.set(mapped);
    this.serviceStatusMap.update((status) => {
      const next = { ...status };
      for (const item of mapped) {
        next[this.customServiceStatusKey(item.name)] = item.isActive;
      }
      return next;
    });

    // Assign-to-clients grid columns are driven by assignmentColumnIds.
    // Include custom service ids so they can be assigned too.
    const customIds = mapped
      .map((m) => Number(m.id))
      .filter((id) => Number.isFinite(id) && id > 0) as number[];
    if (customIds.length > 0) {
      this.assignmentColumnIds.update((cur) =>
        [...new Set([...(cur ?? []), ...customIds])].sort((a, b) => a - b)
      );
    }
  }

  loadCustomServicesForSettings(onLoaded?: () => void): void {
    const parent = this.effectivePracticeParentUserId();
    this.pmSettingsApi
      .getCustomServices(this.settingsClientUniversalId, true, parent > 0 ? parent : undefined)
      .subscribe({
      next: (rows) => {
        this.applyCustomServicesResponse(rows);
        onLoaded?.();
      },
      error: () => {
        this.customAddedServices.set([]);
        onLoaded?.();
      }
    });
  }

  loadSettingsServicesCatalog(): void {
    if (this.servicesSettingsLoading()) {
      return;
    }
    void this.ensureTeamLoadedForServiceManager();
    this.servicesSettingsLoading.set(true);
    const parent = this.effectivePracticeParentUserId();
    forkJoin({
      defaults: this.pmSettingsApi
        .getDefaultServices(true)
        .pipe(catchError(() => of([] as DefaultServiceApiRow[]))),
      customs: this.pmSettingsApi
        .getCustomServices(this.settingsClientUniversalId, true, parent > 0 ? parent : undefined)
        .pipe(catchError(() => of([] as ClientCustomServiceApiRow[]))),
      clientTypeLookups: this.pmTemplateLibraryApi
        .listClientTypes()
        .pipe(catchError(() => of([] as ClientTypeLookupApiRow[]))),
      packages: this.pmSettingsApi.getServicePackages().pipe(catchError(() => of([] as ServicePackageApiRow[])))
    }).subscribe({
      next: ({ defaults, customs, clientTypeLookups, packages }) => {
        this.templateLibraryClientTypes.set(clientTypeLookups ?? []);
        this.templateLibraryContext.clientTypesLoaded = true;
        this.applyDefaultServicesResponse(defaults);
        this.applyCustomServicesResponse(customs);
        this.hydratePackagesFromApi(packages ?? []);
        this.servicesSettingsLoading.set(false);
      },
      error: () => {
        this.servicesSettingsLoading.set(false);
      }
    });
  }

  loadServicePackagesForTab(): void {
    this.packagesLoading.set(true);
    this.packagesError.set(null);
    const loadPkgs = () => {
      this.pmSettingsApi.getServicePackages().subscribe({
        next: (rows) => {
          this.hydratePackagesFromApi(rows);
          this.packagesLoading.set(false);
        },
        error: (err) => {
          this.packagesError.set(this.formatPmApiError(err));
          this.packagesLoading.set(false);
        }
      });
    };
    if (this.catalogServices().length === 0) {
      this.pmSettingsApi.getDefaultServices(true).subscribe({
        next: (rows) => {
          this.applyDefaultServicesResponse(rows);
          loadPkgs();
        },
        error: (err) => {
          this.packagesError.set(this.formatPmApiError(err));
          this.packagesLoading.set(false);
        }
      });
      return;
    }
    loadPkgs();
  }

  private hydratePackagesFromApi(rows: ServicePackageApiRow[]): void {
    const byClientType = new Map<string, PackageGridRow>();
    for (const r of rows) {
      const selectedTemplateServiceIds = r.items
        .filter((i) => i.isSelected)
        .map((i) => Number(i.defaultServiceId))
        .filter((id) => Number.isFinite(id) && id > 0);
      byClientType.set(r.clientTypeCode, {
        clientTypeCode: r.clientTypeCode,
        packageId: r.packageId,
        packageName: r.packageName,
        selectedDefaultServiceIds: selectedTemplateServiceIds
      });
    }

    // Service Packages UX expects all client types to be shown even when API has no row yet.
    for (const clientType of this.availableClientTypes) {
      if (!byClientType.has(clientType)) {
        byClientType.set(clientType, {
          clientTypeCode: clientType,
          packageId: 0,
          packageName: `${clientType} Package`,
          selectedDefaultServiceIds: []
        });
      }
    }

    const mapped = Array.from(byClientType.values()).sort((a, b) => {
      const ai = this.availableClientTypes.indexOf(a.clientTypeCode);
      const bi = this.availableClientTypes.indexOf(b.clientTypeCode);
      if (ai === -1 && bi === -1) {
        return a.clientTypeCode.localeCompare(b.clientTypeCode);
      }
      if (ai === -1) {
        return 1;
      }
      if (bi === -1) {
        return -1;
      }
      return ai - bi;
    });
    this.packageGridRows.set(mapped);
  }

  loadAssignmentsMatrixForTab(onAfterMatrixLoaded?: () => void, usePagedDirectory = true): void {
    this.assignmentsError.set(null);
    const fetchMatrix = () => {
      const cols = this.assignmentColumnIds();
      this.assignmentsLoading.set(true);
      const useDir = this.userContext.practiceContextReady();
      const parent = this.effectiveDirectoryParentUserId();
      const listUser = this.effectiveDirectoryUserIdForClientsList();
      const requestSeq = ++this.assignMatrixRequestSeq;
      this.assignMatrixRequestSub?.unsubscribe();
      if (usePagedDirectory && useDir && parent > 0 && listUser > 0) {
        this.assignMatrixRequestSub = this.pmSettingsApi
          .getAssignmentsMatrixPage(
            parent,
            listUser,
            this.assignMatrixPageNumber(),
            this.assignMatrixPageSize(),
            this.assignSearchQuery()
          )
          .subscribe({
          next: (page) => {
            if (requestSeq !== this.assignMatrixRequestSeq) {
              return;
            }
            this.applyAssignmentsMatrixPage(page, cols);
            this.assignmentsLoading.set(false);
            onAfterMatrixLoaded?.();
          },
          error: (err) => {
            if (requestSeq !== this.assignMatrixRequestSeq) {
              return;
            }
            this.assignmentsError.set(this.formatPmApiError(err));
            this.assignmentsLoading.set(false);
            onAfterMatrixLoaded?.();
          }
        });
        return;
      }

      this.assignMatrixRequestSub = this.pmSettingsApi
        .getAssignmentsMatrix(undefined, useDir ? parent : undefined, useDir ? listUser : undefined)
        .subscribe({
        next: (rows) => {
          if (requestSeq !== this.assignMatrixRequestSeq) {
            return;
          }
          this.assignClientsData.set(this.buildAssignRowsFromMatrix(rows, cols));
          this.assignMatrixTotalCount.set(rows.length);
          this.captureAssignBaselineFromCurrent();
          this.assignmentsLoading.set(false);
          onAfterMatrixLoaded?.();
        },
        error: (err) => {
          if (requestSeq !== this.assignMatrixRequestSeq) {
            return;
          }
          this.assignmentsError.set(this.formatPmApiError(err));
          this.assignmentsLoading.set(false);
          onAfterMatrixLoaded?.();
        }
      });
    };

    if (this.catalogServices().length === 0) {
      this.assignmentsLoading.set(true);
      this.pmSettingsApi.getDefaultServices(true).subscribe({
        next: (rows) => {
          this.applyDefaultServicesResponse(rows);
          // Ensure custom services are included in assignment columns (total includes Custom too).
          const parent = this.effectivePracticeParentUserId();
          this.pmSettingsApi
            .getCustomServices(this.settingsClientUniversalId, true, parent > 0 ? parent : undefined)
            .subscribe({
            next: (customRows) => {
              this.applyCustomServicesResponse(customRows);
              fetchMatrix();
            },
            error: () => {
              this.customAddedServices.set([]);
              fetchMatrix();
            }
          });
        },
        error: (err) => {
          this.assignmentsError.set(this.formatPmApiError(err));
          this.assignmentsLoading.set(false);
          onAfterMatrixLoaded?.();
        }
      });
      return;
    }

    if (this.customAddedServices().length === 0) {
      this.assignmentsLoading.set(true);
      const parent = this.effectivePracticeParentUserId();
      this.pmSettingsApi
        .getCustomServices(this.settingsClientUniversalId, true, parent > 0 ? parent : undefined)
        .subscribe({
        next: (rows) => {
          this.applyCustomServicesResponse(rows);
          fetchMatrix();
        },
        error: () => {
          this.customAddedServices.set([]);
          fetchMatrix();
        }
      });
      return;
    }

    fetchMatrix();
  }

  private applyAssignmentsMatrixPage(page: ServiceAssignmentsMatrixPageApi, cols: number[]): void {
    this.assignClientsData.set(this.buildAssignRowsFromMatrix(page.items ?? [], cols));
    this.assignMatrixTotalCount.set(Math.max(0, Number(page.totalCount) || 0));
    this.assignMatrixPageNumber.set(Math.max(1, Number(page.pageNumber) || 1));
    this.assignMatrixPageSize.set(Math.max(1, Number(page.pageSize) || this.assignMatrixPageSize()));
    this.captureAssignBaselineFromCurrent();
  }

  /** Column id for the built-in "Ad-hoc" service, if it's among the current assignment columns. */
  private resolveAdHocAssignmentColumnId(): number | null {
    const match = this.getAssignmentColumnServices().find(
      (s) => s.name.trim().toLowerCase().replace(/[\s-]+/g, '') === 'adhoc'
    );
    return match ? match.id : null;
  }

  private buildAssignRowsFromMatrix(rows: ServiceAssignmentsMatrixRowApi[], columnIds: number[]): AssignClientRow[] {
    const columnTotal = columnIds.length;
    const adHocColumnId = this.resolveAdHocAssignmentColumnId();
    return rows.map((r) => {
      const byId = new Map<number, boolean>();
      for (const a of r.assignments) {
        const id = Number(a.defaultServiceId);
        if (!Number.isFinite(id) || id <= 0) {
          continue;
        }
        const on = a.isAssigned === true;
        byId.set(id, (byId.get(id) ?? false) || on);
      }
      const columnAssigned = (col: number): boolean => {
        if (!Number.isFinite(col) || col <= 0) {
          return false;
        }
        if (col === adHocColumnId) {
          // "Ad-hoc" is a mandatory default, locked on in the UI (see isAssignMatrixCellDisabled) —
          // always show it assigned, even overriding a stale/legacy explicit isAssigned:false row.
          return true;
        }
        if (!byId.has(col)) {
          return false;
        }
        return byId.get(col) === true;
      };
      const raw = columnIds.map((colId) => columnAssigned(Number(colId)));
      const svcs = raw;
      const coverage = raw.filter(Boolean).length;
      const display = (r.clientDisplayName ?? '').trim();
      const clientType = (r.clientType ?? '').trim() || '—';
      return {
        clientUniversalId: r.clientUniversalId,
        name: display || r.clientUniversalId,
        type: clientType,
        typeClass: clientType === '—' ? 'gray' : this.getEntityTagClass(clientType),
        vatSchemeId: r.vatSchemeId ?? null,
        coverage,
        total: columnTotal,
        svcs,
        allowAutoAssign: r.autoAssignEnabled,
        autoAssignDisabled: false
      };
    });
  }

  private formatPmApiError(err: unknown): string {
    const e = err as { error?: { detail?: string; title?: string }; message?: string };
    return e?.error?.detail || e?.error?.title || e?.message || 'Request failed';
  }

  notifySuccess(message: string): void {
    toast.success(message);
  }

  notifyError(err: unknown): void {
    toast.error(this.formatPmApiError(err));
  }

  private mapCustomFieldApiRow(row: CustomFieldApiRow): CustomFieldItem {
    const normalizedType = (row.fieldType || 'Text') as CustomFieldType;
    return {
      id: `cf-${row.id}`,
      apiId: row.id,
      name: row.fieldName,
      description: row.description ?? '',
      section: (row.section ?? 'Client').trim() || 'Client',
      fieldType: normalizedType,
      required: row.isRequired,
      dropdownOptions: (row.options ?? []).map((o) => o.optionLabel).filter((x) => !!x)
    };
  }

  loadCustomFieldsSettings(): void {
    this.customFieldSettingsLoading.set(true);
    this.customFieldSettingsError.set(null);
    this.pmSettingsApi.getCustomFields(this.effectiveDirectoryParentUserId()).subscribe({
      next: (rows) => {
        this.customFields.set(rows.filter((r) => r.isActive).map((r) => this.mapCustomFieldApiRow(r)));
        this.customFieldSettingsLoading.set(false);
      },
      error: (err) => {
        this.customFieldSettingsLoading.set(false);
        this.customFieldSettingsError.set(this.formatPmApiError(err));
      }
    });
  }

  loadCustomLabelsSettings(): void {
    const parentId = this.effectiveDirectoryParentUserId();
    if (parentId <= 0) {
      return;
    }
    this.customFieldSettingsLoading.set(true);
    this.customFieldSettingsError.set(null);
    this.pmCustomLabels.load(parentId, true).subscribe({
      next: () => {
        this.customLabelsDraft.set(this.pmCustomLabels.baseLabelsSnapshot());
        this.extraCustomLabelsDraft.set(this.pmCustomLabels.extraLabelsSnapshot());
        this.customFieldSettingsLoading.set(false);
      },
      error: (err) => {
        this.customFieldSettingsLoading.set(false);
        this.customFieldSettingsError.set(this.formatPmApiError(err));
      }
    });
  }

  closeServicesFilterDropdowns(): void {
    this.servicesFilterDropdownOpen.set(null);
    this.servicesFilterDdRect.set(null);
  }

  getTotalServicesCount(): number {
    return this.catalogServices().length + this.customAddedServices().length;
  }

  getFrequencyBreakdown(): { label: string; count: number; cls: string; pct: number }[] {
    const map = new Map<string, { label: string; cls: string }>([
      ['Weekly', { label: 'Weekly', cls: 'weekly' }],
      ['2 Weekly', { label: '2 Weekly', cls: 'two-weekly' }],
      ['4 Weekly', { label: '4 Weekly', cls: 'four-weekly' }],
      ['Monthly', { label: 'Monthly', cls: 'monthly' }],
      ['Bi-Monthly', { label: 'Bi-Monthly', cls: 'bi-monthly' }],
      ['Quarterly', { label: 'Quarterly', cls: 'quarterly' }],
      ['Yearly', { label: 'Yearly', cls: 'yearly' }],
      ['One-Time', { label: 'One-Time', cls: 'onetime' }]
    ]);
    const counts = new Map<string, number>();
    for (const s of this.catalogServices()) {
      const key = s.frequency === '—' ? 'Yearly' : s.frequency;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const total = this.catalogServices().length || 1;
    const order = ['Weekly', '2 Weekly', '4 Weekly', 'Monthly', 'Bi-Monthly', 'Quarterly', 'Yearly', 'One-Time'];
    return order
      .filter((k) => counts.has(k))
      .map((k) => {
        const c = counts.get(k) ?? 0;
        const meta = map.get(k)!;
        return { label: meta.label, count: c, cls: meta.cls, pct: Math.round((c / total) * 100) };
      });
  }

  getEntityTagsForDefaultService(defaultServiceId: number): string[] {
    const tags: string[] = [];
    for (const p of this.packageGridRows()) {
      if (p.selectedDefaultServiceIds.includes(defaultServiceId)) {
        tags.push(this.packageClientTypeCodeToLabel(p.clientTypeCode));
      }
    }
    return [...new Set(tags)];
  }

  private packageClientTypeCodeToLabel(code: string): string {
    const trimmed = code.trim();
    if (!trimmed) {
      return trimmed;
    }
    const fromNumeric = this.clientTypeLabelForCode(trimmed);
    if (fromNumeric) {
      return fromNumeric;
    }
    return canonicalClientTypeLabel(trimmed, this.availableClientTypes);
  }

  getEntityTagsForCatalogService(svc: CatalogServiceVm): string[] {
    if (svc.clientTypes.length > 0) {
      return [...svc.clientTypes];
    }
    // Only fall back to service-package membership when the template has no CompanyType in any bundle.
    const fromPackages = this.getEntityTagsForDefaultService(svc.defaultServiceId);
    if (fromPackages.length > 0) {
      return fromPackages;
    }
    // Last resort: the matrix spec's baseline for this built-in service. DEFAULT_SERVICE_CLIENT_TYPES
    // documents itself as "the fallback when a catalog service has no stored clientTypes", but it was
    // only ever used to filter which pills render — never to pre-select them. So a service that had
    // never been customised opened with nothing selected (e.g. Confirmation Statement showing neither
    // LLP nor Limited) instead of its spec baseline.
    return [...(getDefaultClientTypesForServiceName(svc.name) ?? [])];
  }

  getAssignmentColumnServices(): AssignmentColumnServiceVm[] {
    const ids = this.assignmentColumnIds();
    const catalogById = new Map(this.catalogServices().map((s) => [s.defaultServiceId, s]));
    const customById = new Map(
      this.customAddedServices()
        .filter((c) => c.id != null && Number.isFinite(Number(c.id)) && Number(c.id) > 0)
        .map((c) => [Number(c.id), c])
    );

    const out: AssignmentColumnServiceVm[] = [];
    for (const rawId of ids) {
      const id = Number(rawId);
      if (!Number.isFinite(id) || id <= 0) {
        continue;
      }
      const cat = catalogById.get(id);
      if (cat) {
        out.push({
          id,
          trackKey: cat.trackKey,
          name: cat.name,
          frequency: cat.frequency,
          typeLabel: 'Default',
          // Live restriction as saved via Settings → Services → Edit Service's "Selected client
          // types" — previously omitted here, so editing/saving it there had no effect on which
          // toggles this grid disabled; it silently kept using the static spec fallback instead.
          clientTypes: cat.clientTypes.length > 0 ? [...cat.clientTypes] : undefined
        });
        continue;
      }
      const custom = customById.get(id);
      if (custom) {
        out.push({
          id,
          trackKey: `custom:${id}`,
          name: custom.name,
          frequency: custom.frequency,
          typeLabel: 'Custom',
          clientTypes: custom.clientTypes.length > 0 ? [...custom.clientTypes] : undefined
        });
      }
    }
    return out;
  }

  getAssignSourceRows(): AssignClientRow[] {
    return this.assignClientsData();
  }

  assignMatrixFreqClass(frequency: string): string {
    const t = this.getFrequencyTagClass(frequency);
    if (t === 'orange') {
      return 'yellow';
    }
    return t;
  }

  assignMatrixFreqLetter(frequency: string): string {
    if (frequency === 'Monthly') {
      return 'M';
    }
    if (frequency === 'Quarterly') {
      return 'Q';
    }
    if (frequency === 'One-Time') {
      return 'O';
    }
    return 'Y';
  }

  getFrequencyTagClass(frequency: string): string {
    if (frequency === 'Monthly') {
      return 'green';
    }
    if (frequency === 'Quarterly') {
      return 'orange';
    }
    if (frequency === 'One-Time') {
      return 'gray';
    }
    return 'blue';
  }

  initialsFromName(name: string | null | undefined): string {
    const t = (name ?? '').trim();
    if (!t) return '—';
    const parts = t.split(/\s+/).filter(Boolean);
    const a = parts[0]?.[0] ?? '';
    const b = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : (parts[0]?.[1] ?? '');
    const out = (a + b).toUpperCase();
    return out || t.slice(0, 2).toUpperCase();
  }

  // Track modifications for save confirmation
  hasUnsavedChanges = signal(false);
  originalClientData: AssignClientRow[] = [];

  private snapshotAssignRows(rows: AssignClientRow[]): AssignClientRow[] {
    return rows.map((r) => ({
      clientUniversalId: r.clientUniversalId,
      name: r.name,
      type: r.type,
      typeClass: r.typeClass,
      vatSchemeId: r.vatSchemeId,
      coverage: r.coverage,
      total: r.total,
      svcs: [...r.svcs],
      allowAutoAssign: r.allowAutoAssign,
      autoAssignDisabled: r.autoAssignDisabled
    }));
  }

  captureAssignBaselineFromCurrent(): void {
    const rows = this.getAssignSourceRows();
    this.originalClientData = this.snapshotAssignRows(rows);
    this.hasUnsavedChanges.set(false);
  }

  getProgressWidthForPackage(clientTypeCode: string): number {
    const row = this.packageGridRows().find((p) => p.clientTypeCode === clientTypeCode);
    const n = this.catalogServices().length || 1;
    return row ? (row.selectedDefaultServiceIds.length / n) * 100 : 0;
  }

  // Get CSS class for entity tag based on client type
  getEntityTagClass(clientType: string): string {
    const classMap: { [key: string]: string } = {
      'Limited': 'orange',
      'Sole Trader': 'blue',
      'Partnership': 'purple',
      'LLP': 'gray',
      'Individual': 'cyan',
      'Trust': 'orange',
      'Trustee': 'orange',
      'CIS': 'blue',
      'Charity': 'green'
    };
    return classMap[clientType] || 'blue';
  }

  activeAutomationTab = signal<string>('Automation');
  automationSettings = signal<AutomationSettingsVm | null>(null);
  automationLoading = signal(false);
  automationError = signal<string | null>(null);
  activeTeamTab = signal<string>('Team');
  activeIntegrationTab = signal<string>('Integrations');
  activeSecurityTab = signal<string>('Notifications');

  /** Populated from pm-api practice dashboard overview (`recentActivity`). */
  allActivities = signal<ActivityItem[]>([]);

  filteredActivities = computed(() => {
    const tab = this.activeActivityTab();
    const query = this.activitySearchQuery().toLowerCase();
    const type = this.selectedActivityType();

    return this.allActivities().filter(item => {
      // Tab Category Match — User Activity shows all items returned (API filters via performedByUserId)
      const tabMatch = tab === 'Activity' ? true : (tab === 'Email Activity' ? item.type === 'Email' : true);

      // Type Sub-filter Match
      let typeMatch = true;
      if (type !== 'All') {
        const action = item.actionLabel.toLowerCase();
        const typeKey = type.toLowerCase();
        typeMatch =
          action === typeKey ||
          action.startsWith(typeKey) ||
          item.content.toLowerCase().includes(typeKey) ||
          item.description.toLowerCase().includes(typeKey);
      }

      // Time filter: API-backed lists are short; avoid index-based demo slicing.
      const timeMatch = true;

      const searchMatch =
        !query ||
        item.subject.toLowerCase().includes(query) ||
        item.user.toLowerCase().includes(query) ||
        item.content.toLowerCase().includes(query) ||
        item.clientName.toLowerCase().includes(query) ||
        item.module.toLowerCase().includes(query);

      return tabMatch && typeMatch && timeMatch && searchMatch;
    });
  });

  ngAfterViewInit() {
    // Small delay to ensure layout is complete
    setTimeout(() => {
      this.updateSlider();
    }, 100);

    // The navbar is never behind an @if, so one hookup here is enough. The second check after the
    // same 100ms delay is for web fonts: the links measure narrower before the font lands, which
    // would let a bar that does not really fit call itself fine.
    const navbar = this.topNavbarEl?.nativeElement;
    if (navbar) {
      this.topNavResizeObserver = new ResizeObserver(() => this.scheduleTopNavFit());
      this.topNavResizeObserver.observe(navbar);
      this.checkTopNavFit();
      setTimeout(() => this.checkTopNavFit(), 100);
    }
  }

  private topNavSettleTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Measure now, and once more after the resizing stops. A window resize (closing DevTools,
   * snapping a window) arrives as a burst of sizes, and a reading taken mid-way through the layout
   * could leave the bar folded into the hamburger at full width until a reload. The trailing check
   * runs once everything has settled, so the bar always ends on the right answer by itself.
   */
  private scheduleTopNavFit(): void {
    this.checkTopNavFit();
    if (this.topNavSettleTimer) clearTimeout(this.topNavSettleTimer);
    this.topNavSettleTimer = setTimeout(() => {
      this.topNavSettleTimer = null;
      this.checkTopNavFit();
    }, 200);
  }

  @HostListener('window:resize')
  onTopNavWindowResize(): void {
    this.scheduleTopNavFit();
  }

  private clientsToolbarObservedEl?: HTMLElement;

  /** The toolbar sits behind `@if`s (clients-list section / clients-vs-users tab), so its DOM node
   *  is created and destroyed as the user navigates — a one-time ngAfterViewInit hookup would miss it
   *  whenever the Clients list wasn't the first thing rendered. Re-checking every view-checked cycle
   *  (cheap: just an identity comparison) re-attaches the observer whenever the element is recreated. */
  ngAfterViewChecked(): void {
    const el = this.clientsToolbarEl?.nativeElement;
    if (el !== this.clientsToolbarObservedEl) {
      this.clientsToolbarResizeObserver?.disconnect();
      this.clientsToolbarObservedEl = el;
      if (el) {
        this.clientsToolbarResizeObserver = new ResizeObserver(() => this.checkClientsToolbarFit());
        this.clientsToolbarResizeObserver.observe(el);
        this.checkClientsToolbarFit();
      }
    }
  }

  /** Toggles Filter/Analytics/Sort down to icon-only exactly when the toolbar's actual content
   *  no longer fits its available width, instead of guessing a fixed breakpoint. Remembers the
   *  width the row needed while last fully expanded so it only re-expands once there is genuinely
   *  room again — otherwise it would flicker back to expanded, immediately overflow, and re-collapse. */
  private checkClientsToolbarFit(): void {
    const el = this.clientsToolbarEl?.nativeElement;
    if (!el) return;
    const available = el.clientWidth;
    if (!this.clientsToolbarCompact()) {
      this.clientsToolbarExpandedWidth = el.scrollWidth;
      if (el.scrollWidth > available + 1) {
        this.clientsToolbarCompact.set(true);
      }
    } else if (this.clientsToolbarExpandedWidth > 0 && available >= this.clientsToolbarExpandedWidth) {
      this.clientsToolbarCompact.set(false);
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────────────────────────
   *  PLEASE DO NOT "TIDY" THE FOUR THINGS BELOW. Each one looks redundant on its own and each one
   *  was a bug before it was a fix. Removing any of them puts the top nav back into the hamburger
   *  on a full-size screen, and the failure is silent — the bar just never shows the links again.
   *
   *  1. `.nav-menu` is hidden while collapsed with `position: absolute` + `visibility: hidden`,
   *     NOT `display: none`. A `display: none` element measures zero, so the bar could never work
   *     out that there is room for the links again. It collapsed once and stayed that way for the
   *     life of the page, and reloading only repeated the same reading.
   *  2. Nothing here caches "the width it needed last time". A single reading taken mid-layout, or
   *     before the web font landed, used to be enough to wedge it shut permanently. The width is
   *     re-measured on every call and the decision is made from scratch.
   *  3. The hamburger (`.mobile-nav-wrap`) is always excluded from the sum. It only exists while
   *     collapsed, so counting it would compare against a layout that never happens.
   *  4. The `flex-shrink: 0` guards in the CSS are load-bearing. A flex item that quietly shrinks
   *     measures narrower than it needs, so the bar would never notice it had run out of room.
   *
   *  If the nav must change, change what it measures — do not swap the measuring for a breakpoint.
   *  A fixed `@media (max-width: …)` is what this replaced: it hid the links on wide screens that
   *  had room and kept them on narrow ones where they collided with the icons, and the right number
   *  moved every time a nav item was added or a firm name got long.
   *  ───────────────────────────────────────────────────────────────────────────────────────────── */
  /** Folds the top-nav links into the hamburger exactly when they stop fitting beside the brand and
   *  the header icons, instead of guessing a breakpoint. A fixed one was wrong both ways: it hid the
   *  links on wide screens with room to spare, and kept them on narrow ones where they collided with
   *  the icons — and the right number moves every time a nav item is added or a firm name gets long.
   *
   *  What it needs is measured fresh every time and does not depend on the state it is currently in:
   *  the links stay measurable while collapsed (out of flow, hidden) and the hamburger is always left
   *  out of the sum, since it only exists in the collapsed state. An earlier version cached the width
   *  recorded at the moment it collapsed and compared against that; one bad reading — mid-layout, or
   *  before the web font landed — and the bar could never satisfy its own condition again, so it
   *  stayed collapsed for the life of the page and a reload repeated the same bad reading. */
  checkTopNavFit(): void {
    const navbar = this.topNavbarEl?.nativeElement;
    const left = this.topNavLeftEl?.nativeElement;
    const right = this.topNavRightEl?.nativeElement;
    const menu = this.topNavMenuEl?.nativeElement;
    if (!navbar || !left || !right || !menu) return;

    const navbarStyle = getComputedStyle(navbar);
    const padding =
      (parseFloat(navbarStyle.paddingLeft) || 0) + (parseFloat(navbarStyle.paddingRight) || 0);
    const available = navbar.clientWidth - padding;
    // Nothing is laid out yet (hidden tab, first paint) — measuring now would collapse the bar on
    // a width of zero and it would have no reason to ever come back.
    if (available <= 0) return;

    // The open search box is the one item in the bar allowed to shrink, and while it is open it is
    // also holding width that is not the nav's own, so the reading would be neither the natural
    // width nor the minimum. Leave the bar exactly as it is until the search closes.
    if (this.isSearchExpanded()) return;

    const menuWidth = menu.getBoundingClientRect().width;
    if (menuWidth <= 0) return;

    const leftGap = parseFloat(getComputedStyle(left).columnGap) || 0;
    const brandWidth = this.measureNavRowWidth(left, menu);
    const rightWidth = this.measureNavRowWidth(right, null);
    const needed = brandWidth + leftGap + menuWidth + rightWidth;

    // The 1px allowance is for sub-pixel widths: a row that fits exactly can measure a hair over
    // its container and flap between the two states on every resize event.
    this.topNavCollapsed.set(needed > available + 1);
    if (!this.topNavCollapsed()) {
      this.isMobileNavOpen.set(false);
    }
  }

  /** Width a row is using: its children plus the flex gaps between them. `skip` drops one child from
   *  the sum — the links, which are measured on their own because they leave the flow when collapsed.
   *  The hamburger is dropped too: it exists only while collapsed, so counting it would make the bar
   *  look wider than the expanded layout it is being compared against. */
  private measureNavRowWidth(row: HTMLElement, skip: HTMLElement | null): number {
    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
    let total = 0;
    let shown = 0;
    for (const child of Array.from(row.children)) {
      if (child === skip || child.classList.contains('mobile-nav-wrap')) continue;
      const width = child.getBoundingClientRect().width;
      if (width <= 0) continue;
      total += width;
      shown += 1;
    }
    return total + gap * Math.max(0, shown - 1);
  }

  initPackageEventListeners() {
    // Intentionally no-op. Package selection is fully driven by Angular signals.
  }

  updateSlider() {
    const tabs = this.activityTabs.toArray();
    const activeTab = this.activeActivityTab();
    const index = ['Activity', 'Email Activity', 'User Activity', 'Bulk Activity'].indexOf(activeTab);
    if (tabs[index]) {
      const el = tabs[index].nativeElement;
      this.sliderStyle.set({
        left: `${el.offsetLeft}px`,
        width: `${el.offsetWidth}px`
      });
    }
  }

  // ===========================================================================
  // Dropdown / combobox keyboard navigation (ArrowUp / ArrowDown / Enter)
  // ---------------------------------------------------------------------------
  // One highlighted-index signal per dropdown instance, one shared navigation
  // helper, and one keydown handler per instance bound to that instance's
  // search <input>. Selection/business logic is untouched: each handler's
  // onEnter closure calls the exact same function the option row's click /
  // change handler calls.
  // ===========================================================================

  /** Highlighted option index - dashboard "Deadlines" client filter (single-select). */
  dashboardDeadlinesClientHighlighted = signal(0);
  /** Highlighted option index - dashboard "Activity" client filter (single-select). */
  dashboardActivityClientHighlighted = signal(0);
  /** Highlighted option index - dashboard "Tasks" client filter (single-select). */
  dashboardTasksClientHighlighted = signal(0);
  /** Highlighted option index - dashboard "Invoices" client filter (single-select). */
  dashboardInvoicesClientHighlighted = signal(0);
  /** Highlighted option index - communication compose "To" recipients (multi-select). */
  clientsCommEmailToHighlighted = signal(0);
  /** Highlighted option index - bulk communication "To" recipients (multi-select). */
  clientsBulkEmailToHighlighted = signal(0);
  /** Highlighted option index - Add Service manager picker (single-select). */
  addServiceManagerHighlighted = signal(0);
  /** Highlighted option index - Add Service assign-clients picker (multi-select). */
  addServiceAssignClientHighlighted = signal(0);
  /** Highlighted option index - reminder staff picker (multi-select). */
  acsReminderStaffHighlighted = signal(0);
  /** Highlighted option index - reminder client picker (multi-select). */
  acsReminderClientHighlighted = signal(0);
  /** Highlighted option index - Add Service frequency picker (single-select). */
  acsServiceFrequencyHighlighted = signal(0);
  /** Highlighted option index - Add Service email workflow template type picker (single-select). */
  acsEmailWorkflowTemplateTypeHighlighted = signal(0);
  /** Highlighted option index - reminder cascade frequency picker, Unit column (single-select). */
  reminderCascadeUnitHighlighted = signal(0);
  /** Highlighted option index - reminder cascade frequency picker, Timing column (single-select). */
  reminderCascadeTimingHighlighted = signal(0);
  /** Highlighted option index - dashboard "Deadlines" Service multiselect (multi-select). */
  dashboardSvcMultiHighlighted = signal(0);
  /** Highlighted option index - dashboard "Deadlines" Status multiselect (multi-select). */
  dashboardStatusMultiHighlighted = signal(0);
  /** Highlighted option index - "Deadlines by status" chart Clients multiselect (multi-select). */
  dashboardChartClientHighlighted = signal(0);
  /** Highlighted option index - "Deadlines by status" chart Services multiselect (multi-select). */
  dashboardChartServiceHighlighted = signal(0);
  /** Highlighted option index - dashboard Activity widget Type filter (single-select). */
  dashboardActivityTypeHighlighted = signal(0);
  /** Highlighted option index - dashboard Activity widget Category filter (single-select). */
  dashboardActivityCategoryHighlighted = signal(0);
  /** Highlighted option index - dashboard Activity widget Time filter (single-select). */
  dashboardActivityTimeHighlighted = signal(0);
  /** Highlighted option index - dashboard Tasks widget Priority filter (single-select). */
  dashboardTasksPriorityHighlighted = signal(0);
  /** Highlighted option index - dashboard Tasks widget View filter (single-select). */
  dashboardTasksViewHighlighted = signal(0);
  /** Highlighted option index - dashboard Invoices widget Payment status filter (single-select). */
  dashboardInvoicesPaymentHighlighted = signal(0);
  /** Highlighted option index - Compose/Schedule Email "Priority" filter (single-select). */
  clientsCommEmailPriorityHighlighted = signal(0);
  /** Highlighted option index - Compose SMS "Recurring Frequency" filter (single-select). */
  clientsCommSmsRecurringFreqHighlighted = signal(0);
  /** Highlighted option index - Add SMS Template "Template Type" filter (single-select). */
  clientsCommSmsAddTemplateTypeHighlighted = signal(0);
  /** Highlighted option index - Bulk Email "Client Type" filter (single-select). */
  clientsBulkEmailClientTypeHighlighted = signal(0);
  /** Highlighted option index - Bulk SMS "Recurring Frequency" filter (single-select). */
  clientsBulkSmsRecurringFreqHighlighted = signal(0);
  /** Highlighted option index - Bulk Email "Priority" filter (single-select). */
  clientsBulkEmailPriorityHighlighted = signal(0);

  /**
   * Shared keyboard navigation for the hand-rolled dropdown lists in this
   * component. ArrowDown/ArrowUp move the highlight within [0, length - 1];
   * Enter invokes `onEnter` with the highlighted index.
   *
   * `scrollContainerSelector`, when passed, scrolls the newly-highlighted row
   * into view. `scrollIndexOffset` accounts for static/leading rows rendered
   * inside that same container ahead of the `@for`-generated option rows
   * (e.g. a leading "All Clients" row) so `highlighted()` - which only
   * indexes the `@for` options - maps to the right DOM child.
   */
  navigateDdList(
    event: KeyboardEvent,
    highlighted: WritableSignal<number>,
    length: number,
    onEnter: (index: number) => void,
    scrollContainerSelector?: string,
    scrollIndexOffset = 0
  ): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      highlighted.update(i => Math.min(i + 1, length - 1));
      if (scrollContainerSelector) this.scrollDdHighlightedIntoView(scrollContainerSelector, highlighted() + scrollIndexOffset);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      highlighted.update(i => Math.max(i - 1, 0));
      if (scrollContainerSelector) this.scrollDdHighlightedIntoView(scrollContainerSelector, highlighted() + scrollIndexOffset);
      return;
    }
    if (event.key === 'Enter') { event.preventDefault(); const idx = highlighted(); if (idx >= 0 && idx < length) onEnter(idx); }
  }

  /** Scrolls the highlighted row of a hand-rolled dropdown list into view after arrow-key navigation. */
  private scrollDdHighlightedIntoView(containerSelector: string, index: number): void {
    requestAnimationFrame(() => {
      const list = this.host.nativeElement.querySelector(containerSelector);
      const el = list?.children?.[index] as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest' });
    });
  }

  /**
   * Resets every `activeDropdown()`-driven highlight index. Called from
   * `toggleDropdown` so each of those dropdowns starts at the first option.
   */
  private resetActiveDropdownHighlights(): void {
    this.dashboardDeadlinesClientHighlighted.set(0);
    this.dashboardActivityClientHighlighted.set(0);
    this.dashboardTasksClientHighlighted.set(0);
    this.dashboardInvoicesClientHighlighted.set(0);
    this.clientsCommEmailToHighlighted.set(0);
    this.clientsBulkEmailToHighlighted.set(0);
    this.addServiceManagerHighlighted.set(0);
    this.addServiceAssignClientHighlighted.set(0);
    this.acsReminderStaffHighlighted.set(0);
    this.acsReminderClientHighlighted.set(0);
    this.acsServiceFrequencyHighlighted.set(0);
    this.acsEmailWorkflowTemplateTypeHighlighted.set(0);
    this.reminderCascadeUnitHighlighted.set(0);
    this.reminderCascadeTimingHighlighted.set(0);
    this.dashboardSvcMultiHighlighted.set(0);
    this.dashboardStatusMultiHighlighted.set(0);
    this.dashboardChartClientHighlighted.set(0);
    this.dashboardChartServiceHighlighted.set(0);
    this.dashboardActivityTypeHighlighted.set(0);
    this.dashboardActivityCategoryHighlighted.set(0);
    this.dashboardActivityTimeHighlighted.set(0);
    this.dashboardTasksPriorityHighlighted.set(0);
    this.dashboardTasksViewHighlighted.set(0);
    this.dashboardInvoicesPaymentHighlighted.set(0);
    this.clientsScheduleFilterClientHighlighted.set(0);
    this.clientsScheduleFilterScheduledByHighlighted.set(0);
    this.clientsCommFilterClientHighlighted.set(0);
    this.clientsCommFilterDoneByHighlighted.set(0);
    this.clientsCommFilterPriorityHighlighted.set(0);
    this.clientsCommFilterStatusHighlighted.set(0);
    this.clientsCommFilterRecurringHighlighted.set(0);
    this.clientsCommEmailPriorityHighlighted.set(0);
    this.clientsCommSmsRecurringFreqHighlighted.set(0);
    this.clientsCommSmsAddTemplateTypeHighlighted.set(0);
    this.clientsBulkEmailClientTypeHighlighted.set(0);
    this.clientsBulkSmsRecurringFreqHighlighted.set(0);
    this.clientsBulkEmailPriorityHighlighted.set(0);
  }

  // Table Data — populated from pm-api when `pmApiBaseUrl` and practice context are set.
  allDeadlines = signal<DeadlineData[]>([]);

  assignClientsData = signal<AssignClientRow[]>([]);
  readonly dashSubmissionsSearch = signal('');

  toggleDropdown(id: string, event: Event) {
    event.stopPropagation();
    const opening = this.activeDropdown() !== id;
    this.activeDropdown.set(opening ? id : null);
    if (opening) {
      this.resetActiveDropdownHighlights();
    }
    this.isNotificationOpen.set(false); // Close notifications when opening other dropdowns
    if (opening && (id.startsWith('acsRemStaff-') || id.startsWith('acsRemClient-'))) {
      this.addServiceReminderMultiselectSearch.set('');
    }
    if (opening && id === 'acsAssignClient') {
      this.addServiceAssignClientMultiselectSearch.set('');
    }
    if (opening && id === this.acsServiceManagerDropdownId()) {
      this.addServiceManagerSearch.set('');
    }
    if (opening && id === 'clientsScheduleFilterClient') {
      this.clientsScheduleFilterClientSearch.set('');
    }
    if (opening && id === 'dashboardActivityClient') {
      this.dashboardActivityClientSearch.set('');
      void this.loadClientsForBookMeetingPicker();
    }
    if (id.startsWith('reminderClients-') && this.activeDropdown() === id) {
      void this.ensureDirectoryClientsLoadedForServiceReminders();
    }
    if (id.startsWith('acsRemClient-') && this.activeDropdown() === id) {
      void this.ensureDirectoryClientsLoadedForServiceReminders();
    }
    if (id === 'acsAssignClient' && this.activeDropdown() === id) {
      void this.ensureDirectoryClientsLoadedForServiceReminders();
    }
    if (id === 'tasksClients' && this.activeDropdown() === 'tasksClients' && this.userContext.practiceContextReady()) {
      void this.loadClientsForBookMeetingPicker();
    }
    if (!opening && id === 'clientsCommEmailToMs') {
      // Closing the To dropdown (after selecting a recipient) shouldn't leave whatever the user
      // typed to find them still sitting in the trigger input — the selection chips/label already
      // show who's selected, so the leftover search text just looks like it "didn't clear".
      this.clientsCommEmailToSearchQuery.set('');
    }
  }

  private resolveHeaderAnnouncementsViewerAudience(parentUserId: number, actingUserId: number): string {
    return actingUserId === parentUserId ? 'All Staff' : 'Staff';
  }

  refreshHeaderAnnouncementsData(forcePreview = false): void {
    const parentUserId = this.userContext.firmParentUserIdForApi();
    const actingUserId = this.userContext.actingUserMasterIdForApi();
    if (parentUserId <= 0 || actingUserId <= 0) {
      return;
    }

    const viewerAudience = this.resolveHeaderAnnouncementsViewerAudience(parentUserId, actingUserId);
    this.headerAnnouncementsSub?.unsubscribe();

    const shouldLoadPreview = forcePreview || this.isNotificationOpen();
    if (shouldLoadPreview) {
      this.headerAnnouncementsLoading.set(true);
    }

    this.headerAnnouncementsSub = this.pmAnnouncementsApi
      .list({
        parentUserId,
        actingUserId,
        viewerAudience,
        pageNumber: 1,
        pageSize: shouldLoadPreview ? 5 : 1,
      })
      .subscribe({
        next: (result) => {
          const pinned = result.pinned ?? [];
          const items = result.items ?? [];
          const combined = [...pinned, ...items];

          const unreadFromApi = result.unreadCount;
          const unreadFallback = combined.filter((a) => !a.isRead).length;
          this.headerAnnouncementsUnreadCount.set(
            unreadFromApi != null ? unreadFromApi : unreadFallback
          );

          if (shouldLoadPreview) {
            this.headerAnnouncementsPreview.set(combined.slice(0, 5));
            this.headerAnnouncementsLoading.set(false);
          }
        },
        error: () => {
          if (shouldLoadPreview) {
            this.headerAnnouncementsLoading.set(false);
          }
        },
      });
  }

  toggleQuickAdd(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.isQuickAddOpen.update(v => !v);
    if (this.isQuickAddOpen()) {
      this.quickAddQuery.set('');
      const vis = this.quickAddVisibleIndices();
      this.activeQaIndex.set(vis[0] ?? 0); // Reset selection when opening
      queueMicrotask(() => this.qaSearchInput?.nativeElement?.focus());
    }
    this.activeDropdown.set(null);
    this.isNotificationOpen.set(false);
  }

  goToSettingsPage(): void {
    this.isProfileMenuOpen.set(false);
    void this.router.navigateByUrl('/settings');
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.isAppLauncherOpen()) {
      if (event.key === 'Escape') {
        this.isAppLauncherOpen.set(false);
      }
      return;
    }
    if (this.docHubStore.documentHubFilesFilterModalOpen() && event.key === 'Escape') {
      this.documentHubPage?.closeDocumentHubFilesFilterModal();
      event.preventDefault();
      return;
    }
    if (this.isQuickAddOpen()) {
      const inInput = event.target instanceof HTMLInputElement;

      if (event.key === 'Escape') {
        this.isQuickAddOpen.set(false);
        return;
      }

      const visible = this.quickAddVisibleIndices();
      if (visible.length === 0) {
        return;
      }

      const activeIndex = this.activeQaIndex();
      let pos = visible.indexOf(activeIndex);
      if (pos < 0) {
        pos = 0;
        this.activeQaIndex.set(visible[0]!);
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        pos = Math.max(0, pos - 2);
        this.activeQaIndex.set(visible[pos]!);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        pos = Math.min(visible.length - 1, pos + 2);
        this.activeQaIndex.set(visible[pos]!);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        pos = Math.max(0, pos - 1);
        this.activeQaIndex.set(visible[pos]!);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        pos = Math.min(visible.length - 1, pos + 1);
        this.activeQaIndex.set(visible[pos]!);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        this.executeQuickAction(this.activeQaIndex());
      } else {
        if (inInput) {
          // Let typing in the search box behave normally.
          return;
        }
        if (this.quickAddQuery().trim()) {
          return;
        }
        const key = event.key.toLowerCase();
        const idx = this.quickAddItems.findIndex((x) => x.shortcut === key);
        if (idx >= 0) {
          event.preventDefault();
          this.activeQaIndex.set(idx);
          this.executeQuickAction(idx);
        }
      }
    }

    if (event.key === 'Escape' && this.clientsCommunicationFilterPanelOpen()) {
      event.preventDefault();
      this.closeClientsCommunicationFilterPanel();
      return;
    }

    if (event.key === 'Escape' && this.clientsCommunicationEmailDetailRowId()) {
      event.preventDefault();
      this.closeClientsCommunicationEmailDetail();
      return;
    }

    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault();
      this.toggleQuickAdd();
    }
  }

  executeQuickAction(index: number) {
    this.isQuickAddOpen.set(false);
    this.quickAddQuery.set('');

    // Encode the action as a ?qa= URL param and navigate.
    // updateActiveFromUrl reads the param on NavigationEnd and opens the modal
    // synchronously — same call as activeItem.set(), so Angular renders page + modal together.
    switch (index) {
      case 0:
        void this.router.navigate(['/clients'], { queryParams: { qa: 'add-client' } });
        return;
      case 1:
        void this.router.navigate(['/tasks'], { queryParams: { qa: 'new-task' } });
        return;
      case 2:
        void this.router.navigate(['/deadlines'], { queryParams: { qa: 'new-deadline' } });
        return;
      case 3:
        void this.router.navigate(['/clients'], { queryParams: { qa: 'compose-email' } });
        return;
      case 4:
        void this.router.navigate(['/practice'], { queryParams: { tab: 'Finance', qa: 'new-invoice' } });
        return;
      case 5:
        void this.router.navigate(['/practice'], { queryParams: { tab: 'Record Time', qa: 'log-time' } });
        return;
      case 6:
        void this.router.navigate(['/clients'], { queryParams: { qa: 'schedule-meeting' } });
        return;
      case 7:
        void this.router.navigateByUrl('/reports');
        return;
      default:
        return;
    }
  }

  /** True when click is on an open dropdown / multiselect surface (don’t clear activeDropdown). */
  private isClickInsideActiveDropdownSurface(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }
    if (
      target.closest('.clients-email-to-ms') ||
      target.closest('.custom-dropdown.active') ||
      target.closest('.custom-multiselect.active') ||
      target.closest('.svc-row-menu-wrap') ||
      target.closest('.svc-row-menu') ||
      target.closest('.crm-comm-dd-menu') ||
      (this.clientsAddActiveDropdown() != null && target.closest('.crm-comm-dd'))
    ) {
      return true;
    }
    // Assign → Select Client: changing client-type pills should not close the open picker.
    if (this.activeDropdown() === this.acsAssignClientDropdownId()) {
      return !!target.closest('.acs-assign-client-types-box');
    }
    return false;
  }

  /** Add Client / Companies House modals and their fixed custom-select menus. */
  private isClickInsideAddClientModalSurface(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }
    return !!(
      target.closest('.clients-add-form-modal') ||
      target.closest('.clients-company-house-modal') ||
      target.closest('.crm-comm-dd-menu') ||
      target.closest('.pm-form-custom-select')
    );
  }

  /** Capture phase so clicks inside modals that call stopPropagation still close open dropdowns. */
  private readonly onAnnouncementsChanged = (): void => {
    this.refreshHeaderAnnouncementsData(this.isNotificationOpen());
  };

  private readonly onDocumentClickCapture = (event: Event) => {
    const docClickTarget = event.target instanceof Element ? event.target : null;
    const clickInsideNotifications = !!docClickTarget?.closest('.notification-container');
    if (!this.isClickInsideActiveDropdownSurface(event.target)) {
      this.activeDropdown.set(null);
    }
    if (!clickInsideNotifications) {
      this.isNotificationOpen.set(false);
    }
    if (!docClickTarget?.closest('.qa-modal')) {
      this.isQuickAddOpen.set(false);
      this.quickAddQuery.set('');
    }
    if (!docClickTarget?.closest('.mobile-nav-wrap')) {
      this.isMobileNavOpen.set(false);
    }
    this.isAppLauncherOpen.set(false);
    this.isProfileMenuOpen.set(false);
    // A click inside an open shared popover (Filter, Views, Columns, Perform check ...) is the popover's own business: it
    // closes itself on an outside press. Without this, picking a date in a Filter panel closed the panel under it.
    const insidePopover = !!docClickTarget?.closest('pm-popover');
    if (!insidePopover && !docClickTarget?.closest('.clients-filter-wrap')) {
      this.clientsFilterPanelOpen.set(false);
    }
    if (!insidePopover && !docClickTarget?.closest('.clients-users-filter-wrap')) {
      this.clientsUsersFilterPanelOpen.set(false);
    }
    if (!docClickTarget?.closest('.services-filter-wrap')) {
      this.servicesFilterPopoverOpen.set(false);
      this.closeServicesFilterDropdowns();
    }
    if (!insidePopover && !docClickTarget?.closest('.clients-views-wrap')) {
      this.clientsViewsPanelOpen.set(false);
    }
    if (!insidePopover && !docClickTarget?.closest('.clients-columns-wrap')) {
      this.clientsColumnPanelOpen.set(false);
    }
    if (!docClickTarget?.closest('.clients-users-action-wrap')) {
      this.closeClientsUsersActionMenu();
    }
    // Add Client modal: only close dropdowns on outside click; the modal itself only closes via the × button.
    const addClientModalOpen = this.clientsAddFormOpen();
    const clickInsideAddClientModal =
      addClientModalOpen && this.isClickInsideAddClientModalSurface(event.target);
    if (!clickInsideAddClientModal && addClientModalOpen && this.clientsAddActiveDropdown() != null) {
      this.closeClientsAddActiveDropdown();
    }
    this.clientsSortDropdownOpen.set(false);
    if (!insidePopover) this.amlPerformCheckMenuOpen.set(false);
    if (!insidePopover && !docClickTarget?.closest('.clients-aml-filter-wrap')) {
      this.amlComplianceFilterMenuOpen.set(false);
    }
    this.announcementModalDropdownKey.set(null);
    this.announcementsFilterDropdownKey.set(null);
    if (!(event.target instanceof Element) || !event.target.closest('.dash-quick-links')) {
      this.dashboardQuickLinksOpen.set(false);
    }
    if (!(event.target instanceof Element) || !event.target.closest('.dash-export-menu')) {
      this.dashboardExportMenuOpen.set(false);
    }
    if (!(event.target instanceof Element) || !event.target.closest('.assign-export-menu')) {
      this.assignExportMenuOpen.set(false);
    }
    if (!(event.target instanceof Element) || !event.target.closest('.custom-fields-filter-anchor')) {
      this.customFieldFilterPopoverOpen.set(false);
    }
    if (!(event.target instanceof Element) || !event.target.closest('.cf-row-actions')) {
      this.customFieldRowActionsOpenId.set(null);
    }
    if (!(event.target instanceof Element) || !event.target.closest('.notifications-role-dropdown')) {
      this.closeOpenNotificationRoleDropdowns();
    }
  };

  private closeOpenNotificationRoleDropdowns(): void {
    const openEls = document.querySelectorAll<HTMLDetailsElement>('.notifications-role-dropdown[open]');
    openEls.forEach((el) => {
      el.open = false;
    });
  }

  stopProp(event: Event) {
    event.stopPropagation();
  }

  /**
   * For dialog-root keydown.space handlers: stops the key reaching the click-outside-to-dismiss
   * backdrop, but only blocks the browser's default (page scroll / button activation) when the
   * event didn't originate from a text field — otherwise it would swallow spacebar while typing.
   */
  stopPropAllowSpaceInFields(event: Event) {
    event.stopPropagation();
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
      return;
    }
    event.preventDefault();
  }

  onModalContentClick(event: Event) {
    event.stopPropagation();
    this.activeDropdown.set(null);
  }

  isHeaderGlobalSearchModalOpen(): boolean {
    return this.isSearchExpanded() && this.headerGlobalSearchQuery().trim().length > 0;
  }

  @HostListener('window:resize')
  onWindowResizeForGlobalSearch(): void {
    if (this.isHeaderGlobalSearchModalOpen()) {
      this.updateHeaderGlobalSearchModalPlacement();
    }
    this.onClientsToolbarPopoverViewportChange();
  }

  private readonly onClientsToolbarPopoverViewportChange = (): void => {
    if (
      !this.clientsFilterPanelOpen() &&
      !this.clientsUsersFilterPanelOpen() &&
      !this.clientsColumnPanelOpen() &&
      !this.clientsViewsPanelOpen() &&
      !this.clientsCommunicationFilterPanelOpen()
    ) {
      return;
    }
    this.repositionOpenClientsToolbarPopovers();
  };

  scheduleClientsToolbarPopoverReposition(): void {
    if (typeof window === 'undefined') {
      return;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.repositionOpenClientsToolbarPopovers());
    });
  }

  repositionOpenClientsToolbarPopovers(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const configs: Array<{
      open: () => boolean;
      wrapSelector: string;
      panelSelector: string;
      widthRem: number;
      maxHeightRem: number;
      /** The button it hangs under when that isn't inside the wrap (a shared-toolbar button). */
      anchor?: () => HTMLElement | null;
    }> = [
      {
        open: () => this.clientsCommunicationFilterPanelOpen(),
        wrapSelector: '.clients-communication-filter-wrap',
        panelSelector: '.clients-communication-filter-panel',
        anchor: () => this.clientsSectionAnchor(),
        widthRem: 25,
        maxHeightRem: 40
      }
    ];

    for (const cfg of configs) {
      if (!cfg.open()) {
        continue;
      }
      const wrap = document.querySelector(cfg.wrapSelector);
      if (!wrap) {
        continue;
      }
      const trigger = cfg.anchor?.() ?? wrap.querySelector('button');
      const panel = wrap.querySelector<HTMLElement>(cfg.panelSelector);
      if (trigger && panel) {
        this.positionClientsToolbarPopover(trigger, panel, cfg.widthRem, cfg.maxHeightRem);
      }
    }
  }

  /**
   * Anchors clients toolbar popovers (filter / columns / views) in viewport
   * coordinates so they escape `.clients-screen { overflow: hidden }` clipping
   * and stay aligned when the window is resized, zoomed, or scrolled.
   */
  private positionClientsToolbarPopover(
    trigger: HTMLElement,
    panel: HTMLElement,
    widthRem: number,
    maxHeightRem: number
  ): void {
    if (typeof window === 'undefined') {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportH = viewport?.height ?? window.innerHeight;
    const viewportW = viewport?.width ?? window.innerWidth;
    const offsetTop = viewport?.offsetTop ?? 0;
    const offsetLeft = viewport?.offsetLeft ?? 0;
    // Clear of the toolbar card (its 8px padding) with room to breathe, and off the screen edges.
    const gap = 14;
    const margin = 12;
    const widthPx = Math.min(widthRem * 16, viewportW - margin * 2);
    const defaultMaxHeightPx = Math.min(viewportH * 0.72, maxHeightRem * 16);

    let left = rect.right - widthPx;
    left = Math.max(offsetLeft + margin, Math.min(left, offsetLeft + viewportW - margin - widthPx));

    const spaceBelow = offsetTop + viewportH - rect.bottom - gap;
    const spaceAbove = rect.top - offsetTop - gap;
    let top: number;
    let maxHeightPx: number;

    if (spaceBelow >= 160 || spaceBelow >= spaceAbove) {
      top = rect.bottom + gap;
      maxHeightPx = Math.min(defaultMaxHeightPx, spaceBelow - margin);
    } else {
      maxHeightPx = Math.min(defaultMaxHeightPx, spaceAbove - margin);
      top = Math.max(offsetTop + margin, rect.top - gap - maxHeightPx);
    }

    panel.style.position = 'fixed';
    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = `${Math.round(top)}px`;
    panel.style.right = 'auto';
    panel.style.width = `${Math.round(widthPx)}px`;
    panel.style.maxHeight = `${Math.max(120, Math.round(maxHeightPx))}px`;
  }

  searchModuleKey(item: PmGlobalSearchItemApi): string {
    const moduleType = (item.moduleType || '').trim().toLowerCase();
    if (moduleType === 'actions') return 'actions';
    if (moduleType === 'pages') return 'pages';
    if (moduleType === 'clients' || moduleType === 'client') return 'clients';
    if (moduleType === 'teams' || moduleType === 'team') return 'teams';
    if (moduleType === 'tasks' || moduleType === 'task') return 'tasks';
    if (moduleType === 'notes' || moduleType === 'note') return 'notes';
    return 'other';
  }

  updateHeaderGlobalSearchModalPlacement(): void {
    const wrapper = this.headerSearchWrapper?.nativeElement;
    if (!wrapper) {
      return;
    }

    const rect = wrapper.getBoundingClientRect();
    const left = Math.max(8, Math.round(rect.left));
    const width = Math.max(280, Math.round(rect.width));
    this.headerGlobalSearchModalLeftPx.set(left);
    this.headerGlobalSearchModalWidthPx.set(width);
  }

  onHeaderGlobalSearchResultClick(item: PmGlobalSearchItemApi): void {
    const moduleType = (item.moduleType || '').trim().toLowerCase();
    if (moduleType === 'pages' || moduleType === 'actions') {
      // The id is the route, query string included (an action's carries the popup to open).
      void this.router.navigateByUrl((item.id || '/dashboard').trim());
      this.isSearchExpanded.set(false);
      return;
    }
    if (moduleType === 'clients' || moduleType === 'client') {
      const clientId = (item.clientUniversalId || item.id || '').trim();
      if (clientId) {
        void this.router.navigateByUrl(`/clients/${encodeURIComponent(clientId)}`);
      } else {
        void this.router.navigateByUrl('/clients');
      }
    } else if (moduleType === 'tasks' || moduleType === 'task') {
      const taskId = (item.id || '').trim();
      if (taskId) {
        void this.router.navigateByUrl(`/tasks/${encodeURIComponent(taskId)}`);
      } else {
        void this.router.navigateByUrl('/tasks');
      }
    } else if (moduleType === 'teams' || moduleType === 'team') {
      void this.router.navigateByUrl('/practice');
    } else if (moduleType === 'notes' || moduleType === 'note') {
      const clientId = (item.clientUniversalId || '').trim();
      if (clientId) {
        void this.router.navigateByUrl(`/clients/${encodeURIComponent(clientId)}`);
      } else {
        void this.router.navigateByUrl('/clients');
      }
    } else {
      void this.router.navigateByUrl('/dashboard');
    }

    this.isSearchExpanded.set(false);
  }

  setClientsPageTab(tab: 'clients' | 'users') {
    if (tab !== this.clientsPageTab()) {
      this.clientsPageTabSwitching.set(true);
      setTimeout(() => this.clientsPageTabSwitching.set(false), 250);
    }
    const previousTab = this.clientsPageTab();
    if (this.clientsPageTab() === 'clients' && tab !== 'clients') {
      this.persistClientsListUiToStorage();
      this.clientsSearchQueryWritesSuspended = true;
    } else if (tab === 'clients') {
      this.clientsSearchQueryWritesSuspended = false;
      this.clientsUsersSearchWritesSuspended = true;
    }
    if (tab === 'users') {
      this.clientsUsersSearchWritesSuspended = true;
    }
    this.clientsPageTab.set(tab);
    if (tab === 'clients') {
      this.scheduleDirectoryClientsReload(false);
      queueMicrotask(() => {
        this.clientsUsersSearchWritesSuspended = false;
      });
    } else if (tab === 'users') {
      this.clientsUsersSearchQueryForApi.set(this.clientsUsersSearchQuery().trim());
      this.lastClientsUsersHydrationLoadKey.set('');
      void this.ensureClientUsersFirmTotalCount();
      queueMicrotask(() => {
        this.clientsUsersSearchWritesSuspended = false;
      });
    }
    if (previousTab === 'users' && tab !== 'users') {
      this.clearClientsUsersSelection();
      this.closeClientsUsersActionMenu();
    }
  }

  /** Formats `CompanyContactMaster.DateOfCreation` from the directory API. */
  clientUserDateCreatedLabel(row: ClientUserListRow): string {
    const raw = row.dateCreated?.trim();
    if (!raw) {
      return '—';
    }
    const result = formatAppDate(raw);
    return result || '—';
  }

  clientUserLastLoginLabel(row: ClientUserListRow): string {
    const raw = row.lastLogin?.trim();
    if (!raw) {
      return '—';
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) {
      return '—';
    }
    const datePart = formatAppDate(d);
    // Midnight check and the displayed clock both read the saved timezone, so they can't disagree
    // with each other — datePart itself stays local (formatAppDate's contract), so in the narrow
    // case of an instant that's exactly midnight in the saved zone but a different local calendar
    // day, the date shown can lag by one day. Acceptable: this field is a fallback display for a
    // likely-unset/defaulted time, not a precise audit timestamp.
    const { hour: h, minute: m, second: s } = dateFieldsInAppZone(d);
    if (h === 0 && m === 0 && s === 0) {
      return datePart;
    }
    const timePart = d
      .toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: appTimeZoneOption() })
      .toUpperCase();
    return `${datePart} - ${timePart}`;
  }

  resetClientsUsersImportWizardState() {
    this.clientsUsersImportStep.set('upload');
    this.clientsUsersImportFileName.set('');
    this.clientsUsersImportColumnsDetected.set(0);
    this.clientsUsersImportPreviewRows.set([]);
    this.clientsUsersImportSelectedIds.set(new Set());
    this.clientsUsersImportSavePending.set(false);
    this.clientsUsersImportErrors.set([]);
    this.clientsUsersImportStaged.set(false);
    this.clientsUsersImportDraftRows.set([]);
    this.clientsUsersImportPracticeEmails.set(new Set());
    this.clientsUsersImportEmailsLoaded.set(false);
  }

  async ensureClientsUsersImportPracticeEmails(): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      return;
    }
    if (this.clientsUsersImportEmailsLoaded()) {
      return;
    }
    const parent = this.effectiveDirectoryParentUserId();
    try {
      const response = await firstValueFrom(this.pmDirectoryApi.getUserImportPracticeEmails(parent));
      const emails = new Set(
        (response.emails ?? []).map((e) => e.trim().toLowerCase()).filter((e) => e.length > 0)
      );
      this.clientsUsersImportPracticeEmails.set(emails);
      this.clientsUsersImportEmailsLoaded.set(true);
    } catch (e) {
      this.notifyError(e);
    }
  }

  onClientsSearchInput(event: Event): void {
    const el = event.target as HTMLInputElement | null;
    if (!el) {
      return;
    }
    this.setClientsSearchQuery(el.value);
  }

  setClientsSearchQuery(value: string) {
    if (this.clientsSearchQueryWritesSuspended) {
      return;
    }
    const next = value ?? '';
    if (next === this.clientsSearchQuery()) {
      return;
    }
    this.clientsSearchQuery.set(next);
    this.scheduleDirectoryClientsReload(true);
  }

  toggleClientsSortDropdown(event: Event) {
    event.stopPropagation();
    this.clientsFilterPanelOpen.set(false);
    this.clientsViewsPanelOpen.set(false);
    this.clientsColumnPanelOpen.set(false);
    let willOpen = false;
    this.clientsSortDropdownOpen.update((v) => {
      willOpen = !v;
      return !v;
    });
    if (willOpen) {
      this.clientsSortHighlighted.set(0);
    }
  }

  selectClientsListSort(id: string, event: Event) {
    event.stopPropagation();
    const opt = this.clientsSortOptions.find((o) => o.id === id);
    if (opt) {
      this.clientsSortField.set(opt.field);
      this.clientsSortDir.set(opt.dir);
      // Sorting is client-side over the fully-loaded list; just jump back to the first page.
      this.clientsDirectoryPageNumber.set(1);
    }
    this.clientsSortDropdownOpen.set(false);
  }

  /** "Sort clients" dropdown - single-select: pick and close. */
  onClientsSortKeydown(event: KeyboardEvent): void {
    const opts = this.clientsSortOptions;
    this.navigateDdList(event, this.clientsSortHighlighted, opts.length, (i) => {
      this.selectClientsListSort(opts[i].id, event);
    }, '.clients-sort-menu');
  }

  /** Maps catalog column key to sort field (headers); null = not sortable from header. */
  clientsCatalogKeyToSortField(key: string): 'name' | 'grade' | 'revenue' | 'aml' | null {
    switch (key) {
      case 'client':
        return 'name';
      case 'grade':
        return 'grade';
      case 'revenue':
        return 'revenue';
      case 'aml':
        return 'aml';
      default:
        return null;
    }
  }

  clientsSortMenuOptionActive(opt: { field: string; dir: string }): boolean {
    return this.clientsSortField() === opt.field && this.clientsSortDir() === opt.dir;
  }

  onClientsColumnHeaderSort(catalogKey: string, event: Event) {
    event.stopPropagation();
    const field = this.clientsCatalogKeyToSortField(catalogKey);
    if (!field) {
      return;
    }
    if (this.clientsSortField() === field) {
      this.clientsSortDir.update((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      this.clientsSortField.set(field);
      this.clientsSortDir.set('asc');
    }
    // Client-side sort re-orders the whole list; return to the first page so the top is visible.
    this.clientsDirectoryPageNumber.set(1);
  }

  clientsThSortCaretUpOn(catalogKey: string): boolean {
    const f = this.clientsCatalogKeyToSortField(catalogKey);
    return f !== null && this.clientsSortField() === f && this.clientsSortDir() === 'asc';
  }

  clientsThSortCaretDownOn(catalogKey: string): boolean {
    const f = this.clientsCatalogKeyToSortField(catalogKey);
    return f !== null && this.clientsSortField() === f && this.clientsSortDir() === 'desc';
  }

  private normalizeClientsColumnOrder(input: readonly string[]): string[] {
    const known = CLIENTS_CATALOG_FLAT_ORDERED.map((c) => c.key);
    const knownSet = new Set(known);
    const out: string[] = [];
    for (const key of input) {
      if (knownSet.has(key) && !out.includes(key)) {
        out.push(key);
      }
    }
    for (const key of known) {
      if (!out.includes(key)) {
        out.push(key);
      }
    }
    return out;
  }

  clientsCatalogHeaderLabel(col: ClientsCatalogColumnDef): string {
    return col.key === 'deadlines' ? this.getCustomLabel('Deadlines') : col.label;
  }

  openClientsAddFormModal(event?: Event) {
    event?.stopPropagation?.();
    this.clientsAddFormMode.set('create');
    this.clientsAddEditingEntityId.set(null);
    this.clientsAddClientTypeRestrictToIndividual.set(false);
    const storedDraft = this.readClientsAddDraftFromStorage();
    const baseDraft = sanitizeClientsAddValidatedFields(storedDraft?.form ?? emptyClientsAddDraft());
    this.clientsAddDraft.set(baseDraft);
    this.clientsAddPopupExtras.set({});
    this.clientsAddDraftFromStorage.set(!!storedDraft);
    this.clientsAddCustomFieldValues.set(storedDraft?.customFieldValues ?? {});
    this.applyClientsAddCreateDefaults(!storedDraft);
    this.clientsAddClientIdUserEdited.set(!!storedDraft?.form?.clientId?.trim());
    if (this.userContext.practiceContextReady()) {
      void this.loadDirectoryClientsPage(this.clientsDirectoryPageNumber() || 1);
      void this.loadClientsAddAllClientRefs();
    }
    this.clientsAddPanelsOpen.set({
      clientDetails: true,
      contactPerson: true,
      address: true,
      servicesStaff: false,
      dueDiligence: true,
      additionalFields: false,
      portalAccess: false,
      directors: false
    });
    this.clientsAddDirectors.set([]);
    // The popup's directors come from a seed that only re-reads clientsAddDirectors when this version
    // moves; without the bump a new popup got the last edited client's directors.
    this.clientsAddDirectorsSeedVersion.update((v) => v + 1);
    this.clientsAddChOfficers.set([]);
    this.clientsAddChOfficersSelected.set(new Set());
    this.clientsAddChOfficersSa.set(new Set());
    this.clientsAddDirectorFormOpen.set(false);
    this.clientsAddAssignDirectorSelected.set([]);
    void this.loadClientsAddAssignDirectorOptions();
    this.clientsAddFormOpen.set(true);
    void this.ensureWorkspaceProfileFromTeam();
    this.clientsAddContactModalOpen.set(false);
    this.clientsAddContactDraft.set(emptyClientsAddContactDraft());
    this.clientsAddContactOptions.set([]);
    this.clientsAddPrimaryContactOptionsRaw.set([]);
    this.clientsAddKeyContactIds.set([]);
    this.clientsAddContactContext.set('primary');
    this.clientsAddPortalUserOptions.set([]);
    this.clientsAddPortalContactOptions.set([]);
    this.clientsAddPendingPortalUserDrafts.set(new Map());
    this.clientsAddClientIdDuplicate.set(false);
    this.clientsAddRegNoDuplicateError.set(null);
    if (this.clientsAddRegNoCheckTimer !== null) {
      clearTimeout(this.clientsAddRegNoCheckTimer);
      this.clientsAddRegNoCheckTimer = null;
    }
    this.clientsCompanyHouseModalOpen.set(false);
    this.clientsCompanyHouseSearch.set('');
    this.clientsCompanyHouseResults.set([]);
    this.clientsCompanyHouseSearchError.set(null);
    this.clientsCompanyHouseApplyingId.set(null);
    this.clientsAddDraftSavePending.set(false);
    void this.loadTeamAndPermissions();
    this.primeClientsAddFormServicesCatalog();
    void this.loadClientsAddContactOptionsFromPractice();
    void this.loadClientsAddPrimaryContactOptions();
    void this.loadClientsAddPortalUsers();
    this.loadClientsAddCustomFields();
    this.loadClientsAddMasterDataFromClientStore();
    if (this.clientsAddVatSchemes().length > 0) {
      this.applyClientsAddMasterDataDefaults();
    }
  }

  openClientsAddSelectionModal(event?: Event): void {
    event?.stopPropagation?.();
    this.clientsAddSelectionOpen.set(true);
  }

  loadClientsAddMasterDataFromClientStore(): void {
    if (!this.userContext.practiceContextReady()) {
      return;
    }
    if (this.clientsAddMasterDataLoaded || this.clientsAddMasterDataLoading()) {
      return;
    }

    this.clientsAddMasterDataLoading.set(true);
    forkJoin({
      clientTypes: this.pmDirectoryApi.getClientTypes().pipe(catchError(() => of([] as DirectoryClientTypeApi[]))),
      countries: this.pmDirectoryApi.getMasterDataCountries().pipe(catchError(() => of([] as DirectoryMasterDataItemApi[]))),
      currencies: this.pmDirectoryApi.getMasterDataCurrencies().pipe(catchError(() => of([] as DirectoryMasterDataItemApi[]))),
      vatSchemes: this.pmDirectoryApi.getMasterDataVatSchemes().pipe(catchError(() => of([] as DirectoryMasterDataItemApi[]))),
      vatSubmitTypes: this.pmDirectoryApi.getMasterDataVatSubmitTypes().pipe(catchError(() => of([] as DirectoryMasterDataItemApi[])))
    })
      .pipe(finalize(() => this.clientsAddMasterDataLoading.set(false)))
      .subscribe(({ clientTypes, countries, currencies, vatSchemes, vatSubmitTypes }) => {
        const nextClientTypes = this.normalizeClientsAddClientTypes(clientTypes);
        const nextCountries = this.normalizeClientsAddMasterDataRows(countries);
        const nextCurrencies = this.normalizeClientsAddMasterDataRows(currencies);
        const nextVatSchemes = this.normalizeClientsAddMasterDataRows(vatSchemes);
        const nextVatSubmitTypes = this.normalizeClientsAddMasterDataRows(vatSubmitTypes);

        this.clientsAddClientTypes.set(nextClientTypes);
        this.clientsAddCountries.set(nextCountries);
        this.clientsAddCurrencies.set(nextCurrencies);
        this.clientsAddVatSchemes.set(nextVatSchemes);
        this.clientsAddVatSubmitTypes.set(nextVatSubmitTypes);
        this.clientsAddMasterDataLoaded = [nextClientTypes, nextCountries, nextCurrencies, nextVatSchemes, nextVatSubmitTypes].every(
          (rows) => rows.length > 0
        );
        this.applyClientsAddMasterDataDefaults();
      });
  }

  ensureClientsAddMasterDataLoaded(): Promise<void> {
    if (!this.userContext.practiceContextReady() || this.clientsAddMasterDataLoaded) {
      return Promise.resolve();
    }
    this.loadClientsAddMasterDataFromClientStore();
    return new Promise((resolve) => {
      const wait = (): void => {
        if (this.clientsAddMasterDataLoaded || !this.clientsAddMasterDataLoading()) {
          resolve();
          return;
        }
        window.setTimeout(wait, 50);
      };
      wait();
    });
  }

  private normalizeClientsAddClientTypes(rows: readonly DirectoryClientTypeApi[]): DirectoryClientTypeApi[] {
    const seen = new Set<string>();
    return rows
      .map((row) => ({
        id: Math.trunc(Number(row.id)),
        name: (row.name ?? '').trim(),
        description: (row.description ?? '').trim() || null
      }))
      .filter((row) => {
        const key = row.name.toLowerCase();
        if (!Number.isFinite(row.id) || row.id <= 0 || !key || seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
  }

  clientsAddClientTypeOptions(): readonly string[] {
    const apiTypes = this.clientsAddClientTypes()
      .map((row) => (row.name ?? '').trim())
      .filter((name) => name.length > 0);
    const fullTypes = apiTypes.length > 0 ? apiTypes : [...this.clientsFilterTypeOptions];
    const baseTypes = this.clientsAddClientTypeRestrictToIndividual()
      ? fullTypes.filter((name) => ['sole trader', 'individual'].includes(name.toLowerCase()))
      : fullTypes;
    const current = this.clientsAddDraft().companyType.trim();
    if (current && !baseTypes.some((name) => name.toLowerCase() === current.toLowerCase())) {
      return [current, ...baseTypes];
    }
    return baseTypes;
  }

  private normalizeClientsAddMasterDataRows(rows: readonly DirectoryMasterDataItemApi[]): DirectoryMasterDataItemApi[] {
    const seen = new Set<number>();
    return rows
      .map((row) => ({
        id: Math.trunc(Number(row.id)),
        name: (row.name ?? '').trim(),
        code: (row.code ?? '').trim() || null,
        iso2: (row.iso2 ?? '').trim() || null,
        phoneCode: row.phoneCode != null && Number.isFinite(Number(row.phoneCode)) ? Math.trunc(Number(row.phoneCode)) : null,
        symbol: (row.symbol ?? '').trim() || null
      }))
      .filter((row) => {
        if (!Number.isFinite(row.id) || row.id <= 0 || !row.name || seen.has(row.id)) {
          return false;
        }
        seen.add(row.id);
        return true;
      });
  }

  /** No client type is pre-selected — the "Select" placeholder stays until the user picks one. */
  private resolveClientsAddDefaultCompanyType(): string {
    return '';
  }

  applyClientsAddCreateDefaults(assignFreshClientId: boolean): void {
    if (assignFreshClientId) {
      this.clientsAddClientIdUserEdited.set(false);
    }
    const defaultCompanyType = this.resolveClientsAddDefaultCompanyType();
    this.clientsAddDraft.update((d) => {
      const next = { ...d };
      if (assignFreshClientId || !next.companyType.trim()) {
        next.companyType = defaultCompanyType;
      } else {
        const canonical = canonicalClientTypeLabel(next.companyType, this.clientsAddClientTypeOptions());
        next.companyType = canonical || defaultCompanyType;
      }
      if (!next.yearEndDate.trim()) {
        next.yearEndDate = defaultClientsAddYearEndIsoDate();
      }
      if (next.countryId == null || next.countryId <= 0) {
        next.countryId = 222;
        next.country = 'United Kingdom';
      }
      if (next.vatSchemeId == null || next.vatSchemeId <= 0) {
        next.vatSchemeId = CLIENT_LEGACY_NON_VAT_REGISTERED_SCHEME_ID;
        next.vatScheme = 'Non-VAT Registered';
      }
      return next;
    });
    if (assignFreshClientId && !this.clientsAddDraft().clientId.trim()) {
      this.fetchClientsAddNextClientCode();
    }
    this.syncClientsAddClientIdDuplicate(this.clientsAddDraft().clientId);
  }

  /** Backend-generated Client ID suggestion (Client Store's own per-user sequence, not a local guess). */
  private fetchClientsAddNextClientCode(): void {
    this.pmDirectoryApi.getNextClientCode().subscribe({
      next: (res) => {
        // Add Client only: Edit opens through openClientsAddFormModal() too, and this reply can land after
        // it switched to 'edit' — it then filled the edited client's empty Client ID with a new code.
        if (this.clientsAddClientIdUserEdited() || this.clientsAddFormMode() !== 'create') {
          return;
        }
        const code = (res.clientCode ?? '').trim();
        if (!code) {
          return;
        }
        this.clientsAddDraft.update((d) => (d.clientId.trim() ? d : { ...d, clientId: code }));
        if (!this.clientsAddClientIdUserEdited()) {
          this.syncClientsAddClientIdDuplicate(this.clientsAddDraft().clientId);
        }
      },
      error: () => {}
    });
  }

  /** Full, unpaged client list (all pages) used only to check Client ID uniqueness — `clientsDirectoryRows`
   *  holds one page at a time, which is what made the old check miss clients outside that page. */
  private clientsAddAllClientRefs = signal<{ companyMasterId: string; ref: string }[]>([]);

  /** Also called by the Import clients popup before parsing a file, so its Client ID check uses current Client Store data. */
  async loadClientsAddAllClientRefs(): Promise<void> {
    const parentUserId = this.effectiveDirectoryParentUserId();
    const listUserId = this.effectiveDirectoryUserIdForClientsList();
    if (parentUserId <= 0 || listUserId <= 0) {
      return;
    }
    try {
      const page = await firstValueFrom(this.pmDirectoryApi.getAllClients(parentUserId, listUserId, true));
      this.clientsAddAllClientRefs.set(
        (page.items ?? [])
          .map((row) => ({
            companyMasterId: String(row.id ?? '').trim(),
            ref: (row.companyRef ?? row.clientCode ?? '').trim()
          }))
          .filter((r) => r.ref.length > 0)
      );
    } catch {
      this.clientsAddAllClientRefs.set([]);
    }
    this.syncClientsAddClientIdDuplicate(this.clientsAddDraft().clientId);
  }

  /**
   * @param excludeCompanyMasterId When editing an existing client, pass its own `companyMasterId`
   * so its own (unchanged) reference id isn't flagged as a duplicate of itself.
   */
  collectExistingClientReferenceIds(excludeCompanyMasterId?: string): string[] {
    const excludeId = (excludeCompanyMasterId ?? '').trim();
    return this.clientsAddAllClientRefs()
      .filter((r) => !excludeId || r.companyMasterId !== excludeId)
      .map((r) => r.ref);
  }

  /** Instant local duplicate check against the full client list (loadClientsAddAllClientRefs) — no network call. */
  syncClientsAddClientIdDuplicate(clientId: string): void {
    const needle = clientId.trim().toLowerCase();
    if (!needle) {
      this.clientsAddClientIdDuplicate.set(false);
      return;
    }
    const excludeId = this.clientsAddFormMode() === 'edit' ? this.clientsAddEditCompanyMasterId() : undefined;
    const duplicate = this.collectExistingClientReferenceIds(excludeId).some((ref) => ref.toLowerCase() === needle);
    this.clientsAddClientIdDuplicate.set(duplicate);
  }

  scheduleClientsAddRegNoDuplicateCheck(regNo: string, immediate = false): void {
    if (this.clientsAddRegNoCheckTimer !== null) {
      clearTimeout(this.clientsAddRegNoCheckTimer);
      this.clientsAddRegNoCheckTimer = null;
    }
    const normalized = regNo.trim();
    if (!normalized) {
      this.clientsAddRegNoDuplicateError.set(null);
      return;
    }
    const delay = immediate ? 0 : 300;
    this.clientsAddRegNoCheckTimer = setTimeout(() => {
      this.clientsAddRegNoCheckTimer = null;
      const parentUserId = this.effectiveDirectoryParentUserId();
      if (parentUserId <= 0) return;
      this.pmDirectoryApi.checkRegistrationNumberExists(parentUserId, normalized).subscribe({
        next: (res) => {
          if (res.exists) {
            const name = res.clientName ? ` (${res.clientName})` : '';
            this.clientsAddRegNoDuplicateError.set(`Registration number '${normalized}' already exists${name}.`);
          } else {
            this.clientsAddRegNoDuplicateError.set(null);
          }
        },
        error: () => this.clientsAddRegNoDuplicateError.set(null)
      });
    }, delay);
  }

  private applyClientsAddMasterDataDefaults(): void {
    const current = this.clientsAddDraft();
    if (current.countryId == null || current.countryId <= 0) {
      const countryId =
        this.findClientsAddMasterDataIdByName(this.clientsAddCountries(), current.country) ??
        this.findClientsAddMasterDataIdByName(this.clientsAddCountries(), 'United Kingdom');
      if (countryId != null) {
        this.setClientsAddCountryId(String(countryId));
      }
    }
    const schemes = this.clientsAddVatSchemes();
    const vatSchemeIdValid =
      current.vatSchemeId != null &&
      current.vatSchemeId > 0 &&
      !!this.findClientsAddMasterDataRowById(schemes, current.vatSchemeId);
    if (!vatSchemeIdValid) {
      const vatSchemeId = this.resolveClientsAddDefaultVatSchemeId(current);
      if (vatSchemeId != null) {
        this.setClientsAddVatSchemeId(String(vatSchemeId));
      }
    }
    if (!current.yearEndDate.trim()) {
      this.clientsAddDraft.update((d) => ({ ...d, yearEndDate: defaultClientsAddYearEndIsoDate() }));
    }
    const after = this.clientsAddDraft();
    const defaultCompanyType = this.resolveClientsAddDefaultCompanyType();
    const canonical = canonicalClientTypeLabel(after.companyType, this.clientsAddClientTypeOptions());
    if (!canonical || canonical.toLowerCase() !== after.companyType.toLowerCase()) {
      this.setClientsAddField('companyType', canonical || defaultCompanyType);
    } else if (!after.companyType.trim()) {
      this.setClientsAddField('companyType', defaultCompanyType);
    }
  }

  private resolveClientsAddDefaultVatSchemeId(draft: ClientsAddFormDraft): number | null {
    const schemes = this.clientsAddVatSchemes();
    return (
      this.findClientsAddMasterDataIdByName(schemes, draft.vatScheme) ??
      this.findClientsAddMasterDataIdByName(schemes, 'Non-VAT Registered') ??
      this.findClientsAddMasterDataIdByName(schemes, 'Non VAT Registered') ??
      (this.findClientsAddMasterDataRowById(schemes, CLIENT_LEGACY_NON_VAT_REGISTERED_SCHEME_ID)
        ? CLIENT_LEGACY_NON_VAT_REGISTERED_SCHEME_ID
        : null)
    );
  }

  private findClientsAddMasterDataIdByName(rows: readonly DirectoryMasterDataItemApi[], name: string): number | null {
    const wanted = name.trim().toLowerCase();
    if (!wanted) {
      return null;
    }
    return rows.find((row) => (row.name ?? '').trim().toLowerCase() === wanted)?.id ?? null;
  }

  private clientsAddLookupId(raw: unknown): number | null {
    const n = Number(String(raw ?? '').trim());
    return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
  }

  private findClientsAddMasterDataRowById(
    rows: readonly DirectoryMasterDataItemApi[],
    id: number | null
  ): DirectoryMasterDataItemApi | undefined {
    return id == null ? undefined : rows.find((row) => row.id === id);
  }

  setClientsAddCountryId(raw: string): void {
    const countryId = this.clientsAddLookupId(raw);
    const row = this.findClientsAddMasterDataRowById(this.clientsAddCountries(), countryId);
    this.clientsAddDraft.update((d) => ({
      ...d,
      countryId,
      country: row?.name?.trim() || ''
    }));
  }

  setClientsAddVatSchemeId(raw: string): void {
    const vatSchemeId = this.clientsAddLookupId(raw);
    const row = this.findClientsAddMasterDataRowById(this.clientsAddVatSchemes(), vatSchemeId);
    const vatScheme = row?.name?.trim() || '';
    const nowNonVatRegistered = isNonVatRegisteredScheme(vatScheme);
    // Switching to Non-VAT Registered hides VAT Return from the services checklist (see
    // clientsAddFormServiceRows) — drop it from the selection too so it isn't silently saved anyway.
    const vatReturnTrackKeys = nowNonVatRegistered
      ? new Set<string>([
          ...this.catalogServices()
            .filter((s) => s.name.trim().toLowerCase() === 'vat return')
            .map((s) => s.trackKey),
          ...this.customAddedServices()
            .filter((s) => s.name.trim().toLowerCase() === 'vat return')
            .map((s) => `custom-added:${s.id ?? s.name}`)
        ])
      : null;
    this.clientsAddDraft.update((d) => ({
      ...d,
      vatSchemeId,
      vatScheme,
      ...(vatReturnTrackKeys
        ? { selectedServiceKeys: d.selectedServiceKeys.filter((tk) => !vatReturnTrackKeys.has(tk)) }
        : {}),
      ...(nowNonVatRegistered
        ? { vatRegistrationNumber: '', vatRegistrationDate: '' }
        : {})
    }));
  }

  loadClientsAddCustomFields(entityId?: number | string | null): void {
    const parentUserId = this.effectiveDirectoryParentUserId();
    if (parentUserId <= 0) {
      this.clientsAddCustomFieldDefinitions.set([]);
      this.clientsAddCustomFieldsLoading.set(false);
      return;
    }
    this.clientsAddCustomFieldsLoading.set(true);
    this.pmSettingsApi.getCustomFields(parentUserId).subscribe({
      next: (rows) => {
        const defs = rows
          .filter(
            (r) =>
              r.isActive &&
              String(r.section ?? 'Client')
                .trim()
                .toLowerCase() === 'client'
          )
          .map((r) => this.mapCustomFieldApiRow(r));
        this.clientsAddCustomFieldDefinitions.set(defs);
        this.clientsAddCustomFieldsLoading.set(false);
        const entity = entityId ?? this.clientsAddEditingEntityId();
        const entityKey =
          entity != null && String(entity).trim() !== '' ? String(entity).trim() : '';
        if (entityKey) {
          this.loadClientsAddCustomFieldValuesForEntity(entityKey);
        }
      },
      error: () => {
        this.clientsAddCustomFieldDefinitions.set([]);
        this.clientsAddCustomFieldsLoading.set(false);
      }
    });
  }

  private loadClientsAddCustomFieldValuesForEntity(entityKey: number | string): void {
    const parentUserId = this.effectiveDirectoryParentUserId();
    const key = String(entityKey ?? '').trim();
    if (parentUserId <= 0 || !key) {
      return;
    }
    this.pmSettingsApi.getClientCustomFieldValues(parentUserId, key).subscribe({
      next: (rows) => {
        const values: Record<number, string> = {};
        for (const r of rows) {
          const fieldId = Math.trunc(Number(r.fieldId));
          if (!Number.isFinite(fieldId) || fieldId <= 0) {
            continue;
          }
          values[fieldId] = (r.value ?? '').trim();
        }
        for (const f of this.clientsAddCustomFieldsForModal()) {
          const id = f.apiId != null ? Math.trunc(Number(f.apiId)) : 0;
          if (id > 0 && !(id in values)) {
            values[id] = '';
          }
        }
        this.clientsAddCustomFieldValues.set(values);
      },
      error: () => {
        /* keep draft / empty values */
      }
    });
  }

  openSettingsCustomFieldsFromAddClient(event?: Event): void {
    event?.stopPropagation?.();
    this.closeClientsAddFormModal();
    this.activeSettingItem.set('Custom Fields');
    this.activeCustomFieldTab.set('Custom Fields');
    void this.router.navigateByUrl('/settings?tab=Custom%20Fields');
  }

  private async loadClientsAddPortalUsers(): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      this.clientsAddPortalUserOptions.set([]);
      return;
    }
    this.clientsAddPortalUsersLoading.set(true);
    try {
      const rows = await firstValueFrom(
        this.pmDirectoryApi.getClientUsers(this.effectiveDirectoryParentUserId(), {
          userId: this.effectiveDirectoryActingUserIdForContacts()
        })
      );
      // Exclude users already assigned to a client (companyMasterId is non-null when linked).
      const unassigned = rows.filter((r) => r.companyMasterId == null);
      const mapped = unassigned.map((r) => ({
        id: `client-user-${r.contactId}`,
        label: (r.contactDisplayName ?? r.email ?? `User ${r.contactId}`).trim() || `User ${r.contactId}`
      }));
      if (mapped.length > 0) {
        this.clientsAddPortalUserOptions.set(mapped);
        return;
      }
      const fallback = await firstValueFrom(
        this.pmDirectoryApi.getContacts(this.effectiveDirectoryParentUserId(), {
          userId: this.effectiveDirectoryActingUserIdForContacts(),
          clientUsersOnly: true,
          contactType: 1
        })
      );
      this.clientsAddPortalUserOptions.set(
        fallback.map((r) => ({
          id: `client-user-${r.id}`,
          label:
            (r.displayName ?? [r.firstName, r.middleName, r.lastName].filter(Boolean).join(' ')).trim() ||
            r.email?.trim() ||
            `User ${r.id}`
        }))
      );
    } catch {
      try {
        const fallback = await firstValueFrom(
          this.pmDirectoryApi.getContacts(this.effectiveDirectoryParentUserId(), {
            userId: this.effectiveDirectoryActingUserIdForContacts(),
            clientUsersOnly: true,
            contactType: 1
          })
        );
        this.clientsAddPortalUserOptions.set(
          fallback.map((r) => ({
            id: `client-user-${r.id}`,
            label:
              (r.displayName ?? [r.firstName, r.middleName, r.lastName].filter(Boolean).join(' ')).trim() ||
              r.email?.trim() ||
              `User ${r.id}`
          }))
        );
      } catch {
        this.clientsAddPortalUserOptions.set([]);
      }
    } finally {
      this.clientsAddPortalUsersLoading.set(false);
    }
  }

  async loadClientsAddContactOptionsFromPractice(): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      this.clientsAddContactOptions.set([]);
      return;
    }
    this.clientsAddContactOptionsLoading.set(true);
    try {
      const rows = await firstValueFrom(
        this.pmDirectoryApi.getContacts(this.effectiveDirectoryParentUserId(), {
          userId: this.effectiveDirectoryActingUserIdForContacts()
        })
      );
      const mapContactRow = (r: typeof rows[0]) => ({
        id: String(r.id),
        name:
          (r.displayName ?? [r.firstName, r.middleName, r.lastName].filter(Boolean).join(' ')).trim() ||
          r.email?.trim() ||
          `Contact #${r.id}`,
        email: (r.email ?? '').trim(),
        phone: (r.phone ?? '').replace(/\D/g, '').slice(0, 11),
        contactType: r.contactType ?? null,
        portalUserMasterId: (r as { portalUserMasterId?: number | null }).portalUserMasterId ?? null,
        fUserMaster: (r as { f_UserMaster?: number | null }).f_UserMaster ?? null
      });
      const mapped = rows.map(mapContactRow);
      if (mapped.length > 0) {
        this.clientsAddContactOptions.set(mapped);
        return;
      }
      const page = await firstValueFrom(
        this.pmDirectoryApi.getContactsPage(this.effectiveDirectoryParentUserId(), 1, 200, {
          userId: this.effectiveDirectoryActingUserIdForContacts()
        })
      );
      this.clientsAddContactOptions.set(
        (page.items ?? []).map((r) => ({
          id: String(r.id),
          name:
            (r.displayName ?? [r.firstName, r.middleName, r.lastName].filter(Boolean).join(' ')).trim() ||
            r.email?.trim() ||
            `Contact #${r.id}`,
          email: (r.email ?? '').trim(),
          phone: (r.phone ?? '').replace(/\D/g, '').slice(0, 11),
          contactType: r.contactType ?? null,
          portalUserMasterId: (r as { portalUserMasterId?: number | null }).portalUserMasterId ?? null,
          fUserMaster: (r as { f_UserMaster?: number | null }).f_UserMaster ?? null
        }))
      );
    } catch {
      this.clientsAddContactOptions.set([]);
    } finally {
      this.clientsAddContactOptionsLoading.set(false);
    }
  }

  async loadClientsAddPrimaryContactOptions(): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      this.clientsAddPrimaryContactOptionsRaw.set([]);
      return;
    }
    this.clientsAddPrimaryContactLoading.set(true);
    try {
      const rows = await firstValueFrom(
        this.pmDirectoryApi.getContactsAvailableAsPrimary(this.effectiveDirectoryParentUserId())
      );
      this.clientsAddPrimaryContactOptionsRaw.set(
        rows.map((r) => ({
          id: String(r.id),
          name:
            (r.displayName ?? [r.firstName, r.middleName, r.lastName].filter(Boolean).join(' ')).trim() ||
            r.email?.trim() ||
            `Contact #${r.id}`,
          email: (r.email ?? '').trim(),
          phone: (r.phone ?? '').replace(/\D/g, '').slice(0, 11),
          contactType: r.contactType ?? null,
          portalUserMasterId: (r as { portalUserMasterId?: number | null }).portalUserMasterId ?? null,
          fUserMaster: (r as { f_UserMaster?: number | null }).f_UserMaster ?? null
        }))
      );
    } catch {
      this.clientsAddPrimaryContactOptionsRaw.set([]);
    } finally {
      this.clientsAddPrimaryContactLoading.set(false);
    }
  }

  private async maybeOpenClientsEditModalFromQuery(qs: URLSearchParams): Promise<void> {
    const editClientId = (qs.get('editClientId') ?? '').trim();
    if (!editClientId) {
      this.clientsAddFormLastEditQueryKey = '';
      return;
    }
    const editKey = `${editClientId}|${(qs.get('editClientTs') ?? '').trim()}`;
    if (this.clientsAddFormLastEditQueryKey === editKey) {
      return;
    }
    this.clientsAddFormLastEditQueryKey = editKey;
    const returnUrl = (qs.get('returnUrl') ?? '').trim();
    void this.router.navigate(['/clients'], { queryParams: {}, replaceUrl: true });
    void this.openClientsAddFormModalForEdit(editClientId, returnUrl);
  }

  private normalizeClientsAddAmlStatus(raw: string | null | undefined): ClientsAddFormDraft['amlStatus'] {
    const value = String(raw ?? '').trim().toLowerCase();
    if (value === 'clear') return 'Clear';
    if (value === 'pending') return 'Pending';
    if (value === 'review') return 'Review';
    return 'Not started';
  }

  private mapClientServiceNamesToTrackKeys(names: string[] | null | undefined): string[] {
    const serviceNames = (names ?? []).map((n) => String(n ?? '').trim()).filter(Boolean);
    if (serviceNames.length === 0) {
      return [];
    }
    const keyByName = new Map<string, string>();
    for (const row of this.catalogServices()) {
      if (!row.isActive) continue;
      const normalized = normalizeServiceNameKey(row.name);
      if (!keyByName.has(normalized)) {
        keyByName.set(normalized, row.trackKey);
      }
    }
    for (const row of this.customAddedServices()) {
      if (!row.isActive) continue;
      const normalized = normalizeServiceNameKey(row.name);
      if (!keyByName.has(normalized)) {
        keyByName.set(normalized, `custom-added:${row.id ?? row.name}`);
      }
    }
    const selected = new Set<string>();
    for (const name of serviceNames) {
      const key = keyByName.get(normalizeServiceNameKey(name));
      if (key) {
        selected.add(key);
      }
    }
    return [...selected];
  }

  /** Matches agreedServiceNames (a CRM connection's active service titles) to catalog trackKeys and
   *  checks them in the Add Client draft. openClientsAddFormModal() calls primeClientsAddFormServicesCatalog()
   *  just before this runs, and that fires an async catalog load when catalogServices()/customAddedServices()
   *  are both still empty (e.g. first Add Client open this session) — matching against them immediately
   *  in that case would always find zero keys. So: match now if the catalog is already populated,
   *  otherwise stash the names and let the effect below apply them once the catalog signals populate. */
  private applyAgreedServiceNamesToDraft(names: string[] | null | undefined): void {
    const serviceNames = (names ?? []).filter((n) => n.trim().length > 0);
    if (serviceNames.length === 0) {
      return;
    }
    if (this.catalogServices().length > 0 || this.customAddedServices().length > 0) {
      const keys = this.mapClientServiceNamesToTrackKeys(serviceNames);
      if (keys.length > 0) {
        this.clientsAddDraft.update((d) => ({
          ...d,
          selectedServiceKeys: Array.from(new Set([...d.selectedServiceKeys, ...keys]))
        }));
      }
      return;
    }
    this.pendingAgreedServiceNamesForCatalog.set(serviceNames);
  }

  /** Load catalog + custom services for Add Client (always refresh customs so new rows appear). */
  primeClientsAddFormServicesCatalog(): void {
    const catalogEmpty = this.catalogServices().length === 0;
    const customEmpty = this.customAddedServices().length === 0;
    if (catalogEmpty && customEmpty) {
      if (!this.servicesSettingsLoading()) {
        this.loadSettingsServicesCatalog();
      }
      return;
    }
    if (catalogEmpty) {
      this.pmSettingsApi.getDefaultServices(true).subscribe({
        next: (rows) => {
          this.applyDefaultServicesResponse(rows);
          this.loadCustomServicesForSettings();
        },
        error: () => {
          this.loadCustomServicesForSettings();
        }
      });
      return;
    }
    this.loadCustomServicesForSettings();
  }

  toggleClientsAddServiceKey(trackKey: string, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.clientsAddDraft.update((d) => {
      const next = new Set(d.selectedServiceKeys);
      if (checked) {
        next.add(trackKey);
      } else {
        next.delete(trackKey);
      }
      return { ...d, selectedServiceKeys: [...next] };
    });
  }

  closeClientsAddFormModal() {
    this.clientsAddPopupExtras.set({});
    this.pendingCrmConversionSource = null;
    this.pendingAgreedServiceNamesForCatalog.set(null);
    this.clientsAddShowFooterErrors.set(false);
    this.clientsAddActiveDropdown.set(null);
    this.clientsAddFormOpen.set(false);
    this.clientsAddEditingEntityId.set(null);
    this.clientsAddFormMode.set('create');
    this.clientsAddEditClientUniversalId.set('');
    this.clientsAddEditCompanyMasterId.set('');
    this.clientsAddEditReturnUrl.set('');
    this.clientsAddEditLoadPending.set(false);
    this.clientsAddContactModalOpen.set(false);
    this.clientsCompanyHouseModalOpen.set(false);
    this.clientsCompanyHouseSearch.set('');
    this.clientsAddDirectors.set([]);
    this.clientsAddDirectorDeleteIds.set([]);
    this.clientsAddChOfficers.set([]);
    this.clientsAddChOfficersSelected.set(new Set());
    this.clientsAddChOfficersSa.set(new Set());
    this.clientsAddDirectorFormOpen.set(false);
    this.clientsAddAssignDirectorSelected.set([]);
    this.clientsAddAssignDirectorOptions.set([]);
    this.clientsAddAssignDirectorSearch.set('');
    this.clientsAddShowPassword.set(false);
    this.clientsAddShowConfirmPassword.set(false);
    this.clientsAddSicTags.set([]);
    this.clientsAddPrimaryContactDropdownOpen.set(false);
    this.clientsAddPrimaryContactPanelStyle.set(null);
    this.clientsAddKeyContactDropdownOpen.set(false);
    this.clientsAddKeyContactPanelStyle.set(null);
    this.clientsEditingDraftId.set(null);
  }

  onClientsAddModalSurfaceClick(event: Event): void {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('.kc-multiselect') && !target?.closest('.kc-multiselect-panel')) {
      this.clientsAddKeyContactDropdownOpen.set(false);
      this.clientsAddKeyContactPanelStyle.set(null);
    }
    if (!target?.closest('.pc-dropdown') && !target?.closest('.pc-dropdown-panel')) {
      this.clientsAddPrimaryContactDropdownOpen.set(false);
      this.clientsAddPrimaryContactPanelStyle.set(null);
    }
    if (!target?.closest('.pm-form-custom-select') && !target?.closest('.crm-comm-dd')) {
      this.closeClientsAddActiveDropdown();
    }
    this.stopProp(event);
  }

  closeClientsAddActiveDropdown(): void {
    this.clientsAddActiveDropdown.set(null);
  }

  wsInitialsFromName(name: string): string {
    const words = (name || '').trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      return `${words[0]!.charAt(0)}${words[words.length - 1]!.charAt(0)}`.toUpperCase();
    }
    return (name || '').trim().slice(0, 2).toUpperCase() || '?';
  }

  /** Display-only label for the Add Client "Select Primary Contact" chip: falls back to the
   *  contact's own email when it has no name on file, matching kcContactLabel's priority on the
   *  Key Contacts / Overview page — so a contact selected/created with only an email doesn't
   *  render as an empty name with a "?" avatar despite being genuinely selected. */
  wsContactChipLabel(name: string, email: string): string {
    return (name || '').trim() || (email || '').trim();
  }

  wsContactChipInitials(name: string, email: string): string {
    return this.wsInitialsFromName(this.wsContactChipLabel(name, email));
  }

  applyClientsAddContactModalDefaults(): void {
    this.clientsAddContactDraft.set(emptyClientsAddContactDraft());
    const countryId =
      this.findClientsAddMasterDataIdByName(this.clientsAddCountries(), 'United Kingdom') ?? 222;
    const countryRow = this.findClientsAddMasterDataRowById(this.clientsAddCountries(), countryId);
    this.clientsAddContactDraft.update((d) => ({
      ...d,
      countryId,
      country: (countryRow?.name ?? d.country).trim() || 'United Kingdom'
    }));
  }

  /** Allow only IDV-valid characters: alphanumeric and permitted special chars. */
  filterIdvKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || !event.key || event.key.length !== 1) return;
    if (!/^[A-Za-z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  /** Block non-digit characters at keystroke level (UTR No). */
  filterDigitsOnlyKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || !event.key || event.key.length !== 1) return;
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  /** Block non-phone characters at keystroke level. + is only allowed as the very first character. */
  filterPhoneKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || !event.key || event.key.length !== 1) return;
    const input = event.target as HTMLInputElement;
    if (event.key === '+') {
      // Allow + only if cursor is at position 0 and field is empty or has no + yet
      const alreadyHasPlus = input.value.includes('+');
      const cursorAtStart = input.selectionStart === 0;
      if (!cursorAtStart || alreadyHasPlus) {
        event.preventDefault();
      }
      return;
    }
    const allowed = /^[0-9\s\-()[\]]$/;
    if (!allowed.test(event.key)) {
      event.preventDefault();
    }
  }

  clientListRowHasContact(row: ClientListRow): boolean {
    return !!(row.contactPersonNameOnly?.trim() || (row.directoryContactId != null && row.directoryContactId > 0));
  }

  clientListRowContactDisplayName(row: ClientListRow): string {
    const person = (row.contactPersonNameOnly ?? '').trim();
    if (person) {
      return person;
    }
    return row.directoryContactId != null && row.directoryContactId > 0 ? 'Contact' : '';
  }

  private async loadClientsAddSAIndividualOptions(): Promise<void> {
    const parent = this.effectiveDirectoryParentUserId();
    const userId = this.effectiveDirectoryUserIdForClientsList();
    if (parent <= 0 || userId <= 0) return;
    this.clientsAddSAIndividualLoading.set(true);
    try {
      const res = await firstValueFrom(
        this.pmDirectoryApi.getClients(parent, userId, 1, 500, { clientTypes: ['Individual'] })
      );
      this.clientsAddSAIndividualOptions.set(
        (res.items ?? [])
          .filter((c) => c.id && c.companyName)
          .map((c) => ({ id: Number(c.id), name: c.companyName! }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch {
      this.clientsAddSAIndividualOptions.set([]);
    } finally {
      this.clientsAddSAIndividualLoading.set(false);
    }
  }

  private async loadClientsAddAssignDirectorOptions(): Promise<void> {
    const parent = this.effectiveDirectoryParentUserId();
    const userId = this.effectiveDirectoryUserIdForClientsList();
    if (parent <= 0 || userId <= 0) return;
    this.clientsAddAssignDirectorLoading.set(true);
    try {
      const res = await firstValueFrom(
        this.pmDirectoryApi.getClients(parent, userId, 1, 500, {
          clientTypes: ['Limited', 'LLP', 'Partnership', 'Sole Trader']
        })
      );
      this.clientsAddAssignDirectorOptions.set(
        (res.items ?? [])
          .filter((c) => c.clientUniversalId && c.companyName)
          .map((c) => ({ universalId: c.clientUniversalId!, name: c.companyName! }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch {
      this.clientsAddAssignDirectorOptions.set([]);
    } finally {
      this.clientsAddAssignDirectorLoading.set(false);
    }
  }

  setClientsAddField<K extends keyof ClientsAddFormDraft>(key: K, value: ClientsAddFormDraft[K]): void {
    if (key === 'companyType') {
      const raw = String(value ?? '').trim();
      if (!raw) {
        this.clientsAddDraft.update((d) => ({ ...d, companyType: '' }));
        return;
      }
      const canonical = canonicalClientTypeLabel(raw, this.clientsAddClientTypeOptions());
      const isSoleOrIndividual = canonical.toLowerCase().includes('sole') || canonical.toLowerCase().includes('individual');
      if (!isSoleOrIndividual) {
        this.clientsAddDraft.update((d) => ({
          ...d,
          isSALinking: false,
          saLinkCompanyMasterId: null
        }));
      }
      this.clientsAddDraft.update((d) => ({
        ...d,
        companyType: canonical,
        selectedServiceKeys: d.selectedServiceKeys.filter((tk) =>
          this.clientsAddServiceTrackKeyEligibleForCompanyType(tk, canonical)
        )
      }));
      // Re-pull the services catalog so a service activated/assigned to this type in another
      // tab (e.g. Settings → Services) shows up without needing a full page refresh.
      if (!this.servicesSettingsLoading()) {
        this.loadSettingsServicesCatalog();
      }
      return;
    }
    if (key === 'clientId') {
      const normalized = String(value ?? '').trim();
      this.clientsAddClientIdUserEdited.set(true);
      this.clientsAddDraft.update((d) => ({ ...d, clientId: normalized }));
      this.syncClientsAddClientIdDuplicate(normalized);
      return;
    }
    if (key === 'phone') {
      const normalized = normalizeClientPhoneInput(String(value ?? ''));
      this.clientsAddDraft.update((d) => ({ ...d, phone: normalized }));
      return;
    }
    if (key === 'registrationNumber') {
      const normalized = normalizeClientRegistrationNumberInput(String(value ?? ''));
      this.clientsAddDraft.update((d) => ({ ...d, registrationNumber: normalized }));
      this.scheduleClientsAddRegNoDuplicateCheck(normalized);
      return;
    }
    if (key === 'utrNumber') {
      const normalized = normalizeClientUtrInput(String(value ?? ''));
      this.clientsAddDraft.update((d) => ({ ...d, utrNumber: normalized }));
      return;
    }
    if (key === 'idvReference') {
      const normalized = normalizeClientIdvInput(String(value ?? ''));
      this.clientsAddDraft.update((d) => ({ ...d, idvReference: normalized }));
      return;
    }
    if (key === 'vatRegistrationNumber') {
      const normalized = normalizeClientVatRegInput(String(value ?? ''));
      this.clientsAddDraft.update((d) => ({ ...d, vatRegistrationNumber: normalized }));
      return;
    }
    if (key === 'accountOfficeRefNo') {
      // Raw pass-through (capped by the input's maxlength) instead of the strict positional mask:
      // masking silently drops a leading invalid character (e.g. a letter, where the format needs a
      // digit first), which left the field empty and so showed no error at all - typing a single
      // wrong character now stays visible and the format error below shows immediately.
      this.clientsAddDraft.update((d) => ({ ...d, accountOfficeRefNo: String(value ?? '') }));
      return;
    }
    if (key === 'payeRefNo') {
      // Same raw pass-through as accountOfficeRefNo above, for the same reason.
      this.clientsAddDraft.update((d) => ({ ...d, payeRefNo: String(value ?? '') }));
      return;
    }
    const keyName = String(key);
    if (keyName === 'managerUserId' || keyName === 'partnerUserId') {
      const raw = String(value ?? '').trim();
      const parsed = raw.length > 0 ? Number(raw) : NaN;
      const normalized = Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
      this.clientsAddDraft.update((d) => ({
        ...d,
        managerUserId: keyName === 'managerUserId' ? normalized : d.managerUserId,
        partnerUserId: keyName === 'partnerUserId' ? normalized : d.partnerUserId
      }));
      return;
    }
    if (key === 'createPortalUser') {
      const checked = !!value;
      this.clientsAddDraft.update((d) => ({
        ...d,
        createPortalUser: checked,
        portalSelectedUser: checked ? '' : d.portalSelectedUser,
        portalPassword: checked ? d.portalPassword : '',
        portalConfirmPassword: checked ? d.portalConfirmPassword : ''
      }));
      return;
    }
    if (key === 'primaryEmail') {
      const normalized = String(value ?? '').replace(/\s+/g, '').replace(/[^a-zA-Z0-9@._%+-]/g, '');
      this.clientsAddDraft.update((d) => ({ ...d, primaryEmail: normalized }));
      return;
    }
    if (key === 'portalSelectedUser') {
      const selected = String(value ?? '').trim();
      this.clientsAddDraft.update((d) => ({
        ...d,
        portalSelectedUser: selected,
        createPortalUser: selected ? false : d.createPortalUser,
        portalPassword: selected ? '' : d.portalPassword,
        portalConfirmPassword: selected ? '' : d.portalConfirmPassword
      }));
      return;
    }
    if (key === 'contactPhone') {
      const normalized = normalizeClientPhoneInput(String(value ?? ''));
      this.clientsAddDraft.update((d) => {
        const next = {
          ...d,
          contactPhone: normalized,
          contactPersonPhone: normalized
        };
        const contactOk =
          !!next.contactName.trim() && !!next.contactEmail.trim();
        if (!contactOk) {
          next.createPortalUser = false;
          next.portalPassword = '';
          next.portalConfirmPassword = '';
        }
        return next;
      });
      return;
    }
    if (key === 'syncContactEmailToPrimary') {
      const checked = !!value;
      this.clientsAddDraft.update((d) => {
        if (checked) {
          this.clientsAddPrimaryEmailSnapshot = d.primaryEmail;
          return { ...d, syncContactEmailToPrimary: true, primaryEmail: d.contactEmail };
        }
        return { ...d, syncContactEmailToPrimary: false, primaryEmail: this.clientsAddPrimaryEmailSnapshot };
      });
      return;
    }
    if (key === 'isSALinking') {
      const checked = !!value;
      if (checked && this.clientsAddSAIndividualOptions().length === 0) {
        this.loadClientsAddSAIndividualOptions();
      }
      this.clientsAddDraft.update((d) => ({
        ...d,
        isSALinking: checked,
        saLinkCompanyMasterId: checked ? d.saLinkCompanyMasterId : null
      }));
      return;
    }
    if (key === 'saLinkCompanyMasterId') {
      const id = Number(value ?? 0);
      this.clientsAddDraft.update((d) => ({
        ...d,
        saLinkCompanyMasterId: id > 0 ? id : null
      }));
      return;
    }
    if (key === 'contactName' || key === 'contactEmail') {
      this.clientsAddDraft.update((d) => {
        const next =
          key === 'contactEmail'
            ? {
                ...d,
                contactEmail: String(value ?? '')
                  .replace(/\s+/g, '')
                  .replace(/[^a-zA-Z0-9@._%+-]/g, ''),
                contactPersonEmail: String(value ?? '')
                  .replace(/\s+/g, '')
                  .replace(/[^a-zA-Z0-9@._%+-]/g, '')
              }
            : {
                ...d,
                contactName: String(value ?? ''),
                contactPersonName: String(value ?? '').trim()
              };
        if (next.syncContactEmailToPrimary && key === 'contactEmail') {
          next.primaryEmail = next.contactEmail;
        }
        const contactOk =
          !!next.contactName.trim() && !!next.contactEmail.trim();
        if (!contactOk) {
          next.createPortalUser = false;
          next.portalPassword = '';
          next.portalConfirmPassword = '';
        }
        return next;
      });
      return;
    }
    if (key === 'businessStartDate') {
      const normalized = normalizeClientsAddDateInput(String(value ?? ''));
      this.clientsAddDraft.update((d) => {
        const bookStart = d.bookStartDate ?? '';
        const shouldClearBook = !!normalized && !!bookStart && bookStart < normalized;
        return { ...d, businessStartDate: normalized, ...(shouldClearBook ? { bookStartDate: '' } : {}) };
      });
      return;
    }
    if (CLIENTS_ADD_DATE_FIELD_KEYS.has(key)) {
      const normalized = normalizeClientsAddDateInput(String(value ?? ''));
      this.clientsAddDraft.update((d) => ({ ...d, [key]: normalized }));
      return;
    }
    if (key === 'sicCode') {
      const raw = String(value ?? '');
      const tags = raw.split(',').map(s => s.trim()).filter(Boolean);
      this.clientsAddSicTags.set(tags);
    }
    this.clientsAddDraft.update((d) => ({ ...d, [key]: value }));
  }

  private rawCompanyTypeFromCustomServiceRow(r: ClientCustomServiceApiRow): string | null | undefined {
    const o = r as unknown as Record<string, unknown>;
    return (r.companyType ?? o['CompanyType'] ?? o['companyType']) as string | null | undefined;
  }

  private readDefaultServiceIdFromCustomRow(r: ClientCustomServiceApiRow): number | null {
    const o = r as unknown as Record<string, unknown>;
    const raw = r.defaultServiceId ?? o['DefaultServiceId'] ?? o['defaultServiceId'];
    if (raw == null || raw === '') {
      return null;
    }
    const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * Custom row → client types for Add Client: parsed `CompanyType`, else inherited from
   * `defaultServiceId` catalog template, else **all types** (typical PM rows have no CompanyType yet).
   */
  clientTypesForCustomServiceRow(r: ClientCustomServiceApiRow): string[] {
    const parsed = this.parseClientTypesFromSettingsCompanyTypeField(this.rawCompanyTypeFromCustomServiceRow(r));
    if (parsed.length > 0) {
      return parsed;
    }
    const defId = this.readDefaultServiceIdFromCustomRow(r);
    if (defId != null) {
      const cat = this.catalogServices().find((s) => s.defaultServiceId === defId);
      if (cat) {
        return [...this.effectiveClientTypesForAddClientCatalog(cat)];
      }
    }
    // No stored company type: show nothing on the service card (modal still defaults types when editing).
    return [];
  }

  /** `companyType` on PM_ClientService: comma/semicolon-separated codes or labels, or JSON array. */
  private parseClientTypesFromSettingsCompanyTypeField(raw: string | null | undefined): string[] {
    const s = (raw ?? '').trim();
    if (!s) {
      return [];
    }
    if (s.startsWith('[')) {
      try {
        const arr = JSON.parse(s) as unknown;
        if (Array.isArray(arr)) {
          const out: string[] = [];
          for (const item of arr) {
            if (typeof item === 'number' && Number.isFinite(item)) {
              const fromCode = this.clientTypeLabelForCode(String(Math.trunc(item)));
              if (fromCode) {
                out.push(fromCode);
              }
              continue;
            }
            if (typeof item === 'string' && /^\d+$/.test(item.trim())) {
              const fromCode = this.clientTypeLabelForCode(item.trim());
              if (fromCode) {
                out.push(fromCode);
              }
              continue;
            }
            if (typeof item === 'string') {
              const matchLabel = this.availableClientTypes.find(
                (a) => a.toLowerCase() === item.trim().toLowerCase()
              );
              if (matchLabel) {
                out.push(matchLabel);
              }
            }
          }
          return [...new Set(out)];
        }
      } catch {
        /* fall through to delimiter split */
      }
    }
    const out: string[] = [];
    const parts = s.split(/[,;]+/).map((x) => x.trim()).filter(Boolean);
    for (const part of parts) {
      const fromCode = this.clientTypeLabelForCode(part);
      if (fromCode) {
        out.push(fromCode);
        continue;
      }
      const matchLabel = this.availableClientTypes.find((a) => a.toLowerCase() === part.toLowerCase());
      if (matchLabel) {
        out.push(matchLabel);
      }
    }
    return [...new Set(out)];
  }

  private addClientTypeMatchesList(selectedCanonical: string, allowed: readonly string[]): boolean {
    // Same rule as Settings services filters: no client-type tags = available for all types.
    if (!allowed || allowed.length === 0) {
      return true;
    }
    return allowed.some((t) => t.toLowerCase() === selectedCanonical.toLowerCase());
  }

  /** Effective types for Add Client: catalog template `companyType`, then service packages API. */
  private effectiveClientTypesForAddClientCatalog(s: CatalogServiceVm): string[] {
    return this.getEntityTagsForCatalogService(s);
  }

  /** Custom: explicit `clientTypes` from API/settings only. */
  private effectiveClientTypesForAddClientCustom(s: CustomAddedService): string[] {
    if (s.clientTypes.length > 0) {
      return [...s.clientTypes];
    }
    return [];
  }

  catalogServiceVisibleForAddClientType(s: CatalogServiceVm, selectedCompanyTypeRaw: string): boolean {
    if (!selectedCompanyTypeRaw.trim()) {
      return s.isActive;
    }
    const canonical = canonicalClientTypeLabel(selectedCompanyTypeRaw, this.availableClientTypes);
    return this.addClientTypeMatchesList(canonical, this.effectiveClientTypesForAddClientCatalog(s));
  }

  customServiceVisibleForAddClientType(s: CustomAddedService, selectedCompanyTypeRaw: string): boolean {
    if (!selectedCompanyTypeRaw.trim()) {
      return s.isActive;
    }
    const canonical = canonicalClientTypeLabel(selectedCompanyTypeRaw, this.availableClientTypes);
    return this.addClientTypeMatchesList(canonical, this.effectiveClientTypesForAddClientCustom(s));
  }

  private clientsAddServiceTrackKeyEligibleForCompanyType(trackKey: string, canonicalCompanyType: string): boolean {
    if (trackKey.startsWith('d:')) {
      const id = Number(trackKey.slice(2));
      const s = this.catalogServices().find((x) => x.defaultServiceId === id);
      // Keep the selection if the catalog hasn't loaded the service yet (race condition guard).
      return s ? this.catalogServiceVisibleForAddClientType(s, canonicalCompanyType) : true;
    }
    if (trackKey.startsWith('custom-added:')) {
      const rest = trackKey.slice('custom-added:'.length);
      const n = Number(rest);
      const custom =
        Number.isFinite(n) && n > 0
          ? this.customAddedServices().find((c) => c.id != null && Number(c.id) === n)
          : this.customAddedServices().find((c) => c.name === rest);
      return custom ? this.customServiceVisibleForAddClientType(custom, canonicalCompanyType) : true;
    }
    return false;
  }

  /** Map a stored accounting-system value back to dropdown + free-text (a custom value re-opens as "Others"). */
  private clientsAddAccountingSystemSelection(raw: string): { accountingSystem: string; accountingSystemOther: string } {
    const value = (raw ?? '').trim();
    if (!value) return { accountingSystem: '', accountingSystemOther: '' };
    const known = CLIENTS_ADD_ACCOUNTING_SYSTEM_OPTIONS.some((o) => o.value.toLowerCase() === value.toLowerCase());
    return known
      ? { accountingSystem: value, accountingSystemOther: '' }
      : { accountingSystem: 'Others', accountingSystemOther: value };
  }

  /** Map a stored source value back to dropdown + free-text (a custom value re-opens as "Others"). */
  private clientsAddSourceSelection(raw: string): { source: string; sourceOther: string } {
    const value = (raw ?? '').trim();
    if (!value) return { source: 'Not specified', sourceOther: '' };
    // Return the canonical base option string (case-insensitive) so the dropdown highlights correctly.
    const baseMatch = CLIENTS_ADD_SOURCE_OPTIONS.find((o) => o.toLowerCase() === value.toLowerCase());
    if (baseMatch) return { source: baseMatch, sourceOther: '' };
    // If this value is already in the dynamic options (custom source from another client), treat it
    // as a regular known value so the dropdown highlights it without also showing the custom entry input.
    const dynamicMatch = this.clientsAddSourceSelectOptions().find(
      (o) => o !== 'Others' && o.toLowerCase() === value.toLowerCase()
    );
    if (dynamicMatch) return { source: dynamicMatch, sourceOther: '' };
    // Truly new custom value — open as 'Others' with the free-text entry input.
    return { source: 'Others', sourceOther: value };
  }

  private clientsAddDebugToast(
    level: 'warning' | 'success' | 'error',
    title: string,
    options?: { description?: string },
    _context?: string,
    _extra?: unknown
  ): void {
    if (level === 'warning') {
      toast.warning(title, options);
    } else if (level === 'success') {
      toast.success(title, options);
    } else {
      toast.error(title, options);
    }
  }

  async persistClientsAddPrimaryContact(
    parentUserId: number,
    /** Client Store UniversalId of the client (Add Client). A number is the old CompanyMaster id. */
    companyMasterId: string | number,
    draft: ClientsAddFormDraft
  ): Promise<void> {
    if (typeof companyMasterId === 'string') {
      if (!companyMasterId.trim()) {
        return;
      }
    } else if (!Number.isFinite(companyMasterId) || companyMasterId <= 0) {
      return;
    }
    const contactName = (draft.contactPersonName.trim() || draft.contactName.trim());
    // No `|| draft.primaryEmail` fallback here: that conflated the *client's own* email with the
    // *contact person's* email. Whenever selectedContactId turned out to be a pending-prefill
    // placeholder (see the pendingContactId comment below) rather than a real directory contact id,
    // this fell through to the client's Primary Email and silently manufactured a brand-new "ghost"
    // Contact carrying it - which then permanently polluted every future Add Client's contact picker.
    // A contact with no real email on file should simply have no email, not borrow the client's.
    const contactEmail = (draft.contactPersonEmail.trim() || draft.contactEmail.trim());
    // Same reasoning as contactEmail above - no `|| draft.phone` fallback to the client's own number.
    const contactPhone = (draft.contactPersonPhone.trim() || draft.contactPhone.trim());
    if (!contactName && !contactEmail && !contactPhone) {
      return;
    }
    const contactApiUserId = this.effectiveDirectoryActingUserIdForContacts();
    const contactHttpOpts =
      contactApiUserId != null && contactApiUserId > 0 ? { userId: contactApiUserId } : undefined;
    const nameParts = contactName.split(/\s+/).filter(Boolean);
    const firstName = nameParts[0] ?? '';
    const lastName = nameParts.slice(1).join(' ');
    const selectedContactId = draft.selectedContactId.trim();
    const selectedNumericId = Number(selectedContactId);
    const hasSelectedContact = Number.isFinite(selectedNumericId) && selectedNumericId > 0;

    const addressPayload = {
      address1: draft.address1.trim() || null,
      address2: draft.address2.trim() || null,
      city: draft.city.trim() || null,
      county: draft.county.trim() || null,
      postCode: draft.postCode.trim() || null,
      countryId: draft.countryId != null && draft.countryId > 0 ? draft.countryId : null
    };

    try {
      let contactId: number;
      if (hasSelectedContact) {
        // Contacts are shared directory-wide (not owned by a single client), and this form has no
        // editable fields for an already-selected contact (just the read-only chip + remove button)
        // - so there is nothing here the user actually meant to change. Previously this still PATCHed
        // the picked contact's name/email/phone/address using this client's own fallback-derived
        // values, silently overwriting that shared contact's real data (e.g. blanking its name, or
        // stamping this client's Primary Email onto it) every time it was reused as primary contact
        // for a different client. Just link it as-is.
        contactId = Math.trunc(selectedNumericId);
      } else {
        const createBody: CreateDirectoryContactPayload = {
          firstName: firstName || null,
          lastName: lastName || null,
          email: contactEmail || null,
          phone: contactPhone || null,
          category: 1,
          ...addressPayload
        };
        if (draft.createPortalUser) {
          createBody.sendPortalInvite = true;
          createBody.portalPassword = draft.portalPassword.trim();
        }
        const createdContact = await firstValueFrom(
          this.pmDirectoryApi.createDirectoryContact(parentUserId, createBody, contactHttpOpts)
        );
        contactId = createdContact.id;
      }

      await firstValueFrom(
        this.pmDirectoryApi.linkDirectoryContactToClient(companyMasterId, parentUserId, contactId, {
          setAsPrimary: true
        })
      );
    } catch (e: unknown) {
      this.clientsAddDebugToast(
        'warning',
        'Client saved, but primary contact was not created or linked',
        { description: this.formatPmApiError(e).slice(0, 240) },
        'persistClientsAddPrimaryContact',
        {
          error: e,
          createPortalUser: draft.createPortalUser,
          selectedContactId: draft.selectedContactId
        }
      );
    }
  }

  /** @deprecated replaced by {@link persistClientsAddPrimaryContact} */
  private async tryCreateAndLinkPrimaryContactForNewClient(
    parentUserId: number,
    companyMasterId: number,
    draft: ClientsAddFormDraft
  ): Promise<void> {
    await this.persistClientsAddPrimaryContact(parentUserId, companyMasterId, draft);
  }

  loadUnassignedClients(): void {
    const parentUserId = this.effectiveDirectoryParentUserId();
    if (!parentUserId || parentUserId <= 0) return;
    this.unassignedClientsLoading.set(true);
    this.pmDirectoryApi
      .getUnassignedClients(
        parentUserId,
        this.unassignedClientsPageNumber(),
        this.unassignedClientsPageSize(),
        this.unassignedClientsSearchQuery() || undefined
      )
      .subscribe({
        next: (res) => {
          this.unassignedClientsRows.set(res.items);
          this.unassignedClientsTotalCount.set(res.totalCount);
          this.unassignedClientsLoading.set(false);
        },
        error: () => {
          this.unassignedClientsLoading.set(false);
          toast.error('Unable to load unassigned clients');
        }
      });
  }

  loadClientsDraftList(): void {
    const parentUserId = this.effectiveDirectoryParentUserId();
    if (!parentUserId || parentUserId <= 0) {
      return;
    }
    this.clientsDraftListLoading.set(true);
    this.pmDirectoryApi.listClientDrafts(parentUserId).subscribe({
      next: (drafts) => {
        this.clientsDraftList.set(drafts);
        this.clientsDraftListLoading.set(false);
      },
      error: () => {
        this.clientsDraftListLoading.set(false);
        toast.error('Unable to load drafts');
      }
    });
  }

  mergeClientsAddFormDraftFromPartial(parsed: Partial<ClientsAddFormDraft>): ClientsAddFormDraft {
    const base = emptyClientsAddDraft();
    const parsedCountryId = this.clientsAddLookupId(parsed.countryId);
    const parsedVatSchemeId = this.clientsAddLookupId(parsed.vatSchemeId);
    const parsedCurrencyId = this.clientsAddLookupId(parsed.currencyId);
    const parsedVatReturnTypeId = this.clientsAddLookupId(parsed.vatReturnTypeId);
    return sanitizeClientsAddValidatedFields({
      ...base,
      ...parsed,
      companyType: (parsed.companyType ?? '').trim() || base.companyType,
      countryId:
        parsedCountryId ??
        (((parsed.country ?? '').trim().toLowerCase() || base.country.toLowerCase()) === 'united kingdom'
          ? base.countryId
          : null),
      vatSchemeId: parsedVatSchemeId ?? base.vatSchemeId,
      vatScheme: (parsed.vatScheme ?? '').trim() || base.vatScheme,
      currencyId: parsedCurrencyId,
      vatReturnTypeId: parsedVatReturnTypeId,
      selectedServiceKeys: Array.isArray(parsed.selectedServiceKeys)
        ? parsed.selectedServiceKeys.map((x) => String(x))
        : []
    });
  }

  private readClientsAddDraftFromStorage(): ClientsAddDraftStoragePayload | null {
    if (typeof window === 'undefined') {
      return null;
    }
    const raw = pmStorageGet(CLIENTS_ADD_DRAFT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as Partial<ClientsAddDraftStoragePayload> & Partial<ClientsAddFormDraft>;
      if (parsed && typeof parsed === 'object' && parsed.form && typeof parsed.form === 'object') {
        return {
          form: this.mergeClientsAddFormDraftFromPartial(parsed.form),
          customFieldValues: normalizeClientsAddCustomFieldValuesRecord(parsed.customFieldValues)
        };
      }
      return {
        form: this.mergeClientsAddFormDraftFromPartial(parsed),
        customFieldValues: {}
      };
    } catch {
      return null;
    }
  }

  clearClientsAddDraftFromStorage(): void {
    if (typeof window === 'undefined') {
      return;
    }
    pmStorageRemove(CLIENTS_ADD_DRAFT_STORAGE_KEY);
  }
  /** Same universal id rule as directory list / PM matrix (`clientId` or numeric `id`). */
  private universalIdFromDirectoryClientRow(row: DirectoryClientRowApi): string {
    const cid = (row.clientId == null ? '' : String(row.clientId)).trim();
    if (cid.length > 0) {
      return cid;
    }
    if (row.id == null || row.id === undefined || row.id === '') {
      return '';
    }
    return typeof row.id === 'string' ? row.id.trim() : String(row.id);
  }

  onClientsCatalogCellInteraction(event: Event, key: string) {
    if (key === 'contactEmail' || key === 'capiumPayLink') {
      event.stopPropagation();
    }
  }

  /* ---- Clients sub-sections (Schedule / Communication / AML) on the shared pm-page-toolbar ---- */

  /** The section toolbar button the open dropdown hangs under (Group by, AML filter, Perform Check,
   *  and the Communication filter's placement). */
  readonly clientsSectionAnchor = signal<HTMLElement | null>(null);

  loadClientsSavedViews(): void {
    if (!this.userContext.practiceContextReady()) {
      return;
    }
    this.clientsSavedViewsLoading.set(true);
    const p = this.effectiveDirectoryParentUserId();
    const u = this.effectiveDirectoryUserId();
    this.pmSettingsApi.listClientSavedFilterViews(p, u).subscribe({
      next: (rows) => {
        this.clientsSavedViews.set(
          rows.map((r) => ({ ...r, filterJson: this.savedViewFilterJsonString(r) }))
        );
        const appliedId = this.clientsAppliedSavedViewId();
        if (
          appliedId != null &&
          !rows.some((row) => this.normalizeClientsSavedViewId(row.id) === appliedId)
        ) {
          this.clearClientsAppliedSavedViewSelection();
        } else if (appliedId == null) {
          this.tryMatchAppliedSavedViewFromCurrentFilters();
        } else if (this.clientsAppliedSavedViewFilterBaseline() == null) {
          this.clientsAppliedSavedViewFilterBaseline.set(this.serializeClientsFilterSnapshotJson());
        }
        this.clientsSavedViewsLoading.set(false);
      },
      error: (err) => {
        this.lastClientsSavedViewsHydrationLoadKey.set('');
        this.clientsSavedViewsLoading.set(false);
        this.notifyError(err);
      }
    });
  }

  serializeClientsFilterSnapshotJson(): string {
    const payload = {
      v: 1 as const,
      searchQuery: this.clientsSearchQuery(),
      sortField: this.clientsSortField(),
      sortDir: this.clientsSortDir(),
      statuses: [...this.clientsFilterStatuses()],
      clientTypes: [...this.clientsFilterClientTypes()],
      manager: this.clientsFilterManager(),
      grades: [...this.clientsFilterGrades()],
      amlStatuses: [...this.clientsFilterAmlStatuses()],
      services: [...this.clientsFilterServices()],
      quick: [...this.clientsFilterQuick()],
      columnVisibility: { ...this.clientsColumnVisibility() },
      columnOrder: [...this.clientsColumnOrder()],
      viewMode: this.clientsViewMode()
    };
    return JSON.stringify(payload);
  }

  /** Flatten nested `filters` payloads and accept camelCase / PascalCase keys from saved views. */
  private normalizeClientsFilterSnapshotRaw(raw: Record<string, unknown>): Record<string, unknown> {
    const nested = raw['filters'] ?? raw['Filters'];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return { ...raw, ...(nested as Record<string, unknown>) };
    }
    return raw;
  }

  private readSnapshotString(raw: Record<string, unknown>, keys: string[]): string | undefined {
    for (const key of keys) {
      const val = raw[key];
      if (typeof val === 'string' && val.trim().length > 0) {
        return val.trim();
      }
    }
    return undefined;
  }

  private readSnapshotStringArray(raw: Record<string, unknown>, keys: string[]): string[] {
    for (const key of keys) {
      const val = raw[key];
      if (Array.isArray(val)) {
        const arr = val
          .filter((i): i is string => typeof i === 'string')
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
        if (arr.length > 0) {
          return arr;
        }
      }
      if (typeof val === 'string' && val.trim().length > 0) {
        return [val.trim()];
      }
    }
    return [];
  }

  /** Applies filter snapshot fields from parsed JSON (saved views + local persistence). Ignores unknown keys. */
  applyClientsFilterSnapshotFields(
    raw: Record<string, unknown>,
    options?: { preserveSearchIfMissing?: boolean }
  ): void {
    const snapshot = this.normalizeClientsFilterSnapshotRaw(raw);
    const validFields = new Set(['name', 'grade', 'revenue', 'aml']);
    const validDirs = new Set(['asc', 'desc']);
    let sortField = this.readSnapshotString(snapshot, ['sortField', 'SortField']);
    let sortDir = this.readSnapshotString(snapshot, ['sortDir', 'SortDir']);
    if (!sortField || !validFields.has(sortField)) {
      sortField = 'name';
    }
    if (!sortDir || !validDirs.has(sortDir)) {
      sortDir = 'asc';
    }
    const asStatusArr = (x: unknown): ClientsFilterStatus[] => {
      if (!Array.isArray(x)) {
        return [];
      }
      return x.filter(
        (i): i is ClientsFilterStatus =>
          i === 'active' || i === 'inactive' || i === 'activity30' || i === 'activity90'
      );
    };
    const asQuickArr = (x: unknown): ('atRisk' | 'amlIssues' | 'overdue')[] => {
      if (!Array.isArray(x)) {
        return [];
      }
      return x.filter(
        (i): i is 'atRisk' | 'amlIssues' | 'overdue' =>
          i === 'atRisk' || i === 'amlIssues' || i === 'overdue'
      );
    };

    const sq = this.readSnapshotString(snapshot, ['searchQuery', 'SearchQuery']);
    if (sq != null) {
      this.clientsSearchQuery.set(sq);
    } else if (!options?.preserveSearchIfMissing) {
      this.clientsSearchQuery.set('');
    }
    this.clientsSortField.set(sortField as 'name' | 'grade' | 'revenue' | 'aml');
    this.clientsSortDir.set(sortDir as 'asc' | 'desc');
    const restoredStatuses = asStatusArr(
      snapshot['statuses'] ?? snapshot['Statuses'] ?? snapshot['status'] ?? snapshot['Status']
    );
    // Legacy snapshots stored active-only as [] — keep that default when rehydrating.
    this.clientsFilterStatuses.set(restoredStatuses.length === 0 ? ['active'] : restoredStatuses);
    this.clientsFilterClientTypes.set(
      this.readSnapshotStringArray(snapshot, ['clientTypes', 'ClientTypes', 'clientType', 'ClientType'])
    );
    const mgr = this.readSnapshotString(snapshot, ['manager', 'Manager']);
    this.clientsFilterManager.set(mgr && mgr.length > 0 ? mgr : 'All managers');
    this.clientsFilterGrades.set(this.readSnapshotStringArray(snapshot, ['grades', 'Grades', 'grade', 'Grade']));
    this.clientsFilterAmlStatuses.set(
      this.readSnapshotStringArray(snapshot, ['amlStatuses', 'AmlStatuses', 'amlStatus', 'AmlStatus'])
    );
    this.clientsFilterServices.set(this.readSnapshotStringArray(snapshot, ['services', 'Services', 'service', 'Service']));
    this.clientsFilterQuick.set(asQuickArr(snapshot['quick'] ?? snapshot['Quick']));

    const cvRaw = snapshot['columnVisibility'] ?? snapshot['ColumnVisibility'];
    if (cvRaw && typeof cvRaw === 'object' && !Array.isArray(cvRaw)) {
      const baseline = defaultClientsColumnVisibilityMap();
      const next: Record<string, boolean> = { ...baseline };
      const fromSave = cvRaw as Record<string, unknown>;
      for (const key of Object.keys(baseline)) {
        const val = fromSave[key];
        if (typeof val === 'boolean') {
          next[key] = val;
        }
      }
      this.clientsColumnVisibility.set(next);
    }
    const orderRaw = snapshot['columnOrder'] ?? snapshot['ColumnOrder'];
    if (Array.isArray(orderRaw)) {
      const order = this.normalizeClientsColumnOrder(
        orderRaw.filter((k): k is string => typeof k === 'string')
      );
      this.clientsColumnOrder.set(order);
    }

    const vm = snapshot['viewMode'] ?? snapshot['ViewMode'];
    if (vm === 'list' || vm === 'grid') {
      this.clientsViewMode.set(vm);
    }
  }

  private serializeClientsListUiForStorage(): string {
    const raw = JSON.parse(this.serializeClientsFilterSnapshotJson()) as Record<string, unknown>;
    const appliedId = this.clientsAppliedSavedViewId();
    return JSON.stringify({
      ...raw,
      v: 2,
      analyticsStripOpen: this.clientsAnalyticsStripOpen(),
      pageTab: this.clientsPageTab(),
      usersSearchQuery: this.clientsUsersSearchQuery(),
      workspaceSection: this.clientsWorkspaceSection(),
      appliedSavedViewId: appliedId != null && Number.isFinite(appliedId) ? appliedId : null,
      appliedSavedViewFilterBaseline: this.clientsAppliedSavedViewFilterBaseline()
    });
  }

  private persistClientsListUiToStorage(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      pmStorageSet(
        PmWorkspaceComponent.clientsListUiStorageKey,
        this.serializeClientsListUiForStorage()
      );
    } catch {
      /* quota / private mode */
    }
  }

  private restoreClientsListUiFromStorage(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      const rawJson = pmStorageGet(PmWorkspaceComponent.clientsListUiStorageKey);
      if (!rawJson?.trim()) {
        return;
      }
      const raw = JSON.parse(rawJson) as Record<string, unknown>;
      const v = raw['v'];
      const versionOk =
        v === undefined ||
        v === null ||
        v === 1 ||
        v === '1' ||
        Number(v) === 1 ||
        v === 2 ||
        v === '2' ||
        Number(v) === 2;
      if (!versionOk) {
        return;
      }
      this.applyClientsFilterSnapshotFields(raw, { preserveSearchIfMissing: true });

      const ao = raw['analyticsStripOpen'];
      if (typeof ao === 'boolean') {
        this.clientsAnalyticsStripOpen.set(ao);
      }

      const pt = raw['pageTab'];
      if (pt === 'clients' || pt === 'users') {
        this.setClientsPageTab(pt);
      }

      const usq = raw['usersSearchQuery'];
      if (typeof usq === 'string') {
        this.clientsUsersSearchQuery.set(usq);
        this.clientsUsersSearchQueryForApi.set(usq.trim());
      }

      const ws = raw['workspaceSection'];
      const validWs = [
        'list',
        'schedule',
        'communication',
        'submissions',
        'letters',
        'aml'
      ] as const satisfies readonly ClientsWorkspaceSection[];
      if (typeof ws === 'string' && (validWs as readonly string[]).includes(ws)) {
        this.setClientsWorkspaceSection(ws as ClientsWorkspaceSection);
      }

      const appliedRaw = raw['appliedSavedViewId'];
      if (appliedRaw === null) {
        this.clearClientsAppliedSavedViewSelection();
      } else if (typeof appliedRaw === 'number' && Number.isFinite(appliedRaw) && appliedRaw > 0) {
        this.clientsAppliedSavedViewId.set(Math.trunc(appliedRaw));
      } else if (typeof appliedRaw === 'string' && /^\d+$/.test(appliedRaw.trim())) {
        const parsed = Number.parseInt(appliedRaw.trim(), 10);
        if (parsed > 0) {
          this.clientsAppliedSavedViewId.set(parsed);
        }
      }

      const baselineRaw = raw['appliedSavedViewFilterBaseline'];
      if (typeof baselineRaw === 'string' && baselineRaw.trim().length > 0) {
        this.clientsAppliedSavedViewFilterBaseline.set(baselineRaw);
      } else if (this.clientsAppliedSavedViewId() != null) {
        this.clientsAppliedSavedViewFilterBaseline.set(this.serializeClientsFilterSnapshotJson());
      }
    } catch {
      /* corrupt storage */
    }
  }

  /**
   * When filters were restored from localStorage but `appliedSavedViewId` was not yet stored,
   * highlight the matching saved view after the list loads.
   */
  private tryMatchAppliedSavedViewFromCurrentFilters(): void {
    if (this.clientsAppliedSavedViewId() != null) {
      return;
    }
    const views = this.clientsSavedViews();
    if (views.length === 0) {
      return;
    }
    let currentRaw: Record<string, unknown>;
    try {
      currentRaw = JSON.parse(this.serializeClientsFilterSnapshotJson()) as Record<string, unknown>;
    } catch {
      return;
    }
    delete currentRaw['v'];
    const currentNorm = JSON.stringify(currentRaw);
    for (const view of views) {
      const json = this.savedViewFilterJsonString(view);
      if (!json) {
        continue;
      }
      try {
        const viewRaw = JSON.parse(json) as Record<string, unknown>;
        delete viewRaw['v'];
        if (JSON.stringify(viewRaw) === currentNorm) {
          const matchedId = this.normalizeClientsSavedViewId(view.id);
          if (matchedId != null) {
            this.clientsAppliedSavedViewId.set(matchedId);
            this.clientsAppliedSavedViewFilterBaseline.set(this.serializeClientsFilterSnapshotJson());
          }
          return;
        }
      } catch {
        /* skip malformed */
      }
    }
  }

  /** API may expose `filterJson` or `FilterJson`; value may be a string or a parsed object. */
  private savedViewFilterJsonString(view: ClientSavedFilterViewItemApi): string {
    const v = view as unknown as Record<string, unknown>;
    const raw = v['filterJson'] ?? v['FilterJson'];
    if (raw == null) {
      return '';
    }
    if (typeof raw === 'string') {
      return raw.trim();
    }
    if (typeof raw === 'object') {
      try {
        return JSON.stringify(raw);
      } catch {
        return '';
      }
    }
    return String(raw).trim();
  }

  clearClientsAppliedSavedViewSelection(): void {
    this.clientsAppliedSavedViewId.set(null);
    this.clientsAppliedSavedViewFilterBaseline.set(null);
  }

  /** Clears filter/search/sort/column state applied by a saved view and reloads the list. */
  deselectClientsSavedViewState(): void {
    this.clearClientsAppliedSavedViewSelection();
    this.clientsFilterStatuses.set(['active']);
    this.clientsFilterClientTypes.set([]);
    this.clientsFilterManager.set('All managers');
    this.clientsFilterGrades.set([]);
    this.clientsFilterAmlStatuses.set([]);
    this.clientsFilterServices.set([]);
    this.clientsFilterQuick.set([]);
    this.clientsSearchQuery.set('');
    this.clientsSortField.set('name');
    this.clientsSortDir.set('asc');
    this.clientsColumnVisibility.set(defaultClientsColumnVisibilityMap());
    this.clientsColumnOrder.set(CLIENTS_CATALOG_FLAT_ORDERED.map((c) => c.key));
    this.clientsViewMode.set('list');
    this.reloadDirectoryClientsListNow(true, true);
  }

  isClientsSavedViewApplied(view: ClientSavedFilterViewItemApi): boolean {
    const appliedId = this.clientsAppliedSavedViewId();
    const viewId = this.normalizeClientsSavedViewId(view.id);
    return appliedId != null && viewId != null && appliedId === viewId;
  }

  private normalizeClientsSavedViewId(id: unknown): number | null {
    if (typeof id === 'number' && Number.isFinite(id) && id > 0) {
      return Math.trunc(id);
    }
    if (typeof id === 'string' && /^\d+$/.test(id.trim())) {
      const parsed = Number.parseInt(id.trim(), 10);
      return parsed > 0 ? parsed : null;
    }
    return null;
  }

  /** Immediate clients list reload for explicit actions (saved views, reset). */
  reloadDirectoryClientsListNow(resetToFirstPage: boolean, forceRefresh = false): void {
    if (!this.userContext.practiceContextReady()) {
      return;
    }
    if (this.activeItem() !== 'Clients' || this.clientsPageTab() !== 'clients') {
      return;
    }
    if (this.clientsDirectoryReloadTimer) {
      clearTimeout(this.clientsDirectoryReloadTimer);
      this.clientsDirectoryReloadTimer = null;
    }
    const targetPage = resetToFirstPage ? 1 : this.clientsDirectoryPageNumber();
    this.clientsDirectoryPageNumber.set(targetPage);
    void this.loadDirectoryClientsPage(targetPage, { forceRefresh });
  }

  toggleClientsSavedViewLock(view: ClientSavedFilterViewItemApi): void {
    if (!this.userContext.practiceContextReady()) {
      return;
    }
    const p = this.effectiveDirectoryParentUserId();
    const u = this.effectiveDirectoryUserId();
    this.pmSettingsApi
      .updateClientSavedFilterView(view.id, p, u, { isLocked: !view.isLocked, updatedBy: null })
      .subscribe({
        next: () => {
          this.loadClientsSavedViews();
          this.notifySuccess(view.isLocked ? 'View unlocked.' : 'View locked.');
        },
        error: (err) => this.notifyError(err)
      });
  }

  clientsFilterStatusSelected(value: ClientsFilterStatus): boolean {
    return this.clientsFilterStatuses().includes(value);
  }

  clientsFilterClientTypeSelected(type: string): boolean {
    return this.clientsFilterClientTypes().includes(type);
  }

  onClientsFilterManagerDropdownToggle(event: { id: string; event: Event }): void {
    this.toggleDropdown(event.id, event.event);
  }

  clientsFilterGradeSelected(grade: string): boolean {
    return this.clientsFilterGrades().includes(grade);
  }

  clientsFilterServiceSelected(svc: string): boolean {
    return this.clientsFilterServices().includes(svc);
  }

  clientsQuickFilterSelected(key: 'atRisk' | 'amlIssues' | 'overdue'): boolean {
    return this.clientsFilterQuick().includes(key);
  }

  private buildDirectoryClientServerFilters() {
    return {
      q: this.clientsSearchQuery().trim() || undefined,
      statuses: this.clientsFilterStatuses(),
      clientTypes: this.clientsFilterClientTypes().map((x) => x.trim()),
      manager: this.clientsFilterManager().trim(),
      grades: this.clientsFilterGrades().map((x) => x.trim()),
      amlStatuses: this.clientsFilterAmlStatuses().map((x) => x.trim()),
      services: this.clientsFilterServices().map((x) => x.trim()),
      quick: this.clientsFilterQuick().map((x) => x.trim()),
      sortField: this.clientsSortField().trim(),
      sortDir: this.clientsSortDir().trim()
    };
  }

  scheduleDirectoryClientsReload(resetToFirstPage: boolean): void {
    if (!this.userContext.practiceContextReady() || !this.clientsDirectoryApiActive()) {
      return;
    }
    if (this.activeItem() !== 'Clients' || this.clientsPageTab() !== 'clients') {
      return;
    }
    if (this.clientsDirectoryReloadTimer) {
      clearTimeout(this.clientsDirectoryReloadTimer);
      this.clientsDirectoryReloadTimer = null;
    }
    this.clientsDirectoryReloadTimer = setTimeout(() => {
      this.clientsDirectoryReloadTimer = null;
      const targetPage = resetToFirstPage ? 1 : this.clientsDirectoryPageNumber();
      this.clientsDirectoryPageNumber.set(targetPage);
      void this.loadDirectoryClientsPage(targetPage);
    }, 220);
  }

  clientDemoRevenue(row: ClientListRow): number {
    return row.revenueAmount;
  }

  /** Company / client type after · in ref line (for filter chips) */
  clientTypeLabelFromRow(row: ClientListRow): string {
    const parts = row.refLine.split('·');
    const raw = (parts.length > 1 ? parts[parts.length - 1] : row.refLine).trim();
    if (raw === 'Ltd') {
      return 'Limited';
    }
    return raw || '—';
  }

  /** Deterministic color per distinct client-type value (Limited/Sole Trader/Partnership/etc.) —
   *  same hash-into-a-fixed-palette approach as the avatar tones, ported from /preview-table. */
  clientsTypeBadgeTone(value: string): string {
    const tones = ['purple', 'blue', 'green', 'amber', 'rose'];
    const hash = value.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return tones[hash % tones.length];
  }

  clientManagerDemo(row: ClientListRow): string {
    const m = (row.managerName ?? '').trim();
    return m || '—';
  }

  clientGradeLabel(row: ClientListRow): string {
    const g = (row.gradeLabel ?? '').trim();
    return g || '—';
  }

  clientAmlStatusLabel(row: ClientListRow): 'Clear' | 'Pending' | 'Review' | 'None' {
    return row.amlStatus;
  }

  clientServiceMatchesFilterChip(rowSvc: string, filterLabel: string): boolean {
    const map: Record<string, readonly string[]> = {
      Accounts: ['Accounts'],
      'VAT Returns': ['VAT'],
      'SA Tax Return': ['Self Assessment'],
      CT600: ['CT'],
      Payroll: ['Payroll'],
      Bookkeeping: ['Bookkeeping'],
      'Confirmation Statement': ['Confirmation Statement'],
      CIS: ['CIS']
    };
    const keys = map[filterLabel];
    const s = rowSvc.toLowerCase();
    if (!keys) {
      return rowSvc.trim() === filterLabel.trim() || s.includes(filterLabel.trim().toLowerCase());
    }
    return keys.some((k) => rowSvc === k || s.includes(k.toLowerCase()));
  }

  /**
   * Stable unique key for list row UI (checkboxes, action menu).
   * Prefer Client Store universal id — many rows share companyMasterId `0` before Capium3 link.
   */
  clientListRowUiKey(row: ClientListRow): string {
    const uid = (row.directoryClientUniversalId ?? '').trim();
    if (uid) {
      return uid.toLowerCase();
    }
    const rawId = String(row.id ?? '').trim();
    const numericId = Number(rawId);
    if (rawId && Number.isFinite(numericId) && numericId > 0) {
      return rawId;
    }
    const name = (row.name ?? '').trim().toLowerCase();
    const ref = (row.refLine ?? '').trim().toLowerCase();
    return `fallback:${name}:${ref}`;
  }

  /** Company master id for directory bulk APIs; empty when the client is not linked in Capium3. */
  clientListRowBulkCompanyMasterId(row: ClientListRow): string {
    const rawId = String(row.id ?? '').trim();
    const numericId = Number(rawId);
    if (rawId && Number.isFinite(numericId) && numericId > 0) {
      return rawId;
    }
    return '';
  }

  /** Client Store universal id for directory bulk / status / delete. */
  clientListRowDirectoryUniversalId(row: ClientListRow): string {
    return (row.directoryClientUniversalId ?? '').trim();
  }

  clientListRowUniversalIdLooksValid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }

  /** True when the row can be bulk-modified via directory API (Client Store and/or Capium3 id). */
  clientListRowSupportsDirectoryBulk(row: ClientListRow): boolean {
    return (
      !!this.clientListRowBulkCompanyMasterId(row) ||
      this.clientListRowUniversalIdLooksValid(this.clientListRowDirectoryUniversalId(row))
    );
  }

  findClientListRowByUiKey(uiKey: string): ClientListRow | undefined {
    const needle = uiKey.trim().toLowerCase();
    return this.clientsDirectoryRows().find((r) => this.clientListRowUiKey(r).toLowerCase() === needle);
  }

  clientRowStatus(id: string): 'active' | 'inactive' {
    const row =
      this.findClientListRowByUiKey(id) ?? this.clientsDirectoryRows().find((r) => r.id === id);
    if (row) {
      return this.clientRowStatusForRow(row);
    }
    const override = this.clientsStatusById()[id];
    if (override !== undefined) {
      return override;
    }
    return 'active';
  }

  clientRowStatusForRow(row: ClientListRow): 'active' | 'inactive' {
    const uiKey = this.clientListRowUiKey(row);
    const bulkId = this.clientListRowBulkCompanyMasterId(row);
    const override =
      this.clientsStatusById()[uiKey] ?? (bulkId ? this.clientsStatusById()[bulkId] : undefined);
    if (override !== undefined) {
      return override;
    }
    if (row.directoryCompanyStatus === 0) {
      return 'inactive';
    }
    return 'active';
  }

  closeClientsListRowActions(): void {
    this.clientsListRowActionsOpenId.set(null);
  }

  async openClientsAddFormModalForEdit(directoryId: string, returnUrl = ''): Promise<void> {
    const parentUserId = this.effectiveDirectoryParentUserId();
    const listUserId = this.effectiveDirectoryUserIdForClientsList();
    if (!directoryId || parentUserId <= 0 || listUserId <= 0) {
      toast.error('Cannot open client for editing', { description: 'User context not ready.' });
      return;
    }
    // Initialize modal with all standard setup (services catalog, custom fields, team, etc.)
    this.openClientsAddFormModal();
    // Immediately override mode to 'edit'
    this.clientsAddFormMode.set('edit');
    this.clientsAddEditReturnUrl.set(returnUrl);
    this.clientsAddEditLoadPending.set(true);
    // Blank while loading: openClientsAddFormModal() applied the saved Add Client draft (browser storage)
    // and create defaults, which otherwise showed until this client's own data replaced them. The stored
    // draft itself is left alone, so Add Client still restores it.
    this.clientsAddDraft.set({ ...emptyClientsAddDraft(), companyName: 'Loading…' });
    this.clientsAddCustomFieldValues.set({});
    this.clientsAddDraftFromStorage.set(false);
    try {
      const row = await firstValueFrom(this.pmDirectoryApi.getClient(directoryId, parentUserId, listUserId));
      const clientUniversalId = String(row.clientUniversalId ?? row.clientId ?? directoryId).trim();
      const directors = await firstValueFrom(
        this.pmDirectoryApi.getDirectors(clientUniversalId).pipe(catchError(() => of([] as DirectorRowApi[])))
      );
      const companyMasterId = String(row.id ?? '').trim();
      this.clientsAddEditClientUniversalId.set(clientUniversalId);
      this.clientsAddEditCompanyMasterId.set(companyMasterId);

      // Map existing client data into the Add Client draft
      const draft = this.mapClientRowToAddClientDraft(row);
      this.clientsAddDraft.set(draft);
      // Unticking "Make this contact email as primary email" restores this; when the box loads already
      // ticked, nothing set it yet, so unticking would have blanked Primary Email.
      this.clientsAddPrimaryEmailSnapshot = draft.primaryEmail;

      // Restore SIC tags from comma-separated string
      const sicStr = String(row.sicCode ?? row.sicCodes ?? '').trim();
      this.clientsAddSicTags.set(sicStr ? sicStr.split(',').map((s) => s.trim()).filter(Boolean) : []);

      // Load existing directors into the directors list
      const dirDrafts: ClientsAddDirectorDraft[] = (directors ?? []).map((r) => ({
        apiId: r.id ?? null,
        officerRole: (r.officerRole ?? 'director').trim(),
        name: (r.name ?? '').trim(),
        lastName: (r.lastName ?? '').trim(),
        email: (r.email ?? '').trim(),
        mobile: (r.mobile ?? '').trim(),
        address: (r.address ?? '').trim(),
        city: (r.city ?? '').trim(),
        county: (r.county ?? '').trim(),
        country: (r.country ?? '').trim(),
        postCode: (r.postCode ?? '').trim(),
        utr: (r.utr ?? '').trim(),
        nino: (r.nino ?? '').trim(),
        appointmentDate: this.isoDateFromApiStr(r.appointmentDate),
        dateCeased: this.isoDateFromApiStr(r.dateCeased),
        share: '', shareType: '', shareValue: '', salary: '',
        shareholder: r.shareholder === true,
        isActive: r.isActive !== false,
        isCreateClientForSa: r.isCreateClientForSa === true,
        saClientId: '',
        saClientUniversalId: r.saClientUniversalId ?? null,
      }));
      this.clientsAddDirectors.set(dirDrafts);
      this.clientsAddDirectorsSeedVersion.update((v) => v + 1);
      this.clientsAddDirectorDeleteIds.set([]);
      this.clientsAddPanelsOpen.update((p) => ({ ...p, directors: dirDrafts.length > 0 }));

      // Load custom field values for this client
      if (companyMasterId) {
        try {
          const customFieldRows = await firstValueFrom(
            this.pmSettingsApi.getClientCustomFieldValues(parentUserId, companyMasterId).pipe(catchError(() => of([])))
          );
          const valueMap: Record<number, string> = {};
          for (const fv of customFieldRows ?? []) {
            const id = (fv as { fieldId?: number; id?: number }).fieldId ?? (fv as { id?: number }).id ?? 0;
            const val = (fv as { value?: string; fieldValue?: string }).value ?? (fv as { fieldValue?: string }).fieldValue ?? '';
            if (id > 0) valueMap[id] = val;
          }
          this.clientsAddCustomFieldValues.set(valueMap);
        } catch {
          // silently ignore — custom fields optional
        }
      }

      // Restore primary + key contacts as practice contacts (PM_ContactMaster ids — what the picker and the
      // link endpoint use): the practice contacts whose linkedClients include this client. The client's own
      // GET /clients/{uid}/contacts returns Client Store contact ids, so it is only used to map each practice
      // contact to its Client Store id for unlinking on save.
      this.clientsAddEditOriginalLinkedContactIds.set([]);
      this.clientsAddEditClientStoreContactIdByPracticeId.set({});
      if (clientUniversalId) {
        try {
          const [practiceContacts, clientStoreContacts] = await Promise.all([
            firstValueFrom(
              this.pmDirectoryApi.getContacts(parentUserId, { userId: this.effectiveDirectoryActingUserIdForContacts() })
                .pipe(catchError(() => of([] as Capium3ContactRowApi[])))
            ),
            firstValueFrom(
              this.pmDirectoryApi.getContactsForClient(clientUniversalId, parentUserId, listUserId)
                .pipe(catchError(() => of([] as Capium3ContactRowApi[])))
            )
          ]);
          const linkedContacts = practiceContactsLinkedToClient(practiceContacts ?? [], clientUniversalId);
          this.clientsAddEditClientStoreContactIdByPracticeId.set(
            mapPracticeToClientStoreContactIds(linkedContacts, clientStoreContacts ?? [])
          );
          const realPrimary = linkedContacts.find(c => c.isPrimaryContact === true) ?? null;
          if (realPrimary) {
            this.clientsAddDraft.update(d => ({ ...d, selectedContactId: String(realPrimary.id) }));
          }
          const primaryNumId = realPrimary ? Number(realPrimary.id) : null;
          const keyIds = (linkedContacts ?? [])
            .filter(c => Number(c.id) !== primaryNumId)
            .map(c => String(c.id));
          this.clientsAddKeyContactIds.set(keyIds);
          this.clientsAddEditOriginalLinkedContactIds.set((linkedContacts ?? []).map(c => String(c.id)));
        } catch {
          // Non-critical step: a failure here is ignored on purpose.
        }
      }
    } catch (e) {
      toast.error('Could not load client for editing', { description: String(e instanceof Error ? e.message : e).slice(0, 200) });
      this.closeClientsAddFormModal();
    } finally {
      this.clientsAddEditLoadPending.set(false);
    }
  }

  private mapClientRowToAddClientDraft(row: DirectoryClientRowApi): ClientsAddFormDraft {
    const phoneRaw = String(row.phone ?? '').trim();
    const primaryEmail = String(row.eMail ?? row.contactEmail ?? '').trim();
    const contactEmail = String(row.contactEmail ?? row.eMail ?? '').trim();
    const companyType = String(row.companyType ?? '').trim();
    const contactName = [row.contactForename, row.contactSurname]
      .map((x) => String(x ?? '').trim()).filter(Boolean).join(' ') ||
      String(row.contactName ?? '').trim();
    const countryId = (row.countryId ?? row.countryMasterId) != null &&
      Number.isFinite(Number(row.countryId ?? row.countryMasterId)) &&
      Number(row.countryId ?? row.countryMasterId) > 0
      ? Math.trunc(Number(row.countryId ?? row.countryMasterId))
      : 222;
    const managerUserId = row.managerUserId != null && Number.isFinite(Number(row.managerUserId))
      ? Number(row.managerUserId) : null;
    const partnerUserId = row.partnerUserId != null && Number.isFinite(Number(row.partnerUserId))
      ? Number(row.partnerUserId) : null;
    const servicesRaw = (row as { services?: unknown }).services;
    const serviceNames: string[] = typeof servicesRaw === 'string'
      ? servicesRaw.split(',').map((s: string) => s.trim()).filter(Boolean)
      : Array.isArray(servicesRaw) ? (servicesRaw as string[]).map((s) => String(s).trim()).filter(Boolean) : [];
    const selectedServiceKeys = this.mapClientServiceNamesToTrackKeys(serviceNames);
    return {
      ...emptyClientsAddDraft(),
      clientId: String(row.clientCode ?? row.internalReference ?? '').trim(),
      companyName: String(row.companyName ?? '').trim(),
      companyType,
      statusActive: Number(row.companyStatus ?? 1) !== 0,
      registrationNumber: String(row.registrationNumber ?? row.registeredIn ?? '').trim(),
      primaryEmail,
      secondaryEmail: String(row.secondaryEmail ?? row.registeredEmail ?? '').trim(),
      phone: phoneRaw,
      contactPersonName: contactName,
      contactPersonEmail: contactEmail,
      contactPersonPhone: String(row.phone ?? '').replace(/\D/g, '').slice(0, 15),
      contactName,
      contactEmail,
      contactPhone: String(row.phone ?? '').replace(/\D/g, '').slice(0, 15),
      address1: String(row.addressLine1 ?? row.address1 ?? '').trim(),
      address2: String(row.addressLine2 ?? row.address2 ?? '').trim(),
      city: String(row.city ?? '').trim(),
      county: String(row.county ?? '').trim(),
      country: countryId === 222 ? 'United Kingdom' : 'United Kingdom',
      countryId,
      postCode: String(row.postalCode ?? row.postCode ?? '').trim(),
      website: String(row.website ?? '').trim(),
      internalReference: String(row.internalReference ?? row.companyRef ?? '').trim(),
      officeId: String(row.officeId ?? '').trim(),
      sicCode: String(row.sicCode ?? row.sicCodes ?? '').trim(),
      utrNumber: String(row.utrNumber ?? row.taxRef ?? '').trim(),
      nationalInsuranceNo: String(row.nationalInsuranceNo ?? '').trim().toUpperCase(),
      payeRefNo: String(row.payeRefNo ?? '').trim(),
      accountOfficeRefNo: String(row.accountOfficeRefNo ?? '').trim(),
      idvReference: String(row.idvReference ?? '').trim(),
      vatRegistrationNumber: String(row.vatRegistrationNumber ?? row.vatRegNo ?? '').trim(),
      ...this.clientsAddAccountingSystemSelection(String(row.accountingSystem ?? '')),
      billingCycle: String(row.billingCycle ?? '').trim(),
      ...this.clientsAddSourceSelection(String(row.source ?? '')),
      referredBy: String((row as { referredBy?: string | null }).referredBy ?? '').trim(),
      vatScheme: String(row.vatScheme ?? '').trim() || 'Non VAT Registered',
      vatSchemeId: this.parsePositiveIntLocal(row.vatSchemeId),
      currencyId: this.parsePositiveIntLocal(row.currencyId),
      vatReturnTypeId: this.parsePositiveIntLocal(row.vatReturnTypeId),
      amlStatus: this.normalizeClientsAddAmlStatus(row.amlStatus),
      yearEndDate: this.yearEndIsoFromPartsLocal(row.yearEndDay ?? row.finEndDay, row.yearEndMonth ?? row.finEndMonth),
      businessStartDate: this.isoDateFromApiStr(row.startDate ?? null),
      bookStartDate: this.isoDateFromApiStr(row.bookStart ?? null),
      vatRegistrationDate: this.isoDateFromApiStr(row.vatRegisteredDate ?? null),
      idCheckDate: this.isoDateFromApiStr(row.idCheckDate ?? null),
      proposalLetterDate: this.isoDateFromApiStr(row.proposalLetterDate ?? null),
      engagementLetterDate: this.isoDateFromApiStr(row.engagementLetterDate ?? null),
      managerUserId,
      partnerUserId,
      segment: String(row.segment ?? row.segmentGrade ?? '').trim() || 'Bronze',
      segmentGrade: String(row.segmentGrade ?? row.segment ?? '').trim(),
      dateOfBirth: this.isoDateFromApiStr((row as { dateOfBirth?: string | null }).dateOfBirth ?? null),
      isSALinking: !!(row as { isSALinking?: boolean }).isSALinking,
      saLinkCompanyMasterId: this.parsePositiveIntLocal((row as { saLinkCompanyMasterId?: unknown }).saLinkCompanyMasterId),
      ownerName: String((row as { ownerName?: string | null }).ownerName ?? '').trim(),
      hmrcOfficeNo: String((row as { hmrcOfficeNo?: string | null }).hmrcOfficeNo ?? '').trim(),
      selectedServiceKeys,
      // Not row.contactId: that is the Client Store contact id, while the picker and the link endpoint use
      // practice contact (PM_ContactMaster) ids. The edit loader fills in the real primary.
      selectedContactId: '',
      // As saved on Add/Edit Client (PM_ClientPracticeProfile); never saved = false.
      syncContactEmailToPrimary: row.syncContactEmailToPrimary === true
    };
  }

  private isoDateFromApiStr(value: string | null | undefined): string {
    const s = String(value ?? '').trim();
    if (!s) return '';
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    const d = new Date(s);
    return isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private parsePositiveIntLocal(raw: unknown): number | null {
    const n = Number(String(raw ?? '').trim());
    return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
  }

  private yearEndIsoFromPartsLocal(day: number | null | undefined, month: number | null | undefined): string {
    const d = day != null && day > 0 ? Math.trunc(day) : 0;
    const m = month != null && month > 0 ? Math.trunc(month) : 0;
    if (d <= 0 || m <= 0 || m > 12) return '';
    // Day/month only (no real year) — fixed leap year so 29 Feb round-trips correctly instead of
    // clamping to 28 Feb whenever the real current year isn't a leap year.
    const year = 2000;
    const lastDay = new Date(year, m, 0).getDate();
    const safeDay = Math.min(d, lastDay);
    return `${year}-${String(m).padStart(2, '0')}-${String(safeDay).padStart(2, '0')}`;
  }

  resetClientsImportWizardState() {
    this.clientsImportStep.set('upload');
    this.clientsImportFileName.set('');
    this.clientsImportPreviewRows.set([]);
    this.clientsImportFilterTab.set('all');
    this.clientsImportSavePending.set(false);
    this.clientsImportSaveResult.set(null);
    this.clientsImportSaveError.set(null);
  }

  clearClientsSelection() {
    this.clientsRowCheck.set({});
  }

  private mapDirectoryClientRowToListRow(row: DirectoryClientRowApi): ClientListRow {
    const name = (row.companyName ?? '—').trim() || '—';
    const clientRef = (row.clientUniversalId ?? row.clientId ?? '').trim();
    // pm-api sends Id=0 for any client with no legacy SQL id (i.e. created natively in Client
    // Store rather than migrated) — see ClientStoreDirectoryMapper.cs. Falling back to the
    // client's universal id keeps this row's identity unique; without it, every such client
    // collapses to id "0", causing duplicate-key crashes and wrong "/clients/0/..." API calls
    // wherever this row's id is used to pick or look up a client.
    const numericRowId = Math.trunc(Number(row.id)) || 0;
    const id = numericRowId > 0 ? String(numericRowId) : (clientRef || String(numericRowId));
    const clientCode = (row.clientCode ?? row.companyRef ?? '').trim();
    const ctype = (row.companyType ?? '').trim();
    const refLine = [clientCode, ctype].filter(Boolean).join(' · ') || ctype || '—';
    const regNo = (row.registrationNumber ?? row.registeredIn ?? '').trim() || '—';
    const sicCode = (row.sicCode ?? row.sicCodes ?? '').trim() || null;
    const addressLine = (row.addressLine1 ?? row.address1 ?? '').trim();
    const city = (row.city ?? '').trim();
    const county = (row.county ?? '').trim();
    const address = [addressLine, city, county].filter(Boolean).join(', ') || null;
    const postCode = (row.postalCode ?? row.postCode ?? '').trim() || null;
    const vatNumber = (row.vatRegistrationNumber ?? row.vatRegNo ?? '').trim() || null;
    const utrNumber = (row.utrNumber ?? row.taxRef ?? '').trim() || null;
    const payeRefNo = (row.payeRefNo ?? '').trim() || null;
    const accountOfficeRefNo = (row.accountOfficeRefNo ?? '').trim() || null;
    const idvReference = (row.idvReference ?? '').trim() || null;
    const dateOfBirth = formatDirectoryActivity(row.dateOfBirth);
    const niNo = (row.nationalInsuranceNo ?? '').trim() || null;
    const checkResult = (row.result ?? '').trim() || null;
    const lastCheckType = formatAmlCheckTypeLabel(row.amlCheckType);
    const nextCheckDate = formatDirectoryActivity(row.nextCheckDate);
    const companyMasterId = Math.trunc(Number(row.id)) || 0;
    const capiumPayStatus = formatCapiumPayStatusLabelForClient(row.onBoardingStatus, {
      companyType: ctype,
      companyMasterId
    });
    const capiumPayInviteLink = (row.capiumPayInviteLink ?? '').trim() || null;
    const vatRegDate = formatDirectoryActivity(row.vatRegisteredDate);
    const yearEndDay = Number(row.yearEndDay ?? row.finEndDay ?? 0);
    const yearEndMonth = Number(row.yearEndMonth ?? row.finEndMonth ?? 0);
    const yearEnd =
      yearEndDay > 0 && yearEndMonth > 0
        ? formatDayMonthInAppOrder(yearEndDay, yearEndMonth)
        : null;
    const businessStart = formatDirectoryActivity(row.startDate);
    const createdOn = formatDirectoryActivity(row.createdOn);
    const lastUpdatedOn = formatDirectoryActivity(row.lastUpdatedOn);
    const idCheckDate = formatDirectoryActivity(row.idCheckDate);
    const loeSigned = formatDirectoryActivity(row.engagementLetterDate);
    const openTasksCount = Math.max(0, Math.trunc(Number(row.openTasksCount ?? 0)) || 0);
    const overdueTasksCount = Math.max(0, Math.trunc(Number(row.overdueTasksCount ?? 0)) || 0);
    const openRequestsCount = Math.max(0, Math.trunc(Number(row.openRequestsCount ?? 0)) || 0);
    const portalEnabled = row.portalEnabled === true || Number(row.isInviteMailSent ?? 0) > 0;
    const officeId = String(row.officeId ?? '').trim() || null;
    const revenueAmount = Math.max(0, Number(row.revenueGbp ?? 0) || 0);
    const turnoverAmount = Math.max(0, Number(row.turnoverGbp ?? row.revenueGbp ?? 0) || 0);
    const overdueInvoiceCount = Math.max(0, Math.trunc(Number(row.overdueInvoiceCount ?? 0)) || 0);
    const overdueInvoiceAmount = Math.max(0, Number(row.overdueInvoiceGbp ?? 0) || 0);
    const pct = row.onBoardingPercentage;
    const healthScore =
      pct != null && pct >= 0 && pct <= 100 ? Math.round(pct) : 0;
    const email = (row.contactEmail ?? row.eMail ?? '').trim() || '—';
    const contactPersonNameOnly = (row.contactName ?? '').trim();
    const directoryContactId =
      row.contactId != null && Number(row.contactId) > 0 ? Math.trunc(Number(row.contactId)) : null;
    const directoryPhone = (row.phone ?? '').trim();
    const contactNumber = (row.contactPhone ?? '').trim() || directoryPhone || null;
    // Falls back to the contact's email (already computed above, itself '—' when there truly
    // is none) rather than straight to '—' -- a primary contact with an email on file but no
    // name synced yet was rendering the Contact column's "+ Add Contact" prompt even though a
    // contact clearly IS set (the Contact Email column right next to it shows the same email).
    const contactName = contactPersonNameOnly || email;
    const amlStatus = this.inferAmlStatusFromDirectoryRow(row);
    const amlClear = amlStatus === 'Clear';
    const deadlines = this.inferDeadlinesFromDirectoryRow(row);
    const serviceTitles = (row.services ?? []).filter((s) => !!String(s).trim());
    // `services` keeps the existing first-3 behaviour so the client-detail
    // header strip (which has horizontal room) renders unchanged. The
    // clients-list TABLE cell uses `allServices` directly so it can show the
    // first one + a "+N services" pill with a hover popover containing the
    // full list (see `case ('services')` in pm-workspace.component.html).
    const visibleServices = serviceTitles.slice(0, 3);
    const tones: ClientListRow['avatarTone'][] = ['purple', 'blue', 'green', 'amber', 'rose'];
    const contactTones: ClientListRow['contactTone'][] = ['purple', 'blue', 'slate'];
    return {
      id,
      directoryClientUniversalId: clientRef || null,
      name,
      refLine,
      regNo,
      segmentLabel: (row.segment ?? '').trim() || null,
      gradeLabel: (row.segmentGrade ?? row.segment ?? '').trim() || null,
      managerName: (row.managerName ?? '').trim() || null,
      partnerName: (row.partnerName ?? '').trim() || null,
      accountingSystem: (row.accountingSystem ?? '').trim() || null,
      billingCycle: (row.billingCycle ?? '').trim() || null,
      source: (row.source ?? '').trim() || null,
      referredBy: (row.referredBy ?? '').trim() || null,
      vatScheme: (row.vatScheme ?? '').trim() || null,
      sicCode,
      phone: directoryPhone || null,
      address,
      postCode,
      website: (row.website ?? '').trim() || null,
      vatNumber,
      utrNumber,
      payeRefNo,
      accountOfficeRefNo,
      idvReference,
      contactNumber,
      dateOfBirth: dateOfBirth === '—' ? null : dateOfBirth,
      niNo,
      checkResult,
      lastCheckType: lastCheckType === '—' ? null : lastCheckType,
      nextCheckDate: nextCheckDate === '—' ? null : nextCheckDate,
      capiumPayStatus: capiumPayStatus === '—' ? null : capiumPayStatus,
      companyType: ctype || '—',
      companyMasterId,
      onBoardingStatus: row.onBoardingStatus ?? null,
      onBoardingPercentage: row.onBoardingPercentage ?? null,
      isInviteMailSent: Math.max(0, Math.trunc(Number(row.isInviteMailSent ?? 0)) || 0),
      capiumPayInviteLink,
      vatRegDate: vatRegDate === '—' ? null : vatRegDate,
      yearEnd,
      businessStart: businessStart === '—' ? null : businessStart,
      createdOn: createdOn === '—' ? null : createdOn,
      lastUpdatedOn: lastUpdatedOn === '—' ? null : lastUpdatedOn,
      lastActivityOn: lastUpdatedOn === '—' ? null : lastUpdatedOn,
      portalEnabled,
      officeId,
      revenueAmount,
      turnoverAmount,
      overdueInvoiceCount,
      overdueInvoiceAmount,
      clientCode: (row.clientCode ?? row.companyRef ?? '').trim() || null,
      internalRef: (row.companyRef ?? row.clientCode ?? '').trim() || null,
      healthScore,
      openTasksCount,
      overdueTasksCount,
      openRequestsCount,
      idCheckDate: idCheckDate === '—' ? null : idCheckDate,
      loeSigned: loeSigned === '—' ? null : loeSigned,
      contactName,
      directoryPhone,
      contactPersonNameOnly,
      directoryContactId,
      contactInitials: contactInitialsFromName(contactPersonNameOnly || contactName),
      contactEmail: email,
      deadlinesLabel: deadlines.label,
      deadlinesVariant: deadlines.variant,
      amlStatus,
      amlClear,
      activityText: lastUpdatedOn !== '—' ? lastUpdatedOn : formatDirectoryActivity(row.checkedDate),
      services: visibleServices,
      extraServiceCount: Math.max(0, serviceTitles.length - visibleServices.length),
      allServices: serviceTitles,
      avatarTone: hashPickTone(id, tones),
      contactTone: hashPickTone(id, contactTones),
      directoryCompanyStatus: row.companyStatus ?? null,
    };
  }

  private inferAmlStatusFromDirectoryRow(row: DirectoryClientRowApi): 'Clear' | 'Pending' | 'Review' | 'None' {
    const saved = (row.amlStatus ?? '').trim().toLowerCase();
    if (saved === 'clear') {
      return 'Clear';
    }
    if (saved === 'pending' || saved === 'not started') {
      return 'Pending';
    }
    if (saved === 'review') {
      return 'Review';
    }
    if (saved === 'none') {
      return 'None';
    }
    const result = (row.result ?? '').trim().toLowerCase();
    const amlType = (row.amlCheckType ?? '').trim().toLowerCase();
    const checkCount = row.checkCount ?? 0;

    if (!result && !amlType && checkCount <= 0) {
      return 'None';
    }
    if (result.includes('clear') || result.includes('pass')) {
      return 'Clear';
    }
    if (result.includes('review') || result.includes('refer') || result.includes('manual')) {
      return 'Review';
    }
    if (
      result.includes('pending') ||
      result.includes('in progress') ||
      result.includes('not started') ||
      result.includes('fail') ||
      amlType.includes('not started')
    ) {
      return 'Pending';
    }
    if (checkCount <= 0 && !result) {
      return 'None';
    }
    return 'Pending';
  }

  private inferDeadlinesFromDirectoryRow(row: DirectoryClientRowApi): {
    label: string;
    variant: ClientListRow['deadlinesVariant'];
  } {
    const next = row.nextCheckDate ? new Date(row.nextCheckDate) : null;
    if (!next || Number.isNaN(next.getTime())) {
      return { label: '—', variant: 'due-soon' };
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(next);
    d.setHours(0, 0, 0, 0);
    const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
    if (diff < 0) {
      return { label: `Next check ${Math.abs(diff)}d ago`, variant: 'overdue' };
    }
    if (diff === 0) {
      return { label: 'Next check today', variant: 'due-today' };
    }
    return { label: `Next check in ${diff}d`, variant: 'due-soon' };
  }

  async loadClientsCapiumPayAccess(): Promise<void> {
    const userId = this.userContext.userMasterId();
    if (userId <= 0) {
      this.clientsCapiumPayAccess.set(false);
      this.clientsCapiumPayAccessLoaded.set(false);
      return;
    }
    try {
      const res = await firstValueFrom(this.capiumPayApi.getAccess(userId));
      this.clientsCapiumPayAccess.set(res.hasAccess === true);
    } catch {
      this.clientsCapiumPayAccess.set(false);
    } finally {
      this.clientsCapiumPayAccessLoaded.set(true);
    }
  }

  closeClientsCapiumPayInviteModal(): void {
    this.clientsCapiumPayInviteModalOpen.set(false);
    this.clientsCapiumPayInviteRow.set(null);
    this.clientsCapiumPayActiveDropdown.set(null);
    this.clientsCapiumPayCountries.set([]);
    this.clientsCapiumPayInviteDraft.set({
      clientName: '',
      firstName: '',
      lastName: '',
      emailId: '',
      phoneNo: '',
      countryId: 0
    });
  }

  async loadDirectoryClientsPage(page: number, options?: { forceRefresh?: boolean }): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      return;
    }
    const seq = ++this.directoryClientsRequestSeq;
    this.clientsDirectoryLoading.set(true);
    this.clientsDirectoryLoadFailed.set(false);
    try {
      const filters = this.buildDirectoryClientServerFilters();
      const parent = this.effectiveDirectoryParentUserId();
      const listUserId = this.effectiveDirectoryUserIdForClientsList();
      // Fetch the WHOLE filtered list, then sort + paginate client-side. The directory API sorts each
      // server page in isolation AND caps how many rows one page returns (~100), so server pagination
      // made the A-Z order restart every page and never exposed more than a page at once. We walk all
      // server pages and concatenate them; clientsSortedRows() then orders the full set and
      // clientsDirectoryDisplayRows() slices the current display page from it.
      const forceRefresh = options?.forceRefresh === true;
      const FETCH_PAGE_SIZE = 500;
      const first = await firstValueFrom(
        this.directoryClientsCache.getClients(parent, listUserId, 1, FETCH_PAGE_SIZE, {
          ...filters,
          forceRefresh
        })
      );
      if (seq !== this.directoryClientsRequestSeq) {
        return;
      }
      const rawTotal = (first as { totalCount?: unknown; TotalCount?: unknown }).totalCount;
      const altTotal = (first as { totalCount?: unknown; TotalCount?: unknown }).TotalCount;
      const parsedTotal = Number(rawTotal ?? altTotal);
      const total = Number.isFinite(parsedTotal) && parsedTotal >= 0 ? Math.trunc(parsedTotal) : first.items.length;
      const items = [...first.items];
      // `first.items.length` is the server's effective per-page cap (it truncates below what we asked
      // for). Pull the remaining pages at that same size so offsets line up, until we have them all.
      const serverPageSize = first.items.length;
      if (serverPageSize > 0 && items.length < total) {
        const pageCount = Math.ceil(total / serverPageSize);
        for (let p = 2; p <= pageCount; p++) {
          const next = await firstValueFrom(
            this.directoryClientsCache.getClients(parent, listUserId, p, serverPageSize, {
              ...filters,
              forceRefresh
            })
          );
          if (seq !== this.directoryClientsRequestSeq) {
            return;
          }
          if (next.items.length === 0) {
            break;
          }
          items.push(...next.items);
        }
      }
      const rows = items.map((item) => this.mapDirectoryClientRowToListRow(item));
      this.clientsDirectoryRows.set(rows);
      this.clientsDirectoryTotalCount.set(rows.length);
      // Clamp the requested display page to the range now that the full count is known.
      const displaySize = Math.max(1, this.clientsDirectoryPageSize());
      const totalPages = Math.max(1, Math.ceil(rows.length / displaySize) || 1);
      this.clientsDirectoryPageNumber.set(Math.min(Math.max(1, page || 1), totalPages));
      this.clientsDirectoryApiActive.set(true);
      void this.loadClientsCapiumPayAccess();
      void this.loadDirectoryClientsFirmCount();
      void this.ensureClientDetailRowFromDirectory();
    } catch (e: unknown) {
      if (seq !== this.directoryClientsRequestSeq) {
        return;
      }
      this.clientsDirectoryLoadFailed.set(true);
      // Keep last successful rows visible on transient failures.
      if (this.clientsDirectoryRows().length === 0) {
        this.clientsDirectoryApiActive.set(false);
      }
      const description =
        e instanceof HttpErrorResponse
          ? typeof e.error === 'object' && e.error && 'detail' in e.error
            ? String((e.error as { detail?: string }).detail)
            : e.message
          : e instanceof Error
            ? e.message
            : 'Request failed';
      toast.error('Could not load clients from directory', { description: description.slice(0, 240) });
    } finally {
      if (seq === this.directoryClientsRequestSeq) {
        this.clientsDirectoryLoading.set(false);
        this.clientsDirectoryFirstLoadDone.set(true);
      }
    }
  }

  /**
   * Loads the full, unfiltered firm-wide client set into {@link clientsAnalyticsBaselineRows} for
   * the analytics strip — independent of loadDirectoryClientsPage (which reflects the current
   * filter/search state). Uses the dedicated `/directory/clients/all` endpoint (already cached
   * with a 120s TTL by PmDirectoryClientsCacheService), so repeated calls — e.g. every time the
   * user re-enters the Clients tab — are cheap.
   */
  private async loadClientsAnalyticsBaseline(): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      return;
    }
    try {
      const parent = this.effectiveDirectoryParentUserId();
      const listUserId = this.effectiveDirectoryUserIdForClientsList();
      const all = await firstValueFrom(this.directoryClientsCache.getAllClients(parent, listUserId));
      this.clientsAnalyticsBaselineRows.set((all.items ?? []).map((item) => this.mapDirectoryClientRowToListRow(item)));
    } catch {
      // Non-fatal: the analytics strip just keeps showing its last-known values.
    }
  }

  /**
   * Loads real, practice-wide non-draft invoice totals per client for the Revenue Leaders card —
   * `GET /api/v1/finance/invoices/capium3/summaries/practice` returns every invoice for every
   * client in one unpaged call (already wired into PmFinanceInvoicesApiService but not previously
   * consumed anywhere), so this aggregates client-side into a clientUniversalId → total map rather
   * than requiring one request per client.
   */
  private async loadClientsAnalyticsRevenue(): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      return;
    }
    try {
      const parent = this.effectiveDirectoryParentUserId();
      const acting = this.effectiveDirectoryUserId();
      const directoryUserId = this.effectiveDirectoryUserIdForClientsList();
      if (parent <= 0 || acting <= 0) {
        return;
      }
      const rows = await firstValueFrom(
        this.pmFinanceInvoicesApi.listCapium3PracticeSummaries(parent, acting, directoryUserId)
      );
      const totals = new Map<string, number>();
      for (const row of rows ?? []) {
        if (row.invoice?.isDraft) {
          continue;
        }
        const uid = (row.clientUniversalId ?? '').trim();
        if (!uid) {
          continue;
        }
        const amount = Number(row.invoice?.grossAmount) || 0;
        totals.set(uid, (totals.get(uid) ?? 0) + amount);
      }
      this.clientsAnalyticsRevenueByClient.set(totals);
    } catch {
      // Non-fatal: Revenue Leaders just keeps showing its last-known values.
    }
  }

  private async loadDirectoryClientsFirmCount(): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      this.clientsDirectoryFirmTotalCount.set(null);
      return;
    }
    const parent = this.effectiveDirectoryParentUserId();
    const listUserId = this.effectiveDirectoryUserIdForClientsList();
    if (parent <= 0 || listUserId <= 0) {
      return;
    }
    const seq = ++this.directoryClientsFirmCountRequestSeq;
    try {
      const count = await firstValueFrom(this.directoryClientsCache.getClientsCount(parent, listUserId));
      if (seq !== this.directoryClientsFirmCountRequestSeq) {
        return;
      }
      this.clientsDirectoryFirmTotalCount.set(count);
    } catch {
      // Keep the previous count visible; callers fall back to page/list counts when this remains null.
    }
  }

  /** Refreshes the meeting/picker client list when schedule UI, dashboard filters, or an open picker needs it. */
  refreshBookMeetingPickerIfNeeded(force = false): void {
    if (
      force ||
      this.activeItem() === 'Dashboard' ||
      this.clientsWorkspaceSection() === 'schedule' ||
      this.clientsBookMeetingModalOpen() ||
      this.clientsBookMeetingPickerRows().length > 0
    ) {
      void this.loadClientsForBookMeetingPicker(force);
    }
  }

  /**
   * Lightweight full client lookup (`GET …/directory/clients/optional`) — same as Tasks / Finance hubs.
   */
  private async fetchDirectoryClientPickerApiRows(
    parent: number,
    listUserId: number
  ): Promise<DirectoryClientRowApi[]> {
    let pageRes = await firstValueFrom(this.directoryClientsCache.getClientsOptional(parent, listUserId));
    let items = (pageRes.items ?? []).filter(isSelectableDirectoryClientRow);
    const acting = this.effectiveDirectoryUserId();
    if (items.length === 0 && acting > 0 && acting !== listUserId) {
      pageRes = await firstValueFrom(this.directoryClientsCache.getClientsOptional(parent, acting));
      items = (pageRes.items ?? []).filter(isSelectableDirectoryClientRow);
    }
    return items;
  }

  /**
   * Loads directory clients for the Book Meeting dropdown, schedule “client” filter, and dashboard filters.
   */
  async loadClientsForBookMeetingPicker(force = false): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      this.clientsBookMeetingPickerRows.set([]);
      this.directoryClientPickerApiRows.set([]);
      this.bookMeetingPickerCacheKey = '';
      this.clientsDirectoryFirmTotalCount.set(null);
      return;
    }
    const parent = this.effectiveDirectoryParentUserId();
    const listUserId = this.effectiveDirectoryUserIdForClientsList();
    if (parent <= 0 || listUserId <= 0) {
      return;
    }
    void this.loadDirectoryClientsFirmCount();
    const cacheKey = `${parent}:${listUserId}:optional`;
    if (!force && cacheKey === this.bookMeetingPickerCacheKey && this.directoryClientPickerApiRows().length > 0) {
      return;
    }
    const seq = ++this.bookMeetingPickerRequestSeq;
    this.bookMeetingClientsLoading.set(true);
    try {
      const items = await this.fetchDirectoryClientPickerApiRows(parent, listUserId);
      if (seq !== this.bookMeetingPickerRequestSeq) {
        return;
      }
      this.directoryClientPickerApiRows.set(items);
      this.clientsBookMeetingPickerRows.set(items.map((item) => this.mapDirectoryClientRowToListRow(item)));
      this.bookMeetingPickerCacheKey = cacheKey;
    } catch (e: unknown) {
      if (seq !== this.bookMeetingPickerRequestSeq) {
        return;
      }
      this.directoryClientPickerApiRows.set([]);
      this.clientsBookMeetingPickerRows.set([]);
      this.bookMeetingPickerCacheKey = '';
      const activeSection = this.activeItem();
      if (activeSection === 'Clients' || activeSection === 'Dashboard' || activeSection === 'Practice') {
        toast.error('Could not load clients for meeting picker', {
          description: this.pmApiHttpErrorDetail(e).slice(0, 240)
        });
      }
    } finally {
      if (seq === this.bookMeetingPickerRequestSeq) {
        this.bookMeetingClientsLoading.set(false);
      }
    }
  }

  mergedDirectoryClientRowsForScheduleLabels(): ClientListRow[] {
    const byId = new Map<string, ClientListRow>();
    for (const r of this.clientsBookMeetingPickerRows()) {
      byId.set(r.id, r);
    }
    for (const r of this.clientsDirectoryRows()) {
      if (!byId.has(r.id)) {
        byId.set(r.id, r);
      }
    }
    return [...byId.values()];
  }

  findClientRowForBookMeetingPicker(clientListId: string): ClientListRow | undefined {
    const id = clientListId.trim();
    if (!id) {
      return undefined;
    }
    return (
      this.clientsBookMeetingPickerRows().find((r) => r.id === id) ??
      this.clientsDirectoryRows().find((r) => r.id === id)
    );
  }

  /** Custom-styled "Rows per page" dropdown for the Users tab's grid-view pagination footer —
   *  the list view gets this for free from <pm-data-grid>'s own built-in pagination; grid view
   *  has its own separate footer (see clients-directory-pagination) so needs its own instance. */
  clientsUsersGridPageSizeMenuOpen = signal(false);

  @HostListener('document:click')
  closeClientsUsersGridPageSizeMenu(): void {
    this.clientsUsersGridPageSizeMenuOpen.set(false);
  }

  /** Loads unfiltered firm-wide user count once per practice scope (Users tab badge). */
  async ensureClientUsersFirmTotalCount(): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      this.clientsUsersFirmTotalCount.set(0);
      return;
    }
    const parentId = this.effectiveDirectoryParentUserId();
    const actingUserId = this.effectiveDirectoryActingUserIdForContacts();
    if (parentId <= 0 || actingUserId == null || actingUserId <= 0) {
      return;
    }
    const scopeKey = `${parentId}|${actingUserId}`;
    if (this.lastClientsUsersFirmCountKey === scopeKey && this.clientsUsersFirmTotalCount() > 0) {
      return;
    }
    this.lastClientsUsersFirmCountKey = scopeKey;
    const seq = ++this.clientsUsersCountRequestSeq;
    try {
      const res = await firstValueFrom(
        this.pmDirectoryApi.getClientUsersCount(parentId, { userId: actingUserId })
      );
      if (seq !== this.clientsUsersCountRequestSeq) {
        return;
      }
      const n = Number(res.count);
      this.clientsUsersFirmTotalCount.set(Number.isFinite(n) && n >= 0 ? Math.trunc(n) : 0);
    } catch {
      // Keep previous firm total visible on transient failures.
    }
  }

  private clientsUsersHasActiveAdvancedFilters(): boolean {
    return this.clientsUsersActiveFilterCount() > 0;
  }

  private clientUserMatchesAdvancedFilters(row: ClientUserListRow): boolean {
    const types = this.clientsUsersFilterUserTypes();
    if (types.length > 0 && !types.includes(1)) {
      return false;
    }
    const statuses = this.clientsUsersFilterStatuses();
    if (statuses.length > 0) {
      const active = row.isActive;
      const match =
        (statuses.includes('active') && active) || (statuses.includes('inactive') && !active);
      if (!match) {
        return false;
      }
    }
    const clientId = this.clientsUsersFilterClientId().trim();
    if (clientId && row.companyMasterId !== clientId) {
      return false;
    }
    if (
      !this.clientUserIsoInDateRange(
        row.dateCreated,
        this.clientsUsersFilterCreatedFrom(),
        this.clientsUsersFilterCreatedTo()
      )
    ) {
      return false;
    }
    if (
      !this.clientUserIsoInDateRange(
        row.lastLogin,
        this.clientsUsersFilterLoginFrom(),
        this.clientsUsersFilterLoginTo()
      )
    ) {
      return false;
    }
    return true;
  }

  private clientUserIsoInDateRange(iso: string | null, from: string, to: string): boolean {
    const fromKey = from.trim();
    const toKey = to.trim();
    if (!fromKey && !toKey) {
      return true;
    }
    const rowKey = iso ? this.localDateKeyFromIso(iso) : null;
    if (!rowKey) {
      return false;
    }
    if (fromKey && rowKey < fromKey) {
      return false;
    }
    if (toKey && rowKey > toKey) {
      return false;
    }
    return true;
  }

  private localDateKeyFromIso(iso: string): string | null {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return null;
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private mapDirectoryClientUserToRow(r: DirectoryClientUserRowApi): ClientUserListRow {
    const doc = r.dateOfCreation;
    const dateCreated =
      doc != null && String(doc).trim() !== '' ? String(doc).trim() : null;
    const lastLoginRaw = r.lastLogin;
    const lastLogin =
      lastLoginRaw != null && String(lastLoginRaw).trim() !== '' ? String(lastLoginRaw).trim() : null;
    const userNameRaw = (r.contactDisplayName ?? '—').trim() || '—';
    const userName =
      r.contactId < 0
        ? resolvePortalClientUserDisplayName(r.contactDisplayName, null, r.email) ?? userNameRaw
        : userNameRaw;
    const companyMasterId = r.companyMasterId != null ? String(r.companyMasterId) : '0';
    const avatarTones = ['purple', 'blue', 'green', 'amber', 'rose'] as const;
    return {
      rowKey:
        r.companyMasterId != null ? `${r.companyMasterId}-${r.contactId}` : `c-${r.contactId}`,
      contactId: r.contactId,
      companyMasterId,
      userName,
      userInitials: this.initialsFromName(userName),
      userAvatarTone: hashPickTone(`cu-${r.contactId}`, avatarTones),
      companyName: (r.companyName ?? '—').trim() || '—',
      companyAvatarTone: hashPickTone(`cuc-${companyMasterId}-${r.contactId}`, avatarTones),
      email: (r.email ?? '—').trim() || '—',
      phone: (r.phone ?? '—').trim() || '—',
      isActive: r.isActive === true,
      dateCreated,
      lastLogin,
    };
  }

  private buildClientUsersLoadKey(page: number): string {
    const parentId = this.effectiveDirectoryParentUserId();
    const actingUserId = this.effectiveDirectoryActingUserIdForContacts() ?? 0;
    return `${parentId}|${actingUserId}|${this.clientsUsersSearchQueryForApi()}|${page}|${this.clientsUsersPageSize()}`;
  }

  async loadDirectoryClientUsers(page = this.clientsUsersPageNumber()): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      return;
    }
    const loadKey = this.buildClientUsersLoadKey(page);
    if (this.clientsUsersInflightLoadKey === loadKey) {
      return;
    }
    this.clientsUsersInflightLoadKey = loadKey;
    const seq = ++this.clientsUsersRequestSeq;
    this.clientsContactsLoading.set(true);
    const actingUserId = this.effectiveDirectoryActingUserIdForContacts();
    const parentId = this.effectiveDirectoryParentUserId();
    const query = this.clientsUsersSearchQueryForApi();
    try {
      const pageResult = await firstValueFrom(
        this.pmDirectoryApi.getClientUsersPage(parentId, page, this.clientsUsersPageSize(), {
          userId: actingUserId,
          q: query || undefined
        })
      );
      if (seq !== this.clientsUsersRequestSeq) {
        return;
      }
      this.clientsUsersPageNumber.set(pageResult.pageNumber < 1 ? 1 : pageResult.pageNumber);
      this.clientsUsersPageSize.set(pageResult.pageSize > 0 ? pageResult.pageSize : this.clientsUsersPageSize());
      const total = Number(pageResult.totalCount);
      this.clientsUsersFilteredTotalCount.set(Number.isFinite(total) && total >= 0 ? Math.trunc(total) : 0);
      this.clientsClientUserRows.set((pageResult.items ?? []).map((r) => this.mapDirectoryClientUserToRow(r)));
    } catch (e: unknown) {
      if (seq !== this.clientsUsersRequestSeq) {
        return;
      }
      toast.error('Could not load client users', { description: this.pmApiHttpErrorDetail(e).slice(0, 240) });
      this.clientsClientUserRows.set([]);
      this.clientsUsersFilteredTotalCount.set(0);
    } finally {
      if (this.clientsUsersInflightLoadKey === loadKey) {
        this.clientsUsersInflightLoadKey = '';
      }
      if (seq === this.clientsUsersRequestSeq) {
        this.clientsContactsLoading.set(false);
      }
    }
  }

  clearClientsUsersSelection(): void {
    this.clientsUsersRowCheck.set({});
    this.clientsUsersBulkStatusDraft.set('');
    this.clientsUsersBulkDeleteConfirmOpen.set(false);
  }

  closeClientsUsersActionMenu(): void {
    this.clientsUsersActionMenuRowKey.set(null);
  }

  /** Prefer Client Store universal id for `/clients/:id` routes; fall back to company master id. */
  private clientListRowRouteId(row: ClientListRow): string | null {
    const uid = (row.directoryClientUniversalId ?? '').trim();
    if (uid.length > 0 && uid !== '0') {
      return uid;
    }
    const id = String(row.id ?? '').trim();
    if (id.length > 0 && id !== '0') {
      return id;
    }
    return null;
  }

  navigateToClientsList() {
    this.router.navigate(['/clients']);
  }

  closeClientsCommunicationFilterPanel(): void {
    this.clientsCommunicationFilterPanelOpen.set(false);
    this.activeDropdown.set(null);
  }

  closeClientsCommunicationEmailDetail(immediate = false): void {
    this.clientsCommunicationEmailDetailPanelReady.set(false);
    if (immediate) {
      this.clientsCommunicationEmailDetailRowId.set(null);
      return;
    }
    window.setTimeout(() => this.clientsCommunicationEmailDetailRowId.set(null), 320);
  }

  async loadClientsCommSmsTemplates(): Promise<void> {
    const parentUserId = this.userContext.firmParentUserIdForApi();
    if (parentUserId <= 0) return;
    try {
      const items = await firstValueFrom(
        this.smsTemplateApi.dropdown(parentUserId).pipe(catchError(() => of([])))
      );
      this.clientsCommSmsTemplates.set(
        (items ?? []).map(t => ({ id: String(t.id), title: t.title, templateType: String(t.templateType) }))
      );
    } catch { /* silently ignore */ }
  }

  clearBulkSmsOtpCountdown(): void {
    this.bulkSmsOtpCountdown.stop();
    this.clientsBulkSmsOtpSecondsLeft.set(null);
  }

  resetClientsCommEmailForm() {
    this.clientsCommEmailToSelected.set({});
    this.clientsCommEmailToSearchQuery.set('');
    this.clientsCommEmailCcChips.set([]);
    this.clientsCommEmailCcDraft.set('');
    this.clientsCommEmailCcShowError.set(false);
    this.clientsCommEmailBccChips.set([]);
    this.clientsCommEmailBccDraft.set('');
    this.clientsCommEmailBccShowError.set(false);
    this.clientsCommEmailSubject.set('');
    this.clientsCommEmailPriority.set('normal');
    this.clientsCommEmailMessage.set('');
    this.clientsCommEmailScheduleDate.set(this.isoDateLocal(this.addDaysLocal(new Date(), 1)));
    const _uk = this.nowInUkForComms();
    const _hhmm = `${_uk.hh}:${_uk.mm}`;
    this.clientsCommEmailScheduleTime.set(_hhmm);
    this.clientsCommEmailRecurring.set('no');
    this.clientsCommEmailRecurringFromDate.set('');
    this.clientsCommEmailRecurringToDate.set('');
    this.clientsCommEmailRecurringTime.set(_hhmm);
    this.clientsCommEmailRecurringFrequency.set('Week');
    this.clientsCommEmailRecurringWeekday.set('Wed');
    this.clientsCommEmailRecurringWeekOfMonth.set('1');
    this.clientsCommEmailRecurringMonthOfYear.set('1');
    this.clientsCommEmailRecurringDayOfMonth.set(1);
    this.clientsCommEmailAttachmentNames.set([]);
    this.clientsCommEmailAttachmentFiles.set([]);
    this.clientsCommSmsAddContact.set('');
    this.clientsCommSmsRecurring.set('no');
    this.clientsCommSmsRecurringFrequency.set('');
    this.clientsCommSmsRecurringWeekday.set('Wed');
    this.clientsCommSmsRecurringWeekOfMonth.set('1');
    this.clientsCommSmsRecurringMonthOfYear.set('1');
    this.clientsCommSmsRecurringDayOfMonth.set(1);
    this.clientsCommSmsTemplateId.set('');
    this.clientsCommSmsBody.set('');
    this.clientsBulkSmsOtpVerified.set(false);
    this.clientsBulkSmsOtpDigits.set(['', '', '', '', '', '']);
    this.clientsBulkSmsOtpPhase.set('send');
    this.clientsBulkSmsOtpSending.set(false);
    this.clientsBulkSmsOtpVerifying.set(false);
    this.clearBulkSmsOtpCountdown();
  }

  openClientsCommunicationEmail() {
    this.closeClientsCommunicationEmailDetail(true);
    this.closeClientsBulkEmailModal();
    this.clientsCommEmailEditingCommunicationId.set(null);
    this.resetClientsCommEmailForm();
    this.ensureCommunicationRecipientsLoaded();
    this.clientsCommEmailModalKind.set('compose');
    this.initClientsCommEmailRte();
    if (this.clientsCommunicationChannel() === 'sms') {
      void this.loadClientsCommSmsTemplates();
    } else {
      void this.applyDefaultClientsCommEmailSignature();
    }
  }

  toggleClientsBulkCcFirstOnly() {
    const next = !this.clientsBulkEmailApplyCcFirstOnly();
    this.clientsBulkEmailApplyCcFirstOnly.set(next);
    if (next) {
      this.clientsBulkEmailApplyCcToAll.set(false);
    }
  }

  toggleClientsBulkCcToAll() {
    const next = !this.clientsBulkEmailApplyCcToAll();
    this.clientsBulkEmailApplyCcToAll.set(next);
    if (next) {
      this.clientsBulkEmailApplyCcFirstOnly.set(false);
    }
  }

  closeClientsBulkEmailModal() {
    if (this.clientsBulkEmailSaving()) {
      return;
    }
    this.clientsBulkEmailModalOpen.set(false);
    this.clientsBulkSmsOtpVerified.set(false);
    this.clientsBulkSmsOtpDigits.set(['', '', '', '', '', '']);
    this.clientsBulkSmsOtpPhase.set('send');
    this.clearBulkSmsOtpCountdown();
    this.clientsBulkEmailQuill = null;
    const d = this.activeDropdown();
    if (
      d === 'clientsBulkEmailToMs' ||
      d === 'clientsBulkEmailClientType' ||
      d === 'clientsBulkEmailPriority'
    ) {
      this.activeDropdown.set(null);
    }
  }

  ensureCommunicationRecipientsLoaded(): void {
    if (this.userContext.practiceContextReady()) {
      void this.loadBulkCommunicationRecipients();
    }
  }

  private mapCommunicationRecipientToBulkOption(
    row: DirectoryCommunicationRecipientApi
  ): ClientsEmailToOption | null {
    const id = String(row.id ?? '').trim();
    const universalId = (row.clientUniversalId ?? '').trim();
    const listId = id || universalId;
    if (!listId) {
      return null;
    }
    const name = this.bulkRecipientLabelFromApiRow(row);
    const contactPersonName = (row.contactPersonName ?? '').trim();
    const yearEndDay = Number(row.yearEndDay ?? 0);
    const yearEndMonth = Number(row.yearEndMonth ?? 0);
    const yearEnd =
      yearEndDay > 0 && yearEndMonth > 0
        ? formatDayMonthInAppOrder(yearEndDay, yearEndMonth)
        : '';
    const address = [row.address1, row.address2, row.city, row.county, row.postCode]
      .map((p) => (p ?? '').trim())
      .filter(Boolean)
      .join(', ');
    return {
      id: listId,
      name,
      clientType: (row.clientType ?? '').trim() || 'Client',
      email: (row.email ?? '').trim(),
      contactPhone: (row.contactPhone ?? '').trim(),
      ...(contactPersonName ? { contactPersonName } : {}),
      directoryClientUniversalId: universalId || undefined,
      businessClientId: (row.businessClientId ?? '').trim() || undefined,
      registrationNumber: (row.registrationNumber ?? '').trim() || undefined,
      sicCode: (row.sicCode ?? '').trim() || undefined,
      website: (row.website ?? '').trim() || undefined,
      yearEnd: yearEnd || undefined,
      address: address || undefined
    };
  }

  private bulkRecipientLabelFromApiRow(row: DirectoryCommunicationRecipientApi): string {
    const name = (row.name ?? '').trim();
    if (name && name !== '—' && name !== 'Unnamed client') {
      return name;
    }
    const person = (row.contactPersonName ?? '').trim();
    if (person) {
      return person;
    }
    const phone = (row.contactPhone ?? '').trim();
    if (phone) {
      return phone;
    }
    const email = (row.email ?? '').trim();
    if (email) {
      return email;
    }
    const uid = (row.clientUniversalId ?? '').trim();
    if (uid) {
      return uid.length > 12 ? `${uid.slice(0, 8)}…` : uid;
    }
    return 'Unnamed client';
  }

  /** Primary line in bulk SMS To list (phone preferred, then company/contact label). */
  bulkRecipientSmsPrimaryLine(opt: ClientsEmailToOption): string {
    const phone = opt.contactPhone.trim();
    if (phone) {
      return phone;
    }
    return opt.name.trim() || 'No mobile on file';
  }

  invalidateDirectoryClientsCache(parentUserId: number, listUserId: number): void {
    this.directoryClientsCache.invalidate(parentUserId, listUserId);
    this.bulkCommunicationRecipientsCacheKey = '';
    this.clientsBulkCommunicationRecipients.set([]);
  }

  async loadBulkCommunicationRecipients(force = false): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      this.clientsBulkCommunicationRecipients.set([]);
      this.bulkCommunicationRecipientsCacheKey = '';
      return;
    }
    const parent = this.effectiveDirectoryParentUserId();
    const listUserId = this.effectiveDirectoryUserIdForClientsList();
    if (parent <= 0 || listUserId <= 0) {
      return;
    }
    const cacheKey = `${parent}:${listUserId}`;
    if (!force && cacheKey === this.bulkCommunicationRecipientsCacheKey && this.clientsBulkCommunicationRecipients().length > 0) {
      return;
    }
    const seq = ++this.bulkCommunicationRecipientsRequestSeq;
    this.clientsBulkCommunicationRecipientsLoading.set(true);
    try {
      const res = await firstValueFrom(
        this.directoryClientsCache.getCommunicationRecipients(parent, listUserId)
      );
      if (seq !== this.bulkCommunicationRecipientsRequestSeq) {
        return;
      }
      const options = (res.items ?? [])
        .map((item) => this.mapCommunicationRecipientToBulkOption(item))
        .filter((o): o is ClientsEmailToOption => o !== null)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      this.clientsBulkCommunicationRecipients.set(options);
      this.bulkCommunicationRecipientsCacheKey = cacheKey;
    } catch (e: unknown) {
      if (seq !== this.bulkCommunicationRecipientsRequestSeq) {
        return;
      }
      this.bulkCommunicationRecipientsCacheKey = '';
      this.clientsBulkCommunicationRecipients.set([]);
      if (this.clientsBulkEmailModalOpen() || this.clientsCommEmailModalKind()) {
        toast.error('Could not load clients for communication', {
          description: this.pmApiHttpErrorDetail(e).slice(0, 240)
        });
      }
    } finally {
      if (seq === this.bulkCommunicationRecipientsRequestSeq) {
        this.clientsBulkCommunicationRecipientsLoading.set(false);
      }
    }
  }

  setClientsWorkspaceSection(section: ClientsWorkspaceSection) {
    if (this.clientsWorkspaceSection() === 'list' && section !== 'list') {
      this.persistClientsListUiToStorage();
      this.clientsSearchQueryWritesSuspended = true;
    } else if (section === 'list') {
      this.clientsSearchQueryWritesSuspended = false;
    }
    if (section !== this.clientsWorkspaceSection()) {
      this.clientsTabSwitching.set(true);
      setTimeout(() => this.clientsTabSwitching.set(false), 250);
    }
    this.clientsWorkspaceSection.set(section);
    if (section === 'list') {
      this.clientsPageView.set('clients');
    }
    if (section !== 'communication') {
      this.closeClientsCommunicationEmailDetail(true);
      this.closeClientsCommunicationFilterPanel();
    }
    if (section !== 'list') {
      this.clientsSortDropdownOpen.set(false);
      this.clientsColumnPanelOpen.set(false);
      this.clientsViewsPanelOpen.set(false);
      this.clientsFilterPanelOpen.set(false);
    }
    this.documentHubPage?.closeDocumentHubPreview();
    this.docHubStore.documentHubOpenFolderId.set(null);
    this.docHubStore.documentHubFilesSearchQuery.set('');
    if (section === 'submissions') {
      this.submissionsPageIndex.set(0);
      void this.loadSubmissionsFromApi();
    }
    if (section === 'letters') {
      this.lettersSearchQuery.set('');
      this.lettersCategoryFilter.set('all');
      void this.loadClientsForBookMeetingPicker();
    }
    if (section === 'aml') {
      this.amlComplianceResultFilter.set(new Set());
      this.amlComplianceCheckTypeFilter.set(new Set());
      this.amlComplianceDateFrom.set('');
      this.amlComplianceDateTo.set('');
      this.amlComplianceViewMode.set('latest');
      this.amlComplianceSearchQuery.set('');
      this.amlCompliancePageIndex.set(0);
      if (this.userContext.practiceContextReady()) {
        void this.loadAmlPerformChecksFromApi();
        void this.loadAmlComplianceDirectoryClients();
      } else {
        this.amlChecksRows.set([]);
        this.amlComplianceSelectedRowId.set(null);
        this.amlComplianceDirectoryClients.set([]);
        this.amlChecksLoading.set(true);
        window.setTimeout(() => this.amlChecksLoading.set(false), 700);
      }
    } else {
      this.amlCheckModalKind.set(null);
      this.amlTrainingModalOpen.set(false);
      this.amlComplianceLinkOpenRowId.set(null);
      this.amlComplianceLinkPickCompanyIds.set([]);
    }
    if (section === 'schedule' && this.userContext.practiceContextReady()) {
      void this.loadClientsEventSchedules();
      void this.loadClientsForBookMeetingPicker();
    }
    if (section === 'communication' && this.userContext.practiceContextReady()) {
      void this.loadCommunicationSectionData();
    }
    if (section === 'list' && this.userContext.practiceContextReady()) {
      void this.loadDirectoryClientsPage(this.clientsDirectoryPageNumber());
    }
  }

  /** Loads directory clients (if needed) then communication rows for Email/SMS tabs. */
  private async loadCommunicationSectionData(): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      this.clientsCommunicationRows.set([]);
      return;
    }
    // Set before the directory/recipients calls below (not just inside
    // loadClientsCommunicationsFromApi's own set(true)), so the Email/SMS loading overlay is
    // already up the moment the generic 500ms tab-switch spinner (clientsTabSwitching) clears,
    // matching the Schedule/Submissions/AML tabs — otherwise the tab sits with no visible loading
    // state for as long as these two calls take, then the spinner only appears right before the
    // final listFirm fetch.
    this.clientsCommunicationLoading.set(true);
    await Promise.all([
      this.clientsDirectoryRows().length === 0 ? this.loadDirectoryClientsPage(1) : Promise.resolve(),
      this.loadBulkCommunicationRecipients()
    ]);
    await this.loadClientsCommunicationsFromApi();
  }

  async loadClientsCommunicationsFromApi(): Promise<void> {
    const parentUserId = this.effectiveDirectoryParentUserId();
    if (parentUserId <= 0) {
      this.clientsCommunicationRows.set([]);
      this.clientsCommunicationTotalCount.set(0);
      return;
    }

    // Load full client name+type map once (non-paginated) so all communication rows resolve correctly.
    // Uses the same Super-Accountant-aware scoping as the Clients grid (effectiveDirectoryUserIdForClientsList)
    // rather than the plain acting-user id — otherwise this lookup only covers clients assigned to the
    // acting user, while communications themselves are loaded firm-wide, so any communication for a client
    // assigned to someone else would fail to resolve a name and fall back to showing the raw client id.
    if (this.commClientsLookup().size === 0) {
      const userId = this.effectiveDirectoryUserIdForClientsList();
      if (userId > 0) {
        try {
          const allClients = await firstValueFrom(
            this.pmDirectoryApi.getClientsOptional(parentUserId, userId)
          );
          const map = new Map<string, { name: string; type: string }>();
          for (const c of allClients.items ?? []) {
            const uid = ((c.clientUniversalId ?? c.clientId) ?? '').trim();
            if (!uid) continue;
            const name = (c.companyName ?? c.contactName ?? '').trim();
            const rawType = (c.companyType ?? '').trim();
            const type = rawType === 'Ltd' ? 'Limited' : rawType;
            map.set(uid, { name, type });
          }
          this.commClientsLookup.set(map);
        } catch { /* non-fatal */ }
      }
    }

    this.clientsCommunicationLoading.set(true);
    try {
      const pageNumber = this.clientsCommunicationPageNumber();
      const channel = this.clientsCommunicationChannel();
      const communicationType = channel === 'sms' ? 2 : 1;
      const result = await firstValueFrom(
        this.pmClientCommsApi.listFirm({
          parentUserId,
          pageNumber,
          pageSize: this.clientsCommunicationPageSize(),
          communicationType
        })
      );

      // Preserve the API's order (most recently scheduled/updated first) — re-sorting here by the
      // displayed date would rank far-future scheduled items above ones just acted on, which is the
      // opposite of what this list should show.
      const mapped = (result.items ?? []).map((item) => this.mapWorkspaceClientCommunication(item));

      this.clientsCommunicationTotalCount.set(result.totalCount ?? 0);
      this.clientsCommunicationRows.set(mapped);
    } catch {
      this.clientsCommunicationRows.set([]);
      this.clientsCommunicationTotalCount.set(0);
    } finally {
      this.clientsCommunicationLoading.set(false);
    }
  }

  private mapWorkspaceClientCommunication(item: ClientCommunicationSummaryApi): ClientsCommunicationRow {
    const uid = (item.clientUniversalId ?? '').trim();
    const clientMatch = this.clientsDirectoryRows().find(
      (r) => (r.directoryClientUniversalId ?? '').trim() === uid
    );
    const lookupEntry = this.commClientsLookup().get(uid);
    // Resolve client type: non-paginated lookup → paginated directory rows fallback.
    const rawType = lookupEntry?.type ?? (clientMatch ? this.clientTypeLabelFromRow(clientMatch) : '');
    const clientType = rawType || '—';
    // Resolve client name: paginated match → non-paginated lookup → API field → graceful placeholder.
    // Deliberately never falls back to the raw uid — a client can be genuinely unresolvable here (e.g.
    // not tagged to this practice's active module in the Client Store) while its communication history
    // still exists, and showing the internal id to the user is worse than a clear "unresolved" label.
    const resolvedName = clientMatch?.name?.trim() || lookupEntry?.name || item.clientName?.trim() || 'Unknown Client';

    const apiStatus = (item.status ?? '').trim().toLowerCase();
    const status: ClientsCommunicationStatus =
      apiStatus === 'sent' || apiStatus === 'scheduled' || apiStatus === 'draft'
        ? apiStatus
        : item.isScheduled === true
          ? 'scheduled'
          : item.isSend === true
            ? 'sent'
            : 'draft';
    const rawPriority = (item.priority ?? '').toString().trim().toLowerCase();
    const priority: ClientsCommunicationPriority = rawPriority === 'high' ? 'high' : rawPriority === 'low' ? 'low' : 'normal';
    const dateTime = this.resolveWorkspaceCommDateTime(item);
    const to = (item.emailTo ?? '').split(',')[0]?.trim() || '';
    const isSms = Number(item.communicationType) === 2;
    const smsPhone = isSms ? to : '';

    return {
      id: String(item.id),
      subject: item.topic?.trim() || 'Untitled communication',
      preview: item.messageBody?.trim() || '',
      clientName: resolvedName,
      clientId: item.clientUniversalId || '—',
      listClientId: clientMatch?.id || `comm-${item.clientUniversalId || item.id}`,
      clientType,
      contactEmail: isSms ? '' : to,
      ...(smsPhone ? { contactPhone: smsPhone } : {}),
      ...(isSms && item.emailTo?.trim() ? { smsNumbers: item.emailTo.trim() } : {}),
      ...(clientMatch?.contactPersonNameOnly?.trim()
        ? { contactPersonName: clientMatch.contactPersonNameOnly.trim() }
        : {}),
      dateTime,
      status,
      doneBy: this.resolveCommDoneBy(item),
      priority,
      recurring: item.scheduleIsRecurring === true,
      communicationKind: isSms ? 'sms' : 'email',
      detailMessage: item.messageBody?.trim() || '',
      attachments: isSms ? [] : parseCommunicationAttachments(item),
      emailCc: isSms ? '' : (item.emailCc ?? '').trim(),
      emailBcc: isSms ? '' : (item.emailBcc ?? '').trim()
    };
  }

  private resolveCommDoneBy(item: ClientCommunicationSummaryApi): string {
    const fromApi =
      (item.createdByDisplayName ?? '').trim() ||
      (item.userDisplayName ?? '').trim();
    if (fromApi) return fromApi;
    const lookupId = item.createdBy ?? item.userId;
    if (lookupId != null && lookupId > 0) {
      const member = this.teamMembersApi().find((m) => m.userId === Number(lookupId));
      const name = (member?.name ?? '').trim();
      if (name) return name;
    }
    return '—';
  }

  /**
   * The firm-wide comms grid must show the date/time the user picked to schedule the send, not the
   * record's save timestamp. `updatedOn`/`createdOn` reflect when the row was last written (server
   * clock at submit time), which always reads as "now" — that previously made every scheduled item
   * look like it reset to the moment it was saved. Prefer `scheduleDate`/`scheduleTime` when present.
   */
  private resolveWorkspaceCommDateTime(item: ClientCommunicationSummaryApi): string {
    if (item.scheduleDate) {
      const datePart = item.scheduleDate.slice(0, 10);
      const rawTime = item.scheduleTime ?? '';
      const timePart = rawTime.length >= 5 ? rawTime.slice(0, 5) : rawTime || '00:00';
      return `${datePart} ${timePart}`;
    }
    return this.workspaceCommDateTime(item.updatedOn ?? item.createdOn);
  }

  private workspaceCommDateTime(input?: string | null): string {
    if (!input) {
      return `${isoDateLocalFrom(new Date())} 09:00`;
    }
    const d = new Date(input);
    if (Number.isNaN(d.getTime())) {
      return `${isoDateLocalFrom(new Date())} 09:00`;
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day} ${hh}:${mm}`;
  }

  isClientsComingSoonWorkspace(): boolean {
    const s = this.clientsWorkspaceSection();
    return s !== 'communication' && s !== 'list' && s !== 'schedule' && s !== 'submissions' && s !== 'letters' && s !== 'aml';
  }

  private pmApiHttpErrorDetail(e: unknown): string {
    if (e instanceof HttpErrorResponse) {
      const body = e.error;
      if (typeof body === 'object' && body && 'detail' in body) {
        return String((body as { detail?: string }).detail ?? e.message);
      }
      return e.message;
    }
    if (e instanceof Error) {
      return e.message;
    }
    return 'Request failed';
  }

  async loadAmlPerformChecksFromApi(): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      return;
    }
    this.amlChecksLoading.set(true);
    try {
      const page = await firstValueFrom(
        this.pmAmlApi.listPerformChecks(
          this.effectiveDirectoryParentUserId(),
          this.effectiveDirectoryUserIdForClientsList(),
          1,
          200
        )
      );
      const rows = page.items.map((item) => mapAmlPerformCheckApiToRow(item));
      this.amlChecksRows.set(rows);
      this.amlComplianceSelectedRowId.set(null);
    } catch (e) {
      toast.error('Could not load AML checks', { description: this.pmApiHttpErrorDetail(e) });
      this.amlChecksRows.set([]);
      this.amlComplianceSelectedRowId.set(null);
    } finally {
      this.amlChecksLoading.set(false);
    }
  }

  private async fetchAmlDirectoryClientPickerOptions(): Promise<AmlClientPickerOption[]> {
    if (!this.userContext.practiceContextReady()) {
      return [];
    }
    const parentUserId = this.effectiveDirectoryParentUserId();
    const userMasterId = this.effectiveDirectoryUserIdForClientsList();
    if (parentUserId <= 0 || userMasterId <= 0) {
      return [];
    }
    try {
      const page = await firstValueFrom(this.directoryClientsCache.getClients(parentUserId, userMasterId, 1, 500));
      return page.items
        .filter((r) => r.isDeleted !== true)
        .map((r) => {
          const idNum = Number(String(r.id).trim());
          const id = Number.isFinite(idNum) && idNum > 0 ? Math.trunc(idNum) : 0;
          const label =
            [r.companyName, r.clientId].find((x) => typeof x === 'string' && x.trim())?.trim() ?? `Client ${String(r.id)}`;
          return { id, label };
        })
        .filter((o) => o.id > 0);
    } catch {
      return [];
    }
  }

  private async loadAmlComplianceDirectoryClients(): Promise<void> {
    const opts = await this.fetchAmlDirectoryClientPickerOptions();
    this.amlComplianceDirectoryClients.set(opts);
  }

  private eventScheduleApiTimeToHhMm(t: string | null | undefined): string {
    if (!t?.trim()) {
      return '00:00';
    }
    const parts = t.trim().split(':');
    const hi = Math.min(23, Math.max(0, parseInt(parts[0] ?? '0', 10) || 0));
    const mi = Math.min(59, Math.max(0, parseInt(parts[1] ?? '0', 10) || 0));
    return `${String(hi).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
  }

  private parseMeetingTypeFromScheduleMessageBody(raw: string | null | undefined): ClientsScheduleMeeting['meetingType'] | null {
    if (!raw?.trim()) {
      return null;
    }
    try {
      const o = JSON.parse(raw) as { _pm?: { mt?: string } };
      const mt = o._pm?.mt;
      if (mt === 'in-person' || mt === 'phone' || mt === 'video' || mt === 'internal') {
        return mt;
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  private inferMeetingTypeFromLocationText(loc: string): ClientsScheduleMeeting['meetingType'] {
    const s = loc.trim().toLowerCase();
    if (!s) {
      return 'in-person';
    }
    if (/\b(phone|call|mobile|tel)\b/.test(s) || s === 'phone') {
      return 'phone';
    }
    if (/(teams|zoom|meet\.google|video|http|\.com\/meet)/.test(s)) {
      return 'video';
    }
    return 'in-person';
  }

  private meetingTypeFromInviteMeta(
    meta: ReturnType<typeof parseScheduleMeetingInviteMeta>
  ): ClientsScheduleMeeting['meetingType'] | null {
    const mt = meta?.meetingType;
    if (mt === 'in-person' || mt === 'phone' || mt === 'video' || mt === 'internal') {
      return mt;
    }
    return null;
  }

  private meetingTypeFromScheduleApiRow(
    row: EventScheduleResponseApi,
    isPracticeInternal: boolean
  ): ClientsScheduleMeeting['meetingType'] {
    // pm-api's MeetingType column is the source of truth once a row has been saved through either
    // UI's create/update flow. The heuristics below (invite JSON / message body / location text)
    // only exist as a fallback for rows saved before that column existed — new data never needs them.
    const fromColumn = this.meetingTypeFromInviteMeta({ meetingType: row.meetingType ?? undefined });
    const fromInvite = this.meetingTypeFromInviteMeta(parseScheduleMeetingInviteMeta(row.invitePeopleJson));
    const resolved =
      fromColumn ??
      fromInvite ??
      this.parseMeetingTypeFromScheduleMessageBody(row.messageBody) ??
      this.inferMeetingTypeFromLocationText(row.location ?? '');
    // Only mark as 'internal' when it's a practice meeting AND no explicit type was found
    if (isPracticeInternal && resolved === 'in-person') {
      return 'internal';
    }
    return resolved;
  }

  /** Strips formatting (hyphens/braces/case) so the same GUID compares equal even if two API
   *  responses serialize it differently (e.g. "D" vs "N" .NET Guid format). */
  private normalizeGuidForCompare(v: string): string {
    return v.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private mapEventScheduleApiRowToMeeting(row: EventScheduleResponseApi, clientRows: ClientListRow[]): ClientsScheduleMeeting {
    const uid = (row.clientUniversalId ?? '').trim();
    const isPracticeInternal =
      uid === PM_SCHEDULE_PRACTICE_INTERNAL_UNIVERSAL_ID || uid.toLowerCase() === PM_SCHEDULE_PRACTICE_INTERNAL_UNIVERSAL_ID.toLowerCase();
    let clientLabel = 'Unknown client';
    let clientListId = '';
    if (!uid || isPracticeInternal) {
      clientLabel = 'Practice';
      clientListId = '';
    } else {
      const normalizedUid = this.normalizeGuidForCompare(uid);
      const match = clientRows.find(
        (r) => this.normalizeGuidForCompare(r.directoryClientUniversalId ?? '') === normalizedUid
      );
      if (match) {
        clientLabel = match.name;
        clientListId = this.clientListRowRouteId(match) ?? match.id;
      } else {
        clientLabel = 'Unknown client';
        clientListId = '';
      }
    }
    const date = (row.startDate ?? '').trim().slice(0, 10) || '1970-01-01';
    const meta = parseScheduleMeetingInviteMeta(row.invitePeopleJson);
    const fullMessage = pmHtmlToText((row.messageBody ?? '').trim()).replace(/\s+/g, ' ').trim() || '—';
    return {
      id: String(row.id),
      subject: (row.subject ?? '').trim() || '(No subject)',
      messagePreview: this.scheduleMessagePreviewFromHtml(row.messageBody),
      clientLabel,
      clientId: clientListId,
      clientUniversalId: isPracticeInternal ? '' : uid,
      date,
      startTime: this.eventScheduleApiTimeToHhMm(row.startTime),
      endTime: this.eventScheduleApiTimeToHhMm(row.endTime),
      meetingType: this.meetingTypeFromScheduleApiRow(row, !uid || isPracticeInternal),
      location: (row.location ?? '').trim() || '—',
      scheduledByUserId: row.createdBy ?? null,
      fromEmail: (row.fromEmail ?? '').trim(),
      toEmail: (row.toEmail ?? '').trim(),
      ccEmail: (row.ccEmail ?? '').trim(),
      fullMessage,
      timezone: (meta?.timezone ?? '').trim(),
      isDraft: row.isSend === false,
    };
  }

  /** Strip HTML from schedule message for table preview under subject. */
  private scheduleMessagePreviewFromHtml(html: string | null | undefined): string {
    const raw = (html ?? '').trim();
    if (!raw || raw === '<p><br></p>') {
      return '—';
    }
    // Shared cleaner (same as <pm-message-preview>): also unwraps {"v":1,"letter":…} bodies and
    // parses without an innerHTML assignment.
    const text = pmMessagePreviewText(raw);
    if (!text) {
      return '—';
    }
    return text.length > 140 ? `${text.slice(0, 137)}…` : text;
  }

  /**
   * Loads meetings for the Schedule tab. With no `options`, re-fetches whatever range is already
   * loaded (or a default ±90-day window on first load) — fetching the practice's entire unfiltered
   * meeting history on every open was the main cause of a slow Schedule tab. Pass `fromDate`/`toDate`
   * to explicitly (re)set the loaded range (see {@link ensureClientsScheduleRangeLoaded}), or
   * `mustInclude` to widen the currently-loaded range just enough to cover one specific date (e.g. a
   * meeting just created/updated outside the default window) without discarding what's already there.
   */
  async loadClientsEventSchedules(options?: { fromDate?: string; toDate?: string; mustInclude?: string }): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      this.clientsScheduleMeetings.set([]);
      return;
    }
    const today = new Date();
    const windowDays = PmWorkspaceComponent.CLIENTS_SCHEDULE_DEFAULT_WINDOW_DAYS;
    const defaultFrom = this.isoDateLocal(this.addDaysLocal(today, -windowDays));
    const defaultTo = this.isoDateLocal(this.addDaysLocal(today, windowDays));
    let fromDate = options?.fromDate ?? this.clientsScheduleLoadedFromDate ?? defaultFrom;
    let toDate = options?.toDate ?? this.clientsScheduleLoadedToDate ?? defaultTo;
    if (options?.mustInclude) {
      if (options.mustInclude < fromDate) fromDate = options.mustInclude;
      if (options.mustInclude > toDate) toDate = options.mustInclude;
    }

    const seq = ++this.clientsScheduleMeetingsRequestSeq;
    this.clientsScheduleMeetingsLoading.set(true);
    try {
      const items = await firstValueFrom(
        this.pmEventSchedulesApi.list(this.effectiveDirectoryParentUserId(), { fromDate, toDate })
      );
      if (seq !== this.clientsScheduleMeetingsRequestSeq) {
        return;
      }
      this.clientsScheduleLoadedFromDate = fromDate;
      this.clientsScheduleLoadedToDate = toDate;
      const rows = this.mergedDirectoryClientRowsForScheduleLabels();
      this.clientsScheduleMeetings.set(items.map((it) => this.mapEventScheduleApiRowToMeeting(it, rows)));
      void this.loadClientsScheduleGrouped();
    } catch (e: unknown) {
      if (seq !== this.clientsScheduleMeetingsRequestSeq) {
        return;
      }
      this.clientsScheduleMeetings.set([]);
      toast.error('Could not load meetings', { description: this.pmApiHttpErrorDetail(e).slice(0, 240) });
    } finally {
      if (seq === this.clientsScheduleMeetingsRequestSeq) {
        this.clientsScheduleMeetingsLoading.set(false);
      }
    }
  }

  /**
   * Fetches the grid's actual page from pm-api's `GET …/event-schedules/grouped` — grouping and
   * pagination both happen server-side. Client, date-range, scheduled-by and Meeting Type all map
   * onto real `PM_EventSchedule` columns (MeetingType added specifically so this filter no longer has
   * to guess — see `044_pm_event_schedule_meeting_type.sql`) so pm-api filters them exactly. Only the
   * client-name half of search has no backing column, so that alone is narrowed further, client-side,
   * on just the page that comes back — a page can render short of `pageSize` rows while it's active.
   * Meeting Type filtering only "sees" rows saved after that column existed: older rows have
   * MeetingType = NULL and won't match any specific value.
   * Call after anything that should change what the grid shows: filters, search (debounced),
   * Group By, or the page/page-size controls.
   */
  async loadClientsScheduleGrouped(): Promise<void> {
    if (!this.userContext.practiceContextReady()) {
      this.clientsScheduleGroupedGroups.set([]);
      this.clientsScheduleGroupedTotalCount.set(0);
      return;
    }

    const clientFilterId = this.clientsScheduleClientFilter().trim();
    let clientUniversalId: string | undefined;
    if (clientFilterId && clientFilterId !== 'all') {
      clientUniversalId = (this.findClientRowForBookMeetingPicker(clientFilterId)?.directoryClientUniversalId ?? '').trim() || undefined;
    }
    const scheduledByRaw = this.clientsScheduleScheduledByFilter().trim();
    const scheduledByNum = Number(scheduledByRaw);
    const scheduledByUserId = scheduledByRaw && scheduledByRaw !== 'all' && Number.isFinite(scheduledByNum) && scheduledByNum > 0
      ? scheduledByNum
      : undefined;
    // Mirrors filteredScheduleMeetings(): the explicit date filter narrows the already-loaded window,
    // it doesn't replace it — so fall back to the loaded bounds (never both unset once loaded once).
    const fromDate = this.clientsScheduleDateFromFilter().trim() || this.clientsScheduleLoadedFromDate || undefined;
    const toDate = this.clientsScheduleDateToFilter().trim() || this.clientsScheduleLoadedToDate || undefined;
    const search = this.clientsScheduleSearchQuery().trim() || undefined;
    const groupMode: 'day' | 'month' = this.clientsScheduleGroupBy() === 'month' ? 'month' : 'day';
    const typeFilter = this.clientsScheduleTypeFilter();
    const meetingType = typeFilter !== 'all' ? typeFilter : undefined;
    const qLower = search?.toLowerCase();

    const seq = ++this.clientsScheduleGroupedRequestSeq;
    this.clientsScheduleMeetingsLoading.set(true);
    try {
      const res = await firstValueFrom(
        this.pmEventSchedulesApi.listGrouped(this.effectiveDirectoryParentUserId(), groupMode, {
          clientUniversalId,
          fromDate,
          toDate,
          scheduledByUserId,
          search,
          meetingType,
          pageNumber: this.clientsSchedulePageNumber(),
          pageSize: this.clientsSchedulePageSize(),
        })
      );
      if (seq !== this.clientsScheduleGroupedRequestSeq) {
        return;
      }
      const rows = this.mergedDirectoryClientRowsForScheduleLabels();
      const groups = res.groups
        .map((g) => ({
          key: g.key,
          items: g.items
            .map((it) => this.mapEventScheduleApiRowToMeeting(it, rows))
            .filter((m) => !qLower || `${m.subject} ${m.clientLabel} ${m.location}`.toLowerCase().includes(qLower))
        }))
        .filter((g) => g.items.length > 0);
      this.clientsScheduleGroupedGroups.set(groups);
      this.clientsScheduleGroupedTotalCount.set(res.totalCount);
    } catch (e: unknown) {
      if (seq !== this.clientsScheduleGroupedRequestSeq) {
        return;
      }
      this.clientsScheduleGroupedGroups.set([]);
      this.clientsScheduleGroupedTotalCount.set(0);
      toast.error('Could not load meetings', { description: this.pmApiHttpErrorDetail(e).slice(0, 240) });
    } finally {
      if (seq === this.clientsScheduleGroupedRequestSeq) {
        this.clientsScheduleMeetingsLoading.set(false);
      }
    }
  }

  private isoDateLocal(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private addDaysLocal(d: Date, n: number): Date {
    const x = new Date(d.getTime());
    x.setDate(x.getDate() + n);
    return x;
  }

  resetBookMeetingFormForCreate(): void {
    const now = new Date();
    this.bookMeetingEditId.set(null);
    this.bookMeetingDate.set(this.isoDateLocal(now));
    this.bookMeetingStart.set('09:00');
    this.bookMeetingDurationPreset.set('10 min');
    this.applyBookMeetingDurationToEnd();
    this.bookMeetingSubject.set('');
    this.bookMeetingClientId.set('');
    this.bookMeetingClientPanelOpen.set(false);
    this.bookMeetingClientSearch.set('');
    this.bookMeetingClientFlyoutStyle.set(null);
    this.bookMeetingType.set('in-person');
    this.bookMeetingLocation.set('');
    this.bookMeetingCcChips.set([]);
    this.bookMeetingCcDraft.set('');
    this.bookMeetingMessage.set('');
    this.bookMeetingToChips.set([]);
    this.bookMeetingToDraft.set('');
    this.bookMeetingTimezone.set('(UTC+06:00) Dhaka');
    this.bookMeetingInviteSelected.set({});
    this.bookMeetingInvitePanelOpen.set(false);
    this.bookMeetingInviteSearch.set('');
    this.bookMeetingInviteFlyoutStyle.set(null);
    this.bookMeetingToShowError.set(false);
    this.bookMeetingCcShowError.set(false);
    this.bookMeetingTimePanel.set(null);
    this.bookMeetingTimeFlyoutStyle.set(null);
    this.bookMeetingQuill = null;
    const fromLabel = this.userContext.email().trim() || 'Capium Accountant';
    this.bookMeetingFrom.set(fromLabel);
  }

  openClientsBookMeetingModal() {
    this.resetBookMeetingFormForCreate();
    this.clientsBookMeetingModalOpen.set(true);
    void this.loadClientsForBookMeetingPicker();
    void this.loadBookMeetingTeamInviteOptions();
    void this.applyDefaultBookMeetingSignature();
  }

  /** Pre-fills a brand new meeting's message with the firm's saved Email Signature
   *  (Settings → Branding & Portal). Not applied when editing an existing meeting —
   *  that path loads its own saved message instead. */
  private async applyDefaultBookMeetingSignature(): Promise<void> {
    const parentUserId = this.effectiveDirectoryParentUserId();
    if (parentUserId <= 0) return;
    const template = await firstValueFrom(
      this.pmSettingsApi.getBrandingTemplate(parentUserId).pipe(catchError(() => of(null)))
    );
    const signature = template?.emailSignature?.trim();
    if (!signature) return;
    this.bookMeetingMessage.set(this.clientsCommEmailSignatureToHtml(signature));
  }

  closeClientsScheduleRowActions(): void {
    this.clientsScheduleRowActionsOpenId.set(null);
    this.clientsScheduleRowActionsPos.set(null);
  }

  closeBookMeetingClientPanel(): void {
    this.bookMeetingClientPanelOpen.set(false);
    this.bookMeetingClientSearch.set('');
    this.bookMeetingClientFlyoutStyle.set(null);
  }

  applyBookMeetingDurationToEnd(): void {
    const add = SCHEDULE_DURATION_PRESET_MINUTES[this.bookMeetingDurationPreset()];
    if (add == null) {
      return;
    }
    const startM = parseHHmmToMinutes(this.bookMeetingStart());
    if (startM == null) {
      return;
    }
    let endM = startM + add;
    if (endM > SCHEDULE_DAY_END_MINUTES) {
      endM = SCHEDULE_DAY_END_MINUTES;
    }
    this.bookMeetingEnd.set(formatMinutesAsHHmm(endM));
  }

  closeBookMeetingInvitePanel(): void {
    this.bookMeetingInvitePanelOpen.set(false);
    this.bookMeetingInviteSearch.set('');
    this.bookMeetingInviteFlyoutStyle.set(null);
  }

  async loadBookMeetingTeamInviteOptions(): Promise<void> {
    const parentUserId = this.userContext.firmParentUserIdForApi();
    const actingUserId = this.userContext.actingUserMasterIdForApi();
    if (parentUserId <= 0 || actingUserId <= 0) {
      this.bookMeetingInviteOptions.set([]);
      return;
    }
    try {
      const members = await firstValueFrom(
        this.pmTeamApi.listMembers(parentUserId, actingUserId, { includeInactive: false })
      );
      const rows = (members ?? [])
        .filter((m) => (m.status ?? '').toString().toLowerCase() === 'active')
        .map((m, index) => this.mapTeamMemberToBookMeetingInvite(m, index));
      this.bookMeetingInviteOptions.set(rows);
    } catch {
      this.bookMeetingInviteOptions.set([]);
    }
  }

  private mapTeamMemberToBookMeetingInvite(row: TeamMemberRowApi, index: number): BookMeetingInviteOption {
    const name = (row.name ?? '').trim() || `User ${row.userId}`;
    const parts = name.split(/\s+/).filter(Boolean);
    const initials =
      parts.length >= 2
        ? `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase()
        : (name.slice(0, 2) + '?').slice(0, 2).toUpperCase();
    const tones: BookMeetingInviteOption['tone'][] = ['amber', 'blue', 'violet', 'green'];
    return {
      id: `tm-${row.userId}`,
      name,
      email: (row.email ?? '').trim() || undefined,
      initials,
      tone: tones[index % tones.length]!
    };
  }

  /** Closes the Book Meeting invite / client panels when clicking outside them. */
  @HostListener('document:click', ['$event'])
  onDocumentClickCloseBookMeetingPanels(ev: MouseEvent): void {
    if (this.bookMeetingInvitePanelOpen()) {
      const path =
        typeof ev.composedPath === 'function' ? ev.composedPath() : ev.target ? [ev.target] : [];
      const insideInvite = path.some(
        (t) =>
          t instanceof HTMLElement &&
          typeof t.closest === 'function' &&
          (t.closest('.clients-book-invite-ms') || t.closest('.clients-book-invite-panel'))
      );
      if (!insideInvite) {
        this.closeBookMeetingInvitePanel();
      }
    }
    if (this.bookMeetingClientPanelOpen()) {
      const path =
        typeof ev.composedPath === 'function' ? ev.composedPath() : ev.target ? [ev.target] : [];
      const insideClient = path.some(
        (t) =>
          t instanceof HTMLElement &&
          typeof t.closest === 'function' &&
          (t.closest('.clients-book-client-ms') || t.closest('.clients-book-client-panel'))
      );
      if (!insideClient) {
        this.closeBookMeetingClientPanel();
      }
    }
    if (this.bookMeetingTimePanel() !== null) {
      const path =
        typeof ev.composedPath === 'function' ? ev.composedPath() : ev.target ? [ev.target] : [];
      const insideTime = path.some(
        (t) =>
          t instanceof Element &&
          typeof t.closest === 'function' &&
          (!!t.closest('.clients-book-time-dd') || !!t.closest('.clients-book-time-dd-flyout'))
      );
      if (!insideTime) {
        this.bookMeetingTimePanel.set(null);
        this.bookMeetingTimeFlyoutStyle.set(null);
      }
    }
    if (this.clientsScheduleRowActionsOpenId() !== null) {
      const path =
        typeof ev.composedPath === 'function' ? ev.composedPath() : ev.target ? [ev.target] : [];
      const insideActions = path.some(
        (t) => t instanceof HTMLElement && typeof t.closest === 'function' && t.closest('.clients-schedule-row-actions')
      );
      if (!insideActions) {
        this.closeClientsScheduleRowActions();
      }
    }
    if (this.clientsListRowActionsOpenId() !== null) {
      const path =
        typeof ev.composedPath === 'function' ? ev.composedPath() : ev.target ? [ev.target] : [];
      const insideActions = path.some(
        (t) => t instanceof HTMLElement && typeof t.closest === 'function' && t.closest('.clients-list-row-actions')
      );
      if (!insideActions) {
        this.closeClientsListRowActions();
      }
    }
  }

  /** Loads HMRC-style submission dashboard rows from pm-api (Capium 3 `SubmissionDashboardPM`). */
  async loadSubmissionsFromApi(): Promise<void> {
    const parent = this.effectiveDirectoryParentUserId();
    if (parent <= 0) {
      this.submissionsRows.set([]);
      this.submissionsLoading.set(false);
      toast.error('Submissions unavailable', {
        description: 'Set directory parent user id (environment or practice context) to load submissions.'
      });
      return;
    }

    const seq = ++this.submissionsRequestSeq;
    this.submissionsLoading.set(true);
    const clientF = this.submissionsClientFilter();
    const statusF = this.submissionsStatusFilter();
    const from = this.submissionsDateFrom().trim();
    const to = this.submissionsDateTo().trim();

    const statusForApi = statusF === 'all' ? '' : statusF;

    try {
      const res = await firstValueFrom(
        this.pmSubmissionsApi.querySubmissions({
          accountantId: String(parent),
          companyId: clientF === 'all' ? '' : clientF,
          clientType: '',
          status: statusForApi,
          rangeFrom: from || null,
          rangeTo: to || null
        })
      );
      if (seq !== this.submissionsRequestSeq) {
        return;
      }
      const rows = res.items.map((item) => this.mapSubmissionsApiRow(item, statusF, to));
      this.submissionsRows.set(rows);
    } catch (e: unknown) {
      if (seq !== this.submissionsRequestSeq) {
        return;
      }
      this.submissionsRows.set([]);
      toast.error('Could not load submissions', { description: this.pmApiHttpErrorDetail(e).slice(0, 240) });
    } finally {
      if (seq === this.submissionsRequestSeq) {
        this.submissionsLoading.set(false);
      }
    }
  }

  private mapSubmissionsApiRow(
    api: SubmissionsRowApi,
    statusFilter: string,
    submittedOnFallback: string
  ): ClientsSubmissionsRow {
    const fmt = (n: number | null | undefined) => {
      const v = n ?? 0;
      return v === 0 ? '—' : String(v);
    };

    let typeLabel = 'Mixed';
    if ((api.ctCount ?? 0) > 0) {
      typeLabel = 'CT';
    } else if ((api.sa100Count ?? 0) > 0) {
      typeLabel = 'SA100';
    } else if ((api.sa800Count ?? 0) > 0) {
      typeLabel = 'SA800';
    } else if ((api.sa900Count ?? 0) > 0) {
      typeLabel = 'SA900';
    } else if ((api.bkVatCount ?? 0) > 0) {
      typeLabel = 'MTD-VAT';
    } else if ((api.bkCisCount ?? 0) > 0) {
      typeLabel = 'BK-CIS';
    } else if ((api.apCount ?? 0) > 0) {
      typeLabel = 'AP';
    }

    let status: ClientsSubmissionsRow['status'];
    if (statusFilter === 'all') {
      status = (api.totalCount ?? 0) > 0 ? 'submitted' : 'pending';
    } else {
      status = statusFilter as ClientsSubmissionsRow['status'];
    }

    const submittedOn =
      submittedOnFallback.trim().length >= 10 ? submittedOnFallback.trim().slice(0, 10) : undefined;

    return {
      id: String(api.id ?? '').trim() || `row-${Math.random().toString(36).slice(2)}`,
      clientName: api.name?.trim() ? String(api.name) : '—',
      clientId: api.clientId?.trim() ? String(api.clientId) : '—',
      type: typeLabel,
      status,
      ct: fmt(api.ctCount),
      ap: fmt(api.apCount),
      sa100: fmt(api.sa100Count),
      sa800: fmt(api.sa800Count),
      sa900: fmt(api.sa900Count),
      mtdVat: fmt(api.bkVatCount),
      bkCis: fmt(api.bkCisCount),
      pr11d: fmt(api.prP11DCount),
      prP9d: fmt(api.prP9DCount),
      prP46: fmt(api.prP46Count),
      prFps: fmt(api.prPrfpsCount),
      prEps: fmt(api.prEpsCount),
      prEyu: fmt(api.prEyusCount),
      total: fmt(api.totalCount),
      submittedOn
    };
  }

  private submissionsNumericCell(v: string): number {
    const s = String(v ?? '').trim();
    if (s === '—' || s === '-' || s === '') {
      return -1;
    }
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : -1;
  }

  private submissionsSortCompare(
    a: ClientsSubmissionsRow,
    b: ClientsSubmissionsRow,
    key: ClientsSubmissionsSortKey
  ): number {
    switch (key) {
      case 'clientName':
        return a.clientName.localeCompare(b.clientName, undefined, { sensitivity: 'base' });
      case 'clientId':
        return a.clientId.localeCompare(b.clientId, undefined, { sensitivity: 'base' });
      case 'type':
        return a.type.localeCompare(b.type, undefined, { sensitivity: 'base' });
      case 'ct':
        return this.submissionsNumericCell(a.ct) - this.submissionsNumericCell(b.ct);
      case 'ap':
        return this.submissionsNumericCell(a.ap) - this.submissionsNumericCell(b.ap);
      case 'sa100':
        return this.submissionsNumericCell(a.sa100) - this.submissionsNumericCell(b.sa100);
      case 'sa800':
        return this.submissionsNumericCell(a.sa800) - this.submissionsNumericCell(b.sa800);
      case 'sa900':
        return this.submissionsNumericCell(a.sa900) - this.submissionsNumericCell(b.sa900);
      case 'mtdVat':
        return this.submissionsNumericCell(a.mtdVat) - this.submissionsNumericCell(b.mtdVat);
      case 'bkCis':
        return this.submissionsNumericCell(a.bkCis) - this.submissionsNumericCell(b.bkCis);
      case 'pr11d':
        return this.submissionsNumericCell(a.pr11d) - this.submissionsNumericCell(b.pr11d);
      case 'prP9d':
        return this.submissionsNumericCell(a.prP9d) - this.submissionsNumericCell(b.prP9d);
      case 'prP46':
        return this.submissionsNumericCell(a.prP46) - this.submissionsNumericCell(b.prP46);
      case 'prFps':
        return this.submissionsNumericCell(a.prFps) - this.submissionsNumericCell(b.prFps);
      case 'prEps':
        return this.submissionsNumericCell(a.prEps) - this.submissionsNumericCell(b.prEps);
      case 'prEyu':
        return this.submissionsNumericCell(a.prEyu) - this.submissionsNumericCell(b.prEyu);
      case 'total':
        return this.submissionsNumericCell(a.total) - this.submissionsNumericCell(b.total);
      default:
        return 0;
    }
  }

  lettersStatPadded(n: number): string {
    return String(Math.max(0, n)).padStart(2, '0');
  }

  setLettersMainTab(tab: 'templates' | 'sent') {
    this.lettersMainTab.set(tab);
    this.lettersSearchQuery.set('');
  }

  setLettersCategoryFilter(cat: 'all' | ClientsLetterTemplateRow['category']) {
    this.lettersCategoryFilter.set(cat);
  }

  setLettersSearchQuery(v: string) {
    this.lettersSearchQuery.set(v);
  }

  lettersCategoryBadgeLabel(cat: ClientsLetterTemplateRow['category']): string {
    const m: Record<ClientsLetterTemplateRow['category'], string> = {
      engagement: 'Engagement Letter',
      proposal: 'Proposal',
      disengagement: 'Disengagement',
      general: 'General'
    };
    return m[cat];
  }

  lettersSentStatusLabel(status: ClientsLetterSentRow['status']): string {
    const m: Record<ClientsLetterSentRow['status'], string> = {
      signed: 'Signed',
      pending: 'Pending',
      expired: 'Expired',
      sent: 'Sent'
    };
    return m[status];
  }

  lettersTemplateSelected(id: string): boolean {
    return !!this.lettersTemplateSelection()[id];
  }

  setLettersTemplateSelected(id: string, checked: boolean) {
    this.lettersTemplateSelection.update((prev) => ({ ...prev, [id]: checked }));
  }

  lettersToggleSelectAllTemplates(checked: boolean) {
    const vis = this.lettersFilteredTemplates();
    this.lettersTemplateSelection.update((prev) => {
      const next = { ...prev };
      for (const r of vis) {
        next[r.id] = checked;
      }
      return next;
    });
  }

  lettersToggleTemplateEmail(id: string) {
    this.lettersTemplates.update((rows) =>
      rows.map((r) => (r.id === id ? { ...r, emailNotify: !r.emailNotify } : r))
    );
  }

  openLettersGenerateModal() {
    this.lettersGenDocumentName.set('');
    this.lettersGenClientId.set('');
    this.lettersGenDocumentType.set('');
    this.lettersGenTemplateName.set('');
    this.lettersGenerateModalOpen.set(true);
  }

  setClientDetailTab(tab: string) {
    this.clientDetailTab.set(tab);
  }

  toggleClientDetailMetricPanel(panel: 'kpi' | 'deadlinesHealth' | 'tasksHealth') {
    if (panel === 'kpi') {
      this.clientDetailShowKpi.update((v) => !v);
      return;
    }
    if (panel === 'deadlinesHealth') {
      const on = this.clientDetailShowDeadlinesHealth();
      if (on) {
        this.clientDetailShowDeadlinesHealth.set(false);
      } else {
        this.clientDetailShowDeadlinesHealth.set(true);
        this.clientDetailShowTasksHealth.set(false);
      }
      return;
    }
    const onTasks = this.clientDetailShowTasksHealth();
    if (onTasks) {
      this.clientDetailShowTasksHealth.set(false);
    } else {
      this.clientDetailShowTasksHealth.set(true);
      this.clientDetailShowDeadlinesHealth.set(false);
    }
  }

  setClientDetailActionsFilter(filter: 'overdue' | 'dueSoon') {
    this.clientDetailActionsFilter.set(filter);
  }

  toggleClientDetailActivityExpanded() {
    this.clientDetailActivityExpanded.update((v) => !v);
  }

  setClientDetailActivityCategory(category: 'All' | 'Tasks' | 'Clients' | 'Deadlines' | 'Invoices' | 'Emails' | 'Documents' | 'Portal' | 'AML') {
    this.clientDetailActivityCategory.set(category);
  }

  clientDetailDeadlineHealthPct(row: ClientListRow): number {
    const total = row.openRequestsCount;
    if (total <= 0) return 0;
    const overdue = row.overdueTasksCount;
    return Math.max(0, Math.min(100, Math.round(((total - overdue) / total) * 100)));
  }

  clientDetailTasksHealthScore(row: ClientListRow): number {
    const total = row.openTasksCount;
    if (total <= 0) return 0;
    const overdue = row.overdueTasksCount;
    return Math.max(0, Math.min(100, Math.round(((total - overdue) / total) * 100)));
  }

  clientDetailCompanyType(row: ClientListRow): string {
    const parts = row.refLine.split('·');
    return parts.length > 1 ? parts[parts.length - 1].trim() : 'Limited';
  }

  /** Client names are stored/returned however Companies House or the user typed them (often
   *  ALL-CAPS) — display them in normal Title Case regardless. Plain CSS text-transform can't do
   *  this from an already-uppercase source (capitalize only touches the first letter of each
   *  word, it never lowercases the rest), so this normalizes case in the string itself. */
  clientNameDisplay(name: string): string {
    return name.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }

  /** Two-letter avatar initials (e.g. "Ajaved Inc Ltd" → "AI"), matching the /preview-table
   *  pattern — the Client column previously showed only the first letter. */
  clientInitials(name: string): string {
    return name
      .split(/\s+/)
      // Skip punctuation-only words — "RAJEEV & ASHA LTD" otherwise initials to "R&".
      .filter((p) => /[a-z0-9]/i.test(p))
      .map((p) => p.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }

  /** Client code / internal ref (before · in ref line). */
  clientCodeFromRow(row: ClientListRow): string {
    const first = (row.refLine.split('·')[0] ?? '').trim();
    if (first && first !== '—') {
      return first;
    }
    const ref = (row.internalRef ?? '').trim();
    return ref || '—';
  }

  clientDetailServiceLabel(svc: string): string {
    return svc === 'CT' ? 'CT600' : svc;
  }

  clientDetailDemoPhone(_clientId: string): string {
    return '—';
  }

  setSettingActive(label: string, skipAssignConfirm = false) {
    if (
      !skipAssignConfirm &&
      this.activeSettingItem() === 'Services' &&
      this.activeServiceTab() === 'Assign Services' &&
      label !== 'Services' &&
      this.hasUnsavedChanges()
    ) {
      this.pendingAssignLeaveSettingLabel = label;
      this.pendingAssignLeaveServiceTab = null;
      this.showAssignClientsLeaveConfirmModal.set(true);
      return;
    }
    if (label !== this.activeSettingItem()) {
      this.settingsTabSwitching.set(true);
      setTimeout(() => this.settingsTabSwitching.set(false), 500);
    }
    this.activeSettingItem.set(label);
    if (label === 'Services') {
      this.loadSettingsServicesCatalog();
      if (this.activeServiceTab() === 'Service Packages') {
        this.loadServicePackagesForTab();
      }
      if (this.activeServiceTab() === 'Assign Services') {
        this.loadAssignmentsMatrixForTab();
      }
    }
    if (label === 'Automation') {
      this.loadAutomationSettings();
    }
    if (label === 'Branding & Portal') {
      // A fresh section loads itself; this covers clicking Branding & Portal again.
      this.brandingSection?.loadBrandingSettings();
    }
    if (label === 'Integrations') {
      // A fresh section loads itself; this covers clicking Integrations again.
      this.integrationsSection?.loadIntegrationSettings();
    }
    if (label === 'Security & Login Activity') {
      // A fresh section loads itself; this covers clicking Security & Login Activity again.
      this.securitySection?.loadSecuritySessionPolicy();
      this.securitySection?.loadDocumentMasterPassword();
    }
    if (label === 'Notifications') {
      // A fresh section loads itself; this covers clicking Notifications again.
      this.notificationsSection?.loadNotificationPreferences();
      this.notificationsSection?.loadClientAutoCommsPause();
    }
    if (label === 'Custom Fields') {
      if (this.activeCustomFieldTab() === 'Custom Fields') {
        this.loadCustomFieldsSettings();
      } else {
        this.loadCustomLabelsSettings();
      }
    }
    if (label === 'Template Library') {
      // Only reaches an existing tab holder (Template Library clicked again); a fresh one loads its tab itself.
      this.templateLibraryTab?.refreshTemplateLibrarySection();
    }
    if (label === 'Team & Permissions') {
      this.loadTeamAndPermissions();
    }
    if (label === 'Manage Floating Menu') {
      // A fresh section loads itself; this covers clicking Manage Floating Menu again.
      void this.floatingMenuSection?.loadFloatingMenuAssignments();
    }
  }

  effectivePracticeParentUserId(): number {
    return this.pmUserContext.firmParentUserIdForApi();
  }

  effectiveActingUserId(): number {
    return this.pmUserContext.actingUserMasterIdForApi();
  }

  ensurePmContextLoaded(): void {
    this.pmUserContext.hydrateFromAuthToken();
  }

  /** Loads team members once (if needed) and sets `profile` from the acting user row (same ids as Team API). */
  async ensureWorkspaceProfileFromTeam(): Promise<void> {
    this.ensurePmContextLoaded();
    const parent = this.effectivePracticeParentUserId();
    const acting = this.effectiveActingUserId();
    if (parent <= 0 || acting <= 0) {
      return;
    }
    if (this.teamMembersApi().length > 0) {
      this.applyProfileFromActingMember(this.teamMembersApi(), acting);
      return;
    }
    try {
      // Full roster (no forUserId): Add Client manager/partner dropdowns read `teamMembersApi`; a single-user
      // fetch left that signal empty. Still skip assigned-client counts for a light response.
      const members = await firstValueFrom(
        this.pmTeamApi.listMembers(parent, acting, {
          includeInactive: true,
          includeAssignedClientCounts: false
        })
      );
      this.teamMembersApi.set(members ?? []);
      this.applyProfileFromActingMember(members ?? [], acting);
    } catch {
      /* keep placeholder profile until Team loads successfully */
    }
  }

  private applyProfileFromActingMember(members: TeamMemberRowApi[], actingUserId: number): void {
    const vm = buildActingUserProfileVm(members, actingUserId);
    if (!vm) {
      return;
    }
    this.profile.set(vm);
    this.userContext.setDisplayName(vm.name);
  }

  /**
   * Refetches the acting user from pm-api (light `forUserId` list) so the navbar matches dbo.UserMaster
   * after profile personal-details save. Patches {@link teamMembersApi} when that roster is already loaded.
   */
  private async refreshWorkspaceProfileFromTeamLight(): Promise<void> {
    this.ensurePmContextLoaded();
    const parent = this.effectivePracticeParentUserId();
    const acting = this.effectiveActingUserId();
    if (parent <= 0 || acting <= 0) {
      return;
    }
    try {
      const members = await firstValueFrom(
        this.pmTeamApi.listMembers(parent, acting, {
          includeInactive: true,
          includeAssignedClientCounts: false,
          forUserId: acting
        })
      );
      this.applyProfileFromActingMember(members ?? [], acting);
      const row = members?.[0];
      if (!row) {
        return;
      }
      this.teamMembersApi.update((list) => {
        const i = list.findIndex((m) => m.userId === row.userId);
        if (i < 0) {
          return list;
        }
        const next = [...list];
        next[i] = row;
        return next;
      });
    } catch {
      /* keep existing navbar profile */
    }
  }

  async loadTeamAndPermissions(): Promise<void> {
    this.ensurePmContextLoaded();
    const parentUserId = this.effectivePracticeParentUserId();
    const actingUserId = this.effectiveActingUserId();
    if (parentUserId <= 0 || actingUserId <= 0) {
      this.teamApiError.set('Something went wrong.');
      return;
    }

    this.teamApiLoading.set(true);
    this.teamApiError.set(null);
    try {
      const members = await firstValueFrom(this.pmTeamApi.listMembers(parentUserId, actingUserId, { includeInactive: true }));
      this.teamMembersApi.set(members ?? []);
      this.applyProfileFromActingMember(members ?? [], actingUserId);
      // The section is on screen by the time the team call returns; it loads its own role matrices.
      await this.teamPermissionsSection?.loadAllPermissionsMatrices();
    } catch (e) {
      this.teamApiError.set(this.formatPmApiError(e));
    } finally {
      this.teamApiLoading.set(false);
    }
  }

  private userTypeFromRoleLabel(label: string): number {
    const t = (label ?? '').trim().toLowerCase();
    if (t.includes('super')) return 2;
    if (t.includes('accountant')) return 3;
    return 5;
  }

  async onTeamMemberRoleChange(userId: number, roleLabel: string): Promise<void> {
    this.ensurePmContextLoaded();
    const parentUserId = this.effectivePracticeParentUserId();
    const actingUserId = this.effectiveActingUserId();
    const userType = this.userTypeFromRoleLabel(roleLabel);
    try {
      const updated = await firstValueFrom(this.pmTeamApi.updateMember(parentUserId, actingUserId, userId, { userType }));
      this.teamMembersApi.update((list) => list.map((m) => (m.userId === userId ? updated : m)));
      toast.success('Role updated');
    } catch (e) {
      toast.error('Could not update role', { description: this.formatPmApiError(e).slice(0, 220) });
    }
  }

  roleMatrixColumnLabel(roleKey: PermissionRoleMatrixApi['roleKey']): string {
    const labels: Record<PermissionRoleMatrixApi['roleKey'], string> = {
      'super-accountant': 'Super Accountant',
      accountant: 'Accountant',
      staff: 'Staff',
    };
    return labels[roleKey];
  }

  loadAutomationSettings(): void {
    this.automationLoading.set(true);
    this.automationError.set(null);
    this.pmSettingsApi.getAutomationSettings(this.effectiveDirectoryParentUserId()).subscribe({
      next: (model) => {
        this.automationSettings.set(model);
        this.automationLoading.set(false);
      },
      error: (err) => {
        this.automationError.set(this.formatPmApiError(err));
        this.automationLoading.set(false);
      }
    });
  }

  private normalizeOptionalText(value: string): string | null {
    const v = value.trim();
    return v.length > 0 ? v : null;
  }

  private restorePrimaryBrandColor() {
    let stored: string | null = null;
    try {
      stored = pmStorageGet(PmWorkspaceComponent.primaryColorStorageKey);
    } catch {
      stored = null;
    }
    const hex = this.normalizeHexColor(stored ?? '') ?? this.defaultPrimaryBrandColor;
    this.primaryBrandColor.set(hex);
    this.applyPrimaryBrandColorToDocument(hex);
  }

  private restorePortalBrandingPrimaryColor() {
    // Keep the client portal branding primary colour on the brand purple default
    // while the field is readonly (beta) so it stays aligned with the portal preview.
    this.portalBrandingPrimaryColor.set(this.defaultPrimaryBrandColor);
  }

  private restoreEmailHeaderColor() {
    let stored: string | null = null;
    try {
      stored = pmStorageGet(PmWorkspaceComponent.emailHeaderColorStorageKey);
    } catch {
      stored = null;
    }
    const hex = this.normalizeHexColor(stored ?? '') ?? this.defaultPrimaryBrandColor;
    this.emailHeaderColor.set(hex);
  }

  private applyPrimaryBrandColorToDocument(hex: string) {
    if (typeof document === 'undefined') {
      return;
    }
    const root = document.documentElement;
    root.style.setProperty('--capium-purple', hex);
    root.style.setProperty('--capium-purple-dark', this.shadeHexColor(hex, -0.18));
    root.style.setProperty('--capium-purple-light', this.mixTowardWhite(hex, PmWorkspaceComponent.primaryLightWhiteFraction));
    // DS-16: the --ds-color-primary-* scale (tokens.css) shifts with the custom brand colour too,
    // so a firm that sets one stays consistent once styling migrates onto the --ds- tokens.
    // -light's own 0.82 white-mix doesn't line up with any of the 50/100/300/500 steps, so it gets
    // its own alias instead of being forced onto one of them (that would shift the visible colour).
    root.style.setProperty('--ds-color-primary-50', this.mixTowardWhite(hex, 0.95));
    root.style.setProperty('--ds-color-primary-100', this.mixTowardWhite(hex, 0.9));
    root.style.setProperty('--ds-color-primary-300', this.mixTowardWhite(hex, 0.55));
    root.style.setProperty('--ds-color-primary-500', this.mixTowardWhite(hex, 0.2));
    root.style.setProperty('--ds-color-primary-600', hex);
    root.style.setProperty('--ds-color-primary-700', this.shadeHexColor(hex, -0.18));
    root.style.setProperty('--ds-color-primary-light', this.mixTowardWhite(hex, PmWorkspaceComponent.primaryLightWhiteFraction));
  }

  private normalizeHexColor(value: string): string | null {
    const v = value.trim();
    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
      return v.toLowerCase();
    }
    if (/^#[0-9a-fA-F]{3}$/.test(v)) {
      const r = v[1];
      const g = v[2];
      const b = v[3];
      return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
    }
    return null;
  }

  /** Blend color toward white so “light” stays visibly light even for very dark primaries. */
  private mixTowardWhite(hex: string, whiteFraction: number): string {
    const n = parseInt(hex.slice(1), 16);
    let r = (n >> 16) & 255;
    let g = (n >> 8) & 255;
    let b = n & 255;
    const t = Math.min(1, Math.max(0, whiteFraction));
    r = Math.round(r * (1 - t) + 255 * t);
    g = Math.round(g * (1 - t) + 255 * t);
    b = Math.round(b * (1 - t) + 255 * t);
    return `#${[r, g, b]
      .map((x) => Math.min(255, Math.max(0, x)).toString(16).padStart(2, '0'))
      .join('')}`;
  }

  private shadeHexColor(hex: string, amount: number): string {
    const n = parseInt(hex.slice(1), 16);
    let r = (n >> 16) & 255;
    let g = (n >> 8) & 255;
    let b = n & 255;
    if (amount < 0) {
      const f = 1 + amount;
      r = Math.round(Math.max(0, Math.min(255, r * f)));
      g = Math.round(Math.max(0, Math.min(255, g * f)));
      b = Math.round(Math.max(0, Math.min(255, b * f)));
    } else {
      r = Math.round(Math.max(0, Math.min(255, r + (255 - r) * amount)));
      g = Math.round(Math.max(0, Math.min(255, g + (255 - g) * amount)));
      b = Math.round(Math.max(0, Math.min(255, b + (255 - b) * amount)));
    }
    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
  }

  /** Quote-escapes a CSV cell when it contains separators, quotes or newlines. */
  private csvCell(value: string): string {
    const s = value ?? '';
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }

  // ===== Onboarding & KYC firm-level integration ===========================

  /** True if pm-api firm-level criteria endpoints have the parent + companyMaster needed. */
  private templateLibraryFirmCriteriaConfigured(): boolean {
    return this.templateLibraryContext.firmCriteriaConfigured();
  }
  /** Human-readable label for a UI client-type key (see the context service). */
  clientTypeLabelFor(t: OnboardingKycClientType | RiskAssessmentClientType): string {
    return this.templateLibraryContext.clientTypeLabelFor(t);
  }


  private templateLibraryTrustedPreviewHtml(raw: string): SafeHtml {
    return this.domSanitizer.bypassSecurityTrustHtml(pmSanitizeHtml(templateLibraryPreparePreviewHtml(raw)));
  }

  private templateLibraryTrustedRawHtml(raw: string): SafeHtml {
    return this.domSanitizer.bypassSecurityTrustHtml(pmSanitizeHtml(templateLibraryRawToHtml(raw)));
  }
}
